import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { PoundSterling, MapPin, X, Trophy, Calendar, ClipboardList, Users, ArrowDownUp, FileDown, FileClock, ClockCheck, Sheet, CircleX, ChevronRight, ChevronDown, Sparkles, TriangleAlert, Siren, ChartNoAxesCombined } from 'lucide-react';
import jsPDF from 'jspdf';
import { useConfig, type ProfitabilityConfig, type ProfitabilityReportScheduleConfig } from '../contexts/ConfigContext';
import { normaliseBodyStyleClass, normaliseTonnageClass } from '../utils/costRateBasis';
import { formatYmd } from '../utils/ukTime';
import { useDemoMode } from '../contexts/DemoModeContext';
import { useProfitabilitySnapshot } from '../contexts/ProfitabilitySnapshotContext';
import { standardiseVehicleId } from '../utils/vehicleIdStandardise';
import { API_ENDPOINTS } from '../config/api';
import { STATUS_COLORS, CHART_PALETTE } from '../config/colors';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import ProfitVehicleDayMapModal from '../components/profitability/ProfitVehicleDayMapModal';
import {
  mapOrgDepotsFromApiPayload,
  orderRoleOnVehicleMap,
  type OrgDepotForMap,
} from '../utils/mapOrderVehicleRole';
import LineChart from '../components/LineChart';
import MonthPicker from '../components/ui/month-picker';
import QueryBar, {
  PROFIT_AGENT_INPUT_PLACEHOLDER,
  PROFIT_HIGHLIGHTED_CHIP_WORDS_RE,
  PROFIT_MORE_SUGGESTED_PROMPTS,
  PROFIT_PRIMARY_SUGGESTED_PROMPTS,
} from '../components/investigator/QueryBar';
import { useTypingPlaceholder } from '../hooks/useTypingPlaceholder';
import { streamProfitAgentQuery } from '../services/profitabilityAgentApi';
import type { ProfitAgentQueryResponse } from '../types/profitabilityAgent';
import ProfitAnswerCard from '../components/profitAgent/ProfitAnswerCard';
import ProfitAgentEvidenceStrip from '../components/profitAgent/ProfitAgentEvidenceStrip';
import ProfitAnswerSkeleton from '../components/profitAgent/ProfitAnswerSkeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { Button } from '@/components/ui/button';
import { fetchMondayBrief } from '../services/profitabilityBriefApi';
import type { ProfitAgentDrilldown } from '../types/profitabilityAgent';
import type { MondayBriefApiResponse, MondayBriefUi } from '../types/profitabilityBrief';
import { useTruthProfitability, useTruthUtilisation } from '../config/featureFlags';
import {
  fetchTruthMergedCosts,
  fetchTruthProfitabilityConfig,
  fetchTruthMergedOrders,
  fetchTruthCustomerProfitabilityDaily,
  fetchTruthStatus,
  fetchTruthTelematicsDeliveryStops,
  fetchTruthTelematicsRouteSegments,
  truthLastComputedMeta,
} from '../services/profitabilityTruthClient';
import { mapMergedOrderRowsToProfitRecords } from '../utils/mergedOrderProfitRecords';
import {
  extractLookupOrderIdsFromMergedCostRow,
  extractMergedCostOrderBreakdown,
  extractOrderIdsFromMergedCostRow,
  formatOrderIdForOrdersTooltip,
  orderIdShowsSameDayLabel,
  SAME_DAY_JOB_SUFFIX,
  isLikelyEntityUlid,
  orderIdMatchesLookupSelection,
} from '../utils/profitabilityOrderDisplay';
import { fetchTruthUtilisationVehicleDays } from '../services/utilisationTruthClient';
import { useProfitabilityDateRange } from '../hooks/useProfitabilityDateRange';
import { useTruthProfitabilityPanel } from '../hooks/useTruthProfitabilityPanel';
import {
  buildUtilisationLookup,
  buildMondayBriefUtilisationByVehicle,
  computeFleetUtilisationScoreMean,
  resolveUtilisationForSummaryRow,
  type MondayBriefVehicleUtilisation,
} from '../utils/utilisationSummaryJoin';
import type { TruthUtilisationVehicleDayRow } from '../types/utilisationTruth';
import type {
  ProfitabilityAssumptionsConfig,
  ProfitabilityTonnageClass,
  ProfitabilityVehicleType,
  TruthCustomerDailyRow,
  TruthCustomerMonthlyRow,
} from '../types/profitabilityTruth';
import {
  depreciationRatesFromTruthConfig,
  formatVehicleStandingLine,
  resolveStandingCostForMergedRows,
} from '../utils/mondayBriefStandingCost';
import {
  getYesterdayLocalCompact,
  parseProfitabilityDateRangeSuffix,
  toBatchDateRangeSuffix,
  formatLocalDateIso,
} from '../utils/profitabilityDateRange';
import {
  mapMondayBriefApiToUi,
} from '../utils/mapMondayBriefApi';
import {
  buildStoredProfitDrilldown,
  readStoredProfitDrilldown,
  writeStoredProfitDrilldown,
  type StoredProfitDrilldown,
} from '../utils/profitDrilldownNavigation';
import { writeStoredProfitAgentPeriod } from '../utils/profitAgentPeriodStorage';
import {
  consumeMondayBriefFocus,
  MONDAY_BRIEF_FOCUS_EVENT,
} from '../utils/mondayBriefNavigation';
import { useTab } from '../contexts/TabContext';
import { otherWorkLineText } from '../utils/profitPageFromTruth';

/** Title-case a string (e.g. "st austell" -> "St Austell") */
function titleCase(str: string | null | undefined): string {
  const s = typeof str === 'string' ? str : String(str ?? '');
  const trimmed = s.trim();
  if (!trimmed) return '';
  return trimmed.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

/** Title-case a city label; preserve "UK" as-is */
function formatCityLabel(city?: string | null | number | object): string {
  const value = city == null ? '' : typeof city === 'string' ? city : String(city);
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (trimmed.toLowerCase() === 'uk') return 'UK';
  return titleCase(trimmed);
}

/** Format origin and destination as "City A -> City B" for the Route column */
function formatRoute(origin?: string | null | number | object, destination?: string | null | number | object): string {
  const o = formatCityLabel(origin);
  const d = formatCityLabel(destination);
  if (!o && !d) return '';
  if (!o) return d;
  if (!d) return o;
  return `${o} → ${d}`;
}

/** Date-only (YYYY-MM-DD) for grouping/filtering when row.date may include time (e.g. "2026-01-01 00:01:48") */
function getDateOnly(dateStr: string): string {
  if (!dateStr || typeof dateStr !== 'string') return dateStr ?? '';
  return dateStr.length >= 10 ? dateStr.slice(0, 10) : dateStr;
}

/** Drop schedule vehicle IDs that are not in the merged_costs vehicle list (e.g. partial reg typos like "AR0"). */
/** Primary schedule recipient: required + simple RFC-like shape (validated on blur and before Save). */
function schedulePrimaryEmailValidationError(raw: string): string | null {
  const t = (raw ?? '').trim();
  if (!t) return 'Primary email is required.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t)) return 'Enter a valid email address.';
  return null;
}

function pruneScheduleVehicleFilterIds(ids: string[], vehicleOptions: string[]): string[] {
  if (!ids.length || !vehicleOptions.length) return ids;
  const allowed = new Set(
    vehicleOptions
      .map((v) => standardiseVehicleId(v) || String(v).trim())
      .filter((v) => Boolean(v) && v !== '—'),
  );
  return ids.filter((id) => {
    const std = standardiseVehicleId(id) || String(id).trim();
    return Boolean(std) && allowed.has(std);
  });
}

/** Normalise telematics date (DD/MM/YYYY or YYYY-MM-DD) to YYYY-MM-DD */
function normalizeTelematicsDate(dateStr: string): string {
  if (!dateStr || typeof dateStr !== 'string') return '';
  const trimmed = dateStr.trim();
  const ddmmyyyy = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (ddmmyyyy) {
    const [, d, m, y] = ddmmyyyy;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  return trimmed.length >= 10 ? trimmed.slice(0, 10) : trimmed;
}

/** Normalise any date string to YYYY-MM-DD for range comparison (handles DD/MM/YYYY, YYYY-MM-DD, ISO) */
function toComparableDate(dateStr: string): string {
  if (!dateStr || typeof dateStr !== 'string') return '';
  const normalized = normalizeTelematicsDate(dateStr.trim());
  if (normalized) return normalized;
  const d = getDateOnly(dateStr);
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : '';
}


/** Start of profitability reporting period (inclusive). End is computed dynamically as "yesterday" for a live setup. */
const PROFITABILITY_START_DATE = '2026-01-01';

/**
 * Optional fixed end date for testing with historical batch CSVs on S3.
 * Set to null for production / truth-layer live data (end = yesterday, local calendar).
 * Example for a fixed pilot window: '2026-04-19'.
 */
const PROFITABILITY_END_DATE: string | null = null;

/**
 * Returns the date-range suffix for batch profitability APIs (e.g. 20260101_to_20260526).
 * Uses PROFITABILITY_START_DATE through PROFITABILITY_END_DATE, or yesterday when end is null.
 */
function getProfitabilityDateRange(): string {
  const start = PROFITABILITY_START_DATE.replace(/-/g, '');
  const end = PROFITABILITY_END_DATE
    ? PROFITABILITY_END_DATE.replace(/-/g, '')
    : getYesterdayLocalCompact();
  if (start > end) {
    return `${start}_to_${start}`;
  }
  return `${start}_to_${end}`;
}

/**
 * Rasterise the public wordmark SVG so jsPDF can embed it via addImage (PNG).
 * Returns null if fetch/canvas fails (PDF still generates without the logo).
 */
async function loadProfitabilityLogoForPdf(): Promise<string | null> {
  try {
    const base = import.meta.env.BASE_URL.endsWith('/')
      ? import.meta.env.BASE_URL
      : `${import.meta.env.BASE_URL}/`;
    const res = await fetch(`${base}images/landing/logo/zeefleet-logo-full.svg`);
    if (!res.ok) return null;
    const svgText = await res.text();
    const blob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
    const objUrl = URL.createObjectURL(blob);
    try {
      const img = new Image();
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('logo image load failed'));
        img.src = objUrl;
      });
      const w = img.naturalWidth || 700;
      const h = img.naturalHeight || 140;
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.drawImage(img, 0, 0);
      return canvas.toDataURL('image/png');
    } finally {
      URL.revokeObjectURL(objUrl);
    }
  } catch {
    return null;
  }
}

async function loadSparklesIconForPdf(): Promise<string | null> {
  try {
    const svgText = renderToStaticMarkup(
      <Sparkles width={20} height={20} stroke="#a7f3d0" strokeWidth={2} fill="none" />,
    );
    const blob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
    const objUrl = URL.createObjectURL(blob);
    try {
      const img = new Image();
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('sparkles image load failed'));
        img.src = objUrl;
      });
      const w = img.naturalWidth || 20;
      const h = img.naturalHeight || 20;
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.drawImage(img, 0, 0);
      return canvas.toDataURL('image/png');
    } finally {
      URL.revokeObjectURL(objUrl);
    }
  } catch {
    return null;
  }
}

// Simple Info Icon SVG for inline use

// Table Header Info Icon with fixed positioning to escape overflow clipping
const TableHeaderInfoIcon = ({ title }: { title: string }) => (
  <span 
    className="ml-1 align-middle inline-block group relative" 
    tabIndex={0}
    onMouseEnter={(e) => {
      const tooltip = e.currentTarget.querySelector('.header-tooltip') as HTMLElement;
      if (tooltip) {
        const rect = e.currentTarget.getBoundingClientRect();
        tooltip.style.left = `${rect.left + rect.width / 2}px`;
        tooltip.style.top = `${rect.top - 8}px`;
      }
    }}
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-3 w-3 text-ring inline-block"
      fill="none"
      viewBox="0 0 20 20"
      stroke="currentColor"
      aria-hidden="true"
    >
      <circle cx="10" cy="10" r="9" strokeWidth="2" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 7v2m0 4h.01" />
    </svg>
    <span className="header-tooltip fixed -translate-x-1/2 -translate-y-full z-dropdown hidden group-hover:block group-focus:block bg-popover text-xs text-popover-foreground rounded px-2 py-1 border border-border shadow-lg whitespace-nowrap pointer-events-none">
      {title}
    </span>
  </span>
);
interface ProfitRecord {
  orderId: string;
  vehicleId: string;
  date: string;
  revenue: number;
  cost: number;
  profit: number;
  currency: string;
  status: string;
  journeySplitCount?: number; // Optional, for UI only
  // Optional fuel-specific fields
  fleetId?: string;
  transactionId?: string;
  driverId?: string;
  quantity?: number;
  unitPrice?: number;
  // Driver cost (Daily Profitability): from Qargo CSV
  metrics_distance_total?: number; // metres
  destination_timestamp?: string;
  origin_end_timestamp?: string;
  // Route (from orders CSV on S3 / merged_orders)
  origin_city?: string;
  destination_city?: string;
  origin_postal_code?: string;
  destination_postal_code?: string;
  // Merged orders CSV columns (for Qargo's Order section)
  origin_end_timestamp_local?: string;
  destination_end_timestamp_local?: string;
  vehicle_assigned?: string;
  vehicle_pickup?: string;
  /** Hub depot number from getnotes (truth layer) for map Handoff pin. */
  handoff_depot?: string;
  vehicle_observed?: string;
  service_level_name?: string;
  // Geocoded (from orders_geocode.py; used to relate orders to route segments)
  origin_latitude?: number | null;
  origin_longitude?: number | null;
  destination_latitude?: number | null;
  destination_longitude?: number | null;
  /** 'postcode_centroid' = approximate position filled from the postcode. */
  destination_coords_source?: 'postcode_centroid';
  /** 0, 15, or 25 when revenue was imputed (PALLETLINE £15 / HAZCHEM £25); 0 = actual */
  revenue_imputed?: number;
  /** Source integration type from merged_orders (e.g. PALLETLINE, HAZCHEM) */
  order_import_integration_type?: string;
  /** Order goods: quantity (e.g. pallets) and weight (kg) from merged_orders */
  goods_quantity?: string | number;
  goods_weight?: string | number;
  /** Customer from merged_orders CSV (for Customer Profitability section) */
  customer?: string;
  /** Step 4 allocation method when order cost has been computed */
  allocation_method?: 'marginal' | 'fallback_equal_split';
}

/** Telematics daily miles per vehicle per day (supatrak_daily_miles.csv) */
interface TelematicsDailyMiles {
  vehicle: string;
  date: string;
  daily_miles: number;
  num_stops: number;
  driving_hours?: number;
  waiting_hours?: number;
}

/** Telematics delivery stop (supatrak_delivery_stops.csv) */
interface TelematicsDeliveryStop {
  AssetName: string;
  day_date: string;
  stop_number: number;
  arrival_time: string;
  departure_time: string;
  duration_minutes: number;
  postcode: string;
  latitude: number;
  longitude: number;
  stop_type: string;
  miles_to_next_stop: number;
}

/** Pre-processed per-vehicle-per-day profitability row from merged_costs.csv */
interface MergedCostRow {
  date: string;
  vehicle: string;
  daily_miles: number;
  driving_hours: number;
  waiting_hours: number;
  /** Mileage-based fuel £ from profitability pipeline (preferred over jigsaw) */
  fuel_cost?: number;
  driver_cost: number;
  /** Legacy Jigsaw fuel; ignored when fuel_cost is present */
  jigsaw_fuel_cost?: number;
  jigsaw_fuel_quantity?: number;
  jigsaw_fuel_unitPrice?: number;
  revenue: number;
  /** Cost class used at compute time (body style or tonnage). */
  vehicle_type?: string;
  /** Number of orders contributing to this row's revenue (from backend pipeline) */
  order_count?: number;
  pickup_count?: number;
  delivery_count?: number;
  /** Comma-separated order_id values (from backend pipeline) */
  order_ids?: string;
  /** Comma-separated revenue per order, same order as order_ids (e.g. "15,15,518.99") */
  order_revenues?: string;
  /** Comma-separated imputed revenue flags per order, same order as order_ids: "0", "15", or "25" */
  order_revenue_imputed?: string;
  /** Truth-mode per-order breakdown (preferred over order_ids when present) */
  revenue_breakdown?: Array<{
    order_entity_id: string;
    order_name?: string;
    revenue: number;
    imputed?: boolean;
    attribution_tier?:
      | 'declared_same_day'
      | 'note_and_gps'
      | 'note_only'
      | 'planned'
      | 'unknown';
    leg_role?: 'collection' | 'trunk' | 'delivery' | 'unknown';
    handoff_depot?: string;
  }>;
}

/** Keep only plausible order identifiers (prevents CSV/address fragments from entering dropdown/filter state). */
function isLikelyOrderIdentifier(value: string): boolean {
  const s = String(value || '').trim();
  if (!s) return false;
  if (s.length < 4 || s.length > 48) return false;
  // Reject CSV fragments and multiline chunks.
  if (/[\n\r\t,]/.test(s)) return false;
  // Order IDs/names are token-like in this report (e.g. WT263080).
  return /^[A-Za-z0-9][A-Za-z0-9._/-]*$/.test(s);
}

function extractOrderIdsFromMergedField(raw?: string): string[] {
  if (!raw || typeof raw !== 'string') return [];
  return raw
    .split(',')
    .map((id: string) => id.trim())
    .filter((id) => isLikelyOrderIdentifier(id));
}

/** Daily Summary / top tables: merged_costs row includes any selected order name or id (exact, case-insensitive, or id contains token). */
function orderDisplayIdMatchesSelection(orderId: string, selectedOrderIds: string[]): boolean {
  const id = (orderId ?? '').trim();
  if (!id || selectedOrderIds.length === 0) return false;
  const idLower = id.toLowerCase();
  return selectedOrderIds.some((sel) => {
    const s = sel.trim();
    if (!s) return false;
    const sLower = s.toLowerCase();
    return id === s || idLower === sLower || id.includes(s) || idLower.includes(sLower);
  });
}

function mergedCostRowMatchesSelectedOrders(row: MergedCostRow, selectedOrderIds: string[]): boolean {
  if (selectedOrderIds.length === 0) return true;
  const ids = extractOrderIdsFromMergedCostRow(row);
  if (ids.length === 0) return false;
  return ids.some((id) => orderDisplayIdMatchesSelection(id, selectedOrderIds));
}

/** Order id with amber "(same-day job)" suffix when the display id is a ULID. */
function OrderIdWithSameDayLabel({ orderId }: { orderId: string }) {
  return (
    <>
      {orderId}
      {orderIdShowsSameDayLabel(orderId) && (
        <span className="text-warning"> {SAME_DAY_JOB_SUFFIX}</span>
      )}
    </>
  );
}

/**
 * When Lookup has selected orders, keep only matching ids/revenues on a vehicle-day.
 * Without a selection, return the full breakdown (unique display ids).
 */
function scopeOrderBreakdownToLookup(
  breakdown: {
    orderIds: string[];
    orderRevenues: string[];
    orderRevenueImputed: string[];
    orderSameDayFlags: boolean[];
    pickupOrderIds?: string[];
    deliveryOrderIds?: string[];
    pickupCount?: number;
    deliveryCount?: number;
  },
  selectedOrderIds: string[],
): {
  orderIds: string[];
  orderRevenues: string[];
  orderRevenueImputed: string[];
  orderSameDayFlags: boolean[];
  pickupOrderIds: string[];
  deliveryOrderIds: string[];
  pickupCount: number;
  deliveryCount: number;
  matchedRevenue: number | null;
} {
  const pickupAll = breakdown.pickupOrderIds ?? [];
  const deliveryAll = breakdown.deliveryOrderIds ?? breakdown.orderIds;
  if (selectedOrderIds.length === 0) {
    return {
      ...breakdown,
      pickupOrderIds: pickupAll,
      deliveryOrderIds: deliveryAll,
      pickupCount: breakdown.pickupCount ?? pickupAll.length,
      deliveryCount: breakdown.deliveryCount ?? deliveryAll.length,
      matchedRevenue: null,
    };
  }
  const orderIds: string[] = [];
  const orderRevenues: string[] = [];
  const orderRevenueImputed: string[] = [];
  const orderSameDayFlags: boolean[] = [];
  let matchedRevenue = 0;
  for (let i = 0; i < breakdown.orderIds.length; i++) {
    const id = breakdown.orderIds[i]!;
    if (!orderDisplayIdMatchesSelection(id, selectedOrderIds)) continue;
    orderIds.push(id);
    const revStr = breakdown.orderRevenues[i] ?? '';
    orderRevenues.push(revStr);
    orderRevenueImputed.push(breakdown.orderRevenueImputed[i] ?? '0');
    orderSameDayFlags.push(breakdown.orderSameDayFlags[i] ?? false);
    const rev = Number(revStr);
    if (Number.isFinite(rev)) matchedRevenue += rev;
  }
  const pickupOrderIds = pickupAll.filter((id) => orderDisplayIdMatchesSelection(id, selectedOrderIds));
  const deliveryOrderIds = deliveryAll.filter((id) => orderDisplayIdMatchesSelection(id, selectedOrderIds));
  return {
    orderIds,
    orderRevenues,
    orderRevenueImputed,
    orderSameDayFlags,
    pickupOrderIds,
    deliveryOrderIds,
    pickupCount: pickupOrderIds.length,
    deliveryCount: deliveryOrderIds.length,
    matchedRevenue,
  };
}

function mergedCostRowMatchesLookup(
  row: MergedCostRow,
  selectedOrderIds: string[],
  selectedCustomers: string[],
  orderIdToCustomerLower: Map<string, string>,
): boolean {
  const hasOrder = selectedOrderIds.length > 0;
  const hasCustomer = selectedCustomers.length > 0;
  if (!hasOrder && !hasCustomer) return true;

  if (hasOrder && mergedCostRowMatchesSelectedOrders(row, selectedOrderIds)) return true;
  if (!hasCustomer) return false;

  const ids = extractOrderIdsFromMergedField(row.order_ids);
  if (ids.length === 0) return false;
  const selectedLower = selectedCustomers.map((c) => c.trim().toLowerCase()).filter(Boolean);
  return ids.some((id) => {
    const c = (orderIdToCustomerLower.get(id) || '').trim();
    if (!c) return false;
    return selectedLower.includes(c);
  });
}

/** Step 4 monthly customer profitability row (customer_profitability_monthly_*.csv). */
interface CustomerMonthlyProfitabilityRow {
  period: string; // YYYY-MM
  customer_name: string;
  order_count: number;
  total_revenue_gbp: number;
  total_allocated_cost_gbp: number;
  total_allocated_miles: number;
  profit_gbp: number;
  profit_margin_pct: number;
  avg_revenue_per_order_gbp?: number;
  avg_cost_per_order_gbp?: number;
  avg_profit_per_order_gbp?: number;
  cost_per_mile_gbp?: number;
}

function mapCustomerMonthlyProfitabilityRows(
  rows: Array<TruthCustomerMonthlyRow | TruthCustomerDailyRow | Record<string, unknown>>,
): CustomerMonthlyProfitabilityRow[] {
  return rows.map((r) => ({
    period: String(r.period || '').trim(),
    customer_name: String(r.customer_name || '').trim(),
    order_count: Number(r.order_count) || 0,
    total_revenue_gbp: Number(r.total_revenue_gbp) || 0,
    total_allocated_cost_gbp: Number(r.total_allocated_cost_gbp) || 0,
    total_allocated_miles: Number(r.total_allocated_miles) || 0,
    profit_gbp: Number(r.profit_gbp) || 0,
    profit_margin_pct: Number(r.profit_margin_pct) || 0,
    avg_revenue_per_order_gbp: Number(r.avg_revenue_per_order_gbp) || 0,
    avg_cost_per_order_gbp: Number(r.avg_cost_per_order_gbp) || 0,
    avg_profit_per_order_gbp: Number(r.avg_profit_per_order_gbp) || 0,
    cost_per_mile_gbp: Number(r.cost_per_mile_gbp) || 0,
  }));
}

interface CustomerProfitabilitySummaryRow {
  deliveryDate: string;
  customer: string;
  orderCount: number;
  totalQuantity: number;
  totalWeight: number;
  totalRevenue: number;
  totalAllocatedCost: number;
  totalAllocatedMiles: number;
  costPerMileGbp: number;
  totalProfit: number;
  profitMarginPct: number;
  currency: string;
}

/** Distinct order IDs on a daily-summary row (Set from aggregation, or optional array fallback). */
function collectOrderIdsFromDailySummaryRow(r: { orderIds?: Set<string> | string[] }): Set<string> {
  const out = new Set<string>();
  const raw = r.orderIds;
  if (raw instanceof Set) {
    raw.forEach((id) => {
      const s = String(id ?? '').trim();
      if (s) out.add(s);
    });
  } else if (Array.isArray(raw)) {
    raw.forEach((id) => {
      const s = String(id ?? '').trim();
      if (s) out.add(s);
    });
  }
  return out;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function parseYYYYMMDDToDate(yyyymmdd: string): Date | null {
  const s = String(yyyymmdd ?? '').trim();
  if (!/^\d{8}$/.test(s)) return null;
  const y = parseInt(s.slice(0, 4), 10);
  const m = parseInt(s.slice(4, 6), 10);
  const d = parseInt(s.slice(6, 8), 10);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  const dt = new Date(y, m - 1, d);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function getDemoCustomerProfitabilityMonthKeys(): string[] {
  // Use the same underlying API date-range as the report so the demo table always has matching rows.
  const range = getProfitabilityDateRange();
  const [startRaw, endRaw] = range.split('_to_');
  const start = parseYYYYMMDDToDate(startRaw);
  const end = parseYYYYMMDDToDate(endRaw);
  if (!start || !end) return [];

  const out: string[] = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  const endMonthStart = new Date(end.getFullYear(), end.getMonth(), 1);
  while (cursor <= endMonthStart) {
    out.push(`${cursor.getFullYear()}-${pad2(cursor.getMonth() + 1)}`);
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return out;
}

function buildDemoCustomerProfitabilityRows(
  monthKeys: string[],
  customerCount = 64,
): CustomerMonthlyProfitabilityRow[] {
  const out: CustomerMonthlyProfitabilityRow[] = [];
  for (let mi = 0; mi < monthKeys.length; mi++) {
    const period = monthKeys[mi];
    const monthIndex = mi;
    for (let ci = 0; ci < customerCount; ci++) {
      const customerIndex = ci + 1;
      const customer_name = `Customer ${pad2(customerIndex)}`;

      // Deterministic-but-varied figures per (month, customer)
      const order_count = 8 + ((monthIndex * 7 + customerIndex * 11) % 52); // 8-59
      const milesPerOrder = 250 + ((monthIndex * 13 + customerIndex * 29) % 1800); // 250-2049
      const total_allocated_miles = order_count * milesPerOrder;

      const revenuePerOrder = 3500 + ((monthIndex * 17 + customerIndex * 23) % 24000); // 3500-27500
      const total_revenue_gbp = order_count * revenuePerOrder;

      const profit_margin_pct = 6 + ((monthIndex * 5 + customerIndex * 3) % 18); // 6-23
      const totalProfit = total_revenue_gbp * (profit_margin_pct / 100);
      const total_allocated_cost_gbp = total_revenue_gbp - totalProfit;

      const cost_per_mile_gbp =
        total_allocated_miles > 0 ? total_allocated_cost_gbp / total_allocated_miles : 0;

      out.push({
        period,
        customer_name,
        order_count,
        total_revenue_gbp: Math.round(total_revenue_gbp),
        total_allocated_cost_gbp: Math.round(total_allocated_cost_gbp),
        total_allocated_miles: Math.round(total_allocated_miles),
        profit_gbp: Math.round(totalProfit),
        profit_margin_pct: Number(profit_margin_pct.toFixed(1)),
        avg_revenue_per_order_gbp: Number((total_revenue_gbp / order_count).toFixed(2)),
        avg_cost_per_order_gbp: Number((total_allocated_cost_gbp / order_count).toFixed(2)),
        avg_profit_per_order_gbp: Number((totalProfit / order_count).toFixed(2)),
        cost_per_mile_gbp: Number(cost_per_mile_gbp.toFixed(4)),
      });
    }
  }
  return out;
}

function buildDemoProfitabilityDataset(): { mergedRows: MergedCostRow[]; orderRows: ProfitRecord[] } {
  const range = getProfitabilityDateRange();
  const [startRaw, endRaw] = range.split('_to_');
  const start = parseYYYYMMDDToDate(startRaw);
  const end = parseYYYYMMDDToDate(endRaw);
  if (!start || !end) return { mergedRows: [], orderRows: [] };

  const vehicleCount = 28;
  const customerCount = 64;
  const cities = [
    'Cambridge',
    'Peterborough',
    'Huntingdon',
    'Bedford',
    'Milton Keynes',
    'Northampton',
    'Leicester',
    'Nottingham',
    'Birmingham',
    'Coventry',
    'Luton',
    'Ipswich',
  ];

  const mergedRows: MergedCostRow[] = [];
  const orderRows: ProfitRecord[] = [];
  let orderSeq = 1;

  const d = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  while (d <= end) {
    const dateStr = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
    const dayIndex = Math.floor((d.getTime() - start.getTime()) / (24 * 3600 * 1000));
    const dayOfWeek = d.getDay(); // 0..6
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

    for (let vi = 0; vi < vehicleCount; vi++) {
      const vehicleId = `DEMO-VHC-${String(vi + 1).padStart(3, '0')}`;
      const baseDemand = isWeekend ? 0.72 : 1;
      const activitySeed = ((dayIndex * 37 + vi * 19) % 100) / 100;
      const tripCount = Math.max(
        1,
        Math.round((2 + ((vi + dayIndex) % 6)) * baseDemand * (0.75 + activitySeed * 0.7)),
      );

      // Vary utilisation/mileage by vehicle and day so the Daily Utilisation calendar has a broad spread
      // of low/medium/high days across the whole period.
      const miles = Math.round(
        (80 + ((vi * 29 + dayIndex * 17) % 680)) * (isWeekend ? 0.6 : 1.0),
      );
      const drivingHours = Number((miles / (22 + ((vi + dayIndex) % 16))).toFixed(2));
      const waitingHours = Number(
        (0.6 + ((vi * 5 + dayIndex * 3) % 28) / 12).toFixed(2),
      );
      const fuelCostBase = Number(
        (miles * (0.26 + ((vi + dayIndex) % 9) / 100)).toFixed(2),
      );
      const driverCostBase = Number(
        (drivingHours * 23 + waitingHours * 16 + miles * 0.05).toFixed(2),
      );

      const orderIds: string[] = [];
      const orderRevenues: string[] = [];
      const orderImputed: string[] = [];
      let totalRevenue = 0;

      // Inject realistic loss days: around 20–30% of (vehicle, day) combinations run at a loss.
      const lossFlag =
        isWeekend ||
        ((vi + dayIndex) % 7 === 0) ||
        ((vi * 11 + dayIndex * 5) % 17 === 0);

      for (let oi = 0; oi < tripCount; oi++) {
        const orderId = `DEMO-ORD-${String(orderSeq++).padStart(6, '0')}`;
        const rev = Math.round(
          (420 + ((vi * 41 + dayIndex * 23 + oi * 11) % 2100)) *
            (isWeekend ? 0.9 : 1.08),
        );
        totalRevenue += rev;
        orderIds.push(orderId);
        orderRevenues.push(String(rev));
        orderImputed.push('0');

        const customerIndex = ((vi * 13 + dayIndex * 7 + oi * 3) % customerCount) + 1;
        const cityFrom = cities[(vi + oi + dayIndex) % cities.length]!;
        const cityTo = cities[(vi + oi + dayIndex + 3) % cities.length]!;
        const hh = 7 + ((vi + oi) % 11);
        const mm = ((oi * 13 + vi * 7) % 60);
        const arrH = Math.min(23, hh + 2 + ((oi + vi) % 4));
        const minute = pad2(mm);
        const originTimestamp = `${dateStr}T${pad2(hh)}:${minute}:00`;
        const destTimestamp = `${dateStr}T${pad2(arrH)}:${minute}:00`;
        const qty = 1 + ((vi + oi + dayIndex) % 18);
        const weight = Number((120 + ((vi * 43 + oi * 29 + dayIndex * 17) % 2600) + qty * 12).toFixed(1));

        orderRows.push({
          orderId,
          vehicleId,
          date: destTimestamp,
          revenue: rev,
          // Per-order cost is allocated so that, at day level, some vehicle-days are loss-making
          // (lossFlag) and others clearly profitable.
          cost: 0, // filled after loop using lossFlag
          profit: rev,
          currency: 'GBP',
          status: 'COMPLETED',
          journeySplitCount: 1,
          origin_city: cityFrom,
          destination_city: cityTo,
          origin_postal_code: `CB${(vi % 9) + 1} ${((oi + dayIndex) % 9) + 1}ZZ`,
          destination_postal_code: `PE${(vi % 8) + 1} ${((oi + dayIndex + 3) % 9) + 1}ZZ`,
          origin_end_timestamp_local: originTimestamp,
          destination_end_timestamp_local: destTimestamp,
          vehicle_assigned: vehicleId,
          vehicle_observed: vehicleId,
          service_level_name: oi % 5 === 0 ? 'Express' : oi % 3 === 0 ? 'Priority' : 'Standard',
          goods_quantity: qty,
          goods_weight: weight,
          customer: `Customer ${pad2(customerIndex)}`,
          revenue_imputed: 0,
        });
      }

      // Now that we know totalRevenue for this (vehicle, date), allocate order-level costs to hit
      // a realistic net margin: losses on lossFlag days, otherwise positive profit.
      const targetCostFactor = lossFlag ? 1.18 : 0.74;
      const targetTotalCost = Math.round(totalRevenue * targetCostFactor);
      let allocated = 0;
      for (let oi = 0; oi < orderIds.length; oi++) {
        const orderId = orderIds[oi]!;
        const rev = Number(orderRevenues[oi] || 0);
        // Pro-rata allocation by revenue, last order gets the rounding remainder.
        let costForOrder =
          oi === orderIds.length - 1 || totalRevenue <= 0
            ? targetTotalCost - allocated
            : Math.round((rev / totalRevenue) * targetTotalCost);
        if (!Number.isFinite(costForOrder)) costForOrder = 0;
        allocated += costForOrder;
        const rec = orderRows.find((r) => r.orderId === orderId && r.vehicleId === vehicleId);
        if (rec) {
          rec.cost = costForOrder;
        }
      }

      const fuelCost = lossFlag
        ? Number((fuelCostBase * 1.18).toFixed(2))
        : fuelCostBase;
      const driverCost = lossFlag
        ? Number((driverCostBase * 1.12).toFixed(2))
        : driverCostBase;

      mergedRows.push({
        date: dateStr,
        vehicle: vehicleId,
        daily_miles: miles,
        driving_hours: drivingHours,
        waiting_hours: waitingHours,
        fuel_cost: fuelCost,
        driver_cost: driverCost,
        revenue: totalRevenue,
        order_count: orderIds.length,
        order_ids: orderIds.join(','),
        order_revenues: orderRevenues.join(','),
        order_revenue_imputed: orderImputed.join(','),
      });
    }

    d.setDate(d.getDate() + 1);
  }

  return { mergedRows, orderRows };
}

/** Route segment between consecutive stops (supatrak_route_segments.jsonl) */
interface RouteSegment {
  vehicle: string;
  date: string;
  from_stop_number: number;
  to_stop_number: number;
  segment_start: string;
  segment_end: string;
  from_stop: { lat: number; lon: number; postcode?: string };
  to_stop: { lat: number; lon: number; postcode?: string };
  n_points_raw: number;
  n_points_kept: number;
  points: [number, number][]; // [[lat, lon], ...]
}

type ProfitStatusGroup = 'qargo' | 'telematics';

const CONFIRMED_STATUSES = new Set([
  'DEPARTED',
  'IN_TRANSIT',
  'INVOICE_READY',
  'INVOICE_POSTED',
  'INVOICED',
  'DONT_INVOICE',
]);


const METRES_PER_MILE = 1609.344;

/** Driver cost config (from Configuration tab); passed into helpers that need rates/caps */
export interface DriverConfig {
  rates: {
    artic: { mileagePerMile: number; hourly: number; workingHourly: number };
    '26t': { mileagePerMile: number; hourly: number; workingHourly: number };
  };
  maxWorkingHoursPerDay: number;
  /** £/hr fallback for driving/working hours when vehicle-specific rate not used */
  workingHourly: number;
}

/** Fuel cost £/mile from Configuration (body style or tonnage, per costRateBasis). */
function getFuelCostPerMile(
  vehicleType: string,
  p: ProfitabilityConfig,
): number {
  const basis = p.costRateBasis ?? 'body_style';
  if (basis === 'tonnage') {
    const key = normaliseTonnageClass(vehicleType);
    if (key === '3.5T') return p.fuelCost3_5T;
    if (key === '7.5T') return p.fuelCost7_5T;
    if (key === '18T') return p.fuelCost18T;
    if (key === '26T') return p.fuelCost26T;
    if (key === 'Artic') return p.fuelCostArtic;
    return p.fuelCostDefault;
  }
  const normalised = (vehicleType || '').trim();
  if (/^lorry$/i.test(normalised)) return p.fuelCostLorry;
  if (/^tractor\s*unit$/i.test(normalised)) return p.fuelCostTractorUnit;
  if (/^mini\s*truck$/i.test(normalised)) return p.fuelCostMiniTruck;
  if (/^service\s*van$/i.test(normalised)) return p.fuelCostServiceVan;
  if (/^rigid\s*truck$/i.test(normalised)) return p.fuelCostRigidTruck;
  return p.fuelCostDefault;
}

/** Standing Cost £/day from Configuration (body style or tonnage, per costRateBasis). */
function getDepreciationPerDay(
  vehicleType: string,
  p: ProfitabilityConfig,
): number {
  const basis = p.costRateBasis ?? 'body_style';
  if (basis === 'tonnage') {
    const key = normaliseTonnageClass(vehicleType);
    if (key === '3.5T') return p.depreciation3_5T;
    if (key === '7.5T') return p.depreciation7_5T;
    if (key === '18T') return p.depreciation18T;
    if (key === '26T') return p.depreciation26T;
    if (key === 'Artic') return p.depreciationArtic;
    return p.depreciationDefault;
  }
  const normalised = (vehicleType || '').trim();
  if (/^lorry$/i.test(normalised)) return p.depreciationLorry;
  if (/^tractor\s*unit$/i.test(normalised)) return p.depreciationTractorUnit;
  if (/^mini\s*truck$/i.test(normalised)) return p.depreciationMiniTruck;
  if (/^service\s*van$/i.test(normalised)) return p.depreciationServiceVan;
  if (/^rigid\s*truck$/i.test(normalised)) return p.depreciationRigidTruck;
  return p.depreciationDefault;
}

/** Count inclusive calendar days between two YYYY-MM-DD strings (local noon to avoid DST issues). */
function countCalendarDaysInclusive(startISO: string, endISO: string): number {
  const a = new Date(`${startISO}T12:00:00`);
  const b = new Date(`${endISO}T12:00:00`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
  return Math.floor((b.getTime() - a.getTime()) / 86400000) + 1;
}

/** Intersect [aStart,aEnd] with [wStart,wEnd]; all YYYY-MM-DD. */
function intersectDateRanges(
  aStart: string,
  aEnd: string,
  wStart: string | undefined,
  wEnd: string | undefined,
): { start: string; end: string } | null {
  if (!wStart || !wEnd) return { start: aStart, end: aEnd };
  const s = aStart > wStart ? aStart : wStart;
  const e = aEnd < wEnd ? aEnd : wEnd;
  if (s > e) return null;
  return { start: s, end: e };
}

function lastDayOfMonthISO(year: number, month1to12: number): string {
  const last = new Date(year, month1to12, 0).getDate();
  return `${year}-${String(month1to12).padStart(2, '0')}-${String(last).padStart(2, '0')}`;
}

/** Standing cost applies to every calendar day in the period bucket (not only days with telematics rows). */
function getCalendarDaysForStandingRow(
  row: { date: string; vehicleId: string },
  periodType: 'day' | 'week' | 'month',
  effectiveDateWindow: { startDate: string; endDate: string } | null,
  dataMinMax: { min: string; max: string } | null,
  availableWeeks: Array<{ startDate: string; endDate: string; label: string }>,
): number {
  const NO_VEHICLE_ID = '—';
  if (row.vehicleId === NO_VEHICLE_ID) return 0;
  const win = effectiveDateWindow ?? (dataMinMax ? { startDate: dataMinMax.min, endDate: dataMinMax.max } : null);

  if (periodType === 'day') return 1;

  if (periodType === 'week') {
    const w = availableWeeks.find((wk) => row.date >= wk.startDate && row.date <= wk.endDate);
    if (!w) return 1;
    const intr = intersectDateRanges(w.startDate, w.endDate, win?.startDate, win?.endDate);
    if (!intr) return 0;
    return countCalendarDaysInclusive(intr.start, intr.end);
  }

  if (periodType === 'month') {
    const ym = row.date.slice(0, 7);
    const parts = ym.split('-');
    const y = parseInt(parts[0] ?? '', 10);
    const m = parseInt(parts[1] ?? '', 10);
    if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) return 0;
    const monthStart = `${y}-${String(m).padStart(2, '0')}-01`;
    const monthEnd = lastDayOfMonthISO(y, m);
    const intr = intersectDateRanges(monthStart, monthEnd, win?.startDate, win?.endDate);
    if (!intr) return 0;
    return countCalendarDaysInclusive(intr.start, intr.end);
  }

  return 1;
}

function getMergedCostDateBounds(rows: MergedCostRow[]): { min: string; max: string } | null {
  let min = '';
  let max = '';
  for (const row of rows) {
    const d = (row.date || '').slice(0, 10);
    if (!d || d.length < 10) continue;
    if (!min || d < min) min = d;
    if (!max || d > max) max = d;
  }
  return min && max ? { min, max } : null;
}

/** Parse numeric from merged CSV cell (may be string from JSON). */
function parseMergedNumericField(v: unknown): number | undefined {
  if (v === undefined || v === null || v === '') return undefined;
  const n = typeof v === 'number' ? v : parseFloat(String(v).trim());
  return Number.isFinite(n) ? n : undefined;
}

type ProfitabilityFuelParams = Parameters<typeof getFuelCostPerMile>[1];

/**
 * Fuel £ for a merged_costs row: prefer pipeline fuel_cost (mileage-based from backend),
 * else legacy jigsaw_fuel_cost, else Configuration £/mi × miles.
 */
function getMergedFuelCostPounds(
  row: MergedCostRow,
  _stdVid: string,
  miles: number,
  vehicleTypeLabel: string,
  profitability: ProfitabilityFuelParams,
  truthMode = false,
): number {
  const fc = parseMergedNumericField((row as MergedCostRow & { fuel_cost?: unknown }).fuel_cost);
  if (fc !== undefined) return fc;
  const jig = parseMergedNumericField(row.jigsaw_fuel_cost);
  if (jig !== undefined && jig > 0) return jig;
  if (truthMode) return jig ?? 0;
  return miles * getFuelCostPerMile(vehicleTypeLabel, profitability);
}

/**
 * Driver £ for a merged_costs row: prefer pipeline driver_cost (3-part formula from backend),
 * else fallback 26T rates from Configuration (old CSVs).
 */
function getMergedDriverCostPounds(
  row: MergedCostRow,
  miles: number,
  drivingH: number,
  waitingH: number,
  profitability: {
    mileageRate26T?: number;
    workingHourlyRate26T?: number;
    workingHourlyRate?: number;
    hourlyRate26T?: number;
  },
  truthMode = false,
): number {
  const dc = parseMergedNumericField(row.driver_cost);
  if (dc !== undefined) return dc;
  if (truthMode) return 0;
  return (
    miles * (profitability.mileageRate26T ?? 0) +
    drivingH * (profitability.workingHourlyRate26T ?? profitability.workingHourlyRate ?? 20) +
    waitingH * (profitability.hourlyRate26T ?? 0)
  );
}

function getMergedStandingCostPounds(row: MergedCostRow): number {
  return parseMergedNumericField((row as MergedCostRow & { depreciation?: unknown }).depreciation) ?? 0;
}

/** Driver rates for Daily Summary tooltips — truth assumptions when available. */
function resolveDriverRatesForDisplay(
  costClassLabel: string,
  assumptions: ProfitabilityAssumptionsConfig | null | undefined,
  driverConfig: DriverConfig,
): {
  mileagePerMile: number;
  workingHourly: number;
  hourly: number;
  costClassLabel: string;
} {
  if (assumptions) {
    const basis = assumptions.cost_rate_basis ?? 'body_style';
    if (basis === 'tonnage') {
      const key = normaliseTonnageClass(costClassLabel) as ProfitabilityTonnageClass;
      const rates =
        assumptions.driver_cost_tonnage?.[key] ?? assumptions.driver_cost_tonnage?.Default;
      if (rates) {
        return {
          mileagePerMile: rates.mileage_rate,
          workingHourly: rates.driving_hourly_rate,
          hourly: rates.waiting_hourly_rate,
          costClassLabel: key,
        };
      }
    } else {
      const key = (costClassLabel || 'Default') as ProfitabilityVehicleType;
      const rates = assumptions.driver_cost?.[key] ?? assumptions.driver_cost?.Default;
      if (rates) {
        return {
          mileagePerMile: rates.mileage_rate,
          workingHourly: rates.driving_hourly_rate,
          hourly: rates.waiting_hourly_rate,
          costClassLabel: key,
        };
      }
    }
  }
  const rate26t = driverConfig.rates['26t'];
  return {
    mileagePerMile: rate26t.mileagePerMile,
    workingHourly: rate26t.workingHourly,
    hourly: rate26t.hourly,
    costClassLabel: costClassLabel || '26t',
  };
}

/** Mileage for this row in miles; split evenly when order involves multiple vehicles */
function getMileageMiles(row: ProfitRecord): number {
  const metres = row.metrics_distance_total ?? 0;
  const split = row.journeySplitCount ?? 1;
  return metres / split / METRES_PER_MILE;
}

function getDriverCost(row: ProfitRecord, vehicleType: 'artic' | '26t', cfg: DriverConfig): number {
  const rates = cfg.rates[vehicleType];
  // Use same split logic as getMileageMiles so journey-split orders attribute driver cost per vehicle share.
  const miles = getMileageMiles(row);
  // Driver cost = (miles ? £/mile) + (waiting hours ? £/hr). We don't have waiting hours, so use mileage only.
  return miles * rates.mileagePerMile;
}


/**
 * Sort comparison that always places rows whose **sorted column** would display "—" at the bottom,
 * regardless of ascending/descending sort.
 */
function compareWithSortedColumnDashLast(
  sortColumnShowsDashA: boolean,
  sortColumnShowsDashB: boolean,
  primaryCompare: () => number,
): number {
  if (sortColumnShowsDashA && !sortColumnShowsDashB) return 1;
  if (!sortColumnShowsDashA && sortColumnShowsDashB) return -1;
  if (sortColumnShowsDashA && sortColumnShowsDashB) return 0;
  return primaryCompare();
}

/** Empty, em dash, or hyphen only (matches table placeholders). */
export function isDashPlaceholderText(v: unknown): boolean {
  if (v == null) return true;

  if (typeof v === 'string') {
    const t = v.trim();
    return t === '' || t === '—' || t === '-';
  }

  if (typeof v === 'number' || typeof v === 'boolean') {
    return false;
  }

  if (Array.isArray(v)) {
    return v.length === 0;
  }

  if (typeof v === 'object') {
    const maybeLength = 'length' in v ? (v as { length?: number }).length : undefined;
    if (typeof maybeLength === 'number') {
      return maybeLength === 0;
    }
  }

  const t = String(v).trim();
  return t === '' || t === '—' || t === '-';
}

/** Order-level margin % from allocated profit and revenue; null when not yet computed. */
function orderMarginPct(row: ProfitRecord): number | null {
  if (row.allocation_method == null) return null;
  const revenue = row.revenue ?? 0;
  if (revenue <= 0) return null;
  return ((row.profit ?? 0) / revenue) * 100;
}


/** Whether the Qargo / merged-order row would show "—" in the column used for `sortField`. */
export function profitRecordSortColumnIsDash(row: ProfitRecord, sortField: string): boolean {
  const r = row;
  switch (sortField) {
    case 'date':
      return isDashPlaceholderText(r.date);
    case 'orderId':
      return isDashPlaceholderText(r.orderId);
    case 'vehicleId':
      return isDashPlaceholderText(r.vehicleId);
    case 'journeySplitCount':
      return false;
    case 'mileage':
      return getMileageMiles(r) <= 0;
    case 'revenue':
      // Table always renders a currency amount (defaults GBP); £0 is a real value, not "—".
      return false;
    case 'marginPct':
      return orderMarginPct(r) == null;
    case 'cost':
    case 'profit':
      // Cost/Profit cells render "—" until order allocation has run.
      return r.allocation_method == null;
    case 'driverCost':
    case 'orderPlusDriver':
      return r.allocation_method == null;
    case 'originDate': {
      const v = r.origin_end_timestamp_local ?? r.origin_end_timestamp ?? r.date;
      return isDashPlaceholderText(v);
    }
    case 'vehiclePickup':
      return isDashPlaceholderText((r as ProfitRecord).vehicle_pickup ?? '');
    case 'originCity':
      // Column shows formatRoute(origin, destination), not origin alone.
      return isDashPlaceholderText(formatRoute(r.origin_city, r.destination_city));
    case 'vehicleObserved':
      return isDashPlaceholderText(r.vehicle_observed ?? r.vehicleId);
    case 'destinationCity':
      return isDashPlaceholderText(r.destination_city);
    case 'serviceLevel':
      return isDashPlaceholderText(r.service_level_name);
    case 'customer':
      return isDashPlaceholderText(r.customer);
    case 'goodsQuantity':
      return r.goods_quantity == null || String(r.goods_quantity).trim() === '';
    case 'goodsWeight':
      return r.goods_weight == null || String(r.goods_weight).trim() === '';
    default:
      return false;
  }
}

type DailySummarySortRow = {
  date: string;
  vehicleId: string;
  totalMileage?: number;
  totalRevenue: number;
  totalFuelCost: number;
  totalDriverCost?: number;
  totalDepreciation?: number;
  totalProfit: number;
  currency: string;
  periodLabel?: string;
  utilisationScore?: number | null;
  utilisationTooltip?: string | null;
  orderCount?: number;
  orderIds?: string[];
};

function dailySummarySortColumnIsDash(row: DailySummarySortRow, field: string): boolean {
  const hasCosts = (row.totalFuelCost ?? 0) > 0 || (row.totalDriverCost ?? 0) > 0;
  const hasRevenue = (row.totalRevenue ?? 0) > 0;
  switch (field) {
    case 'date':
      return isDashPlaceholderText(row.periodLabel ?? row.date);
    case 'vehicleId':
      return isDashPlaceholderText(row.vehicleId);
    case 'totalMileage':
      return (row.totalMileage ?? 0) <= 0;
    case 'totalRevenue':
      return hasCosts && !hasRevenue;
    case 'totalFuelCost':
    case 'totalDriverCost':
      return hasRevenue && !hasCosts;
    case 'totalCost':
      return hasRevenue && !hasCosts;
    case 'totalProfit':
      return !String(row.currency ?? '').trim();
    case 'utilisationScore':
      return row.utilisationScore == null;
    case 'orderCount':
      return ((row as { deliveryCount?: number }).deliveryCount ?? row.orderCount ?? 0) <= 0;
    case 'pickupCount':
      return ((row as { pickupCount?: number }).pickupCount ?? 0) <= 0;
    default:
      return false;
  }
}

function customerSummarySortColumnIsDash(
  row: CustomerProfitabilitySummaryRow,
  field:
    | 'deliveryDate'
    | 'customer'
    | 'orderCount'
    | 'totalRevenue'
    | 'totalAllocatedCost'
    | 'totalAllocatedMiles'
    | 'costPerMileGbp'
    | 'totalProfit'
    | 'profitMarginPct',
): boolean {
  switch (field) {
    case 'deliveryDate':
      return isDashPlaceholderText(row.deliveryDate);
    case 'customer': {
      const c = String(row.customer ?? '').trim();
      return c === '' || c === '—' || c === '-';
    }
    case 'totalAllocatedMiles':
      return (Number(row.totalAllocatedMiles) || 0) <= 0;
    case 'costPerMileGbp':
      return (Number(row.costPerMileGbp) || 0) <= 0;
    case 'orderCount':
    case 'totalRevenue':
    case 'totalAllocatedCost':
    case 'totalProfit':
    case 'profitMarginPct':
      return false;
    default:
      return false;
  }
}


function JourneySplitModalContent({
  orderId,
  currentVehicleIds,
  vehicleOptions,
  onSave,
  onCancel,
}: {
  orderId: string;
  currentVehicleIds: string[];
  vehicleOptions: string[];
  onSave: (orderId: string, vehicleIds: string[]) => void;
  onCancel: () => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(() => new Set(currentVehicleIds));
  const toggle = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectedList = Array.from(selected).sort();
  return (
    <>
      <div className="max-h-48 overflow-auto rounded-md border border-border bg-background/40">
        <table className="w-full text-sm text-left">
          <thead className="sticky top-0 z-raised bg-card border-b border-border text-foreground">
            <tr>
              <th className="w-10 px-3 py-2 font-medium" scope="col">
                <span className="sr-only">Include</span>
              </th>
              <th className="px-3 py-2 font-medium" scope="col">
                Registration
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {vehicleOptions.map((id) => (
              <tr key={id} className="hover:bg-muted/50">
                <td className="px-3 py-2 align-middle">
                  <input
                    type="checkbox"
                    checked={selected.has(id)}
                    onChange={() => toggle(id)}
                    className="rounded border-border bg-input text-primary"
                    aria-label={`Include vehicle ${id}`}
                  />
                </td>
                <td className="px-3 py-2 font-mono text-foreground">{id}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between gap-2 pt-4 border-t border-border mt-3">
        <span className="text-xs text-muted-foreground">{selected.size} vehicle(s) selected</span>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" type="button" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="default" size="sm" type="button" onClick={() => selected.size > 0 && onSave(orderId, selectedList)} disabled={selected.size === 0}>
            Save
          </Button>
        </div>
      </div>
    </>
  );
}


/** Pulse placeholder for Monday Brief while merged profitability data is loading. */
function MondayBriefSkeleton({ variant = 'default' }: { variant?: 'default' | 'profit-agent' }) {
  return (
    <section
      className="mb-6 bg-background border border-border rounded-xl overflow-hidden"
      aria-busy="true"
      aria-live="polite"
      aria-label="Loading Monday Brief"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-border bg-card/60">
        <div className="flex items-center gap-2">
          <div className="h-4 w-36 rounded bg-primary/35 animate-pulse" />
          <div className="h-3 w-28 rounded bg-secondary/45 animate-pulse" />
        </div>
        {variant !== 'profit-agent' ? (
          <div className="h-3 w-52 max-w-full rounded bg-secondary/35 animate-pulse" />
        ) : null}
      </div>
      <div className="p-4">
        <div className="rounded-lg border border-border bg-card/40 p-4">
          <div className="h-3 w-40 rounded bg-secondary/40 animate-pulse mb-3" />
          <div className="space-y-2 mb-4">
            <div className="h-4 w-full max-w-lg rounded bg-secondary/40 animate-pulse" />
            <div className="h-4 w-full max-w-md rounded bg-secondary/35 animate-pulse" />
          </div>
          <ol className="space-y-4 list-none">
            {[0, 1, 2].map((i) => (
              <li key={i} className="space-y-2">
                <div className="flex gap-3">
                  <div className="h-4 w-5 rounded bg-secondary/35 animate-pulse shrink-0 mt-0.5" />
                  <div className="flex-1 space-y-2 min-w-0">
                    <div className="h-4 w-full rounded bg-secondary/45 animate-pulse" />
                    <div className="h-3 w-full rounded bg-accent/40 animate-pulse" />
                    <div className="h-3 w-full max-w-2xl rounded bg-primary/25 animate-pulse" />
                  </div>
                </div>
              </li>
            ))}
          </ol>
          <div className="mt-4 flex flex-wrap gap-2 items-center">
            <div className="h-3 w-32 rounded bg-secondary/40 animate-pulse" />
            <div className="h-3 flex-1 min-w-48 rounded bg-secondary/30 animate-pulse" />
          </div>
        </div>
      </div>
    </section>
  );
}

/** Pulse placeholders for the four profitability KPI cards while data is loading. */
function ProfitabilityKpiCardsSkeleton() {
  const cards = [
    { border: 'border-chart-10/30', dot: 'bg-chart-10', from: 'from-chart-10/20' },
    { border: 'border-primary/30', dot: 'bg-primary', from: 'from-primary/20' },
    { border: 'border-chart-5/30', dot: 'bg-chart-5', from: 'from-chart-5/20' },
    { border: 'border-chart-2/30', dot: 'bg-chart-2', from: 'from-chart-2/20' },
  ] as const;
  return (
    <div
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6"
      aria-busy="true"
      aria-live="polite"
      aria-label="Loading profitability KPIs"
    >
      {cards.map((c, i) => (
        <div
          key={i}
          className={`relative overflow-hidden bg-gradient-to-br ${c.from} via-muted to-background rounded-xl border ${c.border} p-6 shadow-xl`}
        >
          <div className="absolute top-0 right-0 w-32 h-32 rounded-full -mr-16 -mt-16 blur-2xl opacity-40 bg-white/5" />
          <div className="relative space-y-3">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${c.dot} opacity-70`} />
              <div className="h-3.5 flex-1 max-w-40 rounded bg-secondary/50 animate-pulse" />
            </div>
            <div className="h-10 w-40 rounded bg-secondary/45 animate-pulse" />
            <div className="h-3 w-48 rounded bg-secondary/35 animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Placeholders for daily/weekly/monthly profit calendar + line chart while data loads. */
function ProfitabilityCalendarChartSkeleton() {
  return (
    <div
      className="w-full min-w-0 mb-6 flex flex-col lg:flex-row gap-4 lg:items-stretch"
      aria-busy="true"
      aria-live="polite"
      aria-label="Loading profit calendar and trend chart"
    >
      <div className="w-full lg:w-1/2 min-w-0 bg-card border border-border rounded-lg p-5 h-full min-h-80">
        <div className="h-5 w-52 rounded bg-secondary/50 animate-pulse mb-4" />
        <div className="flex justify-between gap-2 mb-4">
          <div className="h-9 w-18 rounded-lg bg-secondary/40 animate-pulse" />
          <div className="h-5 w-36 rounded bg-secondary/35 animate-pulse self-center" />
          <div className="h-9 w-18 rounded-lg bg-secondary/40 animate-pulse" />
        </div>
        <div className="grid grid-cols-7 gap-2">
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={`h-${i}`} className="h-4 rounded bg-secondary/30 animate-pulse" />
          ))}
          {Array.from({ length: 35 }).map((_, i) => (
            <div key={`c-${i}`} className="aspect-square rounded-md bg-secondary/20 animate-pulse" />
          ))}
        </div>
      </div>
      <div className="min-w-0 flex flex-col lg:w-1/2 w-full">
        <div className="flex-1 bg-card border border-border rounded-lg overflow-hidden min-h-80 p-4 flex flex-col">
          <div className="mt-auto space-y-3 w-full">
            <div className="h-40 w-full rounded-md bg-secondary/25 animate-pulse" />
            <div className="flex justify-between gap-2 px-1">
              <div className="h-2.5 w-12 rounded bg-secondary/35 animate-pulse" />
              <div className="h-2.5 w-12 rounded bg-secondary/35 animate-pulse" />
              <div className="h-2.5 w-12 rounded bg-secondary/35 animate-pulse" />
            </div>
            <div className="h-3 max-w-md mx-auto w-full rounded bg-secondary/30 animate-pulse" />
          </div>
        </div>
      </div>
    </div>
  );
}

type ProfitabilityReportProps = { variant?: 'default' | 'profit-agent' };

export default function ProfitabilityReport({ variant = 'default' }: ProfitabilityReportProps) {
  const useTruth = useTruthProfitability();
  const useTruthUtil = useTruthUtilisation();
  const truthDateRange = useProfitabilityDateRange();
  /** UI period filter (day/week/month) within the loaded Report period — does not drive pipeline status. */
  const [selectedPeriodRange, setSelectedPeriodRange] = useState<{ startDate: string; endDate: string } | null>(null);
  const [truthPipelineRefreshToken, setTruthPipelineRefreshToken] = useState(0);
  const truthFetchGenRef = useRef(0);
  const lastManifestComputedAtRef = useRef<string | null>(null);
  const utilisationRefreshToken = 0;
  // Pipeline status / amber warnings MUST match Report period pickers (not calendar week filters).
  const truthPipeline = useTruthProfitabilityPanel({
    startDate: truthDateRange.startDate,
    endDate: truthDateRange.endDate,
    onDataRefresh: () => setTruthPipelineRefreshToken((t) => t + 1),
    // On-demand coverage reloads full GPS + per vehicle-day projections and can OOM zeecore.
    // Use last_computed.coverage_summary from the manifest instead.
    includeCoverage: false,
  });

  // Reload table data when manifest shows a new compute for the current Report period.
  useEffect(() => {
    if (!useTruth) return;
    lastManifestComputedAtRef.current = null;
  }, [useTruth, truthDateRange.startDate, truthDateRange.endDate]);

  useEffect(() => {
    if (!useTruth) return;
    const meta = truthLastComputedMeta(truthPipeline.statusData?.last_computed ?? null);
    if (
      !meta.computedAt ||
      meta.computedAt === lastManifestComputedAtRef.current ||
      meta.startDate !== truthDateRange.startDate ||
      meta.endDate !== truthDateRange.endDate ||
      (meta.recordCount ?? 0) <= 0
    ) {
      return;
    }
    lastManifestComputedAtRef.current = meta.computedAt;
    setTruthPipelineRefreshToken((t) => t + 1);
  }, [
    useTruth,
    truthPipeline.statusData?.last_computed,
    truthDateRange.startDate,
    truthDateRange.endDate,
  ]);
  const [truthUtilisationRows, setTruthUtilisationRows] = useState<TruthUtilisationVehicleDayRow[]>([]);
  /** Separate contracts left out of these costs (otherWorkVehicles.ts). */
  const [otherWorkLines, setOtherWorkLines] = useState<string[]>([]);
  const [profitabilityAssumptions, setProfitabilityAssumptions] =
    useState<ProfitabilityAssumptionsConfig | null>(null);
  const { isDemoMode } = useDemoMode();

  /**
   * Truth Profit: on first load, discover last_computed coverage and align the report period
   * (avoids empty last-7-days when the only computed window is e.g. a week in June).
   */
  const hasAppliedProfitTruthDefaultRef = useRef(false);
  useEffect(() => {
    if (!useTruth || isDemoMode) return;
    if (hasAppliedProfitTruthDefaultRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const today = formatLocalDateIso(new Date());
        // Manifest-only: skip Athena source probes. A Jan→today probe was taking ~2+ minutes.
        const status = await fetchTruthStatus(today, today, {
          includeCoverage: false,
          probeSources: false,
        });
        if (cancelled) return;
        const meta = truthLastComputedMeta(status.last_computed);
        const start = meta.startDate ? String(meta.startDate).slice(0, 10) : '';
        const end = meta.endDate ? String(meta.endDate).slice(0, 10) : '';
        if (!start || !end || (meta.recordCount ?? 0) <= 0) return;
        if (!/^\d{4}-\d{2}-\d{2}$/.test(start) || !/^\d{4}-\d{2}-\d{2}$/.test(end)) return;
        // Snap to the latest month with data — avoid loading the entire last_computed span.
        const [yStr, mStr] = end.split('-');
        const year = parseInt(yStr, 10);
        const month = parseInt(mStr, 10);
        let rangeStart = start;
        let rangeEnd = end;
        if (year && month >= 1 && month <= 12) {
          rangeStart = `${year}-${String(month).padStart(2, '0')}-01`;
          const lastDay = new Date(year, month, 0).getDate();
          rangeEnd = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
          // Clamp to coverage if last_computed starts mid-month
          if (start > rangeStart) rangeStart = start;
          if (end < rangeEnd) rangeEnd = end;
        }
        hasAppliedProfitTruthDefaultRef.current = true;
        truthDateRange.setDateRange(rangeStart, rangeEnd);
      } catch (err) {
        console.warn('[ProfitabilityReport] Failed to discover profitability coverage for default range:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [useTruth, isDemoMode, truthDateRange.setDateRange]);

  const { config, setProfitabilityReportSchedule } = useConfig();
  const driverConfig: DriverConfig = useMemo(
    () => ({
      rates: {
        artic: {
          mileagePerMile: config.profitability.mileageRateArtic,
          hourly: config.profitability.hourlyRateArtic,
          workingHourly: config.profitability.workingHourlyRateArtic ?? config.profitability.workingHourlyRate ?? 20,
        },
        '26t': {
          mileagePerMile: config.profitability.mileageRate26T,
          hourly: config.profitability.hourlyRate26T,
          workingHourly: config.profitability.workingHourlyRate26T ?? config.profitability.workingHourlyRate ?? 20,
        },
      },
      maxWorkingHoursPerDay: config.profitability.maxDailyHours,
      workingHourly: config.profitability.workingHourlyRate ?? 20,
    }),
    [config.profitability]
  );
  // 'qargo' or 'telematics'
  const [profitAgentQuestion, setProfitAgentQuestion] = useState('');
  type ProfitAgentPageState = 'idle' | 'loading' | 'success' | 'error';
  const [profitAgentPageState, setProfitAgentPageState] = useState<ProfitAgentPageState>('idle');
  const [profitAgentResult, setProfitAgentResult] = useState<ProfitAgentQueryResponse | null>(null);
  const [profitAgentError, setProfitAgentError] = useState('');
  const [profitAgentStreamingComplete, setProfitAgentStreamingComplete] = useState(false);
  const [profitAgentActiveDrilldown, setProfitAgentActiveDrilldown] = useState<ProfitAgentDrilldown | null>(
    null,
  );
  const [mondayBriefApiRaw, setMondayBriefApiRaw] = useState<MondayBriefApiResponse | null>(null);
  const profitAgentSubmittingRef = useRef(false);
  const profitAgentAbortRef = useRef<AbortController | null>(null);
  const profitAgentLastQuestionRef = useRef('');
  const { activeTab, setActiveTab } = useTab();
  const profitAgentTypedPlaceholder = useTypingPlaceholder(
    PROFIT_AGENT_INPUT_PLACEHOLDER,
    variant === 'profit-agent',
  );
  const [mondayBriefOpen, setMondayBriefOpen] = useState(true);
  const [profitTab, setProfitTab] = useState<ProfitStatusGroup>('qargo');
  const [selectedVehicleIds, setSelectedVehicleIds] = useState<string[]>([]);
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
  const [selectedCustomerNames, setSelectedCustomerNames] = useState<string[]>([]);
  const [vehicleIdInputValue, setVehicleIdInputValue] = useState('');
  /** Lookup selection: order name / id chips (filters Daily Summary + Qargo table via selectedOrderIds) */
  const [orderNameInputValue, setOrderNameInputValue] = useState('');
  const [performersShowBottom5, setPerformersShowBottom5] = useState(false);
  const [bestDaysShowBottom5, setBestDaysShowBottom5] = useState(false);
  const [chartHighlightSeries, setChartHighlightSeries] = useState<string | null>(null);
  const [records, setRecords] = useState<ProfitRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreview, setPdfPreview] = useState<{ url: string; filename: string; blob: Blob } | null>(null);
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [pdfPreviewOpenedFromSchedule, setPdfPreviewOpenedFromSchedule] = useState(false);
  const [pdfScheduleOverride, setPdfScheduleOverride] = useState<ProfitabilityReportScheduleConfig | null>(null);
  const [scheduleBanner, setScheduleBanner] = useState<string | null>(null);
  const [schedulePrimaryEmailError, setSchedulePrimaryEmailError] = useState<string | null>(null);
  const [scheduleVehicleSearch, setScheduleVehicleSearch] = useState('');
  const [sortField, setSortField] = useState<'date' | 'orderId' | 'profit' | 'vehicleId' | 'journeySplitCount' | 'mileage' | 'revenue' | 'marginPct' | 'cost' | 'driverCost' | 'orderPlusDriver' | 'originDate' | 'vehiclePickup' | 'originCity' | 'vehicleObserved' | 'destinationCity' | 'serviceLevel' | 'goodsQuantity' | 'goodsWeight' | 'customer'>('orderId');
  const [sortAsc, setSortAsc] = useState<boolean>(true);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [summarySortField, setSummarySortField] = useState<'date' | 'vehicleId' | 'totalMileage' | 'utilisationScore' | 'orderCount' | 'pickupCount' | 'totalRevenue' | 'totalFuelCost' | 'totalDriverCost' | 'totalCost' | 'totalProfit'>('vehicleId');
  const [summarySortAsc, setSummarySortAsc] = useState(true);
  const [summaryPage, setSummaryPage] = useState(1);
  const [summaryRowsPerPage, setSummaryRowsPerPage] = useState(10);
  const [customerPage, setCustomerPage] = useState(1);
  const [customerRowsPerPage, setCustomerRowsPerPage] = useState(10);
  const [customerSortField, setCustomerSortField] = useState<'deliveryDate' | 'customer' | 'orderCount' | 'totalRevenue' | 'totalAllocatedCost' | 'totalAllocatedMiles' | 'costPerMileGbp' | 'totalProfit' | 'profitMarginPct'>('totalProfit');
  const [customerSortAsc, setCustomerSortAsc] = useState(false);
  const trendSectionRef = useRef<HTMLDivElement>(null);
  const customerProfitabilitySectionRef = useRef<HTMLElement>(null);
  const mondayBriefSectionRef = useRef<HTMLElement>(null);
  const qargoOrderSectionRef = useRef<HTMLDivElement>(null);
  const [profitViewMode, setProfitViewMode] = useState<'brief' | 'data'>('brief');
  const [utilisationVehicleLevelMap, setUtilisationVehicleLevelMap] = useState<Map<string, MondayBriefVehicleUtilisation> | null>(null);
  const [utilisationFleetScore, setUtilisationFleetScore] = useState<number | null>(null);
  // Set default date to most recent if available
  const [selectedSummaryDate, setSelectedSummaryDate] = useState<string>('');
  // Period type for date filtering: 'day', 'week', or 'month'
  // Default to 'month' to show full month of data instead of just one day
  const [periodType, setPeriodType] = useState<'day' | 'week' | 'month'>('day');
  // selectedPeriodRange declared above (drives truth pipeline status window)
  /** Daily Profit Calendar: which month is shown (null = first month with data). Enables prev/next month paging. */
  const [calendarPageMonth, setCalendarPageMonth] = useState<{ year: number; month: number } | null>(null);
  /** When true, Qargo order costs (red-coded) are excluded from KPIs, chart, totals, and Daily profitability display */
  const [, setQargoRecords] = useState<ProfitRecord[]>([]);
  // Editable cells (Daily Profitability): click-to-edit overrides
  const [, setEditingCell] = useState<{
    kind: 'vehicleId' | 'journeySplit' | 'revenue' | 'cost' | 'fuelCost' | 'driverCost';
    rowKey: string;
    orderId?: string;
  } | null>(null);
  const [orderVehicleOverrides, setOrderVehicleOverrides] = useState<Record<string, string[]>>({});
  const [rowOverrides] = useState<Record<string, { vehicleId?: string; revenue?: number; cost?: number }>>({});
  /** Daily Profitability: per-row driver cost vehicle type (default 26T); user can switch to Artic */
  const [driverCostVehicleTypeOverrides] = useState<Record<string, 'artic' | '26t'>>({});
  const [journeySplitModal, setJourneySplitModal] = useState<{ orderId: string; currentVehicleIds: string[] } | null>(null);
  const [mapModalRow, setMapModalRow] = useState<{
    vehicleId: string;
    date: string;
    periodLabel?: string;
    orderIds?: string[];
    pickupOrderIds?: string[];
    deliveryOrderIds?: string[];
    startDate?: string;
    endDate?: string;
  } | null>(null);
  /** Org depots (lat/lon + depotNumber) for pick-up/trunk handoff pins on the vehicle map */
  const [orgDepotsForMap, setOrgDepotsForMap] = useState<OrgDepotForMap[]>([]);
  /** When set, the map modal was opened from an order row (Qargo's Order); show order origin/destination markers */
  const [mapModalOrder, setMapModalOrder] = useState<ProfitRecord | null>(null);
  /** Daily Summary by Vehicle: which row is selected (MapPin or Revenue click) for highlight */
  const [selectedDailySummaryRow, setSelectedDailySummaryRow] = useState<{ vehicleId: string; date: string } | null>(null);
  /** Qargo's Order: which row is selected (row click) for highlight; key = orderId|vehicleId|date */
  const [selectedQargoOrderKey, setSelectedQargoOrderKey] = useState<string | null>(null);
  /** When set, show modal with order IDs (and revenues when opened from the revenue cell) */
  const [revenueOrdersModal, setRevenueOrdersModal] = useState<{
    orderIds: string[];
    orderRevenues: string[];
    /** Per-order imputed flag: "0", "15", or "25" (same order as orderIds) */
    orderRevenueImputed?: string[];
    /** Aligned with orderIds — same-day TMS jobs (Orders modal labels) */
    orderSameDayFlags?: boolean[];
    /** 'orders' = IDs only (from Orders count); 'revenue' = ID → revenue */
    mode?: 'revenue' | 'orders';
    currency: string;
    totalRevenue: number;
    periodLabel?: string;
    date?: string;
    vehicleId?: string;
  } | null>(null);
  /** Sort for orders/revenue modal columns (Tracking ID / Revenue) */
  const [revenueOrdersModalSort, setRevenueOrdersModalSort] = useState<{
    field: 'trackingId' | 'revenue';
    asc: boolean;
  }>({ field: 'trackingId', asc: true });
  /** Show "Copied!" next to the order ID that was just copied in the revenue modal */
  const [copiedOrderId, setCopiedOrderId] = useState<string | null>(null);

  /** When true, exclude depot/circuit vehicles from the report (from Configuration vehicle list). */
  const [hideStIvesVehicles, setHideStIvesVehicles] = useState(true);
  /** Vehicle reg (standardised) excluded when depot/circuit filter is on. */
  const [stIvesVehicleIds, setStIvesVehicleIds] = useState<Set<string>>(new Set());
  /** Vehicle Reg (standardised) -> vehicle type string from config vehicle list (e.g. "Lorry", "Tractor Unit") for fuel £/mile lookup */
  const [vehicleIdToType, setVehicleIdToType] = useState<Record<string, string>>({});
  const [vehicleIdToTonnage, setVehicleIdToTonnage] = useState<Record<string, string>>({});
  /** Pre-processed profitability data from merged_costs.csv (uploaded to S3 by backend pipeline) */
  const [mergedCostsData, setMergedCostsData] = useState<MergedCostRow[]>([]);
  const [customerMonthlyProfitability, setCustomerMonthlyProfitability] = useState<CustomerMonthlyProfitabilityRow[]>([]);
  const [customerProfitabilitySource, setCustomerProfitabilitySource] = useState<'step4_daily' | 'step4_monthly' | 'legacy_orders'>('legacy_orders');
  /** Telematics: delivery stops and route segments (S3/local) — used for the route map only */
  const [telematicsDailyMiles] = useState<TelematicsDailyMiles[]>([]);
  const [telematicsDeliveryStops, setTelematicsDeliveryStops] = useState<TelematicsDeliveryStop[]>([]);
  const [routeSegments, setRouteSegments] = useState<RouteSegment[]>([]);
  /** Truth-mode DQ: costs loaded but no stop/segment overlays for the viewed window. */
  const [truthTelematicsMapHint, setTruthTelematicsMapHint] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchGen = ++truthFetchGenRef.current;
    const truthFetchOpts = useTruth ? { cacheBust: truthPipelineRefreshToken } : undefined;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // merged_costs.csv drives the Daily Summary table.
        // Qargo orders feed the "Qargo's Orders" detail tab.
        // Delivery stops + route segments are used by the route map modal only.
        const batchDateRangeSuffix = useTruth
          ? toBatchDateRangeSuffix(truthDateRange.startDate, truthDateRange.endDate)
          : getProfitabilityDateRange();
        const dateRangeQ = `dateRange=${encodeURIComponent(batchDateRangeSuffix)}`;
        const batchIsoRange = useTruth
          ? { startDate: truthDateRange.startDate, endDate: truthDateRange.endDate }
          : parseProfitabilityDateRangeSuffix(batchDateRangeSuffix);
        const fetchStartDate = batchIsoRange.startDate;
        const fetchEndDate = batchIsoRange.endDate;

        const isStale = () => cancelled || fetchGen !== truthFetchGenRef.current;

        const applyQargoOrdersFallback = async (qargoRes: Response): Promise<boolean> => {
          if (!qargoRes.ok) return false;
          const qargoJson = await qargoRes.json();
          const filteredOrders = (Array.isArray(qargoJson.all_orders) ? qargoJson.all_orders : []).filter((rec: ProfitRecord) => {
            const status = (rec.status || '').toUpperCase().trim();
            if (!status) return true;
            if (status.includes('UNCANCELLED')) return true;
            return !status.includes('CANCELLED') && !status.includes('CANCEL');
          });
          setQargoRecords(filteredOrders);
          setRecords(filteredOrders);
          return filteredOrders.length > 0;
        };

        const applyTelematicsResponses = async (
          deliveryStopsRes: Response,
          routeSegmentsRes: Response,
        ) => {
          if (isStale()) return;
          const deliveryStopsJson = await deliveryStopsRes.json().catch(() => ({ delivery_stops: [] }));
          const routeSegmentsJson = await routeSegmentsRes.json().catch(() => ({ route_segments: [] }));
          setTelematicsDeliveryStops(
            Array.isArray(deliveryStopsJson.delivery_stops) ? deliveryStopsJson.delivery_stops : [],
          );
          setRouteSegments(
            Array.isArray(routeSegmentsJson.route_segments) ? routeSegmentsJson.route_segments : [],
          );
        };

        // Demo users must always see anonymized synthetic data on Profitability.
        if (isDemoMode) {
          const demo = buildDemoProfitabilityDataset();
          const monthKeys = getDemoCustomerProfitabilityMonthKeys();
          const demoCustomerRows = buildDemoCustomerProfitabilityRows(monthKeys, 64);
          setMergedCostsData(demo.mergedRows);
          setQargoRecords(demo.orderRows);
          setRecords(demo.orderRows);
          setCustomerMonthlyProfitability(demoCustomerRows);
          setCustomerProfitabilitySource('step4_monthly');
          setTelematicsDeliveryStops([]);
          setRouteSegments([]);
          setTruthTelematicsMapHint(null);
          return;
        }

        if (useTruth) {
          // Clear prior-period overlays immediately so MapPins don't flash stale data.
          setTelematicsDeliveryStops([]);
          setRouteSegments([]);
          setTruthTelematicsMapHint(null);
          const [truthCostsResult, truthOrdersResult, truthCustomerDailyResult] = await Promise.all([
            fetchTruthMergedCosts(fetchStartDate, fetchEndDate, truthFetchOpts),
            fetchTruthMergedOrders(fetchStartDate, fetchEndDate, truthFetchOpts).catch(() => ({
              rows: [],
              revenue_imputation: null,
            })),
            fetchTruthCustomerProfitabilityDaily(fetchStartDate, fetchEndDate, truthFetchOpts).catch(() => ({
              rows: [],
              revenue_imputation: null,
            })),
          ]);

          if (isStale()) return;

          const truthCosts = truthCostsResult.records;
          setOtherWorkLines((truthCostsResult.other_work ?? []).map(otherWorkLineText));
          const truthOrders = truthOrdersResult.rows;
          const truthCustomerDaily = truthCustomerDailyResult.rows;

          setMergedCostsData(truthCosts);

          const dailyMapped = mapCustomerMonthlyProfitabilityRows(truthCustomerDaily);
          setCustomerMonthlyProfitability(dailyMapped);
          setCustomerProfitabilitySource(dailyMapped.length > 0 ? 'step4_daily' : 'legacy_orders');

          let ordersLoaded = false;
          if (truthOrders.length > 0) {
            const mapped = mapMergedOrderRowsToProfitRecords(truthOrders) as ProfitRecord[];
            setQargoRecords(mapped);
            setRecords(mapped);
            ordersLoaded = true;
          }
          if (!ordersLoaded) {
            const qargoRes = await authenticatedFetch('/api/profitability/qargo-orders');
            if (isStale()) return;
            await applyQargoOrdersFallback(qargoRes);
          }

          // Telematics overlays from zeecore (not legacy batch) — load after tables paint.
          void (async () => {
            try {
              let overlayFetchFailed = false;
              const [stops, segments] = await Promise.all([
                fetchTruthTelematicsDeliveryStops(fetchStartDate, fetchEndDate, truthFetchOpts).catch(() => {
                  overlayFetchFailed = true;
                  return [];
                }),
                fetchTruthTelematicsRouteSegments(fetchStartDate, fetchEndDate, truthFetchOpts).catch(() => {
                  overlayFetchFailed = true;
                  return [];
                }),
              ]);
              if (isStale()) return;
              setTelematicsDeliveryStops(stops as TelematicsDeliveryStop[]);
              setRouteSegments(segments as RouteSegment[]);
              if (overlayFetchFailed) {
                // A failed request is not "no data" — don't send people off to re-run compute.
                setTruthTelematicsMapHint(
                  'Could not load telematics stops/maps from the server — try again shortly.',
                );
              } else if (truthCosts.length > 0 && stops.length === 0 && segments.length === 0) {
                setTruthTelematicsMapHint(
                  'No telematics stops for this period — run Order or All compute, or check GPS ingest.',
                );
              } else {
                setTruthTelematicsMapHint(null);
              }
            } catch {
              /* non-blocking */
            }
          })();
          return;
        }

        const [mergedRes, mergedOrdersRes, qargoRes, deliveryStopsRes, routeSegmentsRes, customerMonthlyRes] = await Promise.all([
          authenticatedFetch(`/api/profitability/merged-costs?${dateRangeQ}`),
          authenticatedFetch(`/api/profitability/merged-orders?${dateRangeQ}`),
          authenticatedFetch('/api/profitability/qargo-orders'),
          authenticatedFetch(`/api/profitability/telematics-delivery-stops?${dateRangeQ}`),
          authenticatedFetch(`/api/profitability/telematics-route-segments?${dateRangeQ}`),
          authenticatedFetch(`/api/profitability/customer-profitability-monthly?${dateRangeQ}`),
        ]);

        if (!mergedRes.ok) throw new Error('Failed to load merged_costs.csv from backend');
        const mergedJson = await mergedRes.json();
        if (isStale()) return;
        setMergedCostsData(Array.isArray(mergedJson.rows) ? mergedJson.rows : []);

        if (customerMonthlyRes.ok) {
          const monthlyJson = await customerMonthlyRes.json();
          const rowsRaw = Array.isArray(monthlyJson.rows) ? monthlyJson.rows : [];
          const mappedRows = mapCustomerMonthlyProfitabilityRows(rowsRaw);
          setCustomerMonthlyProfitability(mappedRows);
          setCustomerProfitabilitySource(mappedRows.length > 0 ? 'step4_monthly' : 'legacy_orders');
        } else {
          setCustomerMonthlyProfitability([]);
          setCustomerProfitabilitySource('legacy_orders');
        }

        let ordersLoaded = false;
        if (mergedOrdersRes.ok) {
          const mergedOrdersJson = await mergedOrdersRes.json();
          const orderRows = Array.isArray(mergedOrdersJson.rows) ? mergedOrdersJson.rows : [];
          if (orderRows.length > 0) {
            const mapped = mapMergedOrderRowsToProfitRecords(orderRows) as ProfitRecord[];
            setQargoRecords(mapped);
            setRecords(mapped);
            ordersLoaded = true;
          }
        }
        if (!ordersLoaded) {
          await applyQargoOrdersFallback(qargoRes);
        }

        await applyTelematicsResponses(deliveryStopsRes, routeSegmentsRes);
      } catch (err) {
        if (!cancelled) {
          const message =
            err instanceof Error
              ? err.message
              : err && typeof err === 'object' && typeof (err as { message?: unknown }).message === 'string'
                ? (err as { message: string }).message
                : '';
          setError(message || 'Failed to load data');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    void fetchData();
    return () => {
      cancelled = true;
    };
  }, [isDemoMode, useTruth, truthDateRange.startDate, truthDateRange.endDate, truthPipelineRefreshToken]);

  useEffect(() => {
    if (!useTruth || !useTruthUtil || isDemoMode) {
      setTruthUtilisationRows([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const rows = await fetchTruthUtilisationVehicleDays(
          truthDateRange.startDate,
          truthDateRange.endDate,
          { cacheBust: utilisationRefreshToken },
        );
        if (!cancelled) setTruthUtilisationRows(rows);
      } catch {
        if (!cancelled) setTruthUtilisationRows([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    useTruth,
    useTruthUtil,
    isDemoMode,
    truthDateRange.startDate,
    truthDateRange.endDate,
    utilisationRefreshToken,
  ]);

  useEffect(() => {
    if (!useTruth || isDemoMode) {
      setProfitabilityAssumptions(null);
      return;
    }
    let cancelled = false;
    void fetchTruthProfitabilityConfig()
      .then((cfg) => {
        if (!cancelled) setProfitabilityAssumptions(cfg);
      })
      .catch(() => {
        if (!cancelled) setProfitabilityAssumptions(null);
      });
    return () => {
      cancelled = true;
    };
  }, [useTruth, isDemoMode, truthPipelineRefreshToken]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authenticatedFetch(API_ENDPOINTS.fleetIntelligence.depots());
        const json = await res.json().catch(() => ({}));
        if (cancelled || !res.ok) return;
        if (!cancelled) setOrgDepotsForMap(mapOrgDepotsFromApiPayload(json));
      } catch {
        // Leave prior depots; do not cache a permanent empty set on transient failure.
        if (!cancelled) setOrgDepotsForMap([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authenticatedFetch(API_ENDPOINTS.vehicleList());
        const json = await res.json().catch(() => ({}));
        if (cancelled || !res.ok || !json.ok || !Array.isArray(json.rows)) return;
        const typeMap: Record<string, string> = {};
        const tonnageMap: Record<string, string> = {};
        const stIves = new Set<string>();
        for (const row of json.rows as {
          vehicle_reg?: string;
          type?: string;
          typical_tonnes?: string;
          circuit_name?: string;
        }[]) {
          const reg = (row.vehicle_reg ?? '').trim();
          const type = (row.type ?? '').trim();
          const typical = (row.typical_tonnes ?? '').trim();
          if (!reg) continue;
          const std = standardiseVehicleId(reg);
          if (std) {
            typeMap[std] = type || 'Default';
            if (typical && typical !== '—') tonnageMap[std] = typical;
            const circuit = (row.circuit_name ?? '').trim().toLowerCase();
            if (circuit.includes('st ives')) stIves.add(std);
          }
        }
        if (!cancelled) {
          setVehicleIdToType(typeMap);
          setVehicleIdToTonnage(tonnageMap);
          setStIvesVehicleIds(stIves);
        }
      } catch {
        // Ignore: vehicle list optional for fuel type lookup
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /** Cost-class label for rate lookup (normalised 26T / Lorry / …). */
  const resolveVehicleCostClassLabel = useCallback(
    (stdVid: string, storedCostClass?: string): string => {
      const basis =
        (useTruth
          ? profitabilityAssumptions?.cost_rate_basis
          : config.profitability.costRateBasis) ?? 'body_style';

      const fromConfig = (): string | null => {
        if (basis === 'tonnage') {
          const raw = vehicleIdToTonnage[stdVid] || vehicleIdToType[stdVid];
          if (!raw || /^default$/i.test(raw.trim())) return null;
          const key = normaliseTonnageClass(raw);
          return key === 'Default' ? null : key;
        }
        const raw = vehicleIdToType[stdVid];
        if (!raw || /^default$/i.test(raw.trim())) return null;
        const key = normaliseBodyStyleClass(raw);
        return key === 'Default' ? null : key;
      };

      // Prefer a meaningful class persisted on the vehicle-day projection (matches Compute).
      // Ignore stored "Default"/unrecognised — fall back to Configuration vehicle mapping.
      const stored = storedCostClass?.trim();
      if (stored && !/^default$/i.test(stored)) {
        if (basis === 'tonnage') {
          const key = normaliseTonnageClass(stored);
          if (key !== 'Default') return key;
        } else {
          const key = normaliseBodyStyleClass(stored);
          if (key !== 'Default') return key;
        }
      }

      return fromConfig() ?? 'Default';
    },
    [
      useTruth,
      profitabilityAssumptions?.cost_rate_basis,
      config.profitability.costRateBasis,
      vehicleIdToTonnage,
      vehicleIdToType,
    ],
  );

  // Exclude depot/circuit vehicles from report when the toggle is on.
  const mergedCostsDataFiltered = useMemo(() => {
    if (!hideStIvesVehicles || stIvesVehicleIds.size === 0) return mergedCostsData;
    return mergedCostsData.filter(r => !stIvesVehicleIds.has(standardiseVehicleId(r.vehicle) || ''));
  }, [mergedCostsData, hideStIvesVehicles, stIvesVehicleIds]);

  const briefStandingOptions = useMemo(() => {
    const p = config.profitability;
    const basis =
      (useTruth
        ? profitabilityAssumptions?.cost_rate_basis
        : p.costRateBasis) ?? 'body_style';
    const depreciationByType =
      useTruth && profitabilityAssumptions
        ? depreciationRatesFromTruthConfig(profitabilityAssumptions)
        : basis === 'tonnage'
          ? {
              '3.5T': p.depreciation3_5T,
              '7.5T': p.depreciation7_5T,
              '18T': p.depreciation18T,
              '26T': p.depreciation26T,
              Artic: p.depreciationArtic,
              Default: p.depreciationDefault,
            }
          : {
              Lorry: p.depreciationLorry,
              'Tractor Unit': p.depreciationTractorUnit,
              'Mini Truck': p.depreciationMiniTruck,
              'Service Van': p.depreciationServiceVan,
              'Rigid Truck': p.depreciationRigidTruck,
              Default: p.depreciationDefault,
            };
    const vehicleTypeByVrn: Record<string, string> = {};
    for (const vid of Object.keys(vehicleIdToType)) {
      vehicleTypeByVrn[vid] =
        basis === 'tonnage'
          ? normaliseTonnageClass(vehicleIdToTonnage[vid] || vehicleIdToType[vid] || 'Default')
          : vehicleIdToType[vid] || 'Default';
    }
    return {
      vehicleTypeByVrn,
      depreciationByType,
      standingPolicy:
        (useTruth && profitabilityAssumptions?.standing_cost_policy) || ('calendar_day' as const),
    };
  }, [
    useTruth,
    profitabilityAssumptions,
    config.profitability,
    vehicleIdToType,
    vehicleIdToTonnage,
  ]);

  // Vehicle list for dropdown: sourced from merged_costs.csv; include "—" when there are rows with no vehicle so their revenue can be shown/filtered
  const vehicleOptions = useMemo(() => {
    const ids = new Set<string>();
    let hasNoVehicle = false;
    for (const r of mergedCostsDataFiltered) {
      const vid = standardiseVehicleId(r.vehicle) || (r.vehicle && r.vehicle.trim());
      if (vid) ids.add(vid);
      else hasNoVehicle = true;
    }
    const list = Array.from(ids).sort();
    if (hasNoVehicle) list.push('—');
    return list;
  }, [mergedCostsDataFiltered]);

  /** Drop invalid saved schedule vehicle IDs when merged_costs options are available (e.g. partial reg "AR0"). */
  useEffect(() => {
    const sch = config.profitabilityReportSchedule;
    const ids = sch?.filters?.vehicleIds;
    if (!ids?.length || vehicleOptions.length === 0) return;
    const pruned = pruneScheduleVehicleFilterIds(ids, vehicleOptions);
    const unchanged =
      pruned.length === ids.length && pruned.every((v, i) => v === ids[i]);
    if (unchanged) return;
    setProfitabilityReportSchedule({
      filters: { ...sch?.filters, vehicleIds: pruned },
    });
  }, [vehicleOptions, config.profitabilityReportSchedule, setProfitabilityReportSchedule]);

  const orderOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of records) {
      const id = (r.orderId ?? '').trim();
      if (isLikelyOrderIdentifier(id)) set.add(id);
    }
    for (const row of mergedCostsDataFiltered) {
      for (const id of extractOrderIdsFromMergedField(row.order_ids)) {
        set.add(id);
      }
      // Truth mode: include Palletways TrackingIDs / display names from revenue_breakdown
      for (const id of extractLookupOrderIdsFromMergedCostRow(row)) {
        if (isLikelyOrderIdentifier(id)) set.add(id);
      }
    }
    return Array.from(set).sort();
  }, [records, mergedCostsDataFiltered]);

  const customerOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of records) {
      const c = ((r as ProfitRecord).customer ?? '').trim();
      if (c) set.add(c);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [records]);

  const orderIdToCustomerLower = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of records) {
      const id = (r.orderId ?? '').trim();
      const c = ((r as ProfitRecord).customer ?? '').trim();
      if (id && c && !map.has(id)) map.set(id, c.toLowerCase());
    }
    return map;
  }, [records]);

  /** Telematics mileage: key = "vehicleId|YYYY-MM-DD", value = daily_miles record */
  const telematicsMileageMap = useMemo(() => {
    const map = new Map<string, TelematicsDailyMiles>();
    for (const record of telematicsDailyMiles) {
      const stdVid = standardiseVehicleId(record.vehicle);
      const dateKey = normalizeTelematicsDate(record.date);
      if (stdVid && dateKey) map.set(`${stdVid}|${dateKey}`, record);
    }
    return map;
  }, [telematicsDailyMiles]);


  // Map orderId to number of vehicles (by standardised vehicle reg so variants like "BD22ASZ 2" count as one)
  const orderVehicleCount = useMemo(() => {
    const map: Record<string, Set<string>> = {};
    for (const rec of records) {
      if (!map[rec.orderId]) map[rec.orderId] = new Set();
      map[rec.orderId].add(standardiseVehicleId(rec.vehicleId));
    }
    const countMap: Record<string, number> = {};
    for (const orderId in map) {
      countMap[orderId] = map[orderId].size;
    }
    return countMap;
  }, [records]);

  // When user pages the calendar to a month (or selects a period), this is the active date window. Used by Qargo's Order and trend section.
  const effectiveDateWindow = useMemo((): { startDate: string; endDate: string } | null => {
    if (selectedPeriodRange) return selectedPeriodRange;
    if (useTruth) {
      return { startDate: truthDateRange.startDate, endDate: truthDateRange.endDate };
    }
    if ((periodType === 'day' || periodType === 'week') && calendarPageMonth) {
      const y = calendarPageMonth.year;
      const m = calendarPageMonth.month;
      const startDate = `${y}-${String(m + 1).padStart(2, '0')}-01`;
      const lastDay = new Date(y, m + 1, 0).getDate();
      const endDate = `${y}-${String(m + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      return { startDate, endDate };
    }
    return null;
  }, [periodType, calendarPageMonth, selectedPeriodRange, useTruth, truthDateRange.startDate, truthDateRange.endDate]);

  useEffect(() => {
    const win = selectedPeriodRange ?? effectiveDateWindow;
    if (!win?.startDate || !win?.endDate) return;
    writeStoredProfitAgentPeriod({ date_from: win.startDate, date_to: win.endDate });
  }, [selectedPeriodRange, effectiveDateWindow]);

  const handleProfitAgentSubmit = useCallback(
    async (question: string) => {
      const q = question.trim();
      if (!q || profitAgentSubmittingRef.current) return;

      const win = selectedPeriodRange ?? effectiveDateWindow;
      if (!win?.startDate || !win?.endDate) {
        setProfitAgentPageState('error');
        setProfitAgentError('Select a month or date range before asking a question.');
        return;
      }

      profitAgentAbortRef.current?.abort();
      const controller = new AbortController();
      profitAgentAbortRef.current = controller;

      profitAgentSubmittingRef.current = true;
      profitAgentLastQuestionRef.current = q;
      setProfitAgentPageState('loading');
      setProfitAgentError('');
      setProfitAgentResult(null);
      setProfitAgentActiveDrilldown(null);
      setProfitAgentStreamingComplete(false);
      setProfitAgentQuestion(q);

      try {
        const response = await streamProfitAgentQuery(
          {
            question: q,
            context: {
              date_from: win.startDate,
              date_to: win.endDate,
              margin_threshold_pct: 6,
            },
          },
          () => {},
          controller.signal,
        );
        setProfitAgentResult(response);
        setProfitAgentPageState('success');
      } catch (err) {
        if ((err as { name?: string }).name === 'AbortError') return;
        const msg = err instanceof Error ? err.message : 'Profit Agent request failed.';
        setProfitAgentError(msg);
        setProfitAgentPageState('error');
      } finally {
        profitAgentSubmittingRef.current = false;
      }
    },
    [selectedPeriodRange, effectiveDateWindow],
  );

  const handleProfitAgentRetry = useCallback(() => {
    if (!profitAgentLastQuestionRef.current || profitAgentSubmittingRef.current) return;
    void handleProfitAgentSubmit(profitAgentLastQuestionRef.current);
  }, [handleProfitAgentSubmit]);

  const applyStoredProfitDrilldown = useCallback((parsed: StoredProfitDrilldown) => {
    if (parsed.date_from && parsed.date_to) {
      setSelectedPeriodRange({ startDate: parsed.date_from, endDate: parsed.date_to });
    }
    if (parsed.type === 'vehicle' && parsed.id) {
      setSelectedVehicleIds([standardiseVehicleId(parsed.id)]);
      setSelectedCustomerNames([]);
      setSelectedOrderIds([]);
    } else if (parsed.type === 'customer' && parsed.id) {
      setSelectedCustomerNames([parsed.id]);
      setSelectedVehicleIds([]);
      setSelectedOrderIds([]);
    } else if (parsed.focus_section === 'customer') {
      setSelectedVehicleIds([]);
      setSelectedOrderIds([]);
      if (!parsed.id) setSelectedCustomerNames([]);
    }

    if (parsed.focus_section === 'customer') {
      setProfitViewMode('data');
      setProfitTab('qargo');
      if (parsed.customer_sort_field) {
        setCustomerSortField(parsed.customer_sort_field);
      }
      if (parsed.customer_sort_asc != null) {
        setCustomerSortAsc(parsed.customer_sort_asc);
      }
      setCustomerPage(1);
      window.setTimeout(() => {
        customerProfitabilitySectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
    }
  }, []);

  useEffect(() => {
    const consumeDrilldown = () => {
      if (activeTab !== 'profitability-report') return;
      const parsed = readStoredProfitDrilldown();
      if (!parsed) return;
      applyStoredProfitDrilldown(parsed);
    };

    consumeDrilldown();
    window.addEventListener('zecure:profit-drilldown', consumeDrilldown);
    return () => window.removeEventListener('zecure:profit-drilldown', consumeDrilldown);
  }, [activeTab, applyStoredProfitDrilldown]);

  useEffect(() => {
    const focusMondayBrief = () => {
      if (variant === 'profit-agent') return;
      if (activeTab !== 'profitability-report') return;
      if (!consumeMondayBriefFocus()) return;
      setProfitViewMode('brief');
      setMondayBriefOpen(true);
      window.setTimeout(() => {
        mondayBriefSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
    };

    focusMondayBrief();
    window.addEventListener(MONDAY_BRIEF_FOCUS_EVENT, focusMondayBrief);
    return () => window.removeEventListener(MONDAY_BRIEF_FOCUS_EVENT, focusMondayBrief);
  }, [activeTab, variant]);

  const applyProfitAgentDrilldown = useCallback((d: ProfitAgentDrilldown) => {
    setProfitAgentActiveDrilldown(d);
  }, []);

  const openFullProfitabilityReport = useCallback(
    (d: ProfitAgentDrilldown) => {
      const win = selectedPeriodRange ?? effectiveDateWindow;
      if (!win?.startDate || !win?.endDate) return;
      const stored = buildStoredProfitDrilldown(
        d,
        { date_from: win.startDate, date_to: win.endDate },
        {
          question: profitAgentQuestion || profitAgentResult?.question,
          tools_used: profitAgentResult?.tools_used,
        },
      );
      writeStoredProfitDrilldown(stored);
      setActiveTab('profitability-report');
      applyStoredProfitDrilldown(stored);
    },
    [
      selectedPeriodRange,
      effectiveDateWindow,
      setActiveTab,
      profitAgentQuestion,
      profitAgentResult?.question,
      profitAgentResult?.tools_used,
      applyStoredProfitDrilldown,
    ],
  );

  const profitAgentEvidenceRows = useMemo(() => {
    if (!profitAgentActiveDrilldown) return [];
    const win = selectedPeriodRange ?? effectiveDateWindow;
    if (!win) return [];
    const d = profitAgentActiveDrilldown;
    if (d.type === 'vehicle') {
      if (d.margin_pct != null && d.revenue_gbp != null) {
        return [
          {
            label: d.label ?? d.id,
            revenue: Number(d.revenue_gbp),
            marginPct: d.margin_pct,
            profit: Number(d.margin_gbp ?? 0),
          },
        ];
      }
      const vid = standardiseVehicleId(d.id);
      const rows = mergedCostsDataFiltered.filter(
        (r) => (r.date || '').slice(0, 10) >= win.startDate && (r.date || '').slice(0, 10) <= win.endDate,
      ).filter((r) => standardiseVehicleId(r.vehicle) === vid);
      const revenue = rows.reduce((s, r) => s + (r.revenue ?? 0), 0);
      const fuel = rows.reduce((s, r) => s + (r.fuel_cost ?? r.jigsaw_fuel_cost ?? 0), 0);
      const driver = rows.reduce((s, r) => s + (r.driver_cost ?? 0), 0);
      const standing = resolveStandingCostForMergedRows(rows, briefStandingOptions).standingTotal;
      const profit = revenue - fuel - driver - standing;
      return [
        {
          label: d.label ?? d.id,
          revenue,
          marginPct: revenue > 0 ? (profit / revenue) * 100 : null,
          profit,
        },
      ];
    }
    if (d.type === 'customer') {
      if (d.margin_pct != null && d.revenue_gbp != null) {
        return [
          {
            label: d.label ?? d.id,
            revenue: Number(d.revenue_gbp),
            marginPct: d.margin_pct,
            profit: Number(d.margin_gbp ?? 0),
          },
        ];
      }
      const name = d.label ?? d.id;
      const rows = customerMonthlyProfitability.filter(
        (r) =>
          (r.period || '').slice(0, 7) >= win.startDate.slice(0, 7) &&
          (r.period || '').slice(0, 7) <= win.endDate.slice(0, 7) &&
          String(r.customer_name || '').trim() === name,
      );
      return rows.map((r) => ({
        label: r.period || name,
        revenue: Number(r.total_revenue_gbp ?? 0),
        marginPct: r.profit_margin_pct ?? null,
        profit: Number(r.profit_gbp ?? 0),
      }));
    }
    return [];
  }, [
    profitAgentActiveDrilldown,
    mergedCostsDataFiltered,
    customerMonthlyProfitability,
    selectedPeriodRange,
    effectiveDateWindow,
    briefStandingOptions,
  ]);

  useEffect(() => {
    if (profitViewMode !== 'brief') return;
    const win = selectedPeriodRange ?? effectiveDateWindow;
    if (!win?.startDate || !win?.endDate) {
      setMondayBriefApiRaw(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const api = await fetchMondayBrief({
          date_from: win.startDate,
          date_to: win.endDate,
          margin_threshold_pct: 6,
        });
        if (cancelled) return;
        setMondayBriefApiRaw(api);
      } catch {
        if (!cancelled) setMondayBriefApiRaw(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [profitViewMode, selectedPeriodRange, effectiveDateWindow, variant]);

  useEffect(() => {
    if (profitViewMode !== 'brief') return;
    const win = selectedPeriodRange ?? effectiveDateWindow;
    if (!win?.startDate || !win?.endDate) {
      setUtilisationVehicleLevelMap(null);
      setUtilisationFleetScore(null);
      return;
    }

    const ym = (win.startDate || '').slice(0, 7);
    const parts = ym.split('-');
    const y = Number(parts[0]);
    const m1 = Number(parts[1]);
    const monthStartIso = ym && ym.length === 7 ? `${ym}-01` : win.startDate;
    const monthEndIso =
      ym && ym.length === 7 && Number.isFinite(y) && Number.isFinite(m1) && m1 >= 1 && m1 <= 12
        ? lastDayOfMonthISO(y, m1)
        : win.endDate;

    if (useTruth && useTruthUtil && !isDemoMode) {
      // Reuse report-window vehicle-days when they already cover the brief month.
      if (
        truthUtilisationRows.length > 0 &&
        truthDateRange.startDate <= monthEndIso &&
        truthDateRange.endDate >= monthStartIso
      ) {
        setUtilisationVehicleLevelMap(
          buildMondayBriefUtilisationByVehicle(truthUtilisationRows, monthStartIso, monthEndIso),
        );
        setUtilisationFleetScore(
          computeFleetUtilisationScoreMean(truthUtilisationRows, monthStartIso, monthEndIso),
        );
        return;
      }

      let cancelled = false;
      (async () => {
        try {
          const rows = await fetchTruthUtilisationVehicleDays(monthStartIso, monthEndIso, {
            cacheBust: utilisationRefreshToken,
          });
          if (cancelled) return;
          setUtilisationVehicleLevelMap(buildMondayBriefUtilisationByVehicle(rows, monthStartIso, monthEndIso));
          setUtilisationFleetScore(computeFleetUtilisationScoreMean(rows, monthStartIso, monthEndIso));
        } catch (err) {
          if (cancelled) return;
          console.warn('[MondayBrief] truth utilisation fetch failed:', err);
          setUtilisationVehicleLevelMap(null);
          setUtilisationFleetScore(null);
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    const toYmd = (iso: string) => (iso || '').slice(0, 10).replace(/-/g, '');
    const monthStartYmd = toYmd(monthStartIso);
    const monthEndYmd = toYmd(monthEndIso);

    const parseCsvLine = (line: string) => {
      const out: string[] = [];
      let cur = '';
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const c = line[i];
        if (c === '"') {
          inQuotes = !inQuotes;
          continue;
        }
        if (c === ',' && !inQuotes) {
          out.push(cur.trim());
          cur = '';
          continue;
        }
        cur += c;
      }
      out.push(cur.trim());
      return out;
    };

    const parseCsvToJson = (csvText: string) => {
      const lines = csvText.trim().split(/\r?\n/).filter((l) => l.trim());
      if (lines.length < 2) return [];
      const headers = parseCsvLine(lines[0]);
      return lines.slice(1).map((line) => {
        const vals = parseCsvLine(line);
        const rec: Record<string, string> = {};
        for (let i = 0; i < headers.length; i++) rec[headers[i]] = vals[i] ?? '';
        return rec;
      });
    };

    const getField = (row: Record<string, string>, ...candidates: string[]) => {
      for (const k of candidates) {
        if (k in row) return row[k] ?? '';
        const lk = k.toLowerCase();
        const hit = Object.keys(row).find((kk) => kk.toLowerCase() === lk);
        if (hit) return row[hit] ?? '';
      }
      return '';
    };

    const parseNum = (s: string) => {
      const t = (s ?? '').trim();
      if (!t) return null;
      const n = Number(t);
      return Number.isFinite(n) ? n : null;
    };

    const pickUtilisationPeriodsByPreference = (periods: string[]) => {
      const parseRange = (p: string) => {
        const m = String(p || '').trim().match(/^(\d{8})_to_(\d{8})$/);
        return m ? { start: m[1], end: m[2] } : null;
      };
      const overlaps = (a: { start: string; end: string }, b: { start: string; end: string }) =>
        a.start <= b.end && b.start <= a.end;
      const month = { start: monthStartYmd, end: monthEndYmd };

      const candidates = (periods || [])
        .map((p) => ({ p, r: parseRange(p) }))
        .filter((x): x is { p: string; r: { start: string; end: string } } => Boolean(x.r))
        .filter((x) => overlaps(x.r, month));

      if (candidates.length === 0) return [];

      // Prefer:
      // 1) ranges that fully contain the month
      // 2) smaller ranges (closer to the month)
      // 3) if tie, pick the one that starts closest to month start
      const score = (c: { r: { start: string; end: string } }) => {
        const contains = c.r.start <= month.start && c.r.end >= month.end ? 1 : 0;
        const span = Math.max(0, Number(c.r.end) - Number(c.r.start));
        const startDist = Math.abs(Number(c.r.start) - Number(month.start));
        return { contains, span, startDist };
      };
      const sorted = [...candidates].sort((a, b) => {
        const sa = score(a);
        const sb = score(b);
        if (sa.contains !== sb.contains) return sb.contains - sa.contains;
        if (sa.span !== sb.span) return sa.span - sb.span;
        return sa.startDist - sb.startDist;
      });
      return sorted.map((x) => x.p);
    };

    let cancelled = false;
    (async () => {
      try {
        const periodsRes = await authenticatedFetch(API_ENDPOINTS.periods('logistics'));
        if (!periodsRes.ok) throw new Error(`utilisation periods HTTP ${periodsRes.status}`);
        const periodsJson = (await periodsRes.json()) as unknown;
        const periodsContainer =
          periodsJson && typeof periodsJson === 'object'
            ? (periodsJson as { periods?: unknown })
            : null;
        const periods = Array.isArray(periodsContainer?.periods)
          ? periodsContainer.periods
          : (Array.isArray(periodsJson) ? periodsJson : []);
        const preferredPeriods = pickUtilisationPeriodsByPreference(periods.map(String));
        if (preferredPeriods.length === 0) throw new Error('No utilisation period found overlapping selected month.');

        let csvText: string | null = null;
        let chosenPeriod: string | null = null;
        let lastStatus: number | null = null;
        for (const p of preferredPeriods.slice(0, 8)) {
          const res = await authenticatedFetch(API_ENDPOINTS.vehicleLevelCSV(p, 'logistics'));
          if (res.ok) {
            csvText = await res.text();
            chosenPeriod = p;
            break;
          }
          lastStatus = res.status;
          // If it's not found, try the next candidate; otherwise fail fast.
          if (res.status !== 404) throw new Error(`utilisation vehicle-level CSV HTTP ${res.status}`);
        }
        if (csvText == null || chosenPeriod == null) {
          throw new Error(`utilisation vehicle-level CSV HTTP ${lastStatus ?? 404}`);
        }
        const rows = parseCsvToJson(csvText);

        const map = new Map<string, MondayBriefVehicleUtilisation>();
        const scores: number[] = [];
        for (const r of rows) {
          const rawVid = getField(r, 'Vehicle_ID', 'vehicle_id', 'vehicleId', 'VehicleId', 'Vehicle ID', 'AssetName', 'asset_name');
          const vid = standardiseVehicleId(String(rawVid ?? '').trim()) || String(rawVid ?? '').trim();
          if (!vid) continue;
          const active = parseNum(getField(r, 'active_hours', 'Active_hours', 'Active Hours', 'active hours'));
          const down = parseNum(getField(r, 'downtime_hours', 'Downtime_hours', 'Downtime Hours', 'downtime hours'));
          const utilScore =
            parseNum(getField(r, 'utilisation_score', 'Utilisation_Score', 'Utilisation Score')) ??
            (() => {
              const rate = parseNum(getField(r, 'utilisation_rate', 'Utilisation_rate', 'Utilisation Rate', 'utilisation rate'));
              return rate != null ? rate * 100 : null;
            })();
          if (utilScore != null) scores.push(utilScore);
          map.set(vid, {
            utilisation_score: utilScore ?? 0,
            active_hours: active ?? 0,
            downtime_hours: down ?? 0,
            num_trips: parseNum(getField(r, 'num_trips', 'Num_trips', 'Num Trips', 'num trips', 'trip_count', 'Trip Count')) ?? 0,
            vehicleDays: 1,
          });
        }
        const fleetScore = scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : null;

        if (cancelled) return;
        setUtilisationVehicleLevelMap(map);
        setUtilisationFleetScore(fleetScore);
      } catch (err) {
        if (cancelled) return;
        // Don't spam console on every render; this runs only on month/view change
        console.warn('[MondayBrief] utilisation fetch failed:', err);
        setUtilisationVehicleLevelMap(null);
        setUtilisationFleetScore(null);
      }
    })();

    return () => { cancelled = true; };
  }, [profitViewMode, selectedPeriodRange, effectiveDateWindow, useTruth, useTruthUtil, isDemoMode, utilisationRefreshToken, truthUtilisationRows, truthDateRange.startDate, truthDateRange.endDate]);

  const filteredRecords = useMemo(() => {
    let base = records;
    if (selectedVehicleIds.length > 0) {
      base = base.filter((r) => selectedVehicleIds.includes(standardiseVehicleId(r.vehicleId)));
    }
    if (selectedOrderIds.length > 0) {
      base = base.filter((r) => orderIdMatchesLookupSelection(r.orderId, selectedOrderIds));
    }
    if (selectedCustomerNames.length > 0) {
      const wanted = new Set(selectedCustomerNames.map((c) => c.trim().toLowerCase()).filter(Boolean));
      base = base.filter((r) => wanted.has((((r as ProfitRecord).customer ?? '') as string).trim().toLowerCase()));
    }
    // Filter by selected period or by calendar month: include orders whose Origin Date OR Destination Date falls within the window (so month buttons show orders that originate or deliver in that month)
    const dateWindow = selectedPeriodRange ?? effectiveDateWindow;
    if (dateWindow) {
      const withDateFilter = base.filter((r) => {
        const originDateStr = (r as ProfitRecord).origin_end_timestamp_local ?? (r as ProfitRecord).origin_end_timestamp ?? r.date;
        const destDateStr = (r as ProfitRecord).destination_end_timestamp_local ?? (r as ProfitRecord).destination_timestamp ?? r.date;
        const originD = toComparableDate(originDateStr || '');
        const destD = toComparableDate(destDateStr || '');
        if (!originD && !destD) return true; // unparseable dates: include row so we don't hide data
        const inRange = (d: string) => d && d >= dateWindow.startDate && d <= dateWindow.endDate;
        return inRange(originD) || inRange(destD);
      });
      // When filtering by calendar month only (no explicit period selected), if that would show zero orders, show all orders instead so the user isn't left with an empty table on load
      const isCalendarMonthOnly = !selectedPeriodRange && effectiveDateWindow != null;
      if (isCalendarMonthOnly && withDateFilter.length === 0 && base.length > 0) {
        // Keep base unchanged - don't apply date filter
      } else {
        base = withDateFilter;
      }
    }

    // Apply split for revenue/cost/profit if multiple vehicles
    const adjusted = base.map((rec) => {
      const vehicleCount = orderVehicleCount[rec.orderId] || 1;
      return {
        ...rec,
        revenue: rec.revenue / vehicleCount,
        cost: rec.cost / vehicleCount,
        profit: rec.profit / vehicleCount,
        journeySplitCount: vehicleCount,
      };
    });

    const sorted = [...adjusted].sort((a, b) => {
      let aVal: string | number = '';
      let bVal: string | number = '';
      switch (sortField) {
        case 'date':
          aVal = a.date;
          bVal = b.date;
          break;
        case 'orderId':
          aVal = a.orderId || '';
          bVal = b.orderId || '';
          break;
        case 'vehicleId':
          aVal = a.vehicleId || '';
          bVal = b.vehicleId || '';
          break;
        case 'journeySplitCount':
          aVal = a.journeySplitCount || 1;
          bVal = b.journeySplitCount || 1;
          break;
        case 'mileage':
          aVal = getMileageMiles(a);
          bVal = getMileageMiles(b);
          break;
        case 'revenue':
          aVal = a.revenue;
          bVal = b.revenue;
          break;
        case 'marginPct':
          aVal = orderMarginPct(a as ProfitRecord) ?? -Infinity;
          bVal = orderMarginPct(b as ProfitRecord) ?? -Infinity;
          break;
        case 'cost':
          aVal = a.cost;
          bVal = b.cost;
          break;
        case 'profit':
          aVal = a.profit;
          bVal = b.profit;
          break;
        case 'originDate':
          aVal = (a as ProfitRecord).origin_end_timestamp_local ?? (a as ProfitRecord).origin_end_timestamp ?? a.date ?? '';
          bVal = (b as ProfitRecord).origin_end_timestamp_local ?? (b as ProfitRecord).origin_end_timestamp ?? b.date ?? '';
          break;
        case 'vehiclePickup':
          aVal = (a as ProfitRecord).vehicle_pickup ?? '';
          bVal = (b as ProfitRecord).vehicle_pickup ?? '';
          break;
        case 'originCity':
          aVal = formatRoute((a as ProfitRecord).origin_city, (a as ProfitRecord).destination_city);
          bVal = formatRoute((b as ProfitRecord).origin_city, (b as ProfitRecord).destination_city);
          break;
        case 'vehicleObserved':
          aVal = (a as ProfitRecord).vehicle_observed ?? a.vehicleId ?? '';
          bVal = (b as ProfitRecord).vehicle_observed ?? b.vehicleId ?? '';
          break;
        case 'destinationCity':
          aVal = (a as ProfitRecord).destination_city ?? '';
          bVal = (b as ProfitRecord).destination_city ?? '';
          break;
        case 'serviceLevel':
          aVal = (a as ProfitRecord).service_level_name ?? '';
          bVal = (b as ProfitRecord).service_level_name ?? '';
          break;
        case 'customer':
          aVal = (a as ProfitRecord).customer ?? '';
          bVal = (b as ProfitRecord).customer ?? '';
          break;
        case 'goodsQuantity': {
          const toNum = (x: ProfitRecord) => {
            const v = x.goods_quantity;
            if (v == null || String(v).trim() === '') return -1;
            const n = parseInt(String(v).trim(), 10);
            return Number.isNaN(n) ? -1 : n;
          };
          aVal = toNum(a as ProfitRecord);
          bVal = toNum(b as ProfitRecord);
          break;
        }
        case 'goodsWeight': {
          const toNum = (x: ProfitRecord) => {
            const v = x.goods_weight;
            if (v == null || String(v).trim() === '') return -1;
            const n = parseFloat(String(v).trim());
            return Number.isNaN(n) ? -1 : n;
          };
          aVal = toNum(a as ProfitRecord);
          bVal = toNum(b as ProfitRecord);
          break;
        }
        default:
          break;
      }
      return compareWithSortedColumnDashLast(
        profitRecordSortColumnIsDash(a, sortField),
        profitRecordSortColumnIsDash(b, sortField),
        () => {
          if (typeof aVal === 'string' && typeof bVal === 'string') {
            const cmp = aVal.localeCompare(bVal, undefined, { sensitivity: 'base', numeric: true });
            return sortAsc ? cmp : -cmp;
          }
          if (aVal < bVal) return sortAsc ? -1 : 1;
          if (aVal > bVal) return sortAsc ? 1 : -1;
          return 0;
        },
      );
    });

    return sorted;
  }, [records, selectedVehicleIds, selectedOrderIds, selectedCustomerNames, effectiveDateWindow, selectedPeriodRange, sortField, sortAsc, orderVehicleCount]);

  // Records for chart only: same vehicle/order filters as filteredRecords but NEVER filter by date, so chart always shows full trend lines
  const filteredRecordsFullRange = useMemo(() => {
    let base = records;
    if (selectedVehicleIds.length > 0) {
      base = base.filter((r) => selectedVehicleIds.includes(standardiseVehicleId(r.vehicleId)));
    }
    if (selectedOrderIds.length > 0) {
      base = base.filter((r) => orderIdMatchesLookupSelection(r.orderId, selectedOrderIds));
    }
    if (selectedCustomerNames.length > 0) {
      const wanted = new Set(selectedCustomerNames.map((c) => c.trim().toLowerCase()).filter(Boolean));
      base = base.filter((r) => wanted.has((((r as ProfitRecord).customer ?? '') as string).trim().toLowerCase()));
    }
    const adjusted = base.map((rec) => {
      const vehicleCount = orderVehicleCount[rec.orderId] || 1;
      return {
        ...rec,
        revenue: rec.revenue / vehicleCount,
        cost: rec.cost / vehicleCount,
        profit: rec.profit / vehicleCount,
        journeySplitCount: vehicleCount,
      };
    });
    return adjusted;
  }, [records, selectedVehicleIds, selectedOrderIds, selectedCustomerNames, orderVehicleCount]);

  // Apply orderVehicleOverrides and rowOverrides to get display rows for Daily Profitability
  const { effectiveRecords, effectiveOrderVehicleCount } = useMemo(() => {
    const byOrder = new Map<string, ProfitRecord[]>();
    for (const rec of filteredRecords) {
      const list = byOrder.get(rec.orderId) || [];
      list.push(rec);
      byOrder.set(rec.orderId, list);
    }
    const result: ProfitRecord[] = [];
    const countMap: Record<string, number> = {};
    for (const [orderId, rows] of byOrder) {
      const override = orderVehicleOverrides[orderId];
      if (override && override.length > 0) {
        const totalRevenue = rows[0].revenue * (orderVehicleCount[orderId] || 1);
        const totalCost = rows[0].cost * (orderVehicleCount[orderId] || 1);
        const n = override.length;
        countMap[orderId] = n;
        for (const vid of override) {
          const base = { ...rows[0], vehicleId: vid, revenue: totalRevenue / n, cost: totalCost / n, profit: (totalRevenue - totalCost) / n, journeySplitCount: n };
          const key = `${orderId}-${vid}-${base.date}`;
          const ro = rowOverrides[key];
          result.push(ro ? { ...base, ...ro, profit: ((ro.revenue ?? base.revenue) - (ro.cost ?? base.cost)) } : base);
        }
      } else {
        countMap[orderId] = rows.length;
        for (const row of rows) {
          const key = `${row.orderId}-${row.vehicleId}-${row.date}`;
          const ro = rowOverrides[key];
          const merged = ro ? { ...row, ...ro, profit: ((ro.revenue ?? row.revenue) - (ro.cost ?? row.cost)) } : row;
          result.push(merged);
        }
      }
    }
    const getRowKey = (r: ProfitRecord) => `${r.orderId}-${r.vehicleId}-${r.date}`;
    const sorted = [...result].sort((a, b) => {
      let aVal: string | number = '';
      let bVal: string | number = '';
      switch (sortField) {
        case 'profit':
          aVal = a.profit - getDriverCost(a, driverCostVehicleTypeOverrides[getRowKey(a)] ?? '26t', driverConfig);
          bVal = b.profit - getDriverCost(b, driverCostVehicleTypeOverrides[getRowKey(b)] ?? '26t', driverConfig);
          break;
        case 'driverCost':
          aVal = getDriverCost(a, driverCostVehicleTypeOverrides[getRowKey(a)] ?? '26t', driverConfig);
          bVal = getDriverCost(b, driverCostVehicleTypeOverrides[getRowKey(b)] ?? '26t', driverConfig);
          break;
        case 'orderPlusDriver':
          aVal = a.cost + getDriverCost(a, driverCostVehicleTypeOverrides[getRowKey(a)] ?? '26t', driverConfig);
          bVal = b.cost + getDriverCost(b, driverCostVehicleTypeOverrides[getRowKey(b)] ?? '26t', driverConfig);
          break;
        case 'date':
          aVal = a.date;
          bVal = b.date;
          break;
        case 'orderId':
          aVal = a.orderId || '';
          bVal = b.orderId || '';
          break;
        case 'vehicleId':
          aVal = a.vehicleId || '';
          bVal = b.vehicleId || '';
          break;
        case 'journeySplitCount':
          aVal = a.journeySplitCount || 1;
          bVal = b.journeySplitCount || 1;
          break;
        case 'mileage':
          aVal = getMileageMiles(a);
          bVal = getMileageMiles(b);
          break;
        case 'revenue':
          aVal = a.revenue;
          bVal = b.revenue;
          break;
        case 'marginPct':
          aVal = orderMarginPct(a) ?? -Infinity;
          bVal = orderMarginPct(b) ?? -Infinity;
          break;
        case 'cost':
          aVal = a.cost;
          bVal = b.cost;
          break;
        case 'originDate':
          aVal = a.origin_end_timestamp_local ?? a.origin_end_timestamp ?? a.date ?? '';
          bVal = b.origin_end_timestamp_local ?? b.origin_end_timestamp ?? b.date ?? '';
          break;
        case 'vehiclePickup':
          aVal = a.vehicle_pickup ?? '';
          bVal = b.vehicle_pickup ?? '';
          break;
        case 'originCity':
          aVal = formatRoute(a.origin_city, a.destination_city);
          bVal = formatRoute(b.origin_city, b.destination_city);
          break;
        case 'vehicleObserved':
          aVal = a.vehicle_observed ?? a.vehicleId ?? '';
          bVal = b.vehicle_observed ?? b.vehicleId ?? '';
          break;
        case 'destinationCity':
          aVal = a.destination_city ?? '';
          bVal = b.destination_city ?? '';
          break;
        case 'serviceLevel':
          aVal = a.service_level_name ?? '';
          bVal = b.service_level_name ?? '';
          break;
        case 'customer':
          aVal = a.customer ?? '';
          bVal = b.customer ?? '';
          break;
        case 'goodsQuantity': {
          const toNum = (x: ProfitRecord) => {
            const v = x.goods_quantity;
            if (v == null || String(v).trim() === '') return -1;
            const n = parseInt(String(v).trim(), 10);
            return Number.isNaN(n) ? -1 : n;
          };
          aVal = toNum(a);
          bVal = toNum(b);
          break;
        }
        case 'goodsWeight': {
          const toNum = (x: ProfitRecord) => {
            const v = x.goods_weight;
            if (v == null || String(v).trim() === '') return -1;
            const n = parseFloat(String(v).trim());
            return Number.isNaN(n) ? -1 : n;
          };
          aVal = toNum(a);
          bVal = toNum(b);
          break;
        }
        default:
          break;
      }
      return compareWithSortedColumnDashLast(
        profitRecordSortColumnIsDash(a, sortField),
        profitRecordSortColumnIsDash(b, sortField),
        () => {
          if (typeof aVal === 'string' && typeof bVal === 'string') {
            const cmp = aVal.localeCompare(bVal, undefined, { sensitivity: 'base', numeric: true });
            return sortAsc ? cmp : -cmp;
          }
          if (aVal < bVal) return sortAsc ? -1 : 1;
          if (aVal > bVal) return sortAsc ? 1 : -1;
          return 0;
        },
      );
    });
    return { effectiveRecords: sorted, effectiveOrderVehicleCount: countMap };
  }, [filteredRecords, orderVehicleOverrides, rowOverrides, orderVehicleCount, sortField, sortAsc, driverCostVehicleTypeOverrides]);

  // Get unique dates for the date filter dropdown — sourced from merged_costs.csv
  const availableDates = useMemo(() => {
    const dates = Array.from(new Set(mergedCostsDataFiltered.map(r => r.date.slice(0, 10)))).filter(Boolean).sort().reverse();
    return dates;
  }, [mergedCostsDataFiltered]);

  // Generate week periods from available dates
  const availableWeeks = useMemo(() => {
    if (availableDates.length === 0) return [];
    
    const weeks: { label: string; startDate: string; endDate: string }[] = [];
    const sortedDates = [...availableDates].sort();
    
    // Get the earliest and latest dates
    const earliest = new Date(sortedDates[0]);
    const latest = new Date(sortedDates[sortedDates.length - 1]);
    
    // Start from the Monday of the week containing the earliest date
    const startDate = new Date(earliest);
    const dayOfWeek = startDate.getDay();
    const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    startDate.setDate(startDate.getDate() + daysToMonday);
    
    const currentWeekStart = new Date(startDate);
    let weekNumber = 1;
    
    while (currentWeekStart <= latest) {
      const weekEnd = new Date(currentWeekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      
      const startStr = currentWeekStart.toISOString().split('T')[0];
      const endStr = weekEnd.toISOString().split('T')[0];
      // Only include weeks that have at least one date in our available dates
      const hasData = availableDates.some(date => date >= startStr && date <= endStr);
      if (hasData) {
        weeks.push({
          label: `Week ${weekNumber} (${startStr} to ${endStr})`,
          startDate: startStr,
          endDate: endStr,
        });
        weekNumber++;
      }
      
      currentWeekStart.setDate(currentWeekStart.getDate() + 7);
    }
    
    return weeks.reverse(); // Most recent first
  }, [availableDates]);

  // Generate month periods from available dates
  const availableMonths = useMemo(() => {
    if (availableDates.length === 0) return [];
    
    const months: { label: string; startDate: string; endDate: string }[] = [];
    const monthsMap = new Map<string, { min: string; max: string }>();
    
    availableDates.forEach(date => {
      const yearMonth = date.substring(0, 7); // YYYY-MM
      if (!monthsMap.has(yearMonth)) {
        monthsMap.set(yearMonth, { min: date, max: date });
      } else {
        const current = monthsMap.get(yearMonth)!;
        if (date < current.min) current.min = date;
        if (date > current.max) current.max = date;
      }
    });
    
    const sortedMonths = Array.from(monthsMap.entries()).sort((a, b) => b[0].localeCompare(a[0]));
    
    sortedMonths.forEach(([yearMonth, { min, max }]) => {
      if (!yearMonth || !min || !max) return;
      const parts = yearMonth.split('-');
      const year = parts[0];
      const month = parts[1];
      if (!year || !month) return;
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthIndex = parseInt(month, 10) - 1;
      const monthName = (monthIndex >= 0 && monthIndex < 12) ? monthNames[monthIndex] : month;
      const shortYear = year.length >= 2 ? year.slice(-2) : year;
      months.push({
        label: `${monthName} ${shortYear} (${min} to ${max})`,
        startDate: min,
        endDate: max,
      });
    });
    
    return months;
  }, [availableDates]);

  // Month quick-buttons strip: show 4 at a time with arrows
  const quickMonthButtons = useMemo(
    () => (Array.isArray(availableMonths) ? availableMonths.filter((m) => m?.startDate && m?.endDate) : []),
    [availableMonths],
  );
  // Month picker: map the available months to { year, month } records for the shared <MonthPicker>.
  const profitMonthRecords = useMemo(
    () =>
      quickMonthButtons.map((m) => {
        const [y, mm] = m.startDate.split('-');
        return {
          year: parseInt(y, 10),
          month: parseInt(mm, 10),
          hasData: true,
          startDate: m.startDate,
          endDate: m.endDate,
        };
      }),
    [quickMonthButtons],
  );

  // Derive the currently selected month from the active period range.
  const selectedProfitMonth = useMemo(() => {
    if (!selectedPeriodRange) return null;
    const [y, mm] = selectedPeriodRange.startDate.split('-');
    const year = parseInt(y, 10);
    const month = parseInt(mm, 10);
    if (!year || !month) return null;
    return { year, month };
  }, [selectedPeriodRange]);

  const selectProfitMonth = useCallback(
    (mo: { year: number; month: number; startDate: string; endDate: string }) => {
      setSelectedPeriodRange({ startDate: mo.startDate, endDate: mo.endDate });
      setCalendarPageMonth({ year: mo.year, month: mo.month - 1 });
      setSelectedSummaryDate('');
    },
    [],
  );

  // "Mon YY" (e.g. "Jan 26") to match the existing quick-button labels.
  const formatProfitMonthLabel = useCallback(
    (year: number, month: number) =>
      formatYmd(`${year}-${String(month).padStart(2, '0')}-01`, { month: 'short', year: '2-digit' }),
    [],
  );

  // Get the appropriate options based on period type
  const periodOptions = useMemo(() => {
    switch (periodType) {
      case 'day':
        return availableDates.map(date => ({ label: date, startDate: date, endDate: date }));
      case 'week':
        return availableWeeks ?? [];
      case 'month':
        return Array.isArray(availableMonths) ? availableMonths : [];
      default:
        return [];
    }
  }, [periodType, availableDates, availableWeeks, availableMonths]);

  // Note: selectedSummaryDate starts as empty string, which means "All Dates"

  // Clean up invalid selections when vehicle/order options change (but NOT when date filter changes)
  // This ensures that selecting a date doesn't clear vehicle filters
  useEffect(() => {
    if (selectedVehicleIds.length > 0) {
      const validVehicles = selectedVehicleIds.filter(id => vehicleOptions.includes(id));
      if (validVehicles.length !== selectedVehicleIds.length) {
        setSelectedVehicleIds(validVehicles);
      }
    }
    if (selectedOrderIds.length > 0) {
      const validOrders = selectedOrderIds.filter(
        (id) =>
          orderOptions.some((o) => o === id || o.toLowerCase() === id.toLowerCase()) ||
          (isLikelyOrderIdentifier(id) && !isLikelyEntityUlid(id)),
      );
      if (validOrders.length !== selectedOrderIds.length) {
        setSelectedOrderIds(validOrders);
      }
    }
    if (selectedCustomerNames.length > 0) {
      const wanted = new Set(customerOptions.map((c) => c.toLowerCase()));
      const validCustomers = selectedCustomerNames.filter((c) => wanted.has(c.trim().toLowerCase()));
      if (validCustomers.length !== selectedCustomerNames.length) setSelectedCustomerNames(validCustomers);
    }
  }, [vehicleOptions, orderOptions, customerOptions, selectedCustomerNames]); // Removed selectedPeriodRange from dependencies

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
    setSummaryPage(1);
    setCustomerPage(1);
  }, [selectedPeriodRange, selectedVehicleIds, selectedOrderIds, selectedCustomerNames]);

  // Reset page when tab changes
  useEffect(() => {
    setPage(1);
  }, [profitTab]);

  // Reset selected period and calendar month when period type changes
  useEffect(() => {
    setSelectedSummaryDate('');
    setSelectedPeriodRange(null);
    setCalendarPageMonth(null);
  }, [periodType]);

  // When switching away from month view, allow default month to be set again when returning
  const hasSetDefaultMonthRef = useRef(false);
  useEffect(() => {
    if (periodType !== 'month') hasSetDefaultMonthRef.current = false;
  }, [periodType]);

  // On Report period change: clear stale week/month filters so they re-bind inside the new window.
  const hasSetInitialPeriodRangeRef = useRef(false);
  const hasSnappedTruthMonthRef = useRef(false);
  useEffect(() => {
    hasSetInitialPeriodRangeRef.current = false;
    hasSnappedTruthMonthRef.current = false;
    setSelectedSummaryDate('');
    if (useTruth) {
      setSelectedPeriodRange({
        startDate: truthDateRange.startDate,
        endDate: truthDateRange.endDate,
      });
      const [y, m] = truthDateRange.startDate.split('-');
      if (y && m) {
        setCalendarPageMonth({ year: parseInt(y, 10), month: parseInt(m, 10) - 1 });
      }
      hasSetInitialPeriodRangeRef.current = true;
      return;
    }
    setSelectedPeriodRange(null);
    setCalendarPageMonth(null);
  }, [truthDateRange.startDate, truthDateRange.endDate, useTruth]);

  // On first entry / after Report period change, default selected filter to the most recent available month in scope.
  useEffect(() => {
    if (useTruth) return;
    if (hasSetInitialPeriodRangeRef.current) return;
    if (!selectedPeriodRange && availableMonths.length > 0) {
      const first = availableMonths[0]; // availableMonths is sorted descending, so first is most recent
      setSelectedPeriodRange({ startDate: first.startDate, endDate: first.endDate });
      setSelectedSummaryDate(first.label ?? '');
      hasSetInitialPeriodRangeRef.current = true;
    }
  }, [availableMonths, selectedPeriodRange, useTruth]);

  // Truth: once rows load, snap the UI filter to the most recent month that has data
  // (so month buttons highlight correctly instead of staying on the full report-period window).
  useEffect(() => {
    if (!useTruth || isDemoMode) return;
    if (availableMonths.length === 0) return;
    const first = availableMonths[0];
    if (!first?.startDate || !first?.endDate) return;
    const matchesAMonth = availableMonths.some(
      (m) =>
        selectedPeriodRange?.startDate === m.startDate &&
        selectedPeriodRange?.endDate === m.endDate,
    );
    if (matchesAMonth) {
      hasSnappedTruthMonthRef.current = true;
      return;
    }
    const isFullReportPeriod =
      !!selectedPeriodRange &&
      selectedPeriodRange.startDate === truthDateRange.startDate &&
      selectedPeriodRange.endDate === truthDateRange.endDate;
    // Snap when still on the full report window, or on first bind after a report-period change
    if (!isFullReportPeriod && hasSnappedTruthMonthRef.current) return;
    setSelectedPeriodRange({ startDate: first.startDate, endDate: first.endDate });
    setSelectedSummaryDate(first.label ?? '');
    const [y, m] = first.startDate.split('-');
    if (y && m) {
      setCalendarPageMonth({ year: parseInt(y, 10), month: parseInt(m, 10) - 1 });
    }
    hasSnappedTruthMonthRef.current = true;
  }, [
    useTruth,
    isDemoMode,
    availableMonths,
    selectedPeriodRange,
    truthDateRange.startDate,
    truthDateRange.endDate,
  ]);

  // When in month mode, default to the most recent month (e.g. "Feb 2026") so that button appears selected
  useEffect(() => {
    if (useTruth) return;
    if (periodType === 'month' && availableMonths.length > 0 && !hasSetDefaultMonthRef.current) {
      const first = availableMonths[0]; // availableMonths is sorted descending, so first is most recent
      setSelectedPeriodRange({ startDate: first.startDate, endDate: first.endDate });
      setSelectedSummaryDate(first.label ?? '');
      hasSetDefaultMonthRef.current = true;
    }
  }, [periodType, availableMonths, useTruth]);

  // Default to the most recent month (February when data is Jan?eb) as soon as we're in day/week mode with data, so calendar filtering applies on tab enter.
  useEffect(() => {
    if (useTruth) return;
    if ((periodType === 'day' || periodType === 'week') && availableMonths.length > 0 && calendarPageMonth === null) {
      const first = availableMonths[0];
      const [y, m] = first.startDate.split('-');
      setCalendarPageMonth({ year: parseInt(y, 10), month: parseInt(m, 10) - 1 });
    }
  }, [periodType, availableMonths, calendarPageMonth, useTruth]);

  // Process fuel records into ProfitRecord format for display

  // Fuel records for chart only: vehicle filter but no date filter, so chart always has full-range fuel data

  // Unique unit prices from fuel data for editable Fuel Cost dropdown

  // Fuel records with user overrides applied (for Fuel tab display)

  // Daily Summary — reads directly from merged_costs.csv rows; standing cost = every calendar day in period (not only active days).
  const profitabilitySummaryRows = useMemo(() => {
    const grouped = new Map<string, {
      date: string;
      vehicleId: string;
      totalMileage: number;
      totalRevenue: number;
      totalFuelCost: number;
      totalDriverCost: number;
      totalDepreciation: number;
      totalProfit: number;
      currency: string;
      periodLabel?: string;
      fuelRate?: number;
      driverRate?: number;
      totalDrivingHours?: number;
      totalWaitingHours?: number;
      workingHourlyRate?: number;
      waitingHourlyRate?: number;
      fuelQuantity?: number;
      fuelUnitPrice?: number;
      orderIds: string[];
      orderCount: number;
      pickupCount: number;
      deliveryCount: number;
      pickupOrderIds: string[];
      deliveryOrderIds: string[];
      /** Revenue per order, same order as orderIds (e.g. ["15","15","518.99"]) */
      orderRevenues: string[];
      /** Imputed flag per order: "0", "15", or "25" (same order as orderIds) */
      orderRevenueImputed: string[];
      /** Same-day TMS job flag per order (aligned with orderIds). */
      orderSameDayFlags: boolean[];
      /** Cost class used for rate tooltips (body style or tonnage). */
      costClassLabel?: string;
    }>();

    const getPeriodKey = (date: string, vehicleId: string): string => {
      if (periodType === 'day') return `${date}|${vehicleId}`;
      if (periodType === 'week') {
        const w = availableWeeks.find(w => date >= w.startDate && date <= w.endDate);
        return w ? `${w.startDate}|${w.endDate}|${vehicleId}` : `${date}|${vehicleId}`;
      }
      if (periodType === 'month') return `${(date && date.length >= 7 ? date.substring(0, 7) : date || '')}|${vehicleId}`;
      return `${date}|${vehicleId}`;
    };

    const getPeriodLabel = (date: string): string => {
      if (periodType === 'day') return date;
      if (periodType === 'week') {
        const w = availableWeeks.find(w => date >= w.startDate && date <= w.endDate);
        return w ? w.label : date;
      }
      if (periodType === 'month') {
        const ym = date && date.length >= 7 ? date.substring(0, 7) : '';
        const parts = ym.split('-');
        const year = parts[0];
        const month = parts[1];
        const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        const monthIndex = month != null ? parseInt(month, 10) - 1 : -1;
        const monthName = (monthIndex >= 0 && monthIndex < 12) ? names[monthIndex] : ym;
        const shortYear = year && year.length >= 2 ? year.slice(-2) : year || '';
        return `${monthName} ${shortYear}`;
      }
      return date;
    };

    // Group merged_costs.csv rows by period (supports day / week / month aggregation).
    // Prefer pipeline fuel_cost + driver_cost (mileage-based fuel; 3-part driver); else config / legacy jigsaw.
    // Rows with no vehicle are grouped under "—" so their revenue is still included in the table and totals.
    const NO_VEHICLE_ID = '—';
    const costRowsForSummary =
      selectedOrderIds.length === 0 && selectedCustomerNames.length === 0
        ? mergedCostsDataFiltered
        : mergedCostsDataFiltered.filter((row) =>
            mergedCostRowMatchesLookup(row, selectedOrderIds, selectedCustomerNames, orderIdToCustomerLower),
          );
    for (const row of costRowsForSummary) {
      const dateKey = row.date.slice(0, 10);
      const stdVid = standardiseVehicleId(row.vehicle) || (row.vehicle && row.vehicle.trim()) || NO_VEHICLE_ID;
      if (!dateKey) continue;

      const periodKey = getPeriodKey(dateKey, stdVid);
      const existing = grouped.get(periodKey);

      const miles      = row.daily_miles        ?? 0;
      const vt         = resolveVehicleCostClassLabel(stdVid, row.vehicle_type);
      const configFuelRate = getFuelCostPerMile(vt, config.profitability);
      const fuelCost   = getMergedFuelCostPounds(row, stdVid, miles, vt, config.profitability, useTruth);
      const fuelRate   = miles > 0 ? fuelCost / miles : configFuelRate;
      const drivingH   = row.driving_hours       ?? 0;
      const waitingH   = row.waiting_hours       ?? 0;
      const driverCost = getMergedDriverCostPounds(row, miles, drivingH, waitingH, config.profitability, useTruth);
      const standingCost = useTruth ? getMergedStandingCostPounds(row) : 0;
      // Standing cost is applied after grouping: one charge per calendar day in the period (see fill + finalize below).
      const scoped = scopeOrderBreakdownToLookup(
        extractMergedCostOrderBreakdown(row),
        selectedOrderIds,
      );
      const orderIdsFromRow = scoped.orderIds;
      const revsAligned = scoped.orderRevenues;
      const imputedAligned = scoped.orderRevenueImputed;
      const sameDayAligned = scoped.orderSameDayFlags;
      // Prefer unique display ids (TrackingID / order name) over backend order_count —
      // order_count can inflate when many entity ULIDs map to fewer display ids.
      const orderCountFromRow = orderIdsFromRow.length;
      const pickupIdsFromRow = scoped.pickupOrderIds ?? [];
      const deliveryIdsFromRow = scoped.deliveryOrderIds ?? orderIdsFromRow;
      // Prefer ID-list lengths so counts match right-click modals; fall back to
      // scoped counts when legacy CSV has pickup_count but no per-order leg_role IDs.
      const pickupCountFromRow =
        pickupIdsFromRow.length > 0 ? pickupIdsFromRow.length : (scoped.pickupCount ?? 0);
      const deliveryCountFromRow =
        deliveryIdsFromRow.length > 0
          ? deliveryIdsFromRow.length
          : (scoped.deliveryCount ?? orderCountFromRow);
      const revenue =
        selectedOrderIds.length > 0 && scoped.matchedRevenue != null
          ? scoped.matchedRevenue
          : (row.revenue ?? 0);

      if (existing) {
        existing.totalMileage     += miles;
        existing.totalRevenue     += revenue;
        existing.totalFuelCost    += fuelCost;
        existing.totalDriverCost  += driverCost;
        existing.totalDepreciation = (existing.totalDepreciation ?? 0) + standingCost;
        existing.totalProfit      += revenue - fuelCost - driverCost;
        existing.totalDrivingHours = (existing.totalDrivingHours ?? 0) + drivingH;
        existing.totalWaitingHours = (existing.totalWaitingHours ?? 0) + waitingH;
        if (existing.fuelRate == null) existing.fuelRate = fuelRate;
        if (existing.costClassLabel == null) existing.costClassLabel = vt;
        const seen = new Set(existing.orderIds);
        for (let i = 0; i < orderIdsFromRow.length; i++) {
          const oid = orderIdsFromRow[i]!;
          if (seen.has(oid)) continue;
          seen.add(oid);
          existing.orderIds.push(oid);
          existing.orderRevenues.push(revsAligned[i] ?? '');
          existing.orderRevenueImputed.push(imputedAligned[i] ?? '0');
          existing.orderSameDayFlags.push(sameDayAligned[i] ?? false);
        }
        const seenPickup = new Set(existing.pickupOrderIds);
        for (const oid of pickupIdsFromRow) {
          if (!seenPickup.has(oid)) {
            seenPickup.add(oid);
            existing.pickupOrderIds.push(oid);
          }
        }
        const seenDelivery = new Set(existing.deliveryOrderIds);
        for (const oid of deliveryIdsFromRow) {
          if (!seenDelivery.has(oid)) {
            seenDelivery.add(oid);
            existing.deliveryOrderIds.push(oid);
          }
        }
        existing.orderCount = existing.orderIds.length;
        existing.pickupCount =
          existing.pickupOrderIds.length > 0
            ? existing.pickupOrderIds.length
            : Math.max(existing.pickupCount ?? 0, pickupCountFromRow);
        existing.deliveryCount =
          existing.deliveryOrderIds.length > 0
            ? existing.deliveryOrderIds.length
            : Math.max(existing.deliveryCount ?? 0, deliveryCountFromRow);
      } else {
        grouped.set(periodKey, {
          date:             dateKey,
          vehicleId:        stdVid,
          totalMileage:     miles,
          totalRevenue:     revenue,
          totalFuelCost:    fuelCost,
          totalDriverCost:  driverCost,
          totalDepreciation: standingCost,
          totalProfit:      revenue - fuelCost - driverCost,
          currency:         'GBP',
          periodLabel:      getPeriodLabel(dateKey),
          totalDrivingHours: drivingH,
          totalWaitingHours: waitingH,
          fuelRate:         fuelRate,
          costClassLabel:   vt,
          orderIds:         [...orderIdsFromRow],
          orderCount:       orderCountFromRow,
          pickupCount:      pickupCountFromRow,
          deliveryCount:    deliveryCountFromRow,
          pickupOrderIds:   [...pickupIdsFromRow],
          deliveryOrderIds: [...deliveryIdsFromRow],
          orderRevenues:    [...revsAligned],
          orderRevenueImputed: [...imputedAligned],
          orderSameDayFlags: [...sameDayAligned],
        });
      }
    }

    const dataMinMax = getMergedCostDateBounds(costRowsForSummary);
    const calendarWindow =
      effectiveDateWindow ??
      (dataMinMax ? { startDate: dataMinMax.min, endDate: dataMinMax.max } : null);

    // Day view: add (vehicle, date) rows for every calendar day in range so standing applies on idle days too.
    if (periodType === 'day' && calendarWindow) {
      const vehiclesSet = new Set<string>();
      for (const r of costRowsForSummary) {
        const v =
          standardiseVehicleId(r.vehicle) || (r.vehicle && r.vehicle.trim()) || NO_VEHICLE_ID;
        if (v !== NO_VEHICLE_ID) vehiclesSet.add(v);
      }
      const startD = new Date(`${calendarWindow.startDate}T12:00:00`);
      const endD = new Date(`${calendarWindow.endDate}T12:00:00`);
      for (let cur = new Date(startD); cur.getTime() <= endD.getTime(); cur.setDate(cur.getDate() + 1)) {
        const dStr = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-${String(
          cur.getDate(),
        ).padStart(2, '0')}`;
        for (const vid of vehiclesSet) {
          const pk = getPeriodKey(dStr, vid);
          if (!grouped.has(pk)) {
            grouped.set(pk, {
              date: dStr,
              vehicleId: vid,
              totalMileage: 0,
              totalRevenue: 0,
              totalFuelCost: 0,
              totalDriverCost: 0,
              totalDepreciation: 0,
              totalProfit: 0,
              currency: 'GBP',
              periodLabel: getPeriodLabel(dStr),
              totalDrivingHours: 0,
              totalWaitingHours: 0,
              fuelRate: undefined,
              orderIds: [],
              orderCount: 0,
              pickupCount: 0,
              deliveryCount: 0,
              pickupOrderIds: [],
              deliveryOrderIds: [],
              orderRevenues: [],
              orderRevenueImputed: [],
              orderSameDayFlags: [],
            });
          }
        }
      }
    }

    for (const row of grouped.values()) {
      if (useTruth) {
        row.totalProfit =
          row.totalRevenue - row.totalFuelCost - row.totalDriverCost - row.totalDepreciation;
        continue;
      }
      const dailyRate =
        row.vehicleId === NO_VEHICLE_ID
          ? 0
          : getDepreciationPerDay(resolveVehicleCostClassLabel(row.vehicleId), config.profitability);
      const nCal = getCalendarDaysForStandingRow(
        row,
        periodType,
        effectiveDateWindow,
        dataMinMax,
        availableWeeks,
      );
      row.totalDepreciation = dailyRate * nCal;
      row.totalProfit =
        row.totalRevenue - row.totalFuelCost - row.totalDriverCost - row.totalDepreciation;
    }

    let summaryArray = Array.from(grouped.values());

    // Apply period-range filter (calendar month when paged, or selected date from dropdown)
    if (effectiveDateWindow) {
      summaryArray = summaryArray.filter(
        (r) => r.date >= effectiveDateWindow.startDate && r.date <= effectiveDateWindow.endDate,
      );
    }

    return summaryArray;
  }, [
    mergedCostsDataFiltered,
    effectiveDateWindow,
    selectedOrderIds,
    selectedCustomerNames,
    orderIdToCustomerLower,
    periodType,
    availableWeeks,
    availableMonths,
    vehicleIdToType,
    config.profitability,
    useTruth,
    resolveVehicleCostClassLabel,
  ]);

  // Same aggregation as `profitabilitySummaryRows`, but intentionally does NOT apply `effectiveDateWindow`.
  // Used by scheduled PDF generation which applies its own "Period each run covers" window.
  const profitabilitySummaryRowsFullRange = useMemo(() => {
    const grouped = new Map<string, {
      date: string;
      vehicleId: string;
      totalMileage: number;
      totalRevenue: number;
      totalFuelCost: number;
      totalDriverCost: number;
      totalDepreciation: number;
      totalProfit: number;
      currency: string;
      periodLabel?: string;
      fuelRate?: number;
      driverRate?: number;
      totalDrivingHours?: number;
      totalWaitingHours?: number;
      workingHourlyRate?: number;
      waitingHourlyRate?: number;
      fuelQuantity?: number;
      fuelUnitPrice?: number;
      orderIds: string[];
      orderCount: number;
      pickupCount: number;
      deliveryCount: number;
      pickupOrderIds: string[];
      deliveryOrderIds: string[];
      orderRevenues: string[];
      orderRevenueImputed: string[];
      orderSameDayFlags: boolean[];
      costClassLabel?: string;
    }>();

    const getPeriodKey = (date: string, vehicleId: string): string => {
      if (periodType === 'day') return `${date}|${vehicleId}`;
      if (periodType === 'week') {
        const w = availableWeeks.find(w => date >= w.startDate && date <= w.endDate);
        return w ? `${w.startDate}|${w.endDate}|${vehicleId}` : `${date}|${vehicleId}`;
      }
      if (periodType === 'month') return `${(date && date.length >= 7 ? date.substring(0, 7) : date || '')}|${vehicleId}`;
      return `${date}|${vehicleId}`;
    };

    const getPeriodLabel = (date: string): string => {
      if (periodType === 'day') return date;
      if (periodType === 'week') {
        const w = availableWeeks.find(w => date >= w.startDate && date <= w.endDate);
        return w ? w.label : date;
      }
      if (periodType === 'month') {
        const ym = date && date.length >= 7 ? date.substring(0, 7) : '';
        const parts = ym.split('-');
        const year = parts[0];
        const month = parts[1];
        const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        const monthIndex = month != null ? parseInt(month, 10) - 1 : -1;
        const monthName = (monthIndex >= 0 && monthIndex < 12) ? names[monthIndex] : ym;
        const shortYear = year && year.length >= 2 ? year.slice(-2) : year || '';
        return `${monthName} ${shortYear}`;
      }
      return date;
    };

    const NO_VEHICLE_ID = '—';
    const costRowsForSummary =
      selectedOrderIds.length === 0 && selectedCustomerNames.length === 0
        ? mergedCostsDataFiltered
        : mergedCostsDataFiltered.filter((row) =>
            mergedCostRowMatchesLookup(row, selectedOrderIds, selectedCustomerNames, orderIdToCustomerLower),
          );
    for (const row of costRowsForSummary) {
      const dateKey = row.date.slice(0, 10);
      const stdVid = standardiseVehicleId(row.vehicle) || (row.vehicle && row.vehicle.trim()) || NO_VEHICLE_ID;
      if (!dateKey) continue;

      const periodKey = getPeriodKey(dateKey, stdVid);
      const existing = grouped.get(periodKey);

      const miles      = row.daily_miles        ?? 0;
      const vt         = resolveVehicleCostClassLabel(stdVid, row.vehicle_type);
      const configFuelRate = getFuelCostPerMile(vt, config.profitability);
      const fuelCost   = getMergedFuelCostPounds(row, stdVid, miles, vt, config.profitability, useTruth);
      const fuelRate   = miles > 0 ? fuelCost / miles : configFuelRate;
      const drivingH   = row.driving_hours       ?? 0;
      const waitingH   = row.waiting_hours       ?? 0;
      const driverCost = getMergedDriverCostPounds(row, miles, drivingH, waitingH, config.profitability, useTruth);
      const standingCost = useTruth ? getMergedStandingCostPounds(row) : 0;
      const scoped = scopeOrderBreakdownToLookup(
        extractMergedCostOrderBreakdown(row),
        selectedOrderIds,
      );
      const orderIdsFromRow = scoped.orderIds;
      const revsAligned = scoped.orderRevenues;
      const imputedAligned = scoped.orderRevenueImputed;
      const sameDayAligned = scoped.orderSameDayFlags;
      const orderCountFromRow = orderIdsFromRow.length;
      const pickupIdsFromRow = scoped.pickupOrderIds ?? [];
      const deliveryIdsFromRow = scoped.deliveryOrderIds ?? orderIdsFromRow;
      // Prefer ID-list lengths so counts match right-click modals; fall back to
      // scoped counts when legacy CSV has pickup_count but no per-order leg_role IDs.
      const pickupCountFromRow =
        pickupIdsFromRow.length > 0 ? pickupIdsFromRow.length : (scoped.pickupCount ?? 0);
      const deliveryCountFromRow =
        deliveryIdsFromRow.length > 0
          ? deliveryIdsFromRow.length
          : (scoped.deliveryCount ?? orderCountFromRow);
      const revenue =
        selectedOrderIds.length > 0 && scoped.matchedRevenue != null
          ? scoped.matchedRevenue
          : (row.revenue ?? 0);

      if (existing) {
        existing.totalMileage     += miles;
        existing.totalRevenue     += revenue;
        existing.totalFuelCost    += fuelCost;
        existing.totalDriverCost  += driverCost;
        existing.totalDepreciation = (existing.totalDepreciation ?? 0) + standingCost;
        existing.totalProfit      += revenue - fuelCost - driverCost;
        existing.totalDrivingHours = (existing.totalDrivingHours ?? 0) + drivingH;
        existing.totalWaitingHours = (existing.totalWaitingHours ?? 0) + waitingH;
        if (existing.fuelRate == null) existing.fuelRate = fuelRate;
        if (existing.costClassLabel == null) existing.costClassLabel = vt;
        const seen = new Set(existing.orderIds);
        for (let i = 0; i < orderIdsFromRow.length; i++) {
          const oid = orderIdsFromRow[i]!;
          if (seen.has(oid)) continue;
          seen.add(oid);
          existing.orderIds.push(oid);
          existing.orderRevenues.push(revsAligned[i] ?? '');
          existing.orderRevenueImputed.push(imputedAligned[i] ?? '0');
          existing.orderSameDayFlags.push(sameDayAligned[i] ?? false);
        }
        const seenPickup = new Set(existing.pickupOrderIds);
        for (const oid of pickupIdsFromRow) {
          if (!seenPickup.has(oid)) {
            seenPickup.add(oid);
            existing.pickupOrderIds.push(oid);
          }
        }
        const seenDelivery = new Set(existing.deliveryOrderIds);
        for (const oid of deliveryIdsFromRow) {
          if (!seenDelivery.has(oid)) {
            seenDelivery.add(oid);
            existing.deliveryOrderIds.push(oid);
          }
        }
        existing.orderCount = existing.orderIds.length;
        existing.pickupCount =
          existing.pickupOrderIds.length > 0
            ? existing.pickupOrderIds.length
            : Math.max(existing.pickupCount ?? 0, pickupCountFromRow);
        existing.deliveryCount =
          existing.deliveryOrderIds.length > 0
            ? existing.deliveryOrderIds.length
            : Math.max(existing.deliveryCount ?? 0, deliveryCountFromRow);
      } else {
        grouped.set(periodKey, {
          date:             dateKey,
          vehicleId:        stdVid,
          totalMileage:     miles,
          totalRevenue:     revenue,
          totalFuelCost:    fuelCost,
          totalDriverCost:  driverCost,
          totalDepreciation: standingCost,
          totalProfit:      revenue - fuelCost - driverCost,
          currency:         'GBP',
          periodLabel:      getPeriodLabel(dateKey),
          totalDrivingHours: drivingH,
          totalWaitingHours: waitingH,
          fuelRate:         fuelRate,
          costClassLabel:   vt,
          orderIds:         [...orderIdsFromRow],
          orderCount:       orderCountFromRow,
          pickupCount:      pickupCountFromRow,
          deliveryCount:    deliveryCountFromRow,
          pickupOrderIds:   [...pickupIdsFromRow],
          deliveryOrderIds: [...deliveryIdsFromRow],
          orderRevenues:    [...revsAligned],
          orderRevenueImputed: [...imputedAligned],
          orderSameDayFlags: [...sameDayAligned],
        });
      }
    }

    const dataMinMax = getMergedCostDateBounds(costRowsForSummary);
    const calendarWindow = dataMinMax ? { startDate: dataMinMax.min, endDate: dataMinMax.max } : null;

    if (periodType === 'day' && calendarWindow) {
      const vehiclesSet = new Set<string>();
      for (const r of costRowsForSummary) {
        const v =
          standardiseVehicleId(r.vehicle) || (r.vehicle && r.vehicle.trim()) || NO_VEHICLE_ID;
        if (v !== NO_VEHICLE_ID) vehiclesSet.add(v);
      }
      const startD = new Date(`${calendarWindow.startDate}T12:00:00`);
      const endD = new Date(`${calendarWindow.endDate}T12:00:00`);
      for (let cur = new Date(startD); cur.getTime() <= endD.getTime(); cur.setDate(cur.getDate() + 1)) {
        const dStr = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-${String(
          cur.getDate(),
        ).padStart(2, '0')}`;
        for (const vid of vehiclesSet) {
          const pk = getPeriodKey(dStr, vid);
          if (!grouped.has(pk)) {
            grouped.set(pk, {
              date: dStr,
              vehicleId: vid,
              totalMileage: 0,
              totalRevenue: 0,
              totalFuelCost: 0,
              totalDriverCost: 0,
              totalDepreciation: 0,
              totalProfit: 0,
              currency: 'GBP',
              periodLabel: getPeriodLabel(dStr),
              totalDrivingHours: 0,
              totalWaitingHours: 0,
              fuelRate: undefined,
              orderIds: [],
              orderCount: 0,
              pickupCount: 0,
              deliveryCount: 0,
              pickupOrderIds: [],
              deliveryOrderIds: [],
              orderRevenues: [],
              orderRevenueImputed: [],
              orderSameDayFlags: [],
            });
          }
        }
      }
    }

    for (const row of grouped.values()) {
      if (useTruth) {
        row.totalProfit =
          row.totalRevenue - row.totalFuelCost - row.totalDriverCost - row.totalDepreciation;
        continue;
      }
      const dailyRate =
        row.vehicleId === NO_VEHICLE_ID
          ? 0
          : getDepreciationPerDay(resolveVehicleCostClassLabel(row.vehicleId), config.profitability);
      const nCal = getCalendarDaysForStandingRow(
        row,
        periodType,
        null,
        dataMinMax,
        availableWeeks,
      );
      row.totalDepreciation = dailyRate * nCal;
      row.totalProfit =
        row.totalRevenue - row.totalFuelCost - row.totalDriverCost - row.totalDepreciation;
    }

    return Array.from(grouped.values());
  }, [
    mergedCostsDataFiltered,
    selectedOrderIds,
    selectedCustomerNames,
    orderIdToCustomerLower,
    periodType,
    availableWeeks,
    availableMonths,
    vehicleIdToType,
    config.profitability,
    useTruth,
    resolveVehicleCostClassLabel,
  ]);

  const utilisationLookup = useMemo(
    () => buildUtilisationLookup(truthUtilisationRows),
    [truthUtilisationRows],
  );

  const profitabilitySummaryRowsWithUtil = useMemo(() => {
    if (!useTruth || !useTruthUtil || truthUtilisationRows.length === 0) {
      return profitabilitySummaryRows;
    }
    return profitabilitySummaryRows.map((row) => {
      const resolved = resolveUtilisationForSummaryRow({
        date: row.date,
        vehicleId: row.vehicleId,
        periodType,
        availableWeeks,
        lookup: utilisationLookup,
        allRecords: truthUtilisationRows,
      });
      return {
        ...row,
        utilisationScore: resolved.score,
        utilisationTooltip: resolved.tooltip,
      };
    });
  }, [
    profitabilitySummaryRows,
    useTruth,
    useTruthUtil,
    truthUtilisationRows,
    utilisationLookup,
    periodType,
    availableWeeks,
  ]);

  const dailySummaryData = useMemo(() => {
    // Copy before sort so this memo returns a new array reference when sort keys change.
    // Otherwise filteredSummaryData's useMemo keeps the same dailySummaryData ref and skips,
    // leaving the table in a stale order after header clicks.
    const summaryArray =
      selectedVehicleIds.length > 0
        ? profitabilitySummaryRowsWithUtil.filter((r) => selectedVehicleIds.includes(r.vehicleId))
        : [...profitabilitySummaryRowsWithUtil];

    return summaryArray.sort((a, b) => {
      let aVal: string | number = '';
      let bVal: string | number = '';
      switch (summarySortField) {
        case 'date':         aVal = a.date;                   bVal = b.date;                   break;
        case 'vehicleId':    aVal = a.vehicleId;              bVal = b.vehicleId;              break;
        case 'totalMileage': aVal = a.totalMileage ?? 0;      bVal = b.totalMileage ?? 0;      break;
        case 'utilisationScore':
          aVal = (a as DailySummarySortRow).utilisationScore ?? -1;
          bVal = (b as DailySummarySortRow).utilisationScore ?? -1;
          break;
        case 'orderCount':
          aVal = (a as { deliveryCount?: number }).deliveryCount ?? a.orderCount ?? a.orderIds?.length ?? 0;
          bVal = (b as { deliveryCount?: number }).deliveryCount ?? b.orderCount ?? b.orderIds?.length ?? 0;
          break;
        case 'pickupCount':
          aVal = (a as { pickupCount?: number }).pickupCount ?? 0;
          bVal = (b as { pickupCount?: number }).pickupCount ?? 0;
          break;
        case 'totalRevenue': aVal = a.totalRevenue;           bVal = b.totalRevenue;           break;
        case 'totalFuelCost':   aVal = a.totalFuelCost;       bVal = b.totalFuelCost;          break;
        case 'totalDriverCost': aVal = a.totalDriverCost ?? 0; bVal = b.totalDriverCost ?? 0; break;
        case 'totalCost':
          aVal = (a.totalFuelCost ?? 0) + (a.totalDriverCost ?? 0) + (a.totalDepreciation ?? 0);
          bVal = (b.totalFuelCost ?? 0) + (b.totalDriverCost ?? 0) + (b.totalDepreciation ?? 0);
          break;
        case 'totalProfit':  aVal = a.totalProfit;            bVal = b.totalProfit;            break;
        default:             aVal = a.date;                   bVal = b.date;
      }
      return compareWithSortedColumnDashLast(
        dailySummarySortColumnIsDash(a, summarySortField),
        dailySummarySortColumnIsDash(b, summarySortField),
        () => {
          if (aVal < bVal) return summarySortAsc ? -1 : 1;
          if (aVal > bVal) return summarySortAsc ? 1 : -1;
          return 0;
        },
      );
    });
  }, [profitabilitySummaryRowsWithUtil, selectedVehicleIds, summarySortField, summarySortAsc]);

  // Push per-vehicle profitability snapshot for Fleet Manage (by vehicle type)
  const { setSnapshot } = useProfitabilitySnapshot();
  useEffect(() => {
    if (dailySummaryData.length === 0) {
      setSnapshot(null);
      return;
    }
    const byVehicle: Record<string, { revenue: number; totalCost: number }> = {};
    for (const row of dailySummaryData) {
      const vid = row.vehicleId;
      const totalCost = (row.totalFuelCost ?? 0) + (row.totalDriverCost ?? 0);
      if (!byVehicle[vid]) {
        byVehicle[vid] = { revenue: 0, totalCost: 0 };
      }
      byVehicle[vid].revenue += row.totalRevenue ?? 0;
      byVehicle[vid].totalCost += totalCost;
    }
    const dates = dailySummaryData.map(r => r.date);
    const startDate = selectedPeriodRange?.startDate ?? (dates.length ? dates.reduce((a, b) => (a < b ? a : b)) : '');
    const endDate = selectedPeriodRange?.endDate ?? (dates.length ? dates.reduce((a, b) => (a > b ? a : b)) : '');
    const jigsawOnlyVehicleIds: string[] = [];
    setSnapshot({
      period: { startDate, endDate },
      byVehicle,
      jigsawOnlyVehicleIds: jigsawOnlyVehicleIds.length > 0 ? jigsawOnlyVehicleIds : undefined,
      updatedAt: Date.now(),
    });
  }, [dailySummaryData, selectedPeriodRange?.startDate, selectedPeriodRange?.endDate, setSnapshot]);

  // Full-range aggregation (no date filter) for the line chart so we always show full trend lines

  // Pagination logic for Daily Summary
  const filteredSummaryData = useMemo(() => {
    let filtered = dailySummaryData.filter(
      (row) =>
        (row.totalRevenue > 0) ||
        ((row.totalFuelCost ?? 0) + (row.totalDriverCost ?? 0) > 0) ||
        (row.totalDepreciation ?? 0) > 0 ||
        ((row.orderCount ?? row.orderIds?.length ?? 0) > 0),
    );
    // Apply period range filter (calendar month when paged, or selected date from dropdown)
    const window = effectiveDateWindow ?? selectedPeriodRange;
    if (window) {
      filtered = filtered.filter(row => row.date >= window.startDate && row.date <= window.endDate);
    }
    return filtered;
  }, [dailySummaryData, effectiveDateWindow, selectedPeriodRange]);

  // Top Performers / Top Dates: filteredSummaryData (same rows as KPI fuel/driver/standing and the Daily Summary table).
  const topPerformersFull = useMemo(() => {
    const NO_VEHICLE_ID = '—';
    const isActiveSummaryRowForDayCount = (r: (typeof filteredSummaryData)[number]) => {
      const miles = r.totalMileage ?? 0;
      const rev = r.totalRevenue ?? 0;
      const varCosts = (r.totalFuelCost ?? 0) + (r.totalDriverCost ?? 0);
      const orders = r.orderCount ?? (r.orderIds?.length ?? 0);
      return miles > 0 || rev > 0 || varCosts > 0 || orders > 0;
    };
    const byVehicle = new Map<
      string,
      { vehicle: string; profit: number; revenue: number; miles: number; periods: Set<string>; orders: Set<string> }
    >();
    for (const r of filteredSummaryData) {
      const vid = r.vehicleId;
      if (!vid || vid === NO_VEHICLE_ID) continue;
      const stdVid = standardiseVehicleId(vid) || vid.trim() || NO_VEHICLE_ID;
      if (stdVid === NO_VEHICLE_ID) continue;
      const periodKey =
        periodType === 'day'
          ? (r.date || '').slice(0, 10)
          : (r.periodLabel || r.date || '').trim();
      const existing = byVehicle.get(vid);
      const profit = r.totalProfit;
      const revenue = r.totalRevenue ?? 0;
      const miles = r.totalMileage ?? 0;
      const orderIdsRow = collectOrderIdsFromDailySummaryRow(r);
      const countThisPeriod = Boolean(periodKey) && isActiveSummaryRowForDayCount(r);
      if (existing) {
        existing.profit += profit;
        existing.revenue += revenue;
        existing.miles += miles;
        if (countThisPeriod) existing.periods.add(periodKey);
        orderIdsRow.forEach((id) => existing.orders.add(id));
      } else {
        byVehicle.set(vid, {
          vehicle: vid,
          profit,
          revenue,
          miles,
          periods: countThisPeriod ? new Set([periodKey]) : new Set(),
          orders: new Set(orderIdsRow),
        });
      }
    }
    return Array.from(byVehicle.values())
      .sort((a, b) => b.profit - a.profit)
      .map((v, i) => ({
        rank:         i + 1,
        vehicle:      v.vehicle,
        vehicleType:  vehicleIdToType[standardiseVehicleId(v.vehicle)] ?? 'Default',
        profit:       v.profit,
        revenue:      v.revenue,
        miles:        v.miles,
        activeDays:   v.periods.size,
        orderCount:   v.orders.size,
        margin:       v.revenue > 0 ? Math.round((v.profit / v.revenue) * 100) : null,
      }));
  }, [filteredSummaryData, vehicleIdToType, periodType]);

  const topDatesFull = useMemo(() => {
    const byDate = new Map<
      string,
      { date: string; profit: number; revenue: number; miles: number; vehicles: Set<string>; orders: Set<string> }
    >();
    for (const r of filteredSummaryData) {
      const dateKey =
        periodType === 'day'
          ? (r.date || '').slice(0, 10)
          : (r.periodLabel || r.date || '').trim();
      if (!dateKey) continue;
      const profit = r.totalProfit;
      const revenue = r.totalRevenue ?? 0;
      const miles = r.totalMileage ?? 0;
      const vid = r.vehicleId;
      const orderIdsRow = collectOrderIdsFromDailySummaryRow(r);
      const existing = byDate.get(dateKey);
      if (existing) {
        existing.profit += profit;
        existing.revenue += revenue;
        existing.miles += miles;
        if (vid) existing.vehicles.add(vid);
        orderIdsRow.forEach((id) => existing.orders.add(id));
      } else {
        byDate.set(dateKey, {
          date: dateKey,
          profit,
          revenue,
          miles,
          vehicles: new Set(vid ? [vid] : []),
          orders: new Set(orderIdsRow),
        });
      }
    }
    return Array.from(byDate.values())
      .sort((a, b) => b.profit - a.profit)
      .map((d, i) => ({
        rank:           i + 1,
        date:           d.date,
        profit:         d.profit,
        revenue:        d.revenue,
        miles:          d.miles,
        activeVehicles: d.vehicles.size,
        orderCount:     d.orders.size,
        margin:         d.revenue > 0 ? Math.round((d.profit / d.revenue) * 100) : null,
      }));
  }, [filteredSummaryData, periodType]);

  const topPerformersDisplayed = useMemo(() => {
    if (topPerformersFull.length === 0) return [];
    if (performersShowBottom5) {
      const slice = topPerformersFull.slice(-5).reverse();
      return slice.map((p, i) => ({ ...p, rank: i + 1 }));
    }
    return topPerformersFull.slice(0, 5);
  }, [topPerformersFull, performersShowBottom5]);

  const topDatesDisplayed = useMemo(() => {
    if (topDatesFull.length === 0) return [];
    if (bestDaysShowBottom5) {
      const slice = topDatesFull.slice(-5).reverse();
      return slice.map((d, i) => ({ ...d, rank: i + 1 }));
    }
    return topDatesFull.slice(0, 5);
  }, [topDatesFull, bestDaysShowBottom5]);

  // Monday Brief — derived from profitability rows + assumptions (standing from merged depreciation when truth)
  const mondayBriefDemo = useMemo(() => {
    const THRESHOLD_PCT = 6;

    const selectedWindow = (effectiveDateWindow ?? selectedPeriodRange);
    const selectedYm = (() => {
      const start = (selectedWindow?.startDate || '').slice(0, 7);
      const end = (selectedWindow?.endDate || '').slice(0, 7);
      if (start && start === end) return start;
      // Fallback: use most recent month present in the filtered data
      const latest = mergedCostsDataFiltered
        .map((r) => (r.date || '').slice(0, 7))
        .filter(Boolean)
        .sort()
        .slice(-1)[0];
      return latest || '';
    })();

    const monthWindow = (() => {
      if (selectedWindow && selectedWindow.startDate && selectedWindow.endDate) return selectedWindow;
      if (!selectedYm || selectedYm.length !== 7) return null;
      const parts = selectedYm.split('-');
      const y = Number(parts[0]);
      const m1 = Number(parts[1]);
      if (!Number.isFinite(y) || !Number.isFinite(m1) || m1 < 1 || m1 > 12) return null;
      const startDate = `${selectedYm}-01`;
      const endDate = lastDayOfMonthISO(y, m1);
      return { startDate, endDate };
    })();

    const inWindow = (r: MergedCostRow, win: { startDate: string; endDate: string } | null) => {
      const d = (r.date || '').slice(0, 10);
      if (!win) return false;
      return d >= win.startDate && d <= win.endDate;
    };

    const currentRows = mergedCostsDataFiltered.filter((r) => inWindow(r, monthWindow));

    const baselineWindow = (() => {
      if (!monthWindow) return null;
      const ym = monthWindow.startDate.slice(0, 7);
      const parts = ym.split('-');
      const y = Number(parts[0]);
      const m1 = Number(parts[1]);
      if (!Number.isFinite(y) || !Number.isFinite(m1) || m1 < 1 || m1 > 12) return null;
      const prevM1 = m1 === 1 ? 12 : (m1 - 1);
      const prevY = m1 === 1 ? (y - 1) : y;
      const startDate = `${prevY}-${String(prevM1).padStart(2, '0')}-01`;
      const endDate = lastDayOfMonthISO(prevY, prevM1);
      return { startDate, endDate };
    })();

    const baselineRows = mergedCostsDataFiltered.filter((r) => inWindow(r, baselineWindow));

    const sum = (rows: MergedCostRow[]) => {
      let revenue = 0;
      let fuel = 0;
      let driver = 0;
      const vehicleDayKey = new Set<string>();
      for (const r of rows) {
        const d = (r.date || '').slice(0, 10);
        const v = (standardiseVehicleId(r.vehicle) || r.vehicle || '').trim();
        if (d && v) vehicleDayKey.add(`${v}|${d}`);
        revenue += r.revenue ?? 0;
        fuel += (r.fuel_cost ?? r.jigsaw_fuel_cost ?? 0);
        driver += (r.driver_cost ?? 0);
      }
      const standingResolution = resolveStandingCostForMergedRows(rows, briefStandingOptions);
      const standing = standingResolution.standingTotal;
      const profit = revenue - fuel - driver - standing;
      const marginPct = revenue > 0 ? (profit / revenue) * 100 : null;
      return {
        revenue,
        fuel,
        driver,
        standing,
        profit,
        marginPct,
        vehicleDays: vehicleDayKey.size,
        standingResolution,
      };
    };

    const cur = sum(currentRows);
    const base = sum(baselineRows);

    const topCustomer = (() => {
      const rows = customerMonthlyProfitability
        .filter((r) => (r.period || '').startsWith(selectedYm))
        .filter((r) => (r.order_count ?? 0) > 0);
      if (rows.length === 0) return null;
      const sorted = [...rows].sort((a, b) => (a.profit_margin_pct ?? 0) - (b.profit_margin_pct ?? 0));
      return sorted[0] ?? null;
    })();

    const worstVehicle = (() => {
      const byVehicle = new Map<string, MergedCostRow[]>();
      for (const r of currentRows) {
        const v = (standardiseVehicleId(r.vehicle) || r.vehicle || '').trim();
        if (!v) continue;
        const list = byVehicle.get(v) ?? [];
        list.push(r);
        byVehicle.set(v, list);
      }
      const rows = Array.from(byVehicle.entries()).map(([vehicle, vehicleRows]) => {
        const revenue = vehicleRows.reduce((s, r) => s + (r.revenue ?? 0), 0);
        const fuel = vehicleRows.reduce((s, r) => s + (r.fuel_cost ?? r.jigsaw_fuel_cost ?? 0), 0);
        const driver = vehicleRows.reduce((s, r) => s + (r.driver_cost ?? 0), 0);
        const standingResolution = resolveStandingCostForMergedRows(vehicleRows, briefStandingOptions);
        const standing = standingResolution.standingTotal;
        const profit = revenue - fuel - driver - standing;
        const marginPct = revenue > 0 ? (profit / revenue) * 100 : null;
        const vehicleDays = new Set(
          vehicleRows.map((r) => {
            const d = (r.date || '').slice(0, 10);
            return d ? `${vehicle}|${d}` : '';
          }).filter(Boolean),
        ).size;
        return {
          vehicle,
          profit,
          revenue,
          marginPct,
          vehicleDays,
          standingResolution,
        };
      });
      rows.sort((a, b) => (a.marginPct ?? 999) - (b.marginPct ?? 999));
      return rows[0] ?? null;
    })();

    const formatGbp0 = (n: number) => `£${Math.round(n).toLocaleString()}`;
    const topCostDriver = (fuel: number, driver: number, standing: number) => {
      const pairs: Array<{ k: 'fuel' | 'driver' | 'standing'; v: number }> = [
        { k: 'fuel', v: Math.max(0, fuel) },
        { k: 'driver', v: Math.max(0, driver) },
        { k: 'standing', v: Math.max(0, standing) },
      ];
      pairs.sort((a, b) => b.v - a.v);
      return pairs[0]?.k ?? 'standing';
    };

    const items: Array<{
      kind: 'customer' | 'vehicle' | 'overall';
      highlights: string[];
      title: string;
      body: string;
      action: string;
    }> = [];

    if (topCustomer && (topCustomer.profit_margin_pct ?? 0) < THRESHOLD_PCT) {
      const customerName = String(topCustomer.customer_name || '').trim();
      const revenue = Number(topCustomer.total_revenue_gbp ?? 0) || 0;
      const profit = Number(topCustomer.profit_gbp ?? 0) || 0;
      const orders = Number(topCustomer.order_count ?? 0) || 0;
      const currentMargin = Number(topCustomer.profit_margin_pct ?? 0) || 0;
      items.push({
        kind: 'customer',
        highlights: customerName ? [customerName] : [],
        title: `Customer ${customerName || '—'} margin ${currentMargin.toFixed(1)}% (below ${THRESHOLD_PCT}%)`,
        body:
          orders === 1
            ? `Based on 1 order this month (${formatGbp0(revenue)} revenue) — low volume makes this margin highly sensitive to single-order variation.`
            : `${customerName || 'This customer'} averaged ${currentMargin.toFixed(1)}% margin across ${orders} orders in ${selectedYm || 'the selected month'} (profit ${formatGbp0(profit)} on revenue ${formatGbp0(revenue)}).`,
        action: `Recommended action: rate review on top lanes; confirm whether revenue is observed or imputed.`,
      });
    }

    if (worstVehicle && (worstVehicle.marginPct ?? 0) < (cur.marginPct ?? 0)) {
      const vehicleId = String(worstVehicle.vehicle || '').trim();
      const util = utilisationVehicleLevelMap?.get(standardiseVehicleId(vehicleId) || vehicleId) ?? null;
      const utilScorePct = util?.utilisation_score ?? null;
      const utilLine =
        utilisationVehicleLevelMap == null || !util
          ? ''
          : ` Utilisation score: ${utilScorePct == null ? '—' : `${utilScorePct.toFixed(1)}%`} (${util.active_hours == null ? '—' : `${Math.round(util.active_hours)}h`} active, ${util.downtime_hours == null ? '—' : `${Math.round(util.downtime_hours)}h`} downtime${util.vehicleDays > 1 ? `, ${util.vehicleDays} days` : ''}).`;
      const vehicleStanding = worstVehicle.standingResolution?.standingTotal ?? 0;
      const standingLine = worstVehicle.standingResolution
        ? formatVehicleStandingLine(
            vehicleStanding,
            worstVehicle.standingResolution.chargedVehicleDays,
            worstVehicle.standingResolution.averagePerDay,
            worstVehicle.standingResolution.source,
          )
        : formatGbp0(vehicleStanding);
      // We don't carry fuel/driver per vehicle in worstVehicle, so estimate primary driver using standing vs (revenue - profit)
      const totalCost = Math.max(0, (worstVehicle.revenue ?? 0) - (worstVehicle.profit ?? 0));
      const standingSharePct = totalCost > 0 ? (vehicleStanding / totalCost) * 100 : null;
      const vMargin = worstVehicle.marginPct == null ? null : Number(worstVehicle.marginPct);
      const fleetMargin = cur.marginPct == null ? null : Number(cur.marginPct);
      items.push({
        kind: 'vehicle',
        highlights: vehicleId ? [vehicleId] : [],
        title: `Vehicle ${vehicleId || '—'} margin ${vMargin == null ? '—' : `${vMargin.toFixed(1)}%`} vs fleet ${fleetMargin == null ? '—' : `${fleetMargin.toFixed(1)}%`}`,
        body: `Standing cost dominates at ~${standingSharePct == null ? '—' : `${standingSharePct.toFixed(0)}%`} of total cost (${standingLine}).${utilLine}`,
        action:
          standingSharePct != null && standingSharePct >= 45
            ? `Recommended action: increase utilisation by rebalancing work from other vehicles onto ${vehicleId || 'this vehicle'} on low-activity days. If it stays under-used, consider resizing/off-hiring to reduce standing costs.`
            : `Recommended action: review the worst 3 days for ${vehicleId || 'this vehicle'} (lowest margin) and check whether fuel/driver costs spiked due to route choice, dwell time, or missed backloads.`,
      });
    }

    const deltaFuel = cur.fuel - base.fuel;
    const deltaDriver = cur.driver - base.driver;
    const deltaStanding = cur.standing - base.standing;
    const driverKey = topCostDriver(Math.abs(deltaFuel), Math.abs(deltaDriver), Math.abs(deltaStanding));
    const driverLabel = driverKey === 'fuel' ? 'fuel' : driverKey === 'driver' ? 'driver' : 'standing';

    items.push({
      kind: 'overall',
      highlights: [],
      title: `Overall margin this month (${cur.marginPct == null ? '—' : `${cur.marginPct.toFixed(1)}%`}) vs previous month (${base.marginPct == null ? '—' : `${base.marginPct.toFixed(1)}%`})`,
      body: (() => {
        const parts: Array<{ label: string; v: number }> = [
          { label: 'standing', v: Math.round(cur.standing) },
          { label: 'driver', v: Math.round(cur.driver) },
          { label: 'fuel', v: Math.round(cur.fuel) },
        ];
        parts.sort((a, b) => b.v - a.v);
        return `Primary costs this month: ${parts.map((p) => `${p.label} £${p.v.toLocaleString()}`).join(' + ')}.`;
      })(),
      action: `Recommended action: biggest swing vs last month is ${driverLabel} (Δ ${formatGbp0(driverKey === 'fuel' ? deltaFuel : driverKey === 'driver' ? deltaDriver : deltaStanding)}).${utilisationFleetScore != null ? ` Fleet utilisation score ≈ ${utilisationFleetScore.toFixed(1)}% for this month.` : ''} Start by reviewing the top 5 vehicle-days contributing to that increase and confirm whether it’s utilisation (standing), route/fuel, or driver time.`,
    });

    const prettyEndDate = (() => {
      const end = (monthWindow?.endDate || '').slice(0, 10);
      if (!end) return '';
      const parts = end.split('-');
      const y = Number(parts[0]);
      const m1 = Number(parts[1]);
      const d = Number(parts[2]);
      if (!Number.isFinite(y) || !Number.isFinite(m1) || !Number.isFinite(d)) return end;
      const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
      const name = monthNames[m1 - 1] || String(m1);
      return `${d} ${name} ${y}`;
    })();

    const headerText = prettyEndDate || selectedYm || '—';
    const introText = `Three things need your attention this week.`;
    const confidenceText = 'Confidence: Medium.';

    return {
      items: items.slice(0, 5),
      headerText,
      introText,
      confidenceText,
      meta: { monthWindow, baselineWindow, cur, base, selectedYm, standingResolution: cur.standingResolution },
    };
  }, [
    mergedCostsDataFiltered,
    customerMonthlyProfitability,
    effectiveDateWindow,
    selectedPeriodRange,
    utilisationVehicleLevelMap,
    utilisationFleetScore,
    briefStandingOptions,
  ]);

  const mondayBriefFromApi = useMemo(() => {
    if (!mondayBriefApiRaw) return null;
    const win = selectedPeriodRange ?? effectiveDateWindow;
    return mapMondayBriefApiToUi(mondayBriefApiRaw, {
      selectedYm: win?.startDate?.slice(0, 7) ?? '',
    });
  }, [mondayBriefApiRaw, selectedPeriodRange, effectiveDateWindow]);

  const mondayBriefUi: MondayBriefUi = useMemo(() => {
    const clientBrief: MondayBriefUi = {
      items: mondayBriefDemo.items,
      headerText: mondayBriefDemo.headerText,
      introText: mondayBriefDemo.introText,
      confidenceText: mondayBriefDemo.confidenceText,
      meta: {
        monthWindow: mondayBriefDemo.meta.monthWindow,
        selectedYm: mondayBriefDemo.meta.selectedYm ?? '',
      },
      source: 'client',
    };
    // Prefer API narrative when it has items, but always keep the UI-selected
    // analysis window (API often returns "this week" regardless of filters).
    const withSelectedWindow = (api: MondayBriefUi): MondayBriefUi => ({
      ...api,
      meta: {
        ...api.meta,
        monthWindow: clientBrief.meta.monthWindow ?? api.meta.monthWindow,
        selectedYm: clientBrief.meta.selectedYm || api.meta.selectedYm,
      },
    });
    if (mondayBriefFromApi?.items.length) return withSelectedWindow(mondayBriefFromApi);
    if (mondayBriefFromApi && clientBrief.items.length > 0) return clientBrief;
    if (mondayBriefFromApi) {
      return withSelectedWindow({
        ...mondayBriefFromApi,
        introText:
          'No priority items for this period. Profitability data may be sparse, or margins are above your threshold.',
      });
    }
    return clientBrief;
  }, [mondayBriefFromApi, mondayBriefDemo]);

  type DataQualitySeverity = 'critical' | 'warning';
  type DataQualityImpact = 'excluded' | 'included_flagged' | 'unknown';
  type DataQualityKind =
    | 'imputed_revenue'
    | 'utilisation_missing'
    | 'zero_miles_costs'
    | 'extreme_negative_margin'
    | 'low_order_volume';
  type DataQualityNoticeItem = {
    severity: DataQualitySeverity;
    kind: DataQualityKind;
    title: string;
    detail: string;
    impact: DataQualityImpact;
  };

  const buildDataQualityNoticeForWindow = useCallback((win: { startDate: string; endDate: string } | null | undefined): { totalFlagged: number; items: DataQualityNoticeItem[] } => {
    if (!win?.startDate || !win?.endDate) return { totalFlagged: 0, items: [] };

    const inWin = (d: string) => {
      const dd = (d || '').slice(0, 10);
      return Boolean(dd) && dd >= win.startDate && dd <= win.endDate;
    };

    const summaryRowsInWin = filteredSummaryData.filter((r) => inWin(r.date || r.periodLabel || ''));

    // 1) Order volume sensitivity
    const orderIds = new Set<string>();
    for (const r of summaryRowsInWin) {
      collectOrderIdsFromDailySummaryRow(r).forEach((id) => orderIds.add(id));
    }
    const orderCount = orderIds.size;

    // 2) Imputed revenue (use distinct orders from merged_orders/Qargo records when available)
    const imputedByTypeAndRule = new Map<string, { count: number; revenue: number }>(); // key: `${type}|${rule}`
    const imputedOrders = new Set<string>();
    let imputedRevenue = 0;
    const recordsInWin = (records || []).filter((r) => inWin(r.date || r.destination_end_timestamp_local || r.destination_timestamp || ''));
    for (const rec of recordsInWin) {
      const oid = String(rec.orderId || '').trim();
      if (!oid) continue;
      const rule = Number((rec as ProfitRecord).revenue_imputed ?? 0) || 0;
      if (rule !== 15 && rule !== 25) continue;
      if (imputedOrders.has(oid)) continue;
      imputedOrders.add(oid);
      const typeRaw = String((rec as ProfitRecord).order_import_integration_type || '').trim() || 'Unknown';
      // Tenant-facing label: use business language, not vendor/API names.
      const type =
        /^(qargo|palletways|same\s*day\s*tracker)$/i.test(typeRaw) ? 'Orders' : typeRaw || 'Orders';
      const rev = Number(rec.revenue) || rule; // fallback to rule amount if revenue missing
      imputedRevenue += rev;
      const k = `${type}|${rule}`;
      const cur = imputedByTypeAndRule.get(k) ?? { count: 0, revenue: 0 };
      cur.count += 1;
      cur.revenue += rev;
      imputedByTypeAndRule.set(k, cur);
    }
    const imputedOrderCount = imputedOrders.size;
    const imputedBreakdown = (() => {
      if (imputedByTypeAndRule.size === 0) return '';
      const rows = Array.from(imputedByTypeAndRule.entries()).map(([k, v]) => {
        const [type, rule] = k.split('|');
        return { type, rule: Number(rule) || 0, count: v.count };
      });
      rows.sort((a, b) => b.count - a.count);
      const top = rows.slice(0, 3);
      const parts = top.map((r) => `${r.type}: £${r.rule} × ${r.count}`);
      return parts.length ? ` Imputation rules observed: ${parts.join(' · ')}.` : '';
    })();

    // 3) Costs recorded with zero miles (likely missing telematics mileage)
    const zeroMilesCostRows: Array<{ vehicleId?: string; date?: string }> = [];
    for (const r of summaryRowsInWin) {
      const miles = r.totalMileage ?? 0;
      const totalCost = (r.totalFuelCost ?? 0) + (r.totalDriverCost ?? 0) + (r.totalDepreciation ?? 0);
      if ((miles ?? 0) === 0 && totalCost > 0) {
        zeroMilesCostRows.push({ vehicleId: r.vehicleId, date: r.date || r.periodLabel });
      }
    }

    // 4) Utilisation missing for vehicles in scope (only if we have a utilisation map at all)
    const vehiclesInWin = new Set<string>();
    for (const r of mergedCostsDataFiltered) {
      if (!inWin(r.date || '')) continue;
      const v = (standardiseVehicleId(r.vehicle) || r.vehicle || '').trim();
      if (v && v !== '—') vehiclesInWin.add(v);
    }
    const missingUtilVehicles = utilisationVehicleLevelMap
      ? Array.from(vehiclesInWin).filter((v) => !utilisationVehicleLevelMap.has(v))
      : [];

    // 5) Extreme negative margin rows (sanity check)
    let extremeMarginCount = 0;
    for (const r of summaryRowsInWin) {
      const rev = r.totalRevenue ?? 0;
      const profit = r.totalProfit ?? 0;
      if (rev > 0) {
        const m = (100 * profit) / rev;
        if (m < -300) extremeMarginCount += 1;
      }
    }

    const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

    const items: DataQualityNoticeItem[] = [];

    if (imputedOrderCount > 0) {
      const sev: DataQualitySeverity = imputedOrderCount >= 20 || imputedRevenue >= 1000 ? 'critical' : 'warning';
      items.push({
        severity: sev,
        kind: 'imputed_revenue',
        title: `Imputed revenue (£${Math.round(imputedRevenue).toLocaleString('en-GB')}) used in this period`,
        detail: `Imputed revenue was used on ${imputedOrderCount} ${plural(imputedOrderCount, 'order', 'orders')} this period. Margins on those lanes are less reliable than normal.${imputedBreakdown}`,
        impact: 'included_flagged',
      });
    }

    if (orderCount > 0 && orderCount <= 3) {
      items.push({
        severity: 'warning',
        kind: 'low_order_volume',
        title: 'Low order volume in scope',
        detail: `Only ${orderCount} ${plural(orderCount, 'order', 'orders')} fall in this period window — margins are highly sensitive to single-order variation.`,
        impact: 'included_flagged',
      });
    }

    if (missingUtilVehicles.length > 0) {
      const sample = missingUtilVehicles.slice(0, 4).join(', ');
      items.push({
        // Missing utilisation can materially affect utilisation-driven explanations; treat as critical when widespread.
        severity: missingUtilVehicles.length >= 20 ? 'critical' : 'warning',
        kind: 'utilisation_missing',
        title: `Utilisation data missing for ${missingUtilVehicles.length} vehicle(s)`,
        detail: `Utilisation data isn’t available for ${missingUtilVehicles.length} vehicle(s) in this period${useTruth && useTruthUtil ? ' (not in truth utilisation projection)' : ' (not present in the utilisation export)'}.${sample ? ` Examples: ${sample}.` : ''}`,
        impact: 'unknown',
      });
    }

    if (zeroMilesCostRows.length > 0) {
      const ex = zeroMilesCostRows[0];
      const example = ex?.vehicleId && ex?.date ? ` Example: ${ex.vehicleId} on ${(String(ex.date).slice(0, 10) || ex.date)}.` : '';
      const label = plural(zeroMilesCostRows.length, 'vehicle-day', 'vehicle-days');
      items.push({
        severity: zeroMilesCostRows.length >= 15 ? 'critical' : 'warning',
        kind: 'zero_miles_costs',
        title: `Costs recorded with zero miles (${zeroMilesCostRows.length} ${label})`,
        detail: `${zeroMilesCostRows.length} ${label} show costs but 0 miles. This often indicates missing/late telematics mileage for those days.${example}`,
        impact: 'included_flagged',
      });
    }

    if (extremeMarginCount > 0) {
      const label = plural(extremeMarginCount, 'vehicle-day', 'vehicle-days');
      items.push({
        severity: 'warning',
        kind: 'extreme_negative_margin',
        title: `Extreme negative margin detected (${extremeMarginCount} ${label})`,
        detail: `${extremeMarginCount} ${label} show margin below -300%. This can happen when revenue is missing/imputed or costs are allocated to the wrong day.`,
        impact: 'included_flagged',
      });
    }

    const sevRank = (s: DataQualitySeverity) => (s === 'critical' ? 2 : 1);
    const kindRank: Record<DataQualityKind, number> = {
      utilisation_missing: 90,
      imputed_revenue: 80,
      zero_miles_costs: 70,
      extreme_negative_margin: 40,
      low_order_volume: 10,
    };
    items.sort((a, b) => {
      const ds = sevRank(b.severity) - sevRank(a.severity);
      if (ds !== 0) return ds;
      return (kindRank[b.kind] ?? 0) - (kindRank[a.kind] ?? 0);
    });

    return { totalFlagged: items.length, items };
  }, [filteredSummaryData, mergedCostsDataFiltered, utilisationVehicleLevelMap, records, useTruth, useTruthUtil]);

  const dataQualityWindow = useMemo(() => {
    // Match Report period (truth load window) so notices align with pipeline amber warnings.
    return { startDate: truthDateRange.startDate, endDate: truthDateRange.endDate };
  }, [truthDateRange.startDate, truthDateRange.endDate]);

  const dataQualityNotice = useMemo(
    () => buildDataQualityNoticeForWindow(dataQualityWindow),
    [buildDataQualityNoticeForWindow, dataQualityWindow],
  );

  const renderBriefTitle = useMemo(() => {
    const pill = (text: string, tone: 'customer' | 'vehicle') => {
      const cls =
        tone === 'customer'
          ? 'px-1.5 py-0.5 rounded-md bg-chart-10/10 text-chart-10 ring-1 ring-chart-10/20 font-semibold'
          : 'px-1.5 py-0.5 rounded-md bg-warning/10 text-warning ring-1 ring-warning/20 font-semibold';
      return (
        <span key={`${tone}-${text}`} className={cls}>
          {text}
        </span>
      );
    };
    const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const splitWith = (text: string, highlight: string) => {
      if (!highlight) return [text];
      const re = new RegExp(`(${escape(highlight)})`, 'g');
      return text.split(re);
    };
    return (it: (typeof mondayBriefUi.items)[number]) => {
      const tone = it.kind === 'vehicle' ? 'vehicle' : 'customer';
      const first = (it.highlights?.[0] ?? '').trim();
      if (!first) return <span className="font-semibold text-white">{it.title}</span>;
      const parts = splitWith(it.title, first);
      return (
        <span className="font-semibold text-white">
          {parts.map((p, i) =>
            p === first ? <span key={i} className="mx-1 align-baseline">{pill(p, tone)}</span> : <span key={i}>{p}</span>,
          )}
        </span>
      );
    };
  }, [mondayBriefUi.items]);

  const paginatedSummaryData = useMemo(() => {
    const startIdx = (summaryPage - 1) * summaryRowsPerPage;
    return filteredSummaryData.slice(startIdx, startIdx + summaryRowsPerPage);
  }, [filteredSummaryData, summaryPage, summaryRowsPerPage]);

  const summaryTotalPages = Math.ceil(filteredSummaryData.length / summaryRowsPerPage);

  // Totals for Daily Summary (including driver cost components for Total row tooltip)
  const dailySummaryTotals = useMemo(() => {
    if (filteredSummaryData.length === 0) {
      return {
        totalMileage: 0,
        totalRevenue: 0,
        totalFuelCost: 0,
        totalDriverCost: 0,
        totalDepreciation: 0,
        totalProfit: 0,
        totalDrivingHours: 0,
        totalWaitingHours: 0,
        totalOrders: 0,
        totalPickups: 0,
        totalDeliveries: 0,
        currency: 'GBP' as string,
      };
    }
    const first = filteredSummaryData[0];
    const currency = first?.currency || 'GBP';
    return filteredSummaryData.reduce(
      (acc, row) => {
        const pickupIds = (row as { pickupOrderIds?: string[] }).pickupOrderIds ?? [];
        const deliveryIds = (row as { deliveryOrderIds?: string[] }).deliveryOrderIds ?? row.orderIds ?? [];
        const pickupCount =
          pickupIds.length > 0
            ? pickupIds.length
            : ((row as { pickupCount?: number }).pickupCount ?? 0);
        const deliveryCount =
          deliveryIds.length > 0
            ? deliveryIds.length
            : ((row as { deliveryCount?: number }).deliveryCount
              ?? row.orderCount
              ?? 0);
        return {
          totalMileage: acc.totalMileage + (row.totalMileage ?? 0),
          totalRevenue: acc.totalRevenue + (row.totalRevenue ?? 0),
          totalFuelCost: acc.totalFuelCost + (row.totalFuelCost ?? 0),
          totalDriverCost: acc.totalDriverCost + (row.totalDriverCost ?? 0),
          totalDepreciation: acc.totalDepreciation + (row.totalDepreciation ?? 0),
          totalProfit: acc.totalProfit + (row.totalProfit ?? 0),
          totalDrivingHours: (acc.totalDrivingHours ?? 0) + (row.totalDrivingHours ?? 0),
          totalWaitingHours: (acc.totalWaitingHours ?? 0) + (row.totalWaitingHours ?? 0),
          totalOrders: acc.totalOrders + deliveryCount,
          totalPickups: acc.totalPickups + pickupCount,
          totalDeliveries: acc.totalDeliveries + deliveryCount,
          currency,
        };
      },
      {
        totalMileage: 0,
        totalRevenue: 0,
        totalFuelCost: 0,
        totalDriverCost: 0,
        totalDepreciation: 0,
        totalProfit: 0,
        totalDrivingHours: 0,
        totalWaitingHours: 0,
        totalOrders: 0,
        totalPickups: 0,
        totalDeliveries: 0,
        currency: 'GBP' as string,
      },
    );
  }, [filteredSummaryData]);

  // Total revenue: sum directly from merged_costs.csv (already correctly attributed by the backend)

  // KPI totals respect Period type and Filter by Date: sum from filteredSummaryData (already filtered by period range)
  const kpiTotalsFromDailySummary = useMemo(() => {
    if (filteredSummaryData.length === 0) {
      return {
        totalRevenue: 0,
        totalFuelCost: 0,
        totalDriverCost: 0,
        totalDepreciation: 0,
        totalProfit: 0,
        currency: 'GBP' as string,
        totalRevenueOriginal: 0,
        totalRevenueModelled: 0,
        ordersWithOriginalRevenue: 0,
        ordersWithModelledRevenue: 0,
      };
    }
    const first = filteredSummaryData[0];
    const currency = first?.currency || 'GBP';
    const { totalRevenue, totalFuelCost, totalDriverCost, totalDepreciation, totalProfit, totalRevenueOriginal, totalRevenueModelled, ordersWithOriginalRevenue, ordersWithModelledRevenue } = filteredSummaryData.reduce(
      (acc, row) => {
        let origRev = 0;
        let modRev = 0;
        let origCount = 0;
        let modCount = 0;
        const orderRevenues = (row as { orderRevenues?: string[] }).orderRevenues ?? [];
        const orderRevenueImputed = (row as { orderRevenueImputed?: string[] }).orderRevenueImputed ?? [];
        for (let i = 0; i < orderRevenues.length; i++) {
          const rev = parseFloat(orderRevenues[i]);
          if (isNaN(rev)) continue;
          const imputed = (orderRevenueImputed[i] ?? '').trim();
          if (imputed === '0' || imputed === '') {
            origRev += rev;
            origCount += 1;
          } else {
            modRev += rev;
            modCount += 1;
          }
        }
        return {
          totalRevenue: acc.totalRevenue + (row.totalRevenue ?? 0),
          totalFuelCost: acc.totalFuelCost + (row.totalFuelCost ?? 0),
          totalDriverCost: acc.totalDriverCost + (row.totalDriverCost ?? 0),
          totalDepreciation: acc.totalDepreciation + (row.totalDepreciation ?? 0),
          totalProfit: acc.totalProfit + (row.totalProfit ?? 0),
          totalRevenueOriginal: acc.totalRevenueOriginal + origRev,
          totalRevenueModelled: acc.totalRevenueModelled + modRev,
          ordersWithOriginalRevenue: acc.ordersWithOriginalRevenue + origCount,
          ordersWithModelledRevenue: acc.ordersWithModelledRevenue + modCount,
        };
      },
      { totalRevenue: 0, totalFuelCost: 0, totalDriverCost: 0, totalDepreciation: 0, totalProfit: 0, totalRevenueOriginal: 0, totalRevenueModelled: 0, ordersWithOriginalRevenue: 0, ordersWithModelledRevenue: 0 }
    );
    return {
      totalRevenue,
      totalFuelCost,
      totalDriverCost,
      totalDepreciation,
      totalProfit,
      currency,
      totalRevenueOriginal,
      totalRevenueModelled,
      ordersWithOriginalRevenue,
      ordersWithModelledRevenue,
    };
  }, [filteredSummaryData]);

  // Format date to "Jan 2, 2026" format
  const formatDateForTitle = (dateStr: string): string => {
    if (!dateStr || dateStr.length < 10) return dateStr;
    try {
      const date = new Date(dateStr + 'T12:00:00');
      if (isNaN(date.getTime())) return dateStr;
      const month = date.toLocaleDateString('en-GB', { month: 'short' });
      const day = date.getDate();
      const year = date.getFullYear();
      return `${month} ${day}, ${year}`;
    } catch {
      return dateStr;
    }
  };

  // Format date range to "Jan 2 – Jan 30, 2026" format
  const formatDateRangeForTitle = (startDate: string, endDate: string): string => {
    if (!startDate || !endDate) return '';
    if (startDate === endDate) {
      return formatDateForTitle(startDate);
    }
    try {
      const start = new Date(startDate + 'T12:00:00');
      const end = new Date(endDate + 'T12:00:00');
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        return `${startDate} - ${endDate}`;
      }
      const startMonth = start.toLocaleDateString('en-GB', { month: 'short' });
      const startDay = start.getDate();
      const endMonth = end.toLocaleDateString('en-GB', { month: 'short' });
      const endDay = end.getDate();
      const year = end.getFullYear();
      
      // If same year, only show year at the end
      if (start.getFullYear() === end.getFullYear()) {
        return `${startMonth} ${startDay} – ${endMonth} ${endDay}, ${year}`;
      } else {
        // Different years - show both
        return `${startMonth} ${startDay}, ${start.getFullYear()} – ${endMonth} ${endDay}, ${year}`;
      }
    } catch {
      return `${startDate} - ${endDate}`;
    }
  };

  // Generate chart title parts for custom styling
  const profitabilityChartTitleParts = useMemo(() => {
    const periodPrefix = periodType === 'day' ? 'Daily' : periodType === 'week' ? 'Weekly' : 'Monthly';
    
    // Get vehicle regs (use selectedVehicleIds if any, otherwise show "All Vehicles")
    let vehicleRegsText = '';
    if (selectedVehicleIds.length > 0) {
      vehicleRegsText = selectedVehicleIds.join(', ');
    } else {
      vehicleRegsText = 'All Vehicles';
    }
    
    // Get date/period range - when calendar is paged to a month use that month; else selected date or range from data
    let dateRangeText = '';
    if (effectiveDateWindow) {
      if (effectiveDateWindow.startDate === effectiveDateWindow.endDate) {
        dateRangeText = formatDateForTitle(effectiveDateWindow.startDate);
      } else {
        dateRangeText = formatDateRangeForTitle(effectiveDateWindow.startDate, effectiveDateWindow.endDate);
      }
    } else if (selectedPeriodRange) {
      // When a date filter is selected, show the date(s)
      if (selectedPeriodRange.startDate === selectedPeriodRange.endDate) {
        dateRangeText = formatDateForTitle(selectedPeriodRange.startDate);
      } else {
        dateRangeText = formatDateRangeForTitle(selectedPeriodRange.startDate, selectedPeriodRange.endDate);
      }
    } else if (dailySummaryData.length > 0) {
      // No date filter - show date range from the data
      const dates = dailySummaryData.map(d => d.date).sort();
      let startDate = dates[0];
      let endDate = dates[dates.length - 1];
      // In month view, show full calendar month range (e.g. Jan 1 – Feb 28) not data extent (e.g. Jan 1 – Feb 17)
      if (periodType === 'month' && startDate && endDate) {
        const ymStart = startDate.slice(0, 7);
        const partsEnd = endDate.slice(0, 10).split('-');
        const yEnd = parseInt(partsEnd[0], 10);
        const mEnd = parseInt(partsEnd[1], 10);
        if (Number.isFinite(yEnd) && Number.isFinite(mEnd) && mEnd >= 1 && mEnd <= 12) {
          startDate = `${ymStart}-01`;
          const lastDay = new Date(yEnd, mEnd, 0).getDate();
          endDate = `${endDate.slice(0, 7)}-${String(lastDay).padStart(2, '0')}`;
        }
      }
      if (startDate && endDate) {
        if (startDate === endDate) {
          dateRangeText = formatDateForTitle(startDate);
        } else {
          dateRangeText = formatDateRangeForTitle(startDate, endDate);
        }
      }
    }
    
    return {
      periodPrefix: `${periodPrefix} Profitability Trend`,
      vehicleRegs: vehicleRegsText !== 'All Vehicles' ? vehicleRegsText : '',
      dateRange: dateRangeText,
    };
  }, [periodType, selectedVehicleIds, effectiveDateWindow, selectedPeriodRange, dailySummaryData]);

  // Chart data: aggregates mergedCostsDataFiltered by period WITHOUT date filter, so the chart always
  // shows full range. Vehicle filter IS applied so chart updates when a vehicle is selected.
  const chartDataUnfiltered = useMemo(() => {
    const grouped = new Map<string, {
      date: string; vehicleId: string;
      totalMileage: number; totalRevenue: number; totalFuelCost: number;
      totalDriverCost: number; totalDepreciation: number; totalProfit: number; currency: string; periodLabel?: string;
    }>();

    const getPeriodKey = (date: string, vehicleId: string): string => {
      if (periodType === 'day') return `${date}|${vehicleId}`;
      if (periodType === 'week') {
        const w = availableWeeks.find(w => date >= w.startDate && date <= w.endDate);
        return w ? `${w.startDate}|${w.endDate}|${vehicleId}` : `${date}|${vehicleId}`;
      }
      if (periodType === 'month') return `${(date && date.length >= 7 ? date.substring(0, 7) : date || '')}|${vehicleId}`;
      return `${date}|${vehicleId}`;
    };

    const getPeriodLabel = (date: string): string => {
      if (periodType === 'day') return date;
      if (periodType === 'week') {
        const w = availableWeeks.find(w => date >= w.startDate && date <= w.endDate);
        return w ? w.label : date;
      }
      if (periodType === 'month') {
        const ym = date && date.length >= 7 ? date.substring(0, 7) : '';
        const parts = ym.split('-');
        const year = parts[0];
        const month = parts[1];
        const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        const monthIndex = month != null ? parseInt(month, 10) - 1 : -1;
        const monthName = (monthIndex >= 0 && monthIndex < 12) ? names[monthIndex] : ym;
        const shortYear = year && year.length >= 2 ? year.slice(-2) : year || '';
        return `${monthName} ${shortYear}`;
      }
      return date;
    };

    let rows = mergedCostsDataFiltered;
    if (selectedVehicleIds.length > 0) {
      rows = rows.filter(r => {
        const vid = standardiseVehicleId(r.vehicle) || (r.vehicle && r.vehicle.trim()) || '—';
        return selectedVehicleIds.includes(vid);
      });
    }

    const NO_VEHICLE_ID = '—';
    for (const row of rows) {
      const dateKey = row.date.slice(0, 10);
      const stdVid = standardiseVehicleId(row.vehicle) || (row.vehicle && row.vehicle.trim()) || NO_VEHICLE_ID;
      if (!dateKey) continue;

      const periodKey = getPeriodKey(dateKey, stdVid);
      const existing = grouped.get(periodKey);
      const miles      = row.daily_miles        ?? 0;
      const revenue    = row.revenue             ?? 0;
      const vt         = resolveVehicleCostClassLabel(stdVid);
      const drivingH   = row.driving_hours       ?? 0;
      const waitingH   = row.waiting_hours       ?? 0;
      const fuelCost   = getMergedFuelCostPounds(row, stdVid, miles, vt, config.profitability, useTruth);
      const driverCost = getMergedDriverCostPounds(row, miles, drivingH, waitingH, config.profitability, useTruth);
      const standingCost = useTruth ? getMergedStandingCostPounds(row) : 0;

      if (existing) {
        existing.totalMileage    += miles;
        existing.totalRevenue    += revenue;
        existing.totalFuelCost   += fuelCost;
        existing.totalDriverCost += driverCost;
        existing.totalDepreciation = (existing.totalDepreciation ?? 0) + standingCost;
        existing.totalProfit     += revenue - fuelCost - driverCost;
      } else {
        grouped.set(periodKey, {
          date:            dateKey,
          vehicleId:       stdVid,
          totalMileage:    miles,
          totalRevenue:    revenue,
          totalFuelCost:   fuelCost,
          totalDriverCost: driverCost,
          totalDepreciation: standingCost,
          totalProfit:     revenue - fuelCost - driverCost,
          currency:        'GBP',
          periodLabel:     getPeriodLabel(dateKey),
        });
      }
    }

    const dataMinMaxChart = getMergedCostDateBounds(rows);
    const calendarWindowChart =
      dataMinMaxChart ? { startDate: dataMinMaxChart.min, endDate: dataMinMaxChart.max } : null;

    if (periodType === 'day' && calendarWindowChart) {
      const vehiclesSet = new Set<string>();
      for (const r of rows) {
        const v =
          standardiseVehicleId(r.vehicle) || (r.vehicle && r.vehicle.trim()) || NO_VEHICLE_ID;
        if (v !== NO_VEHICLE_ID) vehiclesSet.add(v);
      }
      const startD = new Date(`${calendarWindowChart.startDate}T12:00:00`);
      const endD = new Date(`${calendarWindowChart.endDate}T12:00:00`);
      for (let cur = new Date(startD); cur.getTime() <= endD.getTime(); cur.setDate(cur.getDate() + 1)) {
        const dStr = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-${String(
          cur.getDate(),
        ).padStart(2, '0')}`;
        for (const vid of vehiclesSet) {
          const pk = getPeriodKey(dStr, vid);
          if (!grouped.has(pk)) {
            grouped.set(pk, {
              date: dStr,
              vehicleId: vid,
              totalMileage: 0,
              totalRevenue: 0,
              totalFuelCost: 0,
              totalDriverCost: 0,
              totalDepreciation: 0,
              totalProfit: 0,
              currency: 'GBP',
              periodLabel: getPeriodLabel(dStr),
            });
          }
        }
      }
    }

    for (const row of grouped.values()) {
      if (useTruth) {
        row.totalProfit =
          row.totalRevenue - row.totalFuelCost - row.totalDriverCost - row.totalDepreciation;
        continue;
      }
      const dailyRate =
        row.vehicleId === NO_VEHICLE_ID
          ? 0
          : getDepreciationPerDay(resolveVehicleCostClassLabel(row.vehicleId), config.profitability);
      const nCal = getCalendarDaysForStandingRow(
        row,
        periodType,
        null,
        dataMinMaxChart,
        availableWeeks,
      );
      row.totalDepreciation = dailyRate * nCal;
      row.totalProfit =
        row.totalRevenue - row.totalFuelCost - row.totalDriverCost - row.totalDepreciation;
    }

    return Array.from(grouped.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [mergedCostsDataFiltered, periodType, availableWeeks, availableMonths, selectedVehicleIds, vehicleIdToType, config.profitability, useTruth]);

  // Line chart always shows the full month (not filtered by selected date): when a date filter is applied, use the full calendar month containing that range so the user sees the entire month trend.
  const chartDataForTrend = useMemo(() => {
    if (!effectiveDateWindow) return chartDataUnfiltered;
    const start = effectiveDateWindow.startDate;
    const [y, m] = start.split('-').map(Number);
    const monthStart = `${y}-${String(m).padStart(2, '0')}-01`;
    const lastDay = new Date(y, m, 0).getDate();
    const monthEnd = `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    return chartDataUnfiltered.filter(
      row => row.date >= monthStart && row.date <= monthEnd
    );
  }, [chartDataUnfiltered, effectiveDateWindow]);

  // Profit data for calendar view: by day, week, or month depending on periodType
  const dailyProfitForCalendar = useMemo(() => {
    const dailyProfit = new Map<string, number>();
    for (const row of chartDataUnfiltered) {
      const dateKey = row.date;
      if (dateKey && dateKey.length >= 10) {
        const dateOnly = dateKey.slice(0, 10);
        const existing = dailyProfit.get(dateOnly) || 0;
        dailyProfit.set(dateOnly, existing + (row.totalProfit ?? 0));
      }
    }
    return dailyProfit;
  }, [chartDataUnfiltered]);

  // Week-level profit for calendar (when periodType === 'week')
  const weekProfitForCalendar = useMemo(() => {
    if (periodType !== 'week') return new Map<string, number>();
    const weekProfit = new Map<string, number>();
    for (const row of chartDataUnfiltered) {
      const dateKey = row.date?.slice(0, 10);
      if (!dateKey) continue;
      const week = availableWeeks.find(w => dateKey >= w.startDate && dateKey <= w.endDate);
      if (week) {
        const key = `${week.startDate}|${week.endDate}`;
        weekProfit.set(key, (weekProfit.get(key) ?? 0) + (row.totalProfit ?? 0));
      }
    }
    return weekProfit;
  }, [chartDataUnfiltered, periodType, availableWeeks]);

  // Month-level profit for calendar (when periodType === 'month')
  const monthProfitForCalendar = useMemo(() => {
    if (periodType !== 'month') return new Map<string, number>();
    if (!Array.isArray(availableMonths) || availableMonths.length === 0) return new Map<string, number>();
    const monthProfit = new Map<string, number>();
    for (const row of chartDataUnfiltered) {
      const dateKey = row.date?.slice(0, 10);
      if (!dateKey) continue;
      const yearMonth = dateKey.slice(0, 7);
      const month = availableMonths.find(m => m && m.startDate && String(m.startDate).startsWith(yearMonth));
      if (month) {
        const key = `${month.startDate}|${month.endDate}`;
        monthProfit.set(key, (monthProfit.get(key) ?? 0) + (row.totalProfit ?? 0));
      }
    }
    return monthProfit;
  }, [chartDataUnfiltered, periodType, availableMonths]);

  // Time series by period for line chart: when calendar is paged to a month use that month only; otherwise full range
  const profitabilityChartSeries = useMemo(() => {
    /** Standing / depreciation must be included so amber line matches KPI Total Costs (Fuel + Driver + Standing). */
    const byPeriod = new Map<
      string,
      { date: string; latestDate: string; revenue: number; fuelCost: number; driverCost: number; standingCost: number; profit: number }
    >();
    
    // Find the last date in merged_costs.csv for the pulsing dot position on the chart
    const lastQargoDate = mergedCostsDataFiltered.length > 0
      ? mergedCostsDataFiltered
          .map(r => r.date.slice(0, 10))
          .filter(d => d)
          .sort()
          .pop() || ''
      : '';
    
    // Aggregate by period (chartDataForTrend is scoped to calendar month when paged, else full chartDataUnfiltered)
    for (const row of chartDataForTrend) {
      // Use periodLabel as key to group by period (handles day/week/month grouping)
      const key = row.periodLabel || row.date;
      const existing = byPeriod.get(key);
      
      if (existing) {
        // Sum values for this period (multiple vehicles/days can map to same period)
        existing.revenue += row.totalRevenue ?? 0;
        existing.fuelCost += row.totalFuelCost ?? 0;
        existing.driverCost += row.totalDriverCost ?? 0;
        existing.standingCost += row.totalDepreciation ?? 0;
        existing.profit += row.totalProfit ?? 0;
        // Keep earliest date for sorting, but track latest date for last point positioning
        if (row.date < existing.date) {
          existing.date = row.date;
        }
        if (row.date > existing.latestDate) {
          existing.latestDate = row.date;
        }
      } else {
        byPeriod.set(key, {
          date: row.date,
          latestDate: row.date,
          revenue: row.totalRevenue ?? 0,
          fuelCost: row.totalFuelCost ?? 0,
          driverCost: row.totalDriverCost ?? 0,
          standingCost: row.totalDepreciation ?? 0,
          profit: row.totalProfit ?? 0,
        });
      }
    }
    
    // Sort by date to ensure chronological order
    const sorted = Array.from(byPeriod.entries()).sort((a, b) => a[1].date.localeCompare(b[1].date));
    
    // When a date filter is selected, respect it - don't filter by last Qargo date
    // Only apply last Qargo date filtering when no date filter is selected (show all data)
    let filteredSorted = sorted;
    
    if (!effectiveDateWindow && !selectedPeriodRange && lastQargoDate && sorted.length > 0) {
      // No date filter selected - filter out points that are after the last Qargo date
      // This ensures the green pulsing dot appears on the actual last Qargo order date, not on later telematics-only dates
      // Find the index of the last point that has a date <= lastQargoDate
      let lastQargoPointIndex = -1;
      for (let i = sorted.length - 1; i >= 0; i--) {
        const pointDate = sorted[i][1].date;
        if (pointDate <= lastQargoDate) {
          lastQargoPointIndex = i;
          break;
        }
      }
      
      // If we found a point at or before the last Qargo date, remove all points after it
      if (lastQargoPointIndex >= 0 && lastQargoPointIndex < sorted.length - 1) {
        filteredSorted = sorted.slice(0, lastQargoPointIndex + 1);
        // Update the last point's latestDate to be the last Qargo date
        const lastPoint = filteredSorted[filteredSorted.length - 1];
        if (periodType === 'day') {
          lastPoint[1].latestDate = lastQargoDate;
        }
      } else if (lastQargoPointIndex === -1 && periodType === 'day') {
        // If no point exists for the last Qargo date, add one
        let revenueForDate = 0;
        let fuelCostForDate = 0;
        let driverCostForDate = 0;
        let standingForDate = 0;
        for (const row of dailySummaryData) {
          if (row.date === lastQargoDate) {
            revenueForDate += row.totalRevenue ?? 0;
            fuelCostForDate += row.totalFuelCost ?? 0;
            driverCostForDate += row.totalDriverCost ?? 0;
            standingForDate += row.totalDepreciation ?? 0;
          }
        }
        const profitForDate = revenueForDate - fuelCostForDate - driverCostForDate - standingForDate;
        filteredSorted.push([lastQargoDate, {
          date: lastQargoDate,
          latestDate: lastQargoDate,
          revenue: revenueForDate,
          fuelCost: fuelCostForDate,
          driverCost: driverCostForDate,
          standingCost: standingForDate,
          profit: profitForDate,
        }]);
      }
    }
    // When selectedPeriodRange is set, dailySummaryData is already filtered, so use all points as-is
    
    // Create chart data series - each point represents aggregated values for that period
    // For the last point, use the last Qargo date as the label if it matches
    const revenueData = filteredSorted.map(([label, agg], idx) => {
      const isLastPoint = idx === filteredSorted.length - 1;
      const useLabel = (isLastPoint && lastQargoDate && periodType === 'day' && agg.date <= lastQargoDate)
        ? lastQargoDate
        : label;
      return { label: useLabel, value: agg.revenue };
    });
    const totalCostsData = filteredSorted.map(([label, agg], idx) => {
      const isLastPoint = idx === filteredSorted.length - 1;
      const useLabel = (isLastPoint && lastQargoDate && periodType === 'day' && agg.date <= lastQargoDate)
        ? lastQargoDate
        : label;
      return { label: useLabel, value: agg.fuelCost + agg.driverCost + agg.standingCost };
    });
    const profitData = filteredSorted.map(([label, agg], idx) => {
      const isLastPoint = idx === filteredSorted.length - 1;
      const useLabel = (isLastPoint && lastQargoDate && periodType === 'day' && agg.date <= lastQargoDate)
        ? lastQargoDate
        : label;
      return { label: useLabel, value: agg.profit };
    });
    
    return [
      { name: 'Revenue', data: revenueData, color: CHART_PALETTE[4], strokeWidth: 1 },
      { name: 'Total Costs', data: totalCostsData, color: CHART_PALETTE[1], strokeWidth: 1 },
      { name: 'Net Profit', data: profitData, color: STATUS_COLORS.success, strokeWidth: 4 },
    ];
  }, [chartDataForTrend, mergedCostsDataFiltered, periodType, effectiveDateWindow, dailySummaryData]);

  // Calculate the selectedXLabel for the chart based on selectedPeriodRange
  // This should match one of the labels in the chart data exactly
  const selectedXLabelForChart = useMemo(() => {
    // Only show line when a date filter is selected (not "All Dates")
    // Note: selectedSummaryDate can be empty string for "All Dates", so we check selectedPeriodRange
    if (!selectedPeriodRange) {
      return null;
    }
    
    // Find the matching label from the chart data
    if (profitabilityChartSeries.length > 0 && profitabilityChartSeries[0].data.length > 0) {
      const chartLabels = profitabilityChartSeries[0].data.map(d => d.label);
      
      if (periodType === 'day') {
        // For day view, labels should be in YYYY-MM-DD format (from periodLabel || date)
        const startDate = selectedPeriodRange.startDate;
        const endDate = selectedPeriodRange.endDate;
        
        // First, try exact match with startDate (most common case)
        if (chartLabels.includes(startDate)) {
          return startDate;
        }
        
        // Try exact match with endDate (for single-day selections, startDate === endDate)
        if (endDate !== startDate && chartLabels.includes(endDate)) {
          return endDate;
        }
        
        // Try to find matching label by extracting date from label
        // Handle cases where label might be formatted differently
        for (const label of chartLabels) {
          // Extract date from label if it contains date info
          const labelDate = label.includes(' - ') ? label.split(' - ')[0].trim() : label;
          // For day view, labelDate should be YYYY-MM-DD format
          if (labelDate === startDate || labelDate === endDate) {
            return label; // Return the full label as it appears in chart
          }
        }
        
        // Find any date in chart labels within the selected range (prefer startDate, then endDate, then any)
        const startDateObj = new Date(startDate + 'T12:00:00');
        const endDateObj = new Date(endDate + 'T12:00:00');
        let closestToStart: string | null = null;
        let closestToEnd: string | null = null;
        let anyInRange: string | null = null;
        let minDiffToStart = Infinity;
        let minDiffToEnd = Infinity;
        
        for (const label of chartLabels) {
          const labelDate = label.includes(' - ') ? label.split(' - ')[0].trim() : label;
          if (/^\d{4}-\d{2}-\d{2}$/.test(labelDate)) {
            const labelDateObj = new Date(labelDate + 'T12:00:00');
            // Check if date is within the selected range
            if (labelDateObj >= startDateObj && labelDateObj <= endDateObj) {
              anyInRange = label; // Any date in range works
              
              // Track closest to startDate
              const diffToStart = Math.abs(labelDateObj.getTime() - startDateObj.getTime());
              if (diffToStart < minDiffToStart) {
                minDiffToStart = diffToStart;
                closestToStart = label;
              }
              
              // Track closest to endDate
              const diffToEnd = Math.abs(labelDateObj.getTime() - endDateObj.getTime());
              if (diffToEnd < minDiffToEnd) {
                minDiffToEnd = diffToEnd;
                closestToEnd = label;
              }
            }
          }
        }
        
        // Prefer closest to startDate, then closest to endDate, then any in range
        if (closestToStart) return closestToStart;
        if (closestToEnd) return closestToEnd;
        if (anyInRange) return anyInRange;
        
        // Final fallback: find the closest date overall (within 7 days of startDate)
        let closestOverall: string | null = null;
        let minDiffOverall = Infinity;
        for (const label of chartLabels) {
          const labelDate = label.includes(' - ') ? label.split(' - ')[0].trim() : label;
          if (/^\d{4}-\d{2}-\d{2}$/.test(labelDate)) {
            const labelDateObj = new Date(labelDate + 'T12:00:00');
            const diff = Math.abs(labelDateObj.getTime() - startDateObj.getTime());
            if (diff < minDiffOverall && diff <= 7 * 24 * 60 * 60 * 1000) {
              minDiffOverall = diff;
              closestOverall = label;
            }
          }
        }
        
        if (closestOverall) return closestOverall;
      } else if (periodType === 'week') {
        // For week view, labels are periodLabel format (e.g., "Jan 01 - Jan 07 25")
        // Find rows in dailySummaryData that fall within the selected range
        const matchingRows = dailySummaryData.filter(row => 
          row.date >= selectedPeriodRange.startDate && row.date <= selectedPeriodRange.endDate
        );
        
        // Get unique periodLabels from matching rows (with proper type guard)
        const periodLabels = [...new Set(matchingRows.map(r => r.periodLabel).filter((pl): pl is string => Boolean(pl)))];
        
        // Find the first periodLabel that exists in chart labels (exact match required)
        for (const periodLabel of periodLabels) {
          if (periodLabel && chartLabels.includes(periodLabel)) {
            return periodLabel;
          }
        }
        
        // Fallback: try to match selectedSummaryDate or any label containing it
        const matchingLabel = chartLabels.find(label => 
          label === selectedSummaryDate || 
          label.includes(selectedSummaryDate) ||
          selectedSummaryDate.includes(label)
        );
        if (matchingLabel) return matchingLabel;
      } else if (periodType === 'month') {
        if (!selectedPeriodRange) return null;
        // For month view, labels are periodLabel format (e.g., "Jan 26")
        // Find rows in dailySummaryData that fall within the selected range
        const matchingRows = dailySummaryData.filter(row => 
          row.date >= selectedPeriodRange.startDate && row.date <= selectedPeriodRange.endDate
        );
        
        // Get unique periodLabels from matching rows (with proper type guard)
        const periodLabels = [...new Set(matchingRows.map(r => r.periodLabel).filter((pl): pl is string => Boolean(pl)))];
        
        // Find the first periodLabel that exists in chart labels (exact match required)
        for (const periodLabel of periodLabels) {
          if (periodLabel && chartLabels.includes(periodLabel)) {
            return periodLabel;
          }
        }
        
        // Fallback: try to match selectedSummaryDate or any label containing it
        const matchingLabel = chartLabels.find(label => 
          label === selectedSummaryDate || 
          label.includes(selectedSummaryDate) ||
          selectedSummaryDate.includes(label)
        );
        if (matchingLabel) return matchingLabel;
      }
    }
    
    return null;
  }, [selectedPeriodRange, selectedSummaryDate, profitabilityChartSeries, periodType, dailySummaryData]);

  // Split records by status: 'qargo' (confirmed orders) and 'telematics' (delivery stops)
  const splitByStatus = useMemo(() => {
    const groups: Record<ProfitStatusGroup, ProfitRecord[]> = {
      qargo: [],
      telematics: [],
    };
    // Qargo order rows: all statuses and revenue values
    for (const rec of effectiveRecords) {
      const journeySplitCount = rec.journeySplitCount ?? effectiveOrderVehicleCount[rec.orderId] ?? 1;
      groups.qargo.push({ ...rec, journeySplitCount });
    }
    // Telematics: convert delivery stops to ProfitRecord format for display
    // Always create telematics records if we have vehicle filter OR date filter OR neither (show all)
    // This ensures the map always renders, even when filtering by date without vehicle selection
    const telematicsRecords: Array<ProfitRecord & { stops?: TelematicsDeliveryStop[] }> = [];
    let filteredStops = telematicsDeliveryStops;
    
    // Apply vehicle filter if vehicles are selected
    if (selectedVehicleIds.length > 0) {
      filteredStops = filteredStops.filter(s => 
        selectedVehicleIds.includes(standardiseVehicleId(s.AssetName))
      );
    }
    
    // Apply date filter if a period range is selected
    if (selectedPeriodRange) {
      filteredStops = filteredStops.filter(s => {
        const dateKey = normalizeTelematicsDate(s.day_date);
        return dateKey >= selectedPeriodRange.startDate && dateKey <= selectedPeriodRange.endDate;
      });
    }
    
    // Group stops by vehicle/date for display
    const stopsByVehicleDate = new Map<string, TelematicsDeliveryStop[]>();
    for (const stop of filteredStops) {
      const stdVid = standardiseVehicleId(stop.AssetName);
      const dateKey = normalizeTelematicsDate(stop.day_date);
      const key = `${stdVid}|${dateKey}`;
      if (!stopsByVehicleDate.has(key)) stopsByVehicleDate.set(key, []);
      stopsByVehicleDate.get(key)!.push(stop);
    }
    
    // Create one record per vehicle/date with stop count
    for (const [key, stops] of stopsByVehicleDate.entries()) {
      const [vehicleId, date] = key.split('|');
      const totalMiles = stops.reduce((sum, s) => sum + (s.miles_to_next_stop || 0), 0);
      const record = {
        orderId: `TELEMATICS-${vehicleId}-${date}`,
        vehicleId,
        date,
        revenue: 0,
        cost: 0,
        profit: 0,
        currency: 'GBP',
        status: 'TELEMATICS',
        journeySplitCount: 1,
        // Store stops data for display
        metrics_distance_total: totalMiles * METRES_PER_MILE,
        stops,
      } as (typeof telematicsRecords)[number];
      telematicsRecords.push(record);
    }
    groups.telematics = telematicsRecords;
    return groups;
  }, [effectiveRecords, effectiveOrderVehicleCount, telematicsDeliveryStops, selectedVehicleIds, selectedPeriodRange]);

  /** Orders in period: origin OR destination date in window (matches filteredRecords; in-transit rows often lack destination). */
  const customerProfitabilityQargo = useMemo(() => {
    const dateWindow = selectedPeriodRange ?? effectiveDateWindow;
    if (!dateWindow) return splitByStatus.qargo;
    return splitByStatus.qargo.filter((r) => {
      const originDateStr =
        r.origin_end_timestamp_local ?? r.origin_end_timestamp ?? r.date ?? '';
      const destDateStr =
        r.destination_end_timestamp_local ?? r.destination_timestamp ?? r.date ?? '';
      const originD = toComparableDate(originDateStr) || getDateOnly(originDateStr);
      const destD = toComparableDate(destDateStr) || getDateOnly(destDateStr);
      if (!originD && !destD) return true;
      const inRange = (d: string) => d && d >= dateWindow.startDate && d <= dateWindow.endDate;
      return inRange(originD) || inRange(destD);
    });
  }, [splitByStatus.qargo, selectedPeriodRange, effectiveDateWindow]);

  /** Legacy (orders-based) customer view: aggregate by customer and delivery date. */
  const customerProfitabilityLegacySummary = useMemo<CustomerProfitabilitySummaryRow[]>(() => {
    const qargo = customerProfitabilityQargo;
    type Agg = { customer: string; deliveryDate: string; orderIds: Set<string>; totalQuantity: number; totalWeight: number; totalRevenue: number; currency: string };
    const byCustomerDate = new Map<string, Agg>();
    for (const row of qargo) {
      const name = (row.customer ?? '').trim() || '—';
      const destDateStr =
        row.destination_end_timestamp_local ?? row.destination_timestamp ?? row.date ?? '';
      const destDateRaw = toComparableDate(destDateStr) || getDateOnly(destDateStr);
      const destDate = destDateRaw && /^\d{4}-\d{2}-\d{2}$/.test(destDateRaw) ? destDateRaw : '';
      const key = `${name}|${destDate}`;
      const existing = byCustomerDate.get(key);
      const orderId = (row.orderId ?? '').trim();
      const revenue = row.revenue ?? 0;
      const currency = row.currency || 'GBP';
      const qty = row.goods_quantity != null && String(row.goods_quantity).trim() !== ''
        ? (typeof row.goods_quantity === 'number' ? row.goods_quantity : parseInt(String(row.goods_quantity).trim(), 10))
        : 0;
      const weight = row.goods_weight != null && String(row.goods_weight).trim() !== ''
        ? (typeof row.goods_weight === 'number' ? row.goods_weight : parseFloat(String(row.goods_weight).trim()))
        : 0;
      const quantity = Number.isNaN(qty) ? 0 : qty;
      const weightVal = Number.isNaN(weight) ? 0 : weight;
      if (existing) {
        if (orderId) existing.orderIds.add(orderId);
        existing.totalRevenue += revenue;
        existing.totalQuantity += quantity;
        existing.totalWeight += weightVal;
      } else {
        const orderIds = new Set<string>();
        if (orderId) orderIds.add(orderId);
        byCustomerDate.set(key, {
          customer: name,
          deliveryDate: destDate,
          orderIds,
          totalQuantity: quantity,
          totalWeight: weightVal,
          totalRevenue: revenue,
          currency,
        });
      }
    }
    return Array.from(byCustomerDate.values()).map((agg) => ({
      customer: agg.customer,
      deliveryDate: agg.deliveryDate,
      orderCount: agg.orderIds.size,
      totalQuantity: agg.totalQuantity,
      totalWeight: agg.totalWeight,
      totalRevenue: agg.totalRevenue,
      totalAllocatedCost: 0,
      totalAllocatedMiles: 0,
      costPerMileGbp: 0,
      totalProfit: agg.totalRevenue,
      profitMarginPct: agg.totalRevenue > 0 ? 100 : 0,
      currency: agg.currency,
    }));
  }, [customerProfitabilityQargo]);

  /** Step 4 customer profitability mapped into UI row shape (daily or monthly). */
  const customerProfitabilityStep4Summary = useMemo<CustomerProfitabilitySummaryRow[]>(() => {
    if (!customerMonthlyProfitability.length) return [];
    const dateWindow = selectedPeriodRange ?? effectiveDateWindow;

    const rows = customerMonthlyProfitability
      .filter((row) => {
        const period = String(row.period || '').trim();
        const isDay = /^\d{4}-\d{2}-\d{2}$/.test(period);
        const isMonth = /^\d{4}-\d{2}$/.test(period);
        if (!isDay && !isMonth) return false;
        if (!dateWindow) return true;
        if (isDay) {
          return period >= dateWindow.startDate && period <= dateWindow.endDate;
        }
        const monthStart = `${period}-01`;
        const dt = new Date(monthStart);
        if (Number.isNaN(dt.getTime())) return false;
        const monthEnd = new Date(dt.getFullYear(), dt.getMonth() + 1, 0);
        const monthEndStr = monthEnd.toISOString().slice(0, 10);
        return monthStart <= dateWindow.endDate && monthEndStr >= dateWindow.startDate;
      })
      .map((row) => ({
        customer: (row.customer_name || '').trim() || '—',
        deliveryDate: String(row.period || '').trim(),
        orderCount: Number(row.order_count) || 0,
        totalQuantity: 0,
        totalWeight: 0,
        totalRevenue: Number(row.total_revenue_gbp) || 0,
        totalAllocatedCost: Number(row.total_allocated_cost_gbp) || 0,
        totalAllocatedMiles: Number(row.total_allocated_miles) || 0,
        totalProfit: Number(row.profit_gbp) || 0,
        currency: 'GBP',
      }));

    // Deduplicate/merge rows that share the same (period, customer). This can happen when
    // multiple overlapping Step 4 exports are appended/merged from storage.
    const byKey = new Map<
      string,
      { customer: string; deliveryDate: string; orderCount: number; totalRevenue: number; totalAllocatedCost: number; totalAllocatedMiles: number; totalProfit: number; currency: string }
    >();
    for (const r of rows) {
      const key = `${r.deliveryDate}|${(r.customer || '').trim().toLowerCase()}`;
      const existing = byKey.get(key);
      if (existing) {
        existing.orderCount += r.orderCount;
        existing.totalRevenue += r.totalRevenue;
        existing.totalAllocatedCost += r.totalAllocatedCost;
        existing.totalAllocatedMiles += r.totalAllocatedMiles;
        existing.totalProfit += r.totalProfit;
      } else {
        byKey.set(key, { ...r });
      }
    }

    return Array.from(byKey.values()).map((r) => ({
      ...r,
      totalQuantity: 0,
      totalWeight: 0,
      costPerMileGbp: r.totalAllocatedMiles > 0 ? r.totalAllocatedCost / r.totalAllocatedMiles : 0,
      profitMarginPct: r.totalRevenue > 0 ? (100 * r.totalProfit) / r.totalRevenue : 0,
    }));
  }, [customerMonthlyProfitability, selectedPeriodRange, effectiveDateWindow]);

  const customerProfitabilitySummary = useMemo<CustomerProfitabilitySummaryRow[]>(() => {
    return (customerProfitabilitySource === 'step4_monthly' || customerProfitabilitySource === 'step4_daily')
      && customerProfitabilityStep4Summary.length > 0
      ? customerProfitabilityStep4Summary
      : customerProfitabilityLegacySummary;
  }, [customerProfitabilitySource, customerProfitabilityStep4Summary, customerProfitabilityLegacySummary]);

  /** Lookup customer filter: table + footer totals only include rows for selected customers. */
  const customerProfitabilitySummaryFiltered = useMemo(() => {
    if (selectedCustomerNames.length === 0) return customerProfitabilitySummary;
    const wanted = new Set(selectedCustomerNames.map((c) => c.trim().toLowerCase()).filter(Boolean));
    return customerProfitabilitySummary.filter((r) =>
      wanted.has((r.customer ?? '').trim().toLowerCase())
    );
  }, [customerProfitabilitySummary, selectedCustomerNames]);

  /** Min/max cost/mile for bar width + colour: use full period summary so customer lookup filter does not rescale bars. */
  const customerCostPerMileRange = useMemo(() => {
    const vals = customerProfitabilitySummary
      .map((r) => Number(r.costPerMileGbp) || 0)
      .filter((v) => v > 0);
    if (vals.length === 0) return { min: 0, max: 0 };
    return { min: Math.min(...vals), max: Math.max(...vals) };
  }, [customerProfitabilitySummary]);

  // Match FleetReport "Overall Util %" bar style thresholds:
  // Green 70-100, Amber 40-69, Red 0-39.
  const getCustomerCostPerMileBarColor = (scorePct: number): string => {
    if (scorePct >= 70) return STATUS_COLORS.success;
    if (scorePct >= 40) return STATUS_COLORS.warning;
    return STATUS_COLORS.destructive;
  };

  const customerSortedSummary = useMemo(() => {
    const list = [...customerProfitabilitySummaryFiltered];
    const safeNum = (v: unknown): number => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };
    const safeText = (v: unknown): string => String(v ?? '').toLowerCase();
    list.sort((a, b) => {
      const aDash = customerSummarySortColumnIsDash(a, customerSortField);
      const bDash = customerSummarySortColumnIsDash(b, customerSortField);
      return compareWithSortedColumnDashLast(aDash, bDash, () => {
        let aVal: number | string = 0;
        let bVal: number | string = 0;
        switch (customerSortField) {
          case 'deliveryDate':
            // Supports both YYYY-MM (monthly) and YYYY-MM-DD (legacy daily fallback).
            aVal = String(a.deliveryDate || '');
            bVal = String(b.deliveryDate || '');
            break;
          case 'customer':
            aVal = safeText(a.customer);
            bVal = safeText(b.customer);
            return customerSortAsc ? String(aVal).localeCompare(String(bVal)) : String(bVal).localeCompare(String(aVal));
          case 'orderCount':
            aVal = safeNum(a.orderCount);
            bVal = safeNum(b.orderCount);
            break;
          case 'totalRevenue':
            aVal = safeNum(a.totalRevenue);
            bVal = safeNum(b.totalRevenue);
            break;
          case 'totalAllocatedCost':
            aVal = safeNum(a.totalAllocatedCost);
            bVal = safeNum(b.totalAllocatedCost);
            break;
          case 'totalAllocatedMiles':
            aVal = safeNum(a.totalAllocatedMiles);
            bVal = safeNum(b.totalAllocatedMiles);
            break;
          case 'costPerMileGbp':
            aVal = safeNum(a.costPerMileGbp);
            bVal = safeNum(b.costPerMileGbp);
            break;
          case 'totalProfit':
            aVal = safeNum(a.totalProfit);
            bVal = safeNum(b.totalProfit);
            break;
          case 'profitMarginPct':
            aVal = safeNum(a.profitMarginPct);
            bVal = safeNum(b.profitMarginPct);
            break;
          default:
            return 0;
        }
        if (aVal < bVal) return customerSortAsc ? -1 : 1;
        if (aVal > bVal) return customerSortAsc ? 1 : -1;
        return 0;
      });
    });
    return list;
  }, [customerProfitabilitySummaryFiltered, customerSortField, customerSortAsc]);

  const customerTotalPages = useMemo(() => {
    return Math.max(1, Math.ceil(customerSortedSummary.length / customerRowsPerPage));
  }, [customerSortedSummary.length, customerRowsPerPage]);

  const customerPaginatedSummary = useMemo(() => {
    const startIdx = (customerPage - 1) * customerRowsPerPage;
    return customerSortedSummary.slice(startIdx, startIdx + customerRowsPerPage);
  }, [customerSortedSummary, customerPage, customerRowsPerPage]);

  /** Customer Profitability totals (uses Step 4 monthly when available). Respects customer lookup filter. */
  const customerProfitabilityTotals = useMemo(() => {
    if (customerProfitabilitySummaryFiltered.length === 0) {
      return {
        totalOrders: 0,
        totalQuantity: 0,
        totalWeight: 0,
        totalRevenue: 0,
        totalAllocatedCost: 0,
        totalAllocatedMiles: 0,
        totalCostPerMileNumerator: 0,
        totalProfit: 0,
        currency: 'GBP' as string
      };
    }
    const currency = customerProfitabilitySummaryFiltered[0]?.currency || 'GBP';
    return customerProfitabilitySummaryFiltered.reduce(
      (acc, row) => ({
        totalOrders: acc.totalOrders + row.orderCount,
        totalQuantity: acc.totalQuantity + row.totalQuantity,
        totalWeight: acc.totalWeight + row.totalWeight,
        totalRevenue: acc.totalRevenue + row.totalRevenue,
        totalAllocatedCost: acc.totalAllocatedCost + row.totalAllocatedCost,
        totalAllocatedMiles: acc.totalAllocatedMiles + row.totalAllocatedMiles,
        totalCostPerMileNumerator: acc.totalCostPerMileNumerator + (row.costPerMileGbp * row.totalAllocatedMiles),
        totalProfit: acc.totalProfit + row.totalProfit,
        currency,
      }),
      {
        totalOrders: 0,
        totalQuantity: 0,
        totalWeight: 0,
        totalRevenue: 0,
        totalAllocatedCost: 0,
        totalAllocatedMiles: 0,
        totalCostPerMileNumerator: 0,
        totalProfit: 0,
        currency: 'GBP' as string
      }
    );
  }, [customerProfitabilitySummaryFiltered]);

  /** Maximum 2-page executive PDF: KPIs, best/worst vehicles & periods, customers & routes (Qargo-backed). */
  const buildProfitabilityPdfOutput = useCallback(async (opts?: { coverage?: 'scheduled' | 'ui'; scheduleOverride?: ProfitabilityReportScheduleConfig }): Promise<{ blob: Blob; filename: string } | null> => {
    const [logoPng, sparklesPng] = await Promise.all([loadProfitabilityLogoForPdf(), loadSparklesIconForPdf()]);
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 14;
    const innerW = pageW - margin * 2;
    const rightX = pageW - margin;
    // Slightly inset the last table column for readability
    const tableRightX = rightX - 4;
    const colName = margin + 11;
    /** Days / customer tables */
    const colOrdersX = rightX - 56;
    /** Period (day/week/month) tables: add Vehicles column to the left of Orders */
    const periodColVehiclesX = rightX - 72;
    const periodColOrdersX = rightX - 54;
    const periodColProfitX = rightX - 32;
    /** Vehicles table (Vehicle Type is left-aligned next to Vehicle; numeric columns remain right-aligned) */
    const vehColTypeX = colName + 32;
    const vehColPeriodX = rightX - 72;
    const vehColOrdersX = rightX - 54;
    const vehColProfitX = rightX - 32;
    const colCustMarginX = rightX - 30;

    /** Sterling amounts for PDF text (Unicode pound sign). */
    const fmtMoneyPdf = (n: number): string => {
      const neg = n < 0 ? '-' : '';
      const abs = Math.round(Math.abs(n));
      return `${neg}\u00A3${abs.toLocaleString('en-GB')}`;
    };

    /** Normalize punctuation that renders incorrectly in core PDF fonts. */
    const sanitizePdfText = (raw: string, maxLen?: number): string => {
      let t = String(raw ?? '')
        .normalize('NFKC')
        .replace(/\s+/g, ' ')
        .trim();
      t = t.replace(/[\u2010-\u2015\u2212\uFE58\uFE63\uFF0D]/g, '-');
      t = t.replace(/\u2026/g, '...');
      t = t.replace(/\u2192|\u2794|\u279D/g, '->');
      t = t.replace(/[\u00B7\u2022\u2023\u2219]/g, '|');
      // Avoid font fallback in core PDF fonts (can cause odd glyph spacing)
      t = t.replace(/\u0394/g, 'delta'); // Δ
      t = t.replace(/\u2248/g, '~');     // ≈
      if (maxLen && t.length > maxLen) t = `${t.slice(0, Math.max(0, maxLen - 3))}...`;
      return t || '-';
    };

    type PdfRankTone = 'top' | 'worst';

    const pdfRankPalette = (tone: PdfRankTone) =>
      tone === 'top'
        ? {
            label: [22, 101, 52] as [number, number, number],
            headerBg: [220, 252, 231] as [number, number, number],
            headerTxt: [21, 128, 61] as [number, number, number],
            rowTxt: [20, 83, 45] as [number, number, number],
            rule: [167, 243, 208] as [number, number, number],
          }
        : {
            label: [185, 28, 28] as [number, number, number],
            headerBg: [254, 226, 226] as [number, number, number],
            headerTxt: [153, 27, 27] as [number, number, number],
            rowTxt: [127, 29, 29] as [number, number, number],
            rule: [254, 202, 202] as [number, number, number],
          };

    const pdfResetBodyText = () => {
      doc.setTextColor(15, 23, 42);
    };

    const footerBandH = 14;
    const footerRow1Y = pageH - 9;
    const footerRow2Y = pageH - 4.8;

    const schForPdf =
      opts?.coverage === 'scheduled' ? (opts?.scheduleOverride ?? config.profitabilityReportSchedule) : null;
    const includeBrief = Boolean(opts?.coverage === 'scheduled' && schForPdf?.attachPdf && schForPdf?.attachBrief);
    const totalPages = includeBrief ? 3 : 2;
    const reportPage1Num = includeBrief ? 2 : 1;
    const reportPage2Num = includeBrief ? 3 : 2;

    const footer = (pageNum: number) => {
      doc.setFillColor(0, 0, 0);
      doc.rect(0, pageH - footerBandH, pageW, footerBandH, 'F');
      doc.setDrawColor(55, 55, 55);
      doc.setLineWidth(0.25);
      doc.line(0, pageH - footerBandH, pageW, pageH - footerBandH);

      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(230, 230, 230);
      doc.text(`Page ${pageNum} / ${totalPages}`, rightX, footerRow1Y, { align: 'right' });
      if (logoPng) {
        const fh = 3.6;
        const fw = fh * (700 / 140);
        doc.addImage(logoPng, 'PNG', margin, footerRow1Y - fh + 0.9, fw, fh);
        doc.text('Profitability summary', margin + fw + 2, footerRow1Y);
      } else {
        doc.text('Profitability summary', margin, footerRow1Y);
      }
      doc.setFontSize(7);
      doc.setTextColor(200, 200, 200);
      doc.text('For more, visit: https://zeefleet.co.uk', pageW / 2, footerRow2Y, { align: 'center' });
      doc.setTextColor(0, 0, 0);
    };

    const sectionHeading = (yy: number, title: string): number => {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text(sanitizePdfText(title), margin, yy);
      yy += 4;
      doc.setDrawColor(203, 213, 225);
      doc.setLineWidth(0.45);
      doc.line(margin, yy, rightX, yy);
      yy += 5;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(15, 23, 42);
      return yy;
    };

    const renderDataQualityNoticePdf = (yy: number, noticeWin: { startDate: string; endDate: string } | null | undefined) => {
      const notice = buildDataQualityNoticeForWindow(noticeWin);
      if (!notice.items.length) return yy;

      const headingY = sectionHeading(yy, 'Data Quality Notice');
      let y2 = headingY;

      // Compact list (max 4 items)
      const maxY = pageH - footerBandH - 6;
      for (const it of notice.items.slice(0, 4)) {
        if (y2 > maxY - 14) break;
        const isCritical = it.severity === 'critical';
        const iconText = isCritical ? '!' : '!';
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9.5);
        doc.setTextColor(isCritical ? 185 : 217, isCritical ? 28 : 119, isCritical ? 28 : 6);
        doc.text(iconText, margin, y2);
        doc.setTextColor(isCritical ? 185 : 217, isCritical ? 28 : 119, isCritical ? 28 : 6);
        const titleLines = doc.splitTextToSize(sanitizePdfText(it.title), innerW - 6);
        doc.text(titleLines[0] || '-', margin + 4, y2);
        y2 += 4.8;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        doc.setTextColor(100, 116, 139);
        const detailLines = doc.splitTextToSize(sanitizePdfText(it.detail), innerW - 6);
        for (const line of detailLines.slice(0, 2)) {
          if (y2 > maxY) break;
          doc.text(line, margin + 4, y2);
          y2 += 4;
        }
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        doc.setTextColor(isCritical ? 185 : 217, isCritical ? 28 : 119, isCritical ? 28 : 6);
        const impactText =
          it.impact === 'excluded'
            ? '→ Excluded from calculations this period.'
            : it.impact === 'included_flagged'
              ? '→ Included in calculations, but flagged for review.'
              : '→ Included; insights may be incomplete.';
        const impactLines = doc.splitTextToSize(sanitizePdfText(impactText), innerW - 6);
        if (impactLines[0]) {
          doc.text(impactLines[0], margin + 4, y2);
          y2 += 5.2;
        } else {
          y2 += 2;
        }
        doc.setTextColor(0, 0, 0);
      }

      return y2 + 2;
    };

    const renderBriefPdfPage = () => {
      // Simple, readable page that mirrors the UI Brief: title + grey context + green action.
      // Header band (match report style)
      doc.setFillColor(0, 0, 0);
      doc.rect(0, 0, pageW, 27, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(17);
      if (logoPng) {
        const lh = 7.8;
        const lw = lh * (700 / 140);
        doc.addImage(logoPng, 'PNG', rightX - lw, 6.5, lw, lh);
      }
      doc.text('Monday Brief — Profitability', margin, 14);

      // Subtitle line (like "Executive snapshot") with sparkles icon
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(230, 230, 230);
      const subY = 21;
      const subtitle = 'Weekly intelligence brief · Data quality notice';
      doc.text(subtitle, margin, subY);
      if (sparklesPng) {
        const w = doc.getTextWidth(subtitle);
        doc.addImage(sparklesPng, 'PNG', margin + w + 1.6, subY - 4.5, 3.6, 3.6);
      }

      // Right-side metadata
      doc.setFontSize(9);
      doc.text(`Period: ${sanitizePdfText(mondayBriefUi.meta.selectedYm || '—')}`, rightX, 22, { align: 'right' });
      doc.setFontSize(8);
      doc.text(`Generated: ${sanitizePdfText(new Date().toLocaleString('en-GB'))}`, rightX, 26, { align: 'right' });
      doc.setTextColor(0, 0, 0);

      let y = 35;
      // Match PDF section styling (header + divider) before brief content.
      y = sectionHeading(y, 'Weekly intelligence brief');
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);
      doc.text(sanitizePdfText(mondayBriefUi.headerText || '—'), margin, y);
      y += 6;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(71, 85, 105);
      const introLines = doc.splitTextToSize(sanitizePdfText(mondayBriefUi.introText || ''), innerW);
      introLines.forEach((line: string) => {
        doc.text(line, margin, y);
        y += 4.2;
      });
      y += 3;

      const maxY = pageH - footerBandH - 6;
      let itemIdx = 0;
      for (const it of mondayBriefUi.items || []) {
        itemIdx += 1;
        if (y > maxY - 20) break;

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setTextColor(15, 23, 42);
        const titleLines = doc.splitTextToSize(sanitizePdfText(`${itemIdx}. ${it.title || '—'}`), innerW);
        titleLines.forEach((line: string) => {
          if (y > maxY) return;
          doc.text(line, margin, y);
          y += 4.8;
        });

        // Grey line (context)
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(100, 116, 139);
        const bodyLines = doc.splitTextToSize(sanitizePdfText(it.body || ''), innerW);
        bodyLines.forEach((line: string) => {
          if (y > maxY) return;
          doc.text(line, margin, y);
          y += 4.1;
        });

        // Green line (recommended action)
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(16, 185, 129);
        const actionLines = doc.splitTextToSize(sanitizePdfText(it.action || ''), innerW);
        actionLines.forEach((line: string) => {
          if (y > maxY) return;
          doc.text(line, margin, y);
          y += 4.1;
        });

        doc.setTextColor(0, 0, 0);
        y += 6;
      }

      if (variant !== 'profit-agent') {
        // Confidence line: render "Confidence: Medium" (first sentence) in black, remainder in grey.
        const confRaw = sanitizePdfText(mondayBriefUi.confidenceText || '');
        const dotIdx = confRaw.indexOf('.');
        const confLead = dotIdx >= 0 ? confRaw.slice(0, dotIdx + 1) : confRaw;
        const confRest = dotIdx >= 0 ? confRaw.slice(dotIdx + 1).trim() : '';
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(15, 23, 42);
        const leadLines = doc.splitTextToSize(confLead, innerW);
        leadLines.slice(0, 2).forEach((line: string) => {
          if (y > maxY) return;
          doc.text(line, margin, y);
          y += 4;
        });
        if (confRest) {
          doc.setTextColor(100, 116, 139);
          const restLines = doc.splitTextToSize(confRest, innerW);
          restLines.slice(0, 2).forEach((line: string) => {
            if (y > maxY) return;
            doc.text(line, margin, y);
            y += 4;
          });
        }
        doc.setTextColor(0, 0, 0);
      }

      // Data Quality Notice should come after the brief content (but still on page 1).
      if (opts?.coverage === 'scheduled') {
        y += 6;
        y = renderDataQualityNoticePdf(y, dataQualityWindow);
      }
    };

    if (includeBrief) {
      renderBriefPdfPage();
      footer(1);
      doc.addPage();
    }

    const tableHeadVehiclePdf = (yy: number, tone: PdfRankTone): number => {
      const pal = pdfRankPalette(tone);
      doc.setFillColor(...pal.headerBg);
      doc.rect(margin, yy - 4.3, innerW, 7.2, 'F');
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...pal.headerTxt);
      doc.text('#', margin + 2, yy);
      doc.text('Vehicle', colName, yy);
      doc.text('Vehicle Type', vehColTypeX, yy);
      // In daily granularity this column is a count of active days, not a specific date.
      const vehiclePeriodLabel = pdfPeriodType === 'day' ? 'Days' : pdfPeriodType === 'week' ? 'Week' : 'Month';
      doc.text(vehiclePeriodLabel, vehColPeriodX, yy, { align: 'right' });
      doc.text('Orders', vehColOrdersX, yy, { align: 'right' });
      doc.text('Profit', vehColProfitX, yy, { align: 'right' });
      doc.text('Miles', tableRightX, yy, { align: 'right' });
      yy += 2;
      doc.setDrawColor(...pal.rule);
      doc.line(margin, yy + 1.5, rightX, yy + 1.5);
      yy += 5;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...pal.rowTxt);
      doc.setFontSize(8);
      return yy;
    };

    const tableHeadDaysPdf = (yy: number, tone: PdfRankTone): number => {
      const pal = pdfRankPalette(tone);
      doc.setFillColor(...pal.headerBg);
      doc.rect(margin, yy - 4.3, innerW, 7.2, 'F');
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...pal.headerTxt);
      doc.text('#', margin + 2, yy);
      doc.text('Date', colName, yy);
      doc.text('Vehicles', periodColVehiclesX, yy, { align: 'right' });
      doc.text('Orders', periodColOrdersX, yy, { align: 'right' });
      doc.text('Profit', periodColProfitX, yy, { align: 'right' });
      doc.text('Miles', tableRightX, yy, { align: 'right' });
      yy += 2;
      doc.setDrawColor(...pal.rule);
      doc.line(margin, yy + 1.5, rightX, yy + 1.5);
      yy += 5;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...pal.rowTxt);
      doc.setFontSize(8);
      return yy;
    };

    const tableHeadRoutePdf = (yy: number, tone: PdfRankTone): number => {
      const pal = pdfRankPalette(tone);
      doc.setFillColor(...pal.headerBg);
      doc.rect(margin, yy - 4.3, innerW, 7.2, 'F');
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...pal.headerTxt);
      doc.text('#', margin + 2, yy);
      doc.text('Route', colName, yy);
      doc.text('Profit', tableRightX, yy, { align: 'right' });
      yy += 2;
      doc.setDrawColor(...pal.rule);
      doc.line(margin, yy + 1.5, rightX, yy + 1.5);
      yy += 5;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...pal.rowTxt);
      doc.setFontSize(8);
      return yy;
    };

    const rowVehiclePdf = (
      yy: number,
      rank: number,
      vehicleId: string,
      vehicleType: string,
      periodCount: number,
      orderCount: number,
      profit: number,
      miles: number | null,
      tone: PdfRankTone,
    ): number => {
      const pal = pdfRankPalette(tone);
      doc.setTextColor(...pal.rowTxt);
      doc.text(String(rank), margin + 2, yy);
      doc.text(sanitizePdfText(vehicleId, 18), colName, yy);
      doc.text(sanitizePdfText(vehicleType, 14), vehColTypeX, yy);
      doc.text(String(periodCount), vehColPeriodX, yy, { align: 'right' });
      doc.text(String(orderCount), vehColOrdersX, yy, { align: 'right' });
      doc.text(fmtMoneyPdf(profit), vehColProfitX, yy, { align: 'right' });
      doc.text(miles != null ? String(Math.round(miles)) : '-', tableRightX, yy, { align: 'right' });
      return yy + 4.4;
    };

    const rowDaysPdf = (
      yy: number,
      rank: number,
      label: string,
      vehicleCount: number,
      orderCount: number,
      profit: number,
      miles: number | null,
      tone: PdfRankTone,
    ): number => {
      const pal = pdfRankPalette(tone);
      doc.setTextColor(...pal.rowTxt);
      doc.text(String(rank), margin + 2, yy);
      doc.text(sanitizePdfText(label, 40), colName, yy);
      doc.text(String(vehicleCount), periodColVehiclesX, yy, { align: 'right' });
      doc.text(String(orderCount), periodColOrdersX, yy, { align: 'right' });
      doc.text(fmtMoneyPdf(profit), periodColProfitX, yy, { align: 'right' });
      doc.text(miles != null ? String(Math.round(miles)) : '-', tableRightX, yy, { align: 'right' });
      return yy + 4.4;
    };

    const tableHeadCustomer = (yy: number, tone: PdfRankTone): number => {
      const pal = pdfRankPalette(tone);
      doc.setFillColor(...pal.headerBg);
      doc.rect(margin, yy - 4.3, innerW, 7.2, 'F');
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...pal.headerTxt);
      doc.text('#', margin + 2, yy);
      doc.text('Customer', colName, yy);
      doc.text('Orders', colOrdersX, yy, { align: 'right' });
      doc.text('Margin', colCustMarginX, yy, { align: 'right' });
      doc.text('Profit', tableRightX, yy, { align: 'right' });
      yy += 2;
      doc.setDrawColor(...pal.rule);
      doc.line(margin, yy + 1.5, rightX, yy + 1.5);
      yy += 5;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...pal.rowTxt);
      doc.setFontSize(8);
      return yy;
    };

    const rowCustomer = (
      yy: number,
      rank: number,
      label: string,
      orders: number,
      marginPct: number | null,
      profit: number,
      tone: PdfRankTone,
    ): number => {
      const pal = pdfRankPalette(tone);
      doc.setTextColor(...pal.rowTxt);
      doc.text(String(rank), margin + 2, yy);
      doc.text(sanitizePdfText(label, 36), colName, yy);
      doc.text(String(orders), colOrdersX, yy, { align: 'right' });
      doc.text(
        marginPct != null && Number.isFinite(marginPct) ? `${marginPct}%` : '-',
        colCustMarginX,
        yy,
        { align: 'right' },
      );
      doc.text(fmtMoneyPdf(profit), tableRightX, yy, { align: 'right' });
      return yy + 4.4;
    };

    const rowTwoCol = (yy: number, rank: number, label: string, profit: number, tone: PdfRankTone): number => {
      const pal = pdfRankPalette(tone);
      doc.setTextColor(...pal.rowTxt);
      doc.text(String(rank), margin + 2, yy);
      doc.text(sanitizePdfText(label, 62), colName, yy);
      doc.text(fmtMoneyPdf(profit), tableRightX, yy, { align: 'right' });
      return yy + 4.4;
    };

    const fmtYmd = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${dd}`;
    };

    const computeScheduledWindow = () => {
      const sch = opts?.scheduleOverride ?? config.profitabilityReportSchedule;
      // Scheduled PDFs should be anchored to the latest available data date (not the viewer's clock),
      // so the "Period" matches what users see in the UI export for the same dataset snapshot.
      const dataBounds = getMergedCostDateBounds(mergedCostsDataFiltered);
      const maxDateStr = dataBounds?.max ? dataBounds.max.slice(0, 10) : null;
      const anchor = maxDateStr ? new Date(`${maxDateStr}T12:00:00`) : new Date();
      const today = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());

      // For daily emails, default to "Yesterday" coverage.
      if (sch.frequency === 'daily') {
        const d = fmtYmd(today);
        return { startDate: d, endDate: d, label: 'Yesterday' };
      }

      const coverage = sch.periodCoverage;
      if (coverage === 'last_7_days') {
        const end = new Date(today);
        const start = new Date(end);
        start.setDate(start.getDate() - 6);
        return { startDate: fmtYmd(start), endDate: fmtYmd(end), label: 'Last 7 days' };
      }
      if (coverage === 'last_30_days') {
        const end = new Date(today);
        const start = new Date(end);
        start.setDate(start.getDate() - 29);
        return { startDate: fmtYmd(start), endDate: fmtYmd(end), label: 'Last 30 days' };
      }
      if (coverage === 'month_to_date') {
        const start = new Date(today.getFullYear(), today.getMonth(), 1);
        const end = new Date(today);
        return { startDate: fmtYmd(start), endDate: fmtYmd(end), label: 'Month to date' };
      }
      if (coverage === 'previous_month') {
        const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const end = new Date(today.getFullYear(), today.getMonth(), 0);
        return { startDate: fmtYmd(start), endDate: fmtYmd(end), label: 'Previous month' };
      }
      // previous_week (default)
      {
        // Previous Monday..Sunday (UK ops)
        const day = today.getDay(); // 0..6 (Sun..Sat)
        const diffToMonday = (day + 6) % 7;
        const thisMonday = new Date(today);
        thisMonday.setDate(thisMonday.getDate() - diffToMonday);
        const prevMonday = new Date(thisMonday);
        prevMonday.setDate(prevMonday.getDate() - 7);
        const prevSunday = new Date(thisMonday);
        prevSunday.setDate(prevSunday.getDate() - 1);
        return { startDate: fmtYmd(prevMonday), endDate: fmtYmd(prevSunday), label: 'Previous week' };
      }
    };

    const scheduledWin = opts?.coverage === 'scheduled' ? computeScheduledWindow() : null;
    const schGran = (opts?.scheduleOverride ?? config.profitabilityReportSchedule).reportGranularity;
    const pdfPeriodType =
      opts?.coverage === 'scheduled'
        ? (schGran === 'daily' ? 'day' : schGran === 'weekly' ? 'week' : 'month')
        : periodType;

    const win = opts?.coverage === 'scheduled' ? scheduledWin : (selectedPeriodRange ?? effectiveDateWindow);
    const coverageLabel = scheduledWin?.label ?? null;
    const periodLabel = win ? `${sanitizePdfText(win.startDate)} to ${sanitizePdfText(win.endDate)}` : 'Full range (no date window)';
    const grain =
      pdfPeriodType === 'day' ? 'Daily rows' : pdfPeriodType === 'week' ? 'Weekly totals' : 'Monthly totals';

    const sectionsForPdf = schForPdf?.sections ?? {
      executiveSnapshot: true,
      fleetKpis: true,
      vehicles: true,
      periods: true,
      customers: true,
      routes: true,
    };
    const scheduleVehicleSet = new Set(
      (schForPdf?.filters?.vehicleIds ?? [])
        .map((v) => standardiseVehicleId(v) || v)
        .filter(Boolean),
    );
    const scheduleHideStIves = Boolean(schForPdf?.filters?.hideStIvesVehicles);

    const inWindow = (dateStr: string | undefined | null) => {
      if (!win?.startDate || !win?.endDate) return true;
      const d = (dateStr || '').slice(0, 10);
      return Boolean(d) && d >= win.startDate && d <= win.endDate;
    };

    const baseSummaryForPdf =
      opts?.coverage === 'scheduled'
        ? profitabilitySummaryRowsFullRange
        : filteredSummaryData;

    const isNonEmptySummaryRow = (row: (typeof baseSummaryForPdf)[number]) =>
      (row.totalRevenue > 0) ||
      ((row.totalFuelCost ?? 0) + (row.totalDriverCost ?? 0) > 0) ||
      (row.totalDepreciation ?? 0) > 0;

    const summaryForPdf = baseSummaryForPdf
      .filter((r) => isNonEmptySummaryRow(r) && inWindow(r.date))
      .filter((r) => {
        if (opts?.coverage !== 'scheduled') return true;
        const std = standardiseVehicleId(r.vehicleId) || r.vehicleId;
        if (scheduleVehicleSet.size > 0 && !scheduleVehicleSet.has(std)) return false;
        if (scheduleHideStIves && stIvesVehicleIds.size > 0) {
          const v = standardiseVehicleId(r.vehicleId) || '';
          if (v && stIvesVehicleIds.has(v)) return false;
        }
        return true;
      });
    // Customer + route sections should use Qargo order rows in the selected window.
    // For scheduled PDFs, the UI state (effectiveDateWindow / selectedPeriodRange) may not match the scheduled window,
    // so we must start from a full-range Qargo set and then apply the scheduled window filter.
    const qargoBaseForPdf =
      opts?.coverage === 'scheduled'
        ? filteredRecordsFullRange.filter((r) => {
            const status = (r.status || '').toUpperCase();
            return CONFIRMED_STATUSES.has(status);
          })
        : customerProfitabilityQargo;
    const qargoForPdf = qargoBaseForPdf.filter((o) => {
      const dest = toComparableDate(o.destination_end_timestamp_local ?? o.destination_timestamp ?? o.date ?? '') || '';
      if (!inWindow(dest)) return false;
      if (opts?.coverage !== 'scheduled') return true;
      const vRaw = (o.vehicle_observed ?? o.vehicleId ?? o.vehicle_assigned ?? '').toString();
      const v = standardiseVehicleId(vRaw) || vRaw.trim();
      if (scheduleVehicleSet.size > 0 && v && !scheduleVehicleSet.has(v)) return false;
      if (scheduleHideStIves && stIvesVehicleIds.size > 0) {
        const vv = standardiseVehicleId(v) || '';
        if (vv && stIvesVehicleIds.has(vv)) return false;
      }
      return true;
    });
    const custSummaryForPdf = customerProfitabilitySummaryFiltered.filter((r) => {
      const key = String(r.deliveryDate || '').trim();
      // Step4 monthly rows use YYYY-MM; treat as month window overlap.
      if (/^\d{4}-\d{2}$/.test(key) && win?.startDate && win?.endDate) {
        const monthStart = `${key}-01`;
        const dt = new Date(monthStart);
        if (Number.isNaN(dt.getTime())) return false;
        const monthEnd = new Date(dt.getFullYear(), dt.getMonth() + 1, 0).toISOString().slice(0, 10);
        return monthStart <= win.endDate && monthEnd >= win.startDate;
      }
      return inWindow(key);
    });

    const computePrevWindow = (): { startDate: string; endDate: string; label: string } | null => {
      if (!win?.startDate || !win?.endDate) return null;
      const parseYmd = (s: string) => {
        const [yy, mm, dd] = s.split('-').map((x) => parseInt(x, 10));
        return new Date(yy, (mm || 1) - 1, dd || 1);
      };
      const start = parseYmd(win.startDate);
      const end = parseYmd(win.endDate);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;

      const dayCount = Math.max(1, Math.round((end.getTime() - start.getTime()) / (24 * 3600 * 1000)) + 1);

      // "Yesterday" -> compare to the day before yesterday (or generally: previous matching window)
      const prevStart = new Date(start);
      prevStart.setDate(prevStart.getDate() - dayCount);
      const prevEnd = new Date(end);
      prevEnd.setDate(prevEnd.getDate() - dayCount);

      // For calendar windows, compare to the previous matching calendar window (not just a dayCount shift).
      if (opts?.coverage === 'scheduled' && coverageLabel === 'Previous month') {
        const prevMonthStart = new Date(start.getFullYear(), start.getMonth() - 1, 1);
        const prevMonthEnd = new Date(start.getFullYear(), start.getMonth(), 0);
        return { startDate: fmtYmd(prevMonthStart), endDate: fmtYmd(prevMonthEnd), label: 'Previous month' };
      }
      if (opts?.coverage === 'scheduled' && coverageLabel === 'Previous week') {
        const ps = new Date(start);
        ps.setDate(ps.getDate() - 7);
        const pe = new Date(end);
        pe.setDate(pe.getDate() - 7);
        return { startDate: fmtYmd(ps), endDate: fmtYmd(pe), label: 'Previous week' };
      }

      // If scheduled + month_to_date, use previous month aligned to day-of-month where possible.
      if (opts?.coverage === 'scheduled' && coverageLabel === 'Month to date') {
        const prevMonthStart = new Date(start.getFullYear(), start.getMonth() - 1, 1);
        const targetDay = end.getDate();
        const prevMonthEndCandidate = new Date(end.getFullYear(), end.getMonth() - 1, targetDay);
        // clamp to last day of previous month
        const lastDayPrevMonth = new Date(end.getFullYear(), end.getMonth(), 0);
        const prevMonthEnd =
          prevMonthEndCandidate.getMonth() === prevMonthStart.getMonth() ? prevMonthEndCandidate : lastDayPrevMonth;
        return { startDate: fmtYmd(prevMonthStart), endDate: fmtYmd(prevMonthEnd), label: 'Previous month (to date)' };
      }

      return { startDate: fmtYmd(prevStart), endDate: fmtYmd(prevEnd), label: 'Previous period' };
    };

    const prevWin = opts?.coverage === 'scheduled' ? computePrevWindow() : null;
    const inPrevWindow = (dateStr: string | undefined | null) => {
      if (!prevWin?.startDate || !prevWin?.endDate) return false;
      const d = (dateStr || '').slice(0, 10);
      return Boolean(d) && d >= prevWin.startDate && d <= prevWin.endDate;
    };
    const summaryPrevForPdf = prevWin ? baseSummaryForPdf.filter((r) => inPrevWindow(r.date)) : [];

    const dailySummaryTotalsForPdf = {
      totalMileage: summaryForPdf.reduce((sum, r) => sum + (r.totalMileage ?? 0), 0),
    };
    const kpiTotalsForPdf = summaryForPdf.reduce(
      (acc, r) => {
        acc.totalRevenue += r.totalRevenue ?? 0;
        acc.totalFuelCost += r.totalFuelCost ?? 0;
        acc.totalDriverCost += r.totalDriverCost ?? 0;
        acc.totalDepreciation += r.totalDepreciation ?? 0;
        acc.totalProfit += r.totalProfit ?? 0;
        return acc;
      },
      {
        totalRevenue: 0,
        totalFuelCost: 0,
        totalDriverCost: 0,
        totalDepreciation: 0,
        totalProfit: 0,
      },
    );

    const kpiTotalsPrevForPdf = summaryPrevForPdf.reduce(
      (acc, r) => {
        acc.totalRevenue += r.totalRevenue ?? 0;
        acc.totalFuelCost += r.totalFuelCost ?? 0;
        acc.totalDriverCost += r.totalDriverCost ?? 0;
        acc.totalDepreciation += r.totalDepreciation ?? 0;
        acc.totalProfit += r.totalProfit ?? 0;
        acc.totalMileage += r.totalMileage ?? 0;
        const ids = collectOrderIdsFromDailySummaryRow(r);
        ids.forEach((id) => acc.orderIds.add(id));
        return acc;
      },
      {
        totalRevenue: 0,
        totalFuelCost: 0,
        totalDriverCost: 0,
        totalDepreciation: 0,
        totalProfit: 0,
        totalMileage: 0,
        orderIds: new Set<string>(),
      },
    );

    const kpiMetaForPdf = (() => {
      const orderIds = new Set<string>();
      for (const r of summaryForPdf) collectOrderIdsFromDailySummaryRow(r).forEach((id) => orderIds.add(id));
      const totalMileage = summaryForPdf.reduce((sum, r) => sum + (r.totalMileage ?? 0), 0);
      return { orderCount: orderIds.size, totalMileage };
    })();

    const topVehiclesForPdf = (() => {
      const NO_VEHICLE_ID = '—';
      const isActiveSummaryRow = (r: (typeof summaryForPdf)[number]) => {
        const miles = r.totalMileage ?? 0;
        const rev = r.totalRevenue ?? 0;
        const varCosts = (r.totalFuelCost ?? 0) + (r.totalDriverCost ?? 0);
        const orders = r.orderCount ?? (r.orderIds?.length ?? 0);
        return miles > 0 || rev > 0 || varCosts > 0 || orders > 0;
      };
      const byVehicle = new Map<
        string,
        { vehicle: string; profit: number; revenue: number; miles: number; periods: Set<string>; orders: Set<string> }
      >();
      for (const r of summaryForPdf) {
        const vid = r.vehicleId;
        if (!vid || vid === NO_VEHICLE_ID) continue;
        const stdVid = standardiseVehicleId(vid) || vid.trim() || NO_VEHICLE_ID;
        if (stdVid === NO_VEHICLE_ID) continue;
        const periodKey =
          pdfPeriodType === 'day'
            ? (r.date || '').slice(0, 10)
            : (r.periodLabel || r.date || '').trim();
        if (!periodKey) continue;
        const existing = byVehicle.get(vid);
        const orderIdsRow = collectOrderIdsFromDailySummaryRow(r);
        const countThisPeriod = isActiveSummaryRow(r);
        if (existing) {
          existing.profit += r.totalProfit;
          existing.revenue += r.totalRevenue ?? 0;
          existing.miles += r.totalMileage ?? 0;
          if (countThisPeriod) existing.periods.add(periodKey);
          orderIdsRow.forEach((id) => existing.orders.add(id));
        } else {
          byVehicle.set(vid, {
            vehicle: vid,
            profit: r.totalProfit,
            revenue: r.totalRevenue ?? 0,
            miles: r.totalMileage ?? 0,
            periods: countThisPeriod ? new Set([periodKey]) : new Set(),
            orders: new Set(orderIdsRow),
          });
        }
      }
      return Array.from(byVehicle.values())
        .sort((a, b) => b.profit - a.profit)
        .map((v, i) => ({
          rank: i + 1,
          vehicle: v.vehicle,
          vehicleType: vehicleIdToType[standardiseVehicleId(v.vehicle)] ?? 'Default',
          profit: v.profit,
          miles: v.miles,
          activeDays: v.periods.size,
          orderCount: v.orders.size,
        }));
    })();

    const topDatesForPdf = (() => {
      const byDate = new Map<
        string,
        { date: string; profit: number; revenue: number; miles: number; vehicles: Set<string>; orders: Set<string> }
      >();
      for (const r of summaryForPdf) {
        const dateKey =
          pdfPeriodType === 'day'
            ? (r.date || '').slice(0, 10)
            : (r.periodLabel || r.date || '').trim();
        if (!dateKey) continue;
        const existing = byDate.get(dateKey);
        const orderIdsRow = collectOrderIdsFromDailySummaryRow(r);
        if (existing) {
          existing.profit += r.totalProfit;
          existing.revenue += r.totalRevenue ?? 0;
          existing.miles += r.totalMileage ?? 0;
          if (r.vehicleId) existing.vehicles.add(r.vehicleId);
          orderIdsRow.forEach((id) => existing.orders.add(id));
        } else {
          byDate.set(dateKey, {
            date: dateKey,
            profit: r.totalProfit,
            revenue: r.totalRevenue ?? 0,
            miles: r.totalMileage ?? 0,
            vehicles: new Set(r.vehicleId ? [r.vehicleId] : []),
            orders: new Set(orderIdsRow),
          });
        }
      }
      return Array.from(byDate.values())
        .sort((a, b) => b.profit - a.profit)
        .map((d, i) => ({
          rank: i + 1,
          date: d.date,
          profit: d.profit,
          miles: d.miles,
          activeVehicles: d.vehicles.size,
          orderCount: d.orders.size,
        }));
    })();

    // --- Cover band (black) ---
    doc.setFillColor(0, 0, 0);
    doc.rect(0, 0, pageW, 27, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(17);
    if (logoPng) {
      const lh = 7.8;
      const lw = lh * (700 / 140);
      doc.addImage(logoPng, 'PNG', rightX - lw, 6.5, lw, lh);
      doc.text('Profitability summary', margin, 14);
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'normal');
      doc.text('Executive snapshot', margin, 21);
    } else {
      doc.text('Profitability summary', margin, 14);
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'normal');
      doc.text('Executive snapshot', margin, 21);
    }
    // Right-aligned header metadata (Period above Generated)
    // Align to bottom of header band (27mm tall)
    if (coverageLabel) {
      doc.setFontSize(9);
      doc.text(`Coverage: ${sanitizePdfText(coverageLabel)}`, rightX, 18, { align: 'right' });
    }
    doc.setFontSize(9);
    doc.text(`Period: ${periodLabel}`, rightX, 22, { align: 'right' });
    doc.setFontSize(8);
    doc.text(`Generated: ${sanitizePdfText(new Date().toLocaleString('en-GB'))}`, rightX, 26, { align: 'right' });
    doc.setTextColor(0, 0, 0);

    let y = 34;

    // Data quality notice should be the first section on the report page (right after the Brief page, when attached).
    // This gives it enough space to show the top 4 items reliably.
    if (opts?.coverage === 'scheduled' && !includeBrief) {
      y = renderDataQualityNoticePdf(y, win);
    }

    if (sectionsForPdf.executiveSnapshot) {
      // --- Context panel ---
      const filtParts: string[] = [];
      if (opts?.coverage === 'scheduled') {
        if (schForPdf?.filters?.vehicleIds?.length) filtParts.push(`Vehicles: ${schForPdf.filters.vehicleIds.join(', ')}`);
        filtParts.push(scheduleHideStIves ? 'Hide depot vehicles: on' : 'Hide depot vehicles: off');
      } else {
        if (selectedVehicleIds.length > 0) filtParts.push(`Vehicles: ${selectedVehicleIds.join(', ')}`);
        if (selectedCustomerNames.length > 0) filtParts.push(`Customers: ${selectedCustomerNames.join(', ')}`);
        if (selectedOrderIds.length > 0) filtParts.push(`Orders filtered: ${selectedOrderIds.length} selected`);
        filtParts.push(
          hideStIvesVehicles
            ? 'Depot vehicles: excluded from report'
            : 'Depot vehicles: included in report',
        );
      }
      const filtText = sanitizePdfText(`Filters: ${filtParts.join(' | ')}`);
      const filtLines = doc.splitTextToSize(filtText, innerW);

      const recipientLine =
        opts?.coverage === 'scheduled'
          ? sanitizePdfText(`Prepared for: ${(schForPdf?.recipientName || schForPdf?.recipientEmail || 'Fleet Manager').trim()}`)
          : null;

      const contextLines: string[] = [
        ...(recipientLine ? [recipientLine] : []),
        sanitizePdfText(`Granularity: ${grain} | View: ${profitTab === 'qargo' ? 'Orders' : 'Telematics'}`),
        ...(isDemoMode ? ['Demo mode - figures are illustrative.'] : []),
        ...filtLines,
      ];
      // title row + padding + (n lines * 4mm) + bottom padding
      const contextBoxH = 12 + contextLines.length * 4 + 4;
      doc.setFillColor(248, 250, 252);
      doc.setDrawColor(226, 232, 240);
      doc.rect(margin, y, innerW, contextBoxH, 'FD');
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(51, 65, 85);
      doc.text('Reporting context', margin + 3, y + 6);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(71, 85, 105);
      let iy = y + 11;
      contextLines.forEach((line) => {
        doc.text(line, margin + 3, iy);
        iy += 4;
      });
      doc.setTextColor(0, 0, 0);
      y += contextBoxH + 8;
    }

    if (opts?.coverage !== 'scheduled' || sectionsForPdf.fleetKpis) {
      // --- KPI strip ---
      y = sectionHeading(y, 'Fleet KPIs (daily summary totals)');
      doc.setFillColor(249, 250, 251);
      doc.rect(margin, y, innerW, 30, 'F');
      doc.setDrawColor(226, 232, 240);
      doc.rect(margin, y, innerW, 30, 'S');
      let ky = y + 7;
      const kpiRows: [string, string][] = [
        [
          'Fleet Mileage',
          `${(dailySummaryTotalsForPdf.totalMileage ?? 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })} mi`,
        ],
        ['Revenue', fmtMoneyPdf(kpiTotalsForPdf.totalRevenue)],
        ['Fuel cost', fmtMoneyPdf(kpiTotalsForPdf.totalFuelCost)],
        ['Driver cost', fmtMoneyPdf(kpiTotalsForPdf.totalDriverCost)],
        ['Standing cost', fmtMoneyPdf(kpiTotalsForPdf.totalDepreciation)],
        ['Profit', fmtMoneyPdf(kpiTotalsForPdf.totalProfit)],
      ];
      doc.setFontSize(9);
      kpiRows.forEach(([label, val]) => {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(71, 85, 105);
        doc.text(`${label}:`, margin + 4, ky);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(15, 23, 42);
        doc.text(val, rightX - 4, ky, { align: 'right' });
        ky += 4.5;
        if (label === 'Profit') ky += 2.5;
      });
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      y += 34;
    }

    // --- Delta strip (scheduled coverage only) ---
    if (opts?.coverage === 'scheduled' && prevWin && sectionsForPdf.fleetKpis) {
      const deltaW = innerW;
      const deltaH = 12.5;
      doc.setFillColor(17, 24, 39);
      doc.rect(margin, y, deltaW, deltaH, 'F');
      doc.setDrawColor(55, 65, 81);
      doc.setLineWidth(0.25);
      doc.rect(margin, y, deltaW, deltaH, 'S');

      const fmtSignedMoney = (n: number) => {
        const sign = n > 0 ? '+' : n < 0 ? '-' : '';
        return `${sign}${fmtMoneyPdf(Math.abs(n))}`;
      };
      const fmtSignedInt = (n: number) => {
        const sign = n > 0 ? '+' : n < 0 ? '-' : '';
        return `${sign}${Math.round(Math.abs(n)).toLocaleString('en-GB')}`;
      };

      const curProfit = kpiTotalsForPdf.totalProfit;
      const prevProfit = kpiTotalsPrevForPdf.totalProfit;
      const curRev = kpiTotalsForPdf.totalRevenue;
      const prevRev = kpiTotalsPrevForPdf.totalRevenue;
      const curMiles = dailySummaryTotalsForPdf.totalMileage;
      const prevMiles = kpiTotalsPrevForPdf.totalMileage;
      const curOrders = kpiMetaForPdf.orderCount;
      const prevOrders = kpiTotalsPrevForPdf.orderIds.size;
      const curMargin = curRev > 0 ? (100 * curProfit) / curRev : 0;
      const prevMargin = prevRev > 0 ? (100 * prevProfit) / prevRev : 0;
      const curCost = kpiTotalsForPdf.totalFuelCost + kpiTotalsForPdf.totalDriverCost + kpiTotalsForPdf.totalDepreciation;
      const prevCost =
        kpiTotalsPrevForPdf.totalFuelCost +
        kpiTotalsPrevForPdf.totalDriverCost +
        kpiTotalsPrevForPdf.totalDepreciation;
      const curCostPerMi = curMiles > 0 ? curCost / curMiles : 0;
      const prevCostPerMi = prevMiles > 0 ? prevCost / prevMiles : 0;

      const dProfit = curProfit - prevProfit;
      const dOrders = curOrders - prevOrders;
      const dMiles = curMiles - prevMiles;
      const dMargin = curMargin - prevMargin;
      const dCpm = curCostPerMi - prevCostPerMi;

      const ty = y + 5.2;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(226, 232, 240);
      const prevTitleBase = (() => {
        if (coverageLabel === 'Previous week') return 'Previous week';
        if (coverageLabel === 'Last 7 days') return 'Previous 7 days';
        if (coverageLabel === 'Last 30 days') return 'Previous 30 days';
        if (coverageLabel === 'Month to date') return 'Previous month (to date)';
        if (coverageLabel === 'Previous month') return 'Previous month';
        if (coverageLabel === 'Yesterday') return 'Previous day';
        return prevWin.label || 'Previous period';
      })();
      doc.text(
        `Change vs ${prevTitleBase} (${sanitizePdfText(prevWin.startDate)} to ${sanitizePdfText(prevWin.endDate)})`,
        margin + 3,
        ty,
      );

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      const leftPad = 3;
      const segW = (innerW - leftPad * 2) / 5;
      const segStart = (i: number) => margin + leftPad + segW * i;
      const segEnd = (i: number) => margin + leftPad + segW * (i + 1) - 2;

      const setDeltaColor = (n: number, invert?: boolean) => {
        // invert=true when lower is better (e.g. cost/mi)
        const good = invert ? n < 0 : n > 0;
        const bad = invert ? n > 0 : n < 0;
        if (good) doc.setTextColor(74, 222, 128);
        else if (bad) doc.setTextColor(248, 113, 113);
        else doc.setTextColor(203, 213, 225);
      };

      doc.setTextColor(203, 213, 225);
      doc.text('Profit:', segStart(0), y + 9.2);
      setDeltaColor(dProfit);
      doc.text(fmtSignedMoney(dProfit), segEnd(0), y + 9.2, { align: 'right' });

      doc.setTextColor(203, 213, 225);
      doc.text('Orders:', segStart(1), y + 9.2);
      setDeltaColor(dOrders);
      doc.text(fmtSignedInt(dOrders), segEnd(1), y + 9.2, { align: 'right' });

      doc.setTextColor(203, 213, 225);
      doc.text('Miles:', segStart(2), y + 9.2);
      setDeltaColor(dMiles);
      doc.text(fmtSignedInt(dMiles), segEnd(2), y + 9.2, { align: 'right' });

      doc.setTextColor(203, 213, 225);
      doc.text('Cost/mi:', segStart(3), y + 9.2);
      setDeltaColor(dCpm, true);
      doc.text(`${dCpm >= 0 ? '+' : ''}\u00A3${Math.abs(dCpm).toFixed(2)}`, segEnd(3), y + 9.2, {
        align: 'right',
      });

      doc.setTextColor(203, 213, 225);
      doc.text('Margin:', segStart(4), y + 9.2);
      setDeltaColor(dMargin);
      doc.text(`${dMargin >= 0 ? '+' : ''}${dMargin.toFixed(1)}%`, segEnd(4), y + 9.2, { align: 'right' });

      doc.setTextColor(0, 0, 0);
      y += deltaH + 6;
    }
    y += 4;

    if (opts?.coverage !== 'scheduled' || sectionsForPdf.vehicles) {
      // --- Vehicles ---
      y = sectionHeading(y, 'Vehicles - top and bottom 5 by total profit');
      const bestV = topVehiclesForPdf.slice(0, 5);
      const worstV = topVehiclesForPdf.length ? [...topVehiclesForPdf].slice(-5).reverse() : [];

      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...pdfRankPalette('top').label);
      doc.text('Highest profit', margin + 2, y);
      y += 5;
      if (bestV.length === 0) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...pdfRankPalette('top').rowTxt);
        doc.text('- no vehicle rows in scope', margin + 2, y);
        pdfResetBodyText();
        y += 6;
      } else {
        y = tableHeadVehiclePdf(y, 'top');
        bestV.forEach((p, i) => {
          y = rowVehiclePdf(
            y,
            i + 1,
            p.vehicle,
            p.vehicleType ?? 'Default',
            p.activeDays ?? 0,
            p.orderCount ?? 0,
            p.profit,
            p.miles ?? null,
            'top',
          );
        });
        pdfResetBodyText();
        y += 3;
      }

      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...pdfRankPalette('worst').label);
      doc.text('Lowest profit', margin + 2, y);
      y += 5;
      if (worstV.length === 0) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...pdfRankPalette('worst').rowTxt);
        doc.text('-', margin + 2, y);
        pdfResetBodyText();
        y += 6;
      } else {
        y = tableHeadVehiclePdf(y, 'worst');
        worstV.forEach((p, i) => {
          y = rowVehiclePdf(
            y,
            i + 1,
            p.vehicle,
            p.vehicleType ?? 'Default',
            p.activeDays ?? 0,
            p.orderCount ?? 0,
            p.profit,
            p.miles ?? null,
            'worst',
          );
        });
        pdfResetBodyText();
        y += 3;
      }
    }

    const beginPage2 = () => {
      footer(reportPage1Num);
      doc.addPage();

      // Page 2 header strip (black)
      doc.setFillColor(0, 0, 0);
      doc.rect(0, 0, pageW, 13, 'F');
      doc.setDrawColor(55, 55, 55);
      doc.setLineWidth(0.25);
      doc.line(0, 13, pageW, 13);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      const p2LogoW = logoPng ? 5.2 * (700 / 140) : 0;
      if (logoPng) {
        const sh = 5.2;
        const sw = sh * (700 / 140);
        doc.addImage(logoPng, 'PNG', rightX - sw, 3.8, sw, sh);
      }
      doc.text('Profitability summary (continued)', margin, 9);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(200, 200, 200);
      doc.text(periodLabel, logoPng ? rightX - p2LogoW - 3 : rightX, 9, { align: 'right' });
      doc.setTextColor(0, 0, 0);

      y = 20;
    };

    const bestD = topDatesForPdf.slice(0, 5);
    const worstD = topDatesForPdf.length ? [...topDatesForPdf].slice(-5).reverse() : [];

    let isOnPage2 = false;
    if (opts?.coverage !== 'scheduled' || sectionsForPdf.periods) {
      // If Days/Periods won't fit above the footer, move them to page 2
      const estDaysH = 16 + (bestD.length + worstD.length) * 4.6 + 18;
      if (y + estDaysH > pageH - footerBandH - 6) {
        beginPage2();
        isOnPage2 = true;
      }

      const periodHeading = pdfPeriodType === 'day' ? 'Days' : 'Periods';
      y = sectionHeading(y, `${periodHeading} - top and bottom 5 by total profit`);

      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...pdfRankPalette('top').label);
      doc.text('Highest profit', margin + 2, y);
      y += 5;
      if (bestD.length === 0) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...pdfRankPalette('top').rowTxt);
        doc.text('- no periods in scope', margin + 2, y);
        pdfResetBodyText();
        y += 6;
      } else {
        y = tableHeadDaysPdf(y, 'top');
        bestD.forEach((p, i) => {
          y = rowDaysPdf(
            y,
            i + 1,
            p.date,
            p.activeVehicles ?? 0,
            p.orderCount ?? 0,
            p.profit,
            p.miles ?? null,
            'top',
          );
        });
        pdfResetBodyText();
        y += 3;
      }

      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...pdfRankPalette('worst').label);
      doc.text('Lowest profit', margin + 2, y);
      y += 5;
      if (worstD.length === 0) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...pdfRankPalette('worst').rowTxt);
        doc.text('-', margin + 2, y);
        pdfResetBodyText();
        y += 6;
      } else {
        y = tableHeadDaysPdf(y, 'worst');
        worstD.forEach((p, i) => {
          y = rowDaysPdf(
            y,
            i + 1,
            p.date,
            p.activeVehicles ?? 0,
            p.orderCount ?? 0,
            p.profit,
            p.miles ?? null,
            'worst',
          );
        });
        pdfResetBodyText();
        y += 3;
      }
    }

    if (!isOnPage2) {
      beginPage2();
    }

    const showCustomersPdf =
      (opts?.coverage !== 'scheduled' || sectionsForPdf.customers) &&
      (opts?.coverage === 'scheduled' ? coverageLabel === 'Previous month' : true);
    if (showCustomersPdf) {
      y = sectionHeading(y, 'Customers - top and bottom 5 by total profit');

      const byCustomerPdf = new Map<string, { profit: number; revenue: number; orders: number }>();
      for (const row of custSummaryForPdf) {
        const name = (row.customer ?? '').trim() || '-';
        const prev = byCustomerPdf.get(name) ?? { profit: 0, revenue: 0, orders: 0 };
        prev.profit += row.totalProfit ?? 0;
        prev.revenue += row.totalRevenue ?? 0;
        prev.orders += row.orderCount ?? 0;
        byCustomerPdf.set(name, prev);
      }
      const custSorted = [...byCustomerPdf.entries()].sort((a, b) => b[1].profit - a[1].profit);
      const bestC = custSorted.slice(0, 5);
      const worstC = custSorted.length > 5 ? [...custSorted].slice(-5).reverse() : [];

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(...pdfRankPalette('top').label);
      doc.text('Highest profit', margin + 2, y);
      y += 5;
      if (bestC.length === 0) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...pdfRankPalette('top').rowTxt);
        doc.text('- no customer aggregates (check order / customer profitability source)', margin + 2, y);
        pdfResetBodyText();
        y += 6;
      } else {
        y = tableHeadCustomer(y, 'top');
        bestC.forEach(([name, agg], i) => {
          const marginPct = agg.revenue > 0 ? Math.round((agg.profit / agg.revenue) * 100) : null;
          y = rowCustomer(y, i + 1, name, agg.orders, marginPct, agg.profit, 'top');
        });
        pdfResetBodyText();
        y += 3;
      }

      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...pdfRankPalette('worst').label);
      doc.text('Lowest profit', margin + 2, y);
      y += 5;
      if (worstC.length === 0) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...pdfRankPalette('worst').rowTxt);
        doc.text('-', margin + 2, y);
        pdfResetBodyText();
        y += 6;
      } else {
        y = tableHeadCustomer(y, 'worst');
        worstC.forEach(([name, agg], i) => {
          const marginPct = agg.revenue > 0 ? Math.round((agg.profit / agg.revenue) * 100) : null;
          y = rowCustomer(y, i + 1, name, agg.orders, marginPct, agg.profit, 'worst');
        });
        pdfResetBodyText();
        y += 3;
      }
    }

    if (opts?.coverage !== 'scheduled' || sectionsForPdf.routes) {
      y = sectionHeading(y, 'Routes (origin -> destination) - top and bottom 5 by fully-loaded order profit');

    // Build cost buckets from the daily summary so we can allocate fuel/driver/standing costs
    // onto each Qargo order using (Delivery vehicle, Destination Date).
    const costByVehicleDay = new Map<string, { fuel: number; driver: number; standing: number }>();
    for (const r of summaryForPdf) {
      const d = (r.date || '').slice(0, 10);
      const v = standardiseVehicleId(r.vehicleId || '') || (r.vehicleId || '').trim();
      if (!d || !v) continue;
      costByVehicleDay.set(`${d}|${v}`, {
        fuel: (r.totalFuelCost ?? 0),
        driver: (r.totalDriverCost ?? 0),
        standing: (r.totalDepreciation ?? 0),
      });
    }

    const orderCountByVehicleDay = new Map<string, number>();
    for (const o of qargoForPdf) {
      const destDateStr = o.destination_end_timestamp_local ?? o.destination_timestamp ?? o.date ?? '';
      const destD = toComparableDate(destDateStr) || getDateOnly(destDateStr);
      const d = (destD || '').slice(0, 10);
      const vObsRaw = o.vehicle_observed ?? o.vehicleId ?? o.vehicle_assigned ?? '';
      const v = standardiseVehicleId(vObsRaw) || String(vObsRaw || '').trim();
      if (!d || !v) continue;
      const k = `${d}|${v}`;
      orderCountByVehicleDay.set(k, (orderCountByVehicleDay.get(k) ?? 0) + 1);
    }

    const routeByOrderProfit = qargoForPdf
      .map((o) => {
        const route = formatRoute(o.origin_city, o.destination_city) || '-';
        const oid = (o.orderId ?? '').trim();
        const label = oid ? `${route} (${oid})` : route;

        const destDateStr = o.destination_end_timestamp_local ?? o.destination_timestamp ?? o.date ?? '';
        const destD = toComparableDate(destDateStr) || getDateOnly(destDateStr);
        const d = (destD || '').slice(0, 10);
        const vObsRaw = o.vehicle_observed ?? o.vehicleId ?? o.vehicle_assigned ?? '';
        const v = standardiseVehicleId(vObsRaw) || String(vObsRaw || '').trim();

        // Base profit from Qargo row (revenue - allocated cost). Then subtract allocated fleet costs.
        const baseProfit = o.profit ?? 0;
        const bucket = d && v ? costByVehicleDay.get(`${d}|${v}`) : undefined;
        const denom = d && v ? (orderCountByVehicleDay.get(`${d}|${v}`) ?? 0) : 0;
        const extraCost =
          bucket && denom > 0 ? (bucket.fuel + bucket.driver + bucket.standing) / denom : 0;

        return { label, profit: baseProfit - extraCost };
      })
      .sort((a, b) => b.profit - a.profit);
    const bestR = routeByOrderProfit.slice(0, 5);
    const worstR = routeByOrderProfit.length > 5 ? [...routeByOrderProfit].slice(-5).reverse() : [];
    
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...pdfRankPalette('top').label);
      doc.text('Highest profit', margin + 2, y);
      y += 5;
      if (bestR.length === 0) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...pdfRankPalette('top').rowTxt);
        doc.text('- no orders in scope for route breakdown', margin + 2, y);
        pdfResetBodyText();
        y += 6;
      } else {
        y = tableHeadRoutePdf(y, 'top');
        bestR.forEach((row, i) => {
          y = rowTwoCol(y, i + 1, row.label, row.profit, 'top');
        });
        pdfResetBodyText();
        y += 3;
      }

      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...pdfRankPalette('worst').label);
      doc.text('Lowest profit', margin + 2, y);
      y += 5;
      if (worstR.length === 0) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...pdfRankPalette('worst').rowTxt);
        doc.text('-', margin + 2, y);
        pdfResetBodyText();
        y += 6;
      } else {
        y = tableHeadRoutePdf(y, 'worst');
        worstR.forEach((row, i) => {
          y = rowTwoCol(y, i + 1, row.label, row.profit, 'worst');
        });
        pdfResetBodyText();
        y += 3;
      }
    }

    y += 4;
    const noteStartY = y;
    const noteMaxY = pageH - footerBandH - 4;
    doc.setFillColor(248, 250, 252);
    doc.rect(margin, noteStartY - 4.2, innerW, Math.max(10, noteMaxY - (noteStartY - 4.2)), 'F');
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.35);
    doc.rect(margin, noteStartY - 4.2, innerW, Math.max(10, noteMaxY - (noteStartY - 4.2)));

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(71, 85, 105);
    doc.text('Notes', margin + 3, y);
    y += 5;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(100, 116, 139);
    const note = doc.splitTextToSize(
      sanitizePdfText(
        'Customer and route sections use order rows in the selected period. KPIs and vehicle/period rankings use the same filtered daily summary as the dashboard. This PDF is a fixed two-page snapshot; export CSV from tables for full detail.',
      ),
      innerW - 6,
    );
    for (const line of note) {
      if (y > noteMaxY) break;
      doc.text(line, margin + 3, y);
      y += 4.1;
    }
    doc.setTextColor(0, 0, 0);

    footer(isOnPage2 ? reportPage2Num : reportPage1Num);

    const safePeriod = (win?.startDate && win?.endDate ? `${win.startDate}_${win.endDate}` : 'period').replace(
      /[^\dA-Za-z_-]/g,
      '_',
    );
    const filename = `profitability_summary_${safePeriod}_${new Date().toISOString().split('T')[0]}.pdf`;
    return { blob: doc.output('blob'), filename };
  }, [
    config.profitabilityReportSchedule,
    profitabilitySummaryRowsFullRange,
    selectedPeriodRange,
    effectiveDateWindow,
    periodType,
    profitTab,
    isDemoMode,
    selectedVehicleIds,
    selectedCustomerNames,
    selectedOrderIds,
    dailySummaryTotals,
    kpiTotalsFromDailySummary,
    topPerformersFull,
    topDatesFull,
    customerProfitabilitySummaryFiltered,
    customerProfitabilityQargo,
    filteredSummaryData,
    hideStIvesVehicles,
    vehicleIdToType,
    mondayBriefUi,
    buildDataQualityNoticeForWindow,
    dataQualityWindow,
    variant,
  ]);

  const openProfitabilityPdfPreviewModal = useCallback(
    async (source?: 'schedule' | 'export', scheduleSnapshot?: ProfitabilityReportScheduleConfig) => {
    setPdfPreviewLoading(true);
    setPdfPreviewOpenedFromSchedule(source === 'schedule');
    if (source === 'schedule' && scheduleSnapshot) {
      setPdfScheduleOverride(scheduleSnapshot);
    }
    try {
      const scheduleForBuild =
        source === 'schedule' ? (scheduleSnapshot ?? pdfScheduleOverride ?? undefined) : undefined;
      const out = await buildProfitabilityPdfOutput({
        coverage: source === 'schedule' ? 'scheduled' : 'ui',
        scheduleOverride: scheduleForBuild,
      });
      if (!out) return;
      setPdfPreview((prev) => {
        if (prev?.url) URL.revokeObjectURL(prev.url);
        return { url: URL.createObjectURL(out.blob), filename: out.filename, blob: out.blob };
      });
      setPdfPreviewOpen(true);
    } finally {
      setPdfPreviewLoading(false);
    }
  },
  [buildProfitabilityPdfOutput, pdfScheduleOverride],
);

  const closeProfitabilityPdfPreview = useCallback(() => {
    setPdfPreviewOpen(false);
    setPdfPreview((prev) => {
      if (prev?.url) URL.revokeObjectURL(prev.url);
      return null;
    });
  }, []);

  const schedule = config.profitabilityReportSchedule;
  const [scheduleDraft, setScheduleDraft] = useState<ProfitabilityReportScheduleConfig>(schedule);
  const scheduleDirty = useMemo(() => JSON.stringify(scheduleDraft) !== JSON.stringify(schedule), [scheduleDraft, schedule]);

  useEffect(() => {
    if (!scheduleModalOpen) return;
    setScheduleDraft(schedule);
    setPdfScheduleOverride(schedule);
    setScheduleVehicleSearch('');
    setScheduleBanner(null);
    setSchedulePrimaryEmailError(null);
  }, [scheduleModalOpen]);

  const closeScheduleModal = useCallback(() => {
    setScheduleModalOpen(false);
    setPdfScheduleOverride(null);
  }, []);

  const computeNextSendText = useCallback((sch: ProfitabilityReportScheduleConfig) => {
    const pad2 = (n: number) => String(n).padStart(2, '0');
    const today = new Date();
    const base = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const [hh, mm] = String(sch.sendTimeLocal || '09:00').split(':').map((x) => parseInt(x, 10));
    if (sch.frequency === 'daily') {
      const dt = new Date(base);
      dt.setHours(Number.isFinite(hh) ? hh : 9, Number.isFinite(mm) ? mm : 0, 0, 0);
      const ymd = `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
      return `${ymd} at ${pad2(dt.getHours())}:${pad2(dt.getMinutes())} (${sch.timezone || 'local'})`;
    }
    const wanted = Number(sch.dayOfWeek ?? 1); // 0..6
    const cur = base.getDay();
    let delta = (wanted - cur + 7) % 7;
    // if it's the same day, schedule next week (assume run hasn't happened yet; safer for UX)
    if (delta === 0) delta = 7;
    const dt = new Date(base);
    dt.setDate(dt.getDate() + delta);
    dt.setHours(Number.isFinite(hh) ? hh : 9, Number.isFinite(mm) ? mm : 0, 0, 0);
    const ymd = `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
    return `${ymd} at ${pad2(dt.getHours())}:${pad2(dt.getMinutes())} (${sch.timezone || 'local'})`;
  }, []);

  const saveScheduleDraft = useCallback(async () => {
    const emailErr = schedulePrimaryEmailValidationError(scheduleDraft.recipientEmail);
    if (emailErr) {
      setSchedulePrimaryEmailError(emailErr);
      return;
    }
    const vids = scheduleDraft.filters?.vehicleIds ?? [];
    const pruned =
      vehicleOptions.length > 0 ? pruneScheduleVehicleFilterIds(vids, vehicleOptions) : vids;
    const toSave: ProfitabilityReportScheduleConfig = {
      ...scheduleDraft,
      filters: { ...scheduleDraft.filters, vehicleIds: pruned },
    };
    setProfitabilityReportSchedule(toSave);
    setScheduleDraft(toSave);
    setPdfScheduleOverride(toSave);
    const next = computeNextSendText(toSave);
    let banner = `Schedule saved. Next send: ${next}.`;
    try {
      const res = await authenticatedFetch(API_ENDPOINTS.profitabilityReportSchedule(), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profitabilityReportSchedule: toSave }),
      });
      if (!res.ok) banner += ' Could not sync to server for email delivery.';
    } catch {
      banner += ' Could not sync to server for email delivery.';
    }
    setScheduleBanner(banner);
  }, [computeNextSendText, scheduleDraft, setProfitabilityReportSchedule, vehicleOptions]);

  const downloadProfitabilityPdfFromPreview = useCallback(() => {
    if (!pdfPreview) return;
    const a = document.createElement('a');
    a.href = pdfPreview.url;
    a.download = pdfPreview.filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    if (pdfPreviewOpenedFromSchedule) {
      setScheduleBanner(scheduleDirty ? 'PDF downloaded. Click Save to persist schedule changes.' : 'PDF downloaded.');
    }
  }, [pdfPreview, pdfPreviewOpenedFromSchedule, scheduleDirty]);

  const downloadProfitabilityCsvFromPreview = useCallback(() => {
    const sch: ProfitabilityReportScheduleConfig | null =
      pdfPreviewOpenedFromSchedule ? (pdfScheduleOverride ?? scheduleDraft) : null;
    if (!sch) return;

    const fmtYmd = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${dd}`;
    };

    const dataBounds = getMergedCostDateBounds(mergedCostsDataFiltered);
    const maxDateStr = dataBounds?.max ? dataBounds.max.slice(0, 10) : null;
    const anchor = maxDateStr ? new Date(`${maxDateStr}T12:00:00`) : new Date();
    const today = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());

    const windowForSchedule = (() => {
      if (sch.frequency === 'daily') {
        const d = fmtYmd(today);
        return { startDate: d, endDate: d };
      }
      const coverage = sch.periodCoverage;
      if (coverage === 'last_7_days') {
        const end = new Date(today);
        const start = new Date(end);
        start.setDate(start.getDate() - 6);
        return { startDate: fmtYmd(start), endDate: fmtYmd(end) };
      }
      if (coverage === 'last_30_days') {
        const end = new Date(today);
        const start = new Date(end);
        start.setDate(start.getDate() - 29);
        return { startDate: fmtYmd(start), endDate: fmtYmd(end) };
      }
      if (coverage === 'month_to_date') {
        const start = new Date(today.getFullYear(), today.getMonth(), 1);
        const end = new Date(today);
        return { startDate: fmtYmd(start), endDate: fmtYmd(end) };
      }
      if (coverage === 'previous_month') {
        const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const end = new Date(today.getFullYear(), today.getMonth(), 0);
        return { startDate: fmtYmd(start), endDate: fmtYmd(end) };
      }
      // previous_week default
      const day = today.getDay(); // 0..6
      const diffToMonday = (day + 6) % 7;
      const thisMonday = new Date(today);
      thisMonday.setDate(thisMonday.getDate() - diffToMonday);
      const prevMonday = new Date(thisMonday);
      prevMonday.setDate(prevMonday.getDate() - 7);
      const prevSunday = new Date(thisMonday);
      prevSunday.setDate(prevSunday.getDate() - 1);
      return { startDate: fmtYmd(prevMonday), endDate: fmtYmd(prevSunday) };
    })();

    const inWindow = (d: string | undefined | null) => {
      const key = (d || '').slice(0, 10);
      return key && key >= windowForSchedule.startDate && key <= windowForSchedule.endDate;
    };

    const vehicleFilter = new Set((sch.filters?.vehicleIds ?? []).map((v) => standardiseVehicleId(v) || v).filter(Boolean));
    const hideStIves = Boolean(sch.filters?.hideStIvesVehicles);

    const esc = (v: unknown) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };

    const applyVehicleFilters = <T,>(list: T[], getVehicleId: (row: T) => string): T[] => {
      return list.filter((row) => {
        const vRaw = getVehicleId(row);
        const v = standardiseVehicleId(vRaw) || vRaw;
        if (vehicleFilter.size > 0 && v && !vehicleFilter.has(v)) return false;
        if (hideStIves && stIvesVehicleIds.size > 0) {
          const std = standardiseVehicleId(v) || '';
          if (std && stIvesVehicleIds.has(std)) return false;
        }
        return true;
      });
    };

    const downloadCsv = (filename: string, header: string[], rows: string[][]) => {
      const lines = [header.join(',')];
      for (const row of rows) lines.push(row.map(esc).join(','));
      const csv = lines.join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    };

    const exports = sch.csvExports ?? { vehicleLevel: true, orderLevel: false, customerLevel: false };
    const willDownload = exports.vehicleLevel || exports.orderLevel || exports.customerLevel;
    if (!willDownload) {
      setScheduleBanner('No CSV export selected. Enable at least one CSV export in Schedule settings.');
      return;
    }

    // 1) Vehicle-level (daily summary rows)
    if (exports.vehicleLevel) {
      const baseRows = profitabilitySummaryRowsFullRange
        .filter((r) => inWindow(r.date));
      const rows = applyVehicleFilters(baseRows, (r) => r.vehicleId ?? '');
      downloadCsv(
        `profitability_vehicle_level_${windowForSchedule.startDate}_${windowForSchedule.endDate}.csv`,
        ['date', 'vehicleId', 'periodLabel', 'pickups', 'deliveries', 'miles', 'revenue', 'fuelCost', 'driverCost', 'standingCost', 'profit', 'currency'],
        rows.map((r) => ([
          (r.date || '').slice(0, 10),
          r.vehicleId,
          r.periodLabel ?? '',
          String(r.pickupCount ?? 0),
          String(r.deliveryCount ?? r.orderCount ?? (r.orderIds?.length ?? 0)),
          (r.totalMileage ?? 0).toFixed(2),
          (r.totalRevenue ?? 0).toFixed(2),
          (r.totalFuelCost ?? 0).toFixed(2),
          (r.totalDriverCost ?? 0).toFixed(2),
          (r.totalDepreciation ?? 0).toFixed(2),
          (r.totalProfit ?? 0).toFixed(2),
          r.currency ?? 'GBP',
        ] as string[])),
      );
    }

    // 2) Order-level (Qargo confirmed rows in scheduled window)
    if (exports.orderLevel) {
      const qargoBase = filteredRecordsFullRange.filter((r) => {
        const status = (r.status || '').toUpperCase();
        return CONFIRMED_STATUSES.has(status);
      });
      const qargoRows0 = qargoBase.filter((o) => {
        const dest = toComparableDate(o.destination_end_timestamp_local ?? o.destination_timestamp ?? o.date ?? '') || '';
        return inWindow(dest);
      });
      const qargoRows = applyVehicleFilters(qargoRows0, (r) => String(r.vehicle_observed ?? r.vehicleId ?? r.vehicle_assigned ?? ''));

      downloadCsv(
        `profitability_order_level_${windowForSchedule.startDate}_${windowForSchedule.endDate}.csv`,
        ['orderId', 'deliveryDate', 'vehicleObserved', 'vehiclePickup', 'originCity', 'destinationCity', 'revenue', 'cost', 'profit', 'currency', 'serviceLevel'],
        qargoRows.map((o) => {
          const destStr = o.destination_end_timestamp_local ?? o.destination_timestamp ?? o.date ?? '';
          const dest = (toComparableDate(destStr) || getDateOnly(destStr) || '').slice(0, 10);
          return [
            o.orderId ?? '',
            dest,
            o.vehicle_observed ?? '',
            o.vehicle_pickup ?? '',
            o.origin_city ?? '',
            o.destination_city ?? '',
            String((o.revenue ?? 0).toFixed(2)),
            String((o.cost ?? 0).toFixed(2)),
            String((o.profit ?? 0).toFixed(2)),
            o.currency ?? 'GBP',
            o.service_level_name ?? '',
          ];
        }),
      );
    }

    // 3) Customer-level (customer profitability summary rows in scheduled window)
    if (exports.customerLevel) {
      const custRows0 = customerProfitabilitySummaryFiltered.filter((r) => {
        const key = String(r.deliveryDate || '').trim();
        if (/^\d{4}-\d{2}$/.test(key)) {
          // monthly row: include if overlaps scheduled window
          const monthStart = `${key}-01`;
          const dt = new Date(monthStart);
          if (Number.isNaN(dt.getTime())) return false;
          const monthEnd = new Date(dt.getFullYear(), dt.getMonth() + 1, 0).toISOString().slice(0, 10);
          return monthStart <= windowForSchedule.endDate && monthEnd >= windowForSchedule.startDate;
        }
        return inWindow(key);
      });

      downloadCsv(
        `profitability_customer_level_${windowForSchedule.startDate}_${windowForSchedule.endDate}.csv`,
        ['deliveryDate', 'customer', 'orders', 'revenue', 'allocatedCost', 'allocatedMiles', 'profit', 'marginPct', 'currency'],
        custRows0.map((r) => {
          const rev = r.totalRevenue ?? 0;
          const profit = r.totalProfit ?? 0;
          const margin = rev > 0 ? (100 * profit) / rev : 0;
          return [
            r.deliveryDate ?? '',
            r.customer ?? '',
            String(r.orderCount ?? 0),
            String(rev.toFixed(2)),
            String((r.totalAllocatedCost ?? 0).toFixed(2)),
            String((r.totalAllocatedMiles ?? 0).toFixed(2)),
            String(profit.toFixed(2)),
            String(margin.toFixed(1)),
            r.currency ?? 'GBP',
          ];
        }),
      );
    }

    if (pdfPreviewOpenedFromSchedule) {
      setScheduleBanner(scheduleDirty ? 'CSV downloaded. Click Save to persist schedule changes.' : 'CSV downloaded.');
    }
  }, [
    mergedCostsDataFiltered,
    customerProfitabilitySummaryFiltered,
    filteredRecordsFullRange,
    pdfPreviewOpenedFromSchedule,
    pdfScheduleOverride,
    profitabilitySummaryRowsFullRange,
    scheduleBanner,
    scheduleDirty,
    scheduleDraft,
    stIvesVehicleIds,
  ]);

  useEffect(() => {
    const handler = () => {
      void openProfitabilityPdfPreviewModal();
    };
    window.addEventListener('zecure-open-pdf-preview', handler);
    return () => window.removeEventListener('zecure-open-pdf-preview', handler);
  }, [openProfitabilityPdfPreviewModal]);

  // Get the count of records for the current view (confirmed, upcoming, or fuel)


  // Pagination logic for Daily Profitability (Qargo tab: origin or destination date in selected period)

  const hasMapDataForRow = useCallback((vehicleId: string, date: string): boolean => {
    const stdVid = standardiseVehicleId(vehicleId);
    const dateKey = normalizeTelematicsDate(date) || getDateOnly(date);
    if (!stdVid || !dateKey) return false;
    
    // Check if there are stops with valid coordinates for this vehicle/date
    const hasStops = telematicsDeliveryStops.some(stop => {
      const stopVid = standardiseVehicleId(stop.AssetName);
      const stopDate = normalizeTelematicsDate(stop.day_date);
      return stopVid === stdVid && stopDate === dateKey &&
        stop.latitude && stop.longitude && stop.latitude !== 0 && stop.longitude !== 0;
    });
    
    // Also check if there are route segments with GPS points for this vehicle/date
    const hasRouteSegments = routeSegments.some(seg => {
      const segVid = standardiseVehicleId(seg.vehicle);
      const segDate = normalizeTelematicsDate(seg.date) || getDateOnly(seg.date);
      return segVid === stdVid && segDate === dateKey &&
        seg.points && Array.isArray(seg.points) && seg.points.length > 0;
    });
    
    return hasStops || hasRouteSegments;
  }, [telematicsDeliveryStops, routeSegments]);

  // Filter periodOptions to only show dates with map data when vehicles are selected
  const filteredPeriodOptionsForMap = useMemo(() => {
    if (selectedVehicleIds.length === 0) {
      return periodOptions; // Show all dates if no vehicles selected
    }
    
    return periodOptions.filter(option => {
      // Check if any selected vehicle has map data for this period
      return selectedVehicleIds.some(vehicleId => {
        if (periodType === 'day') {
          return hasMapDataForRow(vehicleId, option.startDate);
        } else {
          // For week/month, check if any date in the range has map data
          const start = new Date(option.startDate + 'T12:00:00');
          const end = new Date(option.endDate + 'T12:00:00');
          const d = new Date(start);
          while (d <= end) {
            const dateStr = d.toISOString().slice(0, 10);
            if (hasMapDataForRow(vehicleId, dateStr)) {
              return true;
            }
            d.setDate(d.getDate() + 1);
          }
          return false;
        }
      });
    });
  }, [periodOptions, selectedVehicleIds, periodType, hasMapDataForRow]);

  // Calculate total pages for Daily Profitability based on current tab's data

  const qargoOrderTotalPages = useMemo(() => {
    return Math.max(1, Math.ceil(customerProfitabilityQargo.length / rowsPerPage));
  }, [customerProfitabilityQargo, rowsPerPage]);

  // Order table page slice (all statuses in customerProfitabilityQargo)
  const paginatedQargo = useMemo(() => {
    const startIdx = (page - 1) * rowsPerPage;
    return customerProfitabilityQargo.slice(startIdx, startIdx + rowsPerPage);
  }, [customerProfitabilityQargo, page, rowsPerPage]);

  /** Totals for Qargo's Order table footer: sum Revenue across visible qargo rows */
  const qargoOrderTotals = useMemo(() => {
    const qargo = customerProfitabilityQargo;
    let totalRevenue = 0;
    let totalCost = 0;
    let totalProfit = 0;
    let hasAllocatedCost = false;
    const currency = qargo[0]?.currency || 'GBP';
    for (const row of qargo) {
      totalRevenue += row.revenue ?? 0;
      if ((row as ProfitRecord).allocation_method != null) {
        hasAllocatedCost = true;
        totalCost += row.cost ?? 0;
        totalProfit += row.profit ?? 0;
      }
    }
    return { totalRevenue, totalCost, totalProfit, hasAllocatedCost, currency };
  }, [customerProfitabilityQargo]);

  const qargoOrderMarginPct = useMemo(() => {
    if (!qargoOrderTotals.hasAllocatedCost || qargoOrderTotals.totalRevenue <= 0) return null;
    return (qargoOrderTotals.totalProfit / qargoOrderTotals.totalRevenue) * 100;
  }, [qargoOrderTotals]);

  /** When filtering by customer(s), KPI Total Costs = sum of Customer Profitability allocated costs (fair when one vehicle-day serves multiple customers). */
  const customerFilteredAllocatedCostTotal = useMemo(() => {
    if (selectedCustomerNames.length === 0) return 0;
    return customerProfitabilitySummaryFiltered.reduce(
      (sum, r) => sum + (Number(r.totalAllocatedCost) || 0),
      0
    );
  }, [selectedCustomerNames.length, customerProfitabilitySummaryFiltered]);

  // KPI reconciliation values: keep Net Profit / Margin consistent with displayed Revenue + Total Costs cards.
  const kpiTotalCosts = useMemo(() => {
    if (selectedCustomerNames.length > 0) {
      return customerFilteredAllocatedCostTotal;
    }
    return (
      kpiTotalsFromDailySummary.totalFuelCost +
      kpiTotalsFromDailySummary.totalDriverCost +
      (kpiTotalsFromDailySummary.totalDepreciation ?? 0)
    );
  }, [
    selectedCustomerNames.length,
    customerFilteredAllocatedCostTotal,
    kpiTotalsFromDailySummary.totalFuelCost,
    kpiTotalsFromDailySummary.totalDriverCost,
    kpiTotalsFromDailySummary.totalDepreciation,
  ]);

  /** Revenue for KPI row: merged_costs total (Daily Vehicle Summary) unless filtering by customer (then customer table revenue). */
  const kpiRevenueDisplayed = useMemo(() => {
    if (selectedCustomerNames.length > 0) {
      return customerProfitabilityTotals.totalRevenue;
    }
    return kpiTotalsFromDailySummary.totalRevenue;
  }, [
    selectedCustomerNames.length,
    customerProfitabilityTotals.totalRevenue,
    kpiTotalsFromDailySummary.totalRevenue,
  ]);

  const kpiDisplayCurrency = useMemo(
    () =>
      (selectedCustomerNames.length > 0
        ? customerProfitabilityTotals.currency
        : kpiTotalsFromDailySummary.currency) ||
      qargoOrderTotals.currency ||
      'GBP',
    [
      selectedCustomerNames.length,
      customerProfitabilityTotals.currency,
      kpiTotalsFromDailySummary.currency,
      qargoOrderTotals.currency,
    ],
  );

  const kpiNetProfitDisplayed = useMemo(
    () => kpiRevenueDisplayed - kpiTotalCosts,
    [kpiRevenueDisplayed, kpiTotalCosts],
  );


  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      // Default: descending for profit, revenue, cost; ascending for others
      setSortAsc(['profit', 'revenue', 'marginPct', 'cost', 'orderPlusDriver'].includes(field) ? false : true);
    }
  };


  const handleOrderVehicleOverride = (orderId: string, vehicleIds: string[]) => {
    setOrderVehicleOverrides(prev => ({ ...prev, [orderId]: vehicleIds }));
    setJourneySplitModal(null);
    setEditingCell(null);
  };


  return (
    <div className={`w-full min-w-0${variant === 'profit-agent' ? ' zoe-fluid-scale' : ''}`}>
      <style>{`
        /* Hide calendar scrollbar but keep scroll (prevents hover-triggered scrollbar flashes) */
        .calendar-scroll {
          scrollbar-width: none;      /* Firefox */
          -ms-overflow-style: none;   /* IE/Edge legacy */
          scrollbar-gutter: stable;   /* Prevent layout shifts */
        }
        .calendar-scroll::-webkit-scrollbar { display: none; } /* Chrome/Safari */
      `}</style>

      {/* Journey Split editor modal */}
      {journeySplitModal && (
        <div className="fixed inset-0 z-dropdown flex items-center justify-center bg-black/60" onClick={() => setJourneySplitModal(null)}>
          <div className="bg-popover border border-border rounded-lg shadow-xl max-w-md w-full mx-4 max-h-screen-80 flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-border font-semibold text-foreground">
              Edit Journey Split <br/>
              <span className="text-sm text-muted-foreground">Order #{journeySplitModal.orderId}</span>
            </div>
            <div className="px-4 py-3 text-xs text-muted-foreground">
              Select the vehicles for this order. Revenue and cost will be split equally.
            </div>
            <div className="px-4 py-2 overflow-y-auto flex-1">
              <JourneySplitModalContent
                orderId={journeySplitModal.orderId}
                currentVehicleIds={journeySplitModal.currentVehicleIds}
                vehicleOptions={vehicleOptions}
                onSave={handleOrderVehicleOverride}
                onCancel={() => setJourneySplitModal(null)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Map Modal for Daily Summary row */}
      {mapModalRow && (
        <ProfitVehicleDayMapModal
          key={`${mapModalRow.vehicleId}-${mapModalRow.date}-${mapModalOrder?.orderId || 'vehicle'}`}
          row={mapModalRow}
          order={mapModalOrder}
          stops={telematicsDeliveryStops}
          segments={routeSegments}
          orgDepots={orgDepotsForMap}
          orders={records}
          mergedCosts={mergedCostsDataFiltered}
          config={config.profitability}
          vehicleIdToType={vehicleIdToType}
          vehicleIdToTonnage={vehicleIdToTonnage}
          telematicsMileageMap={telematicsMileageMap}
          onClose={() => {
            setMapModalRow(null);
            setMapModalOrder(null);
            setCopiedOrderId(null);
          }}
        />
      )}

      {/* Orders / revenue modal (Daily Vehicle Summary: right-click Orders count or Revenue cell) */}
      {revenueOrdersModal && (() => {
        const isOrdersMode = revenueOrdersModal.mode === 'orders';
        const rows = revenueOrdersModal.orderIds.map((oid, i) => ({
          oid,
          displayId: formatOrderIdForOrdersTooltip(
            oid,
            revenueOrdersModal.orderSameDayFlags?.[i] ?? false,
          ),
          revRaw: revenueOrdersModal.orderRevenues[i] ?? '',
          revNum: (() => {
            const rev = revenueOrdersModal.orderRevenues[i];
            if (rev === undefined || rev === '') return Number.NaN;
            const n = Number(rev);
            return Number.isFinite(n) ? n : Number.NaN;
          })(),
          imp: revenueOrdersModal.orderRevenueImputed?.[i],
        }));
        const sorted = [...rows].sort((a, b) => {
          const dir = revenueOrdersModalSort.asc ? 1 : -1;
          if (revenueOrdersModalSort.field === 'revenue' && !isOrdersMode) {
            const aN = Number.isFinite(a.revNum) ? a.revNum : -Infinity;
            const bN = Number.isFinite(b.revNum) ? b.revNum : -Infinity;
            if (aN !== bN) return (aN - bN) * dir;
          }
          return a.displayId.localeCompare(b.displayId, undefined, { numeric: true, sensitivity: 'base' }) * dir;
        });
        const toggleSort = (field: 'trackingId' | 'revenue') => {
          setRevenueOrdersModalSort((prev) =>
            prev.field === field ? { field, asc: !prev.asc } : { field, asc: true },
          );
        };
        const sortMark = (field: 'trackingId' | 'revenue') =>
          revenueOrdersModalSort.field === field ? (revenueOrdersModalSort.asc ? ' ↑' : ' ↓') : '';

        return (
        <div className="fixed inset-0 z-dropdown flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => { setRevenueOrdersModal(null); setCopiedOrderId(null); setRevenueOrdersModalSort({ field: 'trackingId', asc: true }); }}>
          <div className="bg-popover rounded-lg border border-border shadow-2xl max-w-lg w-full max-h-screen-85 overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div>
                <h3 className="text-lg font-bold text-chart-5">
                  {isOrdersMode ? 'Orders' : 'Revenue Calculation'}
                </h3>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {revenueOrdersModal.periodLabel ?? revenueOrdersModal.date ?? 'Orders'}
                  {revenueOrdersModal.vehicleId ? ` · ${revenueOrdersModal.vehicleId}` : ''}
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => { setRevenueOrdersModal(null); setCopiedOrderId(null); setRevenueOrdersModalSort({ field: 'trackingId', asc: true }); }}
                className="hover:bg-card"
                aria-label="Close"
              >
                <X className="w-5 h-5 text-muted-foreground" aria-hidden />
              </Button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              <div className="text-muted-foreground text-sm mb-2">Total orders: <span className="text-white font-medium">{revenueOrdersModal.orderIds.length}</span></div>
              {revenueOrdersModal.orderIds.length === 0 && (
                <p className="text-muted-foreground text-sm mb-3">
                  {isOrdersMode
                    ? 'No order IDs for this row.'
                    : 'No per-order breakdown for this row; total below matches the cell.'}
                </p>
              )}
              {revenueOrdersModal.orderIds.length > 0 && (
                <div
                  className={`flex items-center gap-2 px-2 pb-2 mb-1 border-b border-border text-xs font-semibold uppercase tracking-wide text-muted-foreground ${
                    isOrdersMode ? '' : 'justify-between'
                  }`}
                >
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleSort('trackingId')}
                    className="h-auto px-0 text-left hover:text-chart-5"
                  >
                    Tracking ID{sortMark('trackingId')}
                  </Button>
                  {!isOrdersMode && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleSort('revenue')}
                      className="h-auto px-0 text-right hover:text-chart-5 shrink-0"
                    >
                      Revenue{sortMark('revenue')}
                    </Button>
                  )}
                </div>
              )}
              <ul className="space-y-1.5 text-sm">
                {sorted.map((row, i) => {
                  const justCopied = copiedOrderId === row.oid;
                  if (isOrdersMode) {
                    return (
                      <li key={`${row.oid}-${i}`} className="flex justify-between items-center font-mono py-1 px-2 rounded bg-card/50">
                        <div className="flex items-center gap-2 min-w-0">
                          <Button
                            type="button"
                            variant="ghost"
                            onClick={() => {
                              navigator.clipboard.writeText(row.oid).then(() => {
                                setCopiedOrderId(row.oid);
                                setTimeout(() => setCopiedOrderId(null), 1500);
                              }).catch(() => {});
                            }}
                            className="h-auto justify-start truncate px-0 text-left text-foreground hover:text-chart-5 hover:underline"
                            title={`Click to copy: ${row.oid}`}
                          >
                            <OrderIdWithSameDayLabel orderId={row.oid} />
                          </Button>
                          {justCopied && <span className="text-primary text-xs shrink-0">Copied!</span>}
                        </div>
                      </li>
                    );
                  }
                  const revStr = Number.isFinite(row.revNum)
                    ? row.revNum.toLocaleString(undefined, { style: 'currency', currency: revenueOrdersModal.currency, minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    : '—';
                  const modelledLabel = row.imp === '15' ? ' (Modelled £15)' : row.imp === '25' ? ' (Modelled £25)' : '';
                  return (
                    <li key={`${row.oid}-${i}`} className="flex justify-between items-center font-mono py-1 px-2 rounded bg-card/50">
                      <div className="flex items-center gap-2 min-w-0">
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => {
                            navigator.clipboard.writeText(row.oid).then(() => {
                              setCopiedOrderId(row.oid);
                              setTimeout(() => setCopiedOrderId(null), 1500);
                            }).catch(() => {});
                          }}
                          className="h-auto justify-start truncate px-0 text-left text-foreground hover:text-chart-5 hover:underline"
                          title={`Click to copy: ${row.oid}`}
                        >
                          <OrderIdWithSameDayLabel orderId={row.oid} />
                        </Button>
                        {justCopied && <span className="text-primary text-xs shrink-0">Copied!</span>}
                      </div>
                      <span className="text-chart-5 shrink-0">{revStr}{modelledLabel && <span className="text-warning text-xs ml-1" title="Revenue was missing; estimated from service level">{modelledLabel}</span>}</span>
                    </li>
                  );
                })}
              </ul>
              {!isOrdersMode && (
                <div className="mt-4 pt-3 border-t border-border font-semibold text-chart-5 text-center">
                  Total: {revenueOrdersModal.totalRevenue.toLocaleString(undefined, { style: 'currency', currency: revenueOrdersModal.currency, minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              )}
            </div>
          </div>
        </div>
        );
      })()}

      <header className="mb-6">
        <div
          className={
            variant === 'profit-agent'
              ? 'flex flex-col gap-4'
              : 'flex flex-col gap-2'
          }
        >
          <div className={variant === 'profit-agent' ? 'w-full min-w-0' : undefined}>
            <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
              <PoundSterling className="w-8 h-8 text-primary" />
              {variant === 'profit-agent' ? 'Profit Agent' : 'Profitability Report'}
            </h2>
            <p className="text-sm text-muted-foreground max-w-3xl">
              {variant === 'profit-agent'
                ? "Understand what's driving your margins this week and model pricing decisions before you commit."
                : useTruth
                  ? 'Revenue from attributed orders. Fuel and driver costs from telematics mileage and declared rates.'
                  : 'Revenue from orders. Fuel and driver costs from telematics mileage.'}
            </p>
            {useTruth && truthTelematicsMapHint ? (
              <p className="mt-2 text-xs text-warning/90 max-w-3xl" role="status">
                {truthTelematicsMapHint}
              </p>
            ) : null}
            {variant === 'profit-agent' ? (
              <>
                <div className="mt-5 max-w-4xl space-y-3">
                  <QueryBar
                    onSubmit={handleProfitAgentSubmit}
                    isLoading={profitAgentPageState === 'loading'}
                    zoeChipIntroOnboarded
                    placeholder={profitAgentTypedPlaceholder}
                    primarySuggestedPrompts={PROFIT_PRIMARY_SUGGESTED_PROMPTS}
                    moreSuggestedPrompts={PROFIT_MORE_SUGGESTED_PROMPTS}
                    highlightPattern={PROFIT_HIGHLIGHTED_CHIP_WORDS_RE}
                    submitLabel="Investigate"
                    value={profitAgentQuestion}
                    onValueChange={setProfitAgentQuestion}
                  />
                  {profitAgentPageState === 'idle' && <EmptyState />}

                  {profitAgentPageState === 'loading' && <ProfitAnswerSkeleton />}

                  {profitAgentPageState === 'error' && (
                    <ErrorState
                      message={profitAgentError}
                      onRetry={handleProfitAgentRetry}
                      isLoading={false}
                    />
                  )}

                  {profitAgentPageState === 'success' && profitAgentResult ? (
                    <div className="space-y-3">
                      <ProfitAnswerCard
                        result={profitAgentResult}
                        activeDrilldownId={
                          profitAgentActiveDrilldown
                            ? `${profitAgentActiveDrilldown.type}|${profitAgentActiveDrilldown.id}`
                            : null
                        }
                        onDrilldown={applyProfitAgentDrilldown}
                        onStreamingComplete={() => setProfitAgentStreamingComplete(true)}
                      />
                      {profitAgentStreamingComplete && profitAgentActiveDrilldown ? (
                        <ProfitAgentEvidenceStrip
                          drilldown={profitAgentActiveDrilldown}
                          rows={profitAgentEvidenceRows}
                          onOpenFullReport={() => openFullProfitabilityReport(profitAgentActiveDrilldown)}
                        />
                      ) : null}
                    </div>
                  ) : null}
                </div>
                <div className="mt-4 pt-4 border-t border-border flex flex-col gap-3 w-full">
                  <div className="flex flex-wrap items-center justify-end gap-2 w-full">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          void openProfitabilityPdfPreviewModal('export');
                        }}
                        disabled={loading || availableMonths.length === 0}
                        title={availableMonths.length === 0 ? 'Select data range when months are available' : undefined}
                        className="border-primary/80 bg-primary/10 text-primary hover:bg-primary/20 hover:border-primary"
                        loading={pdfPreviewLoading}
                      >
                        <FileDown className="w-4 h-4 shrink-0" aria-hidden />
                        Export PDF report
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setPeriodType(
                            schedule.reportGranularity === 'daily'
                              ? 'day'
                              : schedule.reportGranularity === 'weekly'
                                ? 'week'
                                : 'month',
                          );
                          setScheduleBanner(null);
                          setScheduleDraft(schedule);
                          setPdfScheduleOverride(schedule);
                          setScheduleModalOpen(true);
                        }}
                        disabled={loading}
                        className="border-border/80 bg-card/80 hover:bg-secondary"
                      >
                        <FileClock className="w-4 h-4 shrink-0" aria-hidden />
                        Schedule report
                      </Button>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-3 w-full">
                    <div className="inline-flex items-center rounded-xl border border-border bg-background/40 p-1 shrink-0">
                      <Button
                        type="button"
                        variant={profitViewMode === 'brief' ? 'default' : 'secondary'}
                        onClick={() => setProfitViewMode('brief')}
                        className={`group rounded-lg font-semibold ${
                          profitViewMode === 'brief'
                            ? 'ring-1 ring-primary/35'
                            : 'text-foreground hover:bg-card border border-transparent'
                        }`}
                        aria-pressed={profitViewMode === 'brief'}
                      >
                        Brief
                        <Sparkles
                          className={`w-4 h-4 transition-transform duration-200 ${
                            profitViewMode === 'brief'
                              ? 'text-white'
                              : 'text-muted-foreground group-hover:text-primary'
                          } group-hover:scale-110 group-hover:animate-pulse`}
                          aria-hidden
                        />
                      </Button>
                      <Button
                        type="button"
                        variant={profitViewMode === 'data' ? 'default' : 'secondary'}
                        onClick={() => setProfitViewMode('data')}
                        className={`ml-1 group rounded-lg font-semibold ${
                          profitViewMode === 'data'
                            ? 'ring-1 ring-primary/35'
                            : 'text-foreground hover:bg-card border border-transparent'
                        }`}
                        aria-pressed={profitViewMode === 'data'}
                      >
                        Dynamic Pricing
                        <ChartNoAxesCombined
                          className={`w-4 h-4 shrink-0 transition-transform duration-200 ${
                            profitViewMode === 'data'
                              ? 'text-white'
                              : 'text-muted-foreground group-hover:text-chart-10'
                          } group-hover:scale-110 group-hover:animate-pulse`}
                          aria-hidden
                        />
                      </Button>
                    </div>
                    {profitMonthRecords.length > 0 ? (
                      <MonthPicker
                        months={profitMonthRecords}
                        selected={selectedProfitMonth}
                        onSelect={selectProfitMonth}
                        formatLabel={formatProfitMonthLabel}
                      />
                    ) : null}
                  </div>
                </div>
              </>
            ) : null}
            {variant !== 'profit-agent' ? (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-xs text-muted-foreground min-w-0">
                <span className="font-medium text-foreground">Data sources:</span>
                <span className="px-2 py-1 rounded text-white bg-chart-5 border border-chart-5">
                  Revenue
                </span>
                <span className="text-muted-foreground" aria-hidden></span>
                <span className="px-2 py-1 rounded text-white bg-chart-2 border border-chart-2">
                  Fuel cost
                </span>
                <span className="text-muted-foreground" aria-hidden></span>
                <span className="px-2 py-1 rounded text-white bg-chart-7 border border-chart-7">
                  Driver cost
                </span>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2 shrink-0">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    void openProfitabilityPdfPreviewModal('export');
                  }}
                  disabled={loading || availableMonths.length === 0}
                  title={availableMonths.length === 0 ? 'Select data range when months are available' : undefined}
                  className="border-primary/80 bg-primary/10 text-primary hover:bg-primary/20 hover:border-primary"
                  loading={pdfPreviewLoading}
                >
                  <FileDown className="w-4 h-4 shrink-0" aria-hidden />
                  Export PDF report
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setPeriodType(
                      schedule.reportGranularity === 'daily'
                        ? 'day'
                        : schedule.reportGranularity === 'weekly'
                          ? 'week'
                          : 'month',
                    );
                    setScheduleBanner(null);
                    setScheduleDraft(schedule);
                    setPdfScheduleOverride(schedule);
                    setScheduleModalOpen(true);
                  }}
                  disabled={loading}
                  className="border-border/80 bg-card/80 hover:bg-secondary"
                >
                  <FileClock className="w-4 h-4 shrink-0" aria-hidden />
                  Schedule report
                </Button>
              </div>
            </div>
            ) : null}

            {variant !== 'profit-agent' ? (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <div className="inline-flex items-center rounded-xl border border-border bg-background/40 p-1 shrink-0">
                <Button
                  type="button"
                  variant={profitViewMode === 'brief' ? 'default' : 'secondary'}
                  onClick={() => setProfitViewMode('brief')}
                  className={`group rounded-lg font-semibold ${
                    profitViewMode === 'brief'
                      ? 'ring-1 ring-primary/35'
                      : 'text-foreground hover:bg-card border border-transparent'
                  }`}
                  aria-pressed={profitViewMode === 'brief'}
                >
                  Brief
                  <Sparkles
                    className={`w-4 h-4 transition-transform duration-200 ${
                      profitViewMode === 'brief'
                        ? 'text-white'
                        : 'text-muted-foreground group-hover:text-primary'
                    } group-hover:scale-110 group-hover:animate-pulse`}
                    aria-hidden
                  />
                </Button>
                <Button
                  type="button"
                  variant={profitViewMode === 'data' ? 'default' : 'secondary'}
                  onClick={() => setProfitViewMode('data')}
                  className={`ml-1 rounded-lg font-semibold ${
                    profitViewMode === 'data'
                      ? 'ring-1 ring-primary/35'
                      : 'text-foreground hover:bg-card border border-transparent'
                  }`}
                  aria-pressed={profitViewMode === 'data'}
                >
                  Data
                </Button>
              </div>
              {profitMonthRecords.length > 0 ? (
                <MonthPicker
                  months={profitMonthRecords}
                  selected={selectedProfitMonth}
                  onSelect={selectProfitMonth}
                  formatLabel={formatProfitMonthLabel}
                />
              ) : null}
            </div>
            ) : null}
          </div>
        </div>
      </header>

      {pdfPreviewOpen && pdfPreview && (
        <div
          className="fixed inset-0 z-sticky flex items-center justify-center p-2 sm:p-3 bg-black/70"
          role="dialog"
          aria-modal="true"
          aria-labelledby="profit-pdf-preview-title"
        >
          <div className="bg-popover border border-border rounded-xl shadow-2xl w-full max-w-modal h-modal flex flex-col overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border shrink-0">
              <h2 id="profit-pdf-preview-title" className="text-lg font-semibold text-white truncate pr-2">
                PDF preview — {pdfPreview.filename}
              </h2>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={closeProfitabilityPdfPreview}
                className="rounded-lg text-muted-foreground hover:text-foreground hover:bg-card"
                aria-label="Close preview"
              >
                <X className="w-5 h-5" aria-hidden />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground px-4 py-2 border-b border-border shrink-0">
              Check the layout and figures. Download the report when you are satisfied with this snapshot.
            </p>
            <iframe
              title="Profitability PDF preview"
              src={`${pdfPreview.url}#toolbar=0`}
              className="w-full flex-1 min-h-0 bg-card border-0"
            />
            {pdfPreviewOpenedFromSchedule && scheduleBanner ? (
              <div
                className="relative shrink-0 border-t border-primary/90 bg-primary/20 text-primary shadow-highlight-inset"
                role="status"
                aria-live="polite"
              >
                <div className="flex flex-wrap items-center justify-center gap-2 px-12 py-3 text-sm font-medium text-center">
                  <ClockCheck className="w-5 h-5 shrink-0 text-primary" strokeWidth={2.25} aria-hidden />
                  <span>{scheduleBanner}</span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="iconSm"
                  onClick={() => setScheduleBanner(null)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-primary hover:bg-primary/40"
                  aria-label="Dismiss message"
                >
                  <CircleX className="w-5 h-5" strokeWidth={2.25} aria-hidden />
                </Button>
              </div>
            ) : null}
            <div className="flex flex-wrap items-center justify-end gap-2 px-4 py-3 border-t border-border bg-popover/90 shrink-0">
              <Button
                type="button"
                variant="secondary"
                onClick={closeProfitabilityPdfPreview}
              >
                {pdfPreviewOpenedFromSchedule ? 'Back to schedule' : 'Close'}
              </Button>
              {pdfPreviewOpenedFromSchedule && (pdfScheduleOverride ?? scheduleDraft).attachCsv && (
                <Button
                  type="button"
                  variant="warning"
                  size="lg"
                  onClick={downloadProfitabilityCsvFromPreview}
                  className="rounded-xl border-0 px-5 py-2.5 font-extrabold text-white shadow-amber-soft transition-all duration-200 bg-gradient-to-br from-warning to-warning/80 hover:from-warning hover:to-warning/90 hover:shadow-amber-strong"
                >
                  <Sheet className="w-4 h-4 shrink-0" strokeWidth={2.25} aria-hidden />
                  Download CSV
                </Button>
              )}
              <Button
                type="button"
                onClick={() => {
                  downloadProfitabilityPdfFromPreview();
                }}
              >
                <FileDown className="w-4 h-4" aria-hidden />
                Download PDF
              </Button>
            </div>
          </div>
        </div>
      )}

      {scheduleModalOpen && (
        <div
          className="fixed inset-0 z-sticky flex items-center justify-center p-2 sm:p-3 bg-black/70"
          role="dialog"
          aria-modal="true"
          aria-labelledby="profit-schedule-title"
        >
          <div className="bg-popover border border-border rounded-xl shadow-2xl w-full max-w-modal h-modal flex flex-col overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border shrink-0">
              <h2 id="profit-schedule-title" className="text-lg font-semibold text-white truncate pr-2">
                Scheduled report (email)
              </h2>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={closeScheduleModal}
                className="rounded-lg text-muted-foreground hover:text-foreground hover:bg-card"
                aria-label="Close scheduling"
              >
                <X className="w-5 h-5" aria-hidden />
              </Button>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-5">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                <div className="flex items-start gap-2">
                  <FileClock className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-lg font-semibold text-white">Schedule settings</h3>
                    <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
                      Preferences are stored in this browser when you click Save.
                    </p>
                  </div>
                </div>
                <label className="inline-flex items-center gap-2 cursor-pointer select-none shrink-0">
                  <input
                    type="checkbox"
                    checked={scheduleDraft.enabled}
                    onChange={(e) => setScheduleDraft((prev) => ({ ...prev, enabled: e.target.checked }))}
                    className="rounded border-border bg-input text-primary focus:ring-primary"
                  />
                  <span className="text-sm text-foreground">
                    Schedule <span className="font-semibold">enabled</span>
                  </span>
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="md:col-span-2 lg:col-span-1">
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Primary email <span className="text-destructive">*</span>
                    </label>
                    <input
                      type="email"
                      autoComplete="email"
                      placeholder="fleet.manager@example.com"
                      value={scheduleDraft.recipientEmail}
                      onChange={(e) => {
                        const v = e.target.value;
                        setScheduleDraft((prev) => ({ ...prev, recipientEmail: v }));
                        setSchedulePrimaryEmailError(null);
                      }}
                      onBlur={(e) => setSchedulePrimaryEmailError(schedulePrimaryEmailValidationError(e.target.value))}
                      aria-invalid={Boolean(schedulePrimaryEmailError)}
                      className={`w-full bg-input border text-white rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-primary ${
                        schedulePrimaryEmailError ? 'border-destructive focus:border-destructive' : 'border-border focus:border-primary'
                      }`}
                    />
                    {schedulePrimaryEmailError ? (
                      <p className="mt-1 text-xs text-destructive" role="alert">
                        {schedulePrimaryEmailError}
                      </p>
                    ) : null}
                  </div>
                  <div className="md:col-span-2 lg:col-span-2">
                    <label className="block text-sm font-medium text-foreground mb-1">Recipient name (optional)</label>
                    <input
                      type="text"
                      placeholder="e.g. Alex (Fleet Manager)"
                      value={scheduleDraft.recipientName}
                      onChange={(e) => setScheduleDraft((prev) => ({ ...prev, recipientName: e.target.value }))}
                      className="w-full bg-input border border-border text-white rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                    />
                  </div>
                  <div className="md:col-span-2 lg:col-span-2">
                    <label className="block text-sm font-medium text-foreground mb-1">CC (comma-separated)</label>
                    <input
                      type="text"
                      placeholder="ops@example.com, finance@example.com"
                      value={scheduleDraft.extraCcEmails}
                      onChange={(e) => setScheduleDraft((prev) => ({ ...prev, extraCcEmails: e.target.value }))}
                      className="w-full bg-input border border-border text-white rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                    />
                  </div>
                  <div className="md:col-span-2 lg:col-span-2">
                    <label className="block text-sm font-medium text-foreground mb-1">Email subject (optional)</label>
                    <input
                      type="text"
                      placeholder="Weekly ops profitability snapshot"
                      value={scheduleDraft.emailSubject}
                      onChange={(e) => setScheduleDraft((prev) => ({ ...prev, emailSubject: e.target.value }))}
                      className="w-full bg-input border border-border text-white rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                    />
                  </div>
                  <div className="md:col-span-2 lg:col-span-3">
                    <label className="block text-sm font-medium text-foreground mb-1">Email message (optional)</label>
                    <textarea
                      rows={3}
                      placeholder="This is your weekly profitability summary."
                      value={scheduleDraft.emailMessage}
                      onChange={(e) => setScheduleDraft((prev) => ({ ...prev, emailMessage: e.target.value }))}
                      className="w-full bg-input border border-border text-white rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Report granularity (PDF) <span className="text-destructive">*</span>
                    </label>
                    <select
                      value={scheduleDraft.reportGranularity}
                      onChange={(e) => {
                        const next = e.target.value as typeof scheduleDraft.reportGranularity;
                        setScheduleDraft((prev) => ({ ...prev, reportGranularity: next }));
                        // PDF preview uses the Profit page's periodType for aggregation.
                        // Keep them in sync so changing granularity here immediately impacts Preview PDF.
                        setPeriodType(next === 'daily' ? 'day' : next === 'weekly' ? 'week' : 'month');
                      }}
                      className="w-full bg-input border border-border text-white rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
                    >
                      <option value="daily">Daily rows</option>
                      <option value="weekly">Weekly totals</option>
                      <option value="monthly">Monthly totals</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Send frequency <span className="text-destructive">*</span>
                    </label>
                    <select
                      value={scheduleDraft.frequency}
                      onChange={(e) =>
                        setScheduleDraft((prev) => ({ ...prev, frequency: e.target.value as typeof scheduleDraft.frequency }))
                      }
                      className="w-full bg-input border border-border text-white rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
                    >
                      <option value="daily">Every day</option>
                      <option value="weekly">Once a week</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Day (weekly only){' '}
                      {scheduleDraft.frequency === 'weekly' ? <span className="text-destructive">*</span> : null}
                    </label>
                    <select
                      value={scheduleDraft.dayOfWeek}
                      disabled={scheduleDraft.frequency !== 'weekly'}
                      onChange={(e) => setScheduleDraft((prev) => ({ ...prev, dayOfWeek: Number(e.target.value) }))}
                      className="w-full bg-input border border-border text-white rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-primary disabled:opacity-50"
                    >
                      <option value={0}>Sunday</option>
                      <option value={1}>Monday</option>
                      <option value={2}>Tuesday</option>
                      <option value={3}>Wednesday</option>
                      <option value={4}>Thursday</option>
                      <option value={5}>Friday</option>
                      <option value={6}>Saturday</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Send at (local time) <span className="text-destructive">*</span>
                    </label>
                    <input
                      type="time"
                      value={scheduleDraft.sendTimeLocal}
                      onChange={(e) => setScheduleDraft((prev) => ({ ...prev, sendTimeLocal: e.target.value }))}
                      className="w-full bg-input border border-border text-white rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Timezone <span className="text-destructive">*</span>
                    </label>
                    <input
                      type="text"
                      value={scheduleDraft.timezone}
                      onChange={(e) => setScheduleDraft((prev) => ({ ...prev, timezone: e.target.value }))}
                      placeholder="Europe/London"
                      className="w-full bg-input border border-border text-white rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Period each run covers <span className="text-destructive">*</span>
                    </label>
                    <select
                      value={scheduleDraft.periodCoverage}
                      onChange={(e) =>
                        setScheduleDraft((prev) => ({
                          ...prev,
                          periodCoverage: e.target.value as typeof scheduleDraft.periodCoverage,
                        }))
                      }
                      className="w-full bg-input border border-border text-white rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
                    >
                      <option value="previous_week">Previous calendar week</option>
                      <option value="previous_month">Previous calendar month</option>
                      <option value="month_to_date">Month to date</option>
                      <option value="last_7_days">Rolling last 7 days</option>
                      <option value="last_30_days">Rolling last 30 days</option>
                    </select>
                  </div>
                  <div className="flex items-end">
                    <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={Boolean(scheduleDraft.attachBrief)}
                        onChange={(e) =>
                          setScheduleDraft((prev) => ({
                            ...prev,
                            // Brief is rendered inside the PDF, so enabling it should enable PDF too.
                            attachPdf: e.target.checked ? true : prev.attachPdf,
                            attachBrief: e.target.checked,
                          }))
                        }
                        className="rounded border-border bg-input text-primary focus:ring-primary"
                      />
                      <span className="text-sm text-foreground">Attach Brief</span>
                    </label>
                  </div>
                  <div className="flex items-end">
                    <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={scheduleDraft.attachPdf}
                        onChange={(e) => setScheduleDraft((prev) => ({ ...prev, attachPdf: e.target.checked }))}
                        className="rounded border-border bg-input text-primary focus:ring-primary"
                      />
                      <span className="text-sm text-foreground">Attach PDF</span>
                    </label>
                  </div>
                  <div className="flex items-end">
                    <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={scheduleDraft.attachCsv}
                        onChange={(e) => setScheduleDraft((prev) => ({ ...prev, attachCsv: e.target.checked }))}
                        className="rounded border-border bg-input text-primary focus:ring-primary"
                      />
                      <span className="text-sm text-foreground">Attach CSV</span>
                    </label>
                  </div>
                  {scheduleDraft.attachCsv && (
                    <div className="md:col-span-2 lg:col-span-3">
                      <div className="rounded-lg border border-border bg-card/40 p-3">
                        <div className="text-sm font-semibold text-foreground mb-2">CSV exports</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                          {([
                            ['vehicleLevel', 'Vehicle-level table'],
                            ['orderLevel', 'Order-level table'],
                            ['customerLevel', 'Customer-level table'],
                          ] as const).map(([key, label]) => (
                            <label key={key} className="inline-flex items-center gap-2 cursor-pointer select-none">
                              <input
                                type="checkbox"
                                checked={scheduleDraft.csvExports?.[key] ?? (key === 'vehicleLevel')}
                                onChange={(e) =>
                                  setScheduleDraft((prev) => ({
                                    ...prev,
                                    csvExports: {
                                      ...(prev.csvExports ?? { vehicleLevel: true, orderLevel: false, customerLevel: false }),
                                      [key]: e.target.checked,
                                    },
                                  }))
                                }
                                className="rounded border-border bg-input text-primary focus:ring-primary"
                              />
                              <span className="text-sm text-foreground">{label}</span>
                            </label>
                          ))}
                        </div>
                        <div className="text-xs text-muted-foreground mt-2">
                          These correspond to the vehicle/order/customer tables on the Profit page.
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="md:col-span-2 lg:col-span-3">
                    <div className="rounded-lg border border-border bg-card/40 p-3">
                      <div className="text-sm font-semibold text-foreground mb-2">Report content (PDF sections)</div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {([
                          ['executiveSnapshot', 'Executive snapshot'],
                          ['fleetKpis', 'Fleet KPIs (cost breakdown)'],
                          ['vehicles', 'Vehicle rankings'],
                          ['periods', scheduleDraft.reportGranularity === 'daily' ? 'Top/bottom 5 days' : 'Top/bottom 5 periods'],
                          ['customers', 'Customer breakdown'],
                          ['routes', 'Routes breakdown'],
                        ] as const).map(([key, label]) => (
                          <label key={key} className="inline-flex items-center gap-2 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={scheduleDraft.sections?.[key] ?? true}
                              onChange={(e) =>
                                setScheduleDraft((prev) => ({
                                  ...prev,
                                  sections: { ...prev.sections, [key]: e.target.checked },
                                }))
                              }
                              className="rounded border-border bg-input text-primary focus:ring-primary"
                            />
                            <span className="text-sm text-foreground">{label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="md:col-span-2 lg:col-span-3">
                    <div className="rounded-lg border border-border bg-card/40 p-3">
                      <div className="text-sm font-semibold text-foreground mb-2">Scheduled report scope (filters)</div>
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                        <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={scheduleDraft.filters?.hideStIvesVehicles ?? false}
                            onChange={(e) =>
                              setScheduleDraft((prev) => ({
                                ...prev,
                                filters: { ...prev.filters, hideStIvesVehicles: e.target.checked },
                              }))
                            }
                            className="rounded border-border bg-input text-primary focus:ring-primary"
                          />
                          <span className="text-sm text-foreground">Hide depot vehicles</span>
                        </label>
                        <div className="lg:col-span-2">
                          <label className="block text-sm font-medium text-foreground mb-1">Vehicle filter (optional)</label>
                          <div className="flex flex-col sm:flex-row gap-2 mb-2 sm:items-center">
                            <input
                              type="text"
                              value={scheduleVehicleSearch}
                              onChange={(e) => setScheduleVehicleSearch(e.target.value)}
                              placeholder="Search vehicles…"
                              className="w-full sm:flex-1 bg-input border border-border text-white rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
                            />
                            {(scheduleDraft.filters?.vehicleIds?.length ?? 0) > 0 && (
                              <Button
                                type="button"
                                variant="link"
                                size="sm"
                                onClick={() =>
                                  setScheduleDraft((prev) => ({
                                    ...prev,
                                    filters: { ...prev.filters, vehicleIds: [] },
                                  }))
                                }
                                className="shrink-0 px-1 text-warning hover:text-warning underline-offset-2 hover:underline"
                              >
                                Clear vehicle selection
                              </Button>
                            )}
                          </div>
                          <div className="max-h-44 overflow-auto rounded-md border border-border bg-background/30">
                            <table className="w-full text-sm text-left">
                              <thead className="sticky top-0 z-raised bg-card border-b border-border text-foreground">
                                <tr>
                                  <th className="w-10 px-3 py-2 font-medium" scope="col">
                                    <span className="sr-only">Include</span>
                                  </th>
                                  <th className="px-3 py-2 font-medium" scope="col">
                                    Registration
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-border">
                                {vehicleOptions
                                  .filter((v) => v.toLowerCase().includes(scheduleVehicleSearch.trim().toLowerCase()))
                                  .map((v) => {
                                    const std = standardiseVehicleId(v) || v;
                                    const selected = new Set(
                                      (scheduleDraft.filters?.vehicleIds ?? []).map((x) => standardiseVehicleId(x) || x),
                                    );
                                    const isChecked = selected.has(std);
                                    return (
                                      <tr key={v} className="hover:bg-card/40">
                                        <td className="px-3 py-2 align-middle">
                                          <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={(e) => {
                                              const checked = e.target.checked;
                                              setScheduleDraft((prev) => {
                                                const cur = new Set(
                                                  (prev.filters?.vehicleIds ?? []).map((x) => standardiseVehicleId(x) || x),
                                                );
                                                if (checked) cur.add(std);
                                                else cur.delete(std);
                                                return {
                                                  ...prev,
                                                  filters: { ...prev.filters, vehicleIds: Array.from(cur) },
                                                };
                                              });
                                            }}
                                            className="rounded border-border bg-input text-primary focus:ring-primary"
                                            aria-label={`Include vehicle ${v}`}
                                          />
                                        </td>
                                        <td className="px-3 py-2 font-mono text-foreground">{v}</td>
                                      </tr>
                                    );
                                  })}
                              </tbody>
                            </table>
                            {vehicleOptions.filter((v) => v.toLowerCase().includes(scheduleVehicleSearch.trim().toLowerCase())).length === 0 && (
                              <div className="px-3 py-2 text-sm text-muted-foreground border-t border-border">No vehicles match your search.</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
            </div>

            {scheduleBanner ? (
              <div
                className="relative shrink-0 border-t border-primary/90 bg-primary/20 text-primary shadow-highlight-inset"
                role="status"
                aria-live="polite"
              >
                <div className="flex flex-wrap items-center justify-center gap-2 px-12 py-3 text-sm font-medium text-center sm:px-14">
                  <ClockCheck className="w-5 h-5 shrink-0 text-primary" strokeWidth={2.25} aria-hidden />
                  <span>{scheduleBanner}</span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="iconSm"
                  onClick={() => setScheduleBanner(null)}
                  className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 text-primary hover:bg-primary/40"
                  aria-label="Dismiss message"
                >
                  <CircleX className="w-5 h-5" strokeWidth={2.25} aria-hidden />
                </Button>
              </div>
            ) : null}

            <div className="flex flex-wrap items-center justify-end gap-2 px-4 py-3 border-t border-border bg-popover/90 shrink-0">
              <Button
                type="button"
                variant="secondary"
                onClick={closeScheduleModal}
              >
                Close
              </Button>
              <Button
                type="button"
                onClick={saveScheduleDraft}
                disabled={!scheduleDirty}
              >
                Save
              </Button>
              <Button
                type="button"
                onClick={() => {
                  void openProfitabilityPdfPreviewModal('schedule', scheduleDraft);
                }}
                disabled={loading || pdfPreviewLoading || availableMonths.length === 0}
              >
                <FileDown className="w-4 h-4" aria-hidden />
                Preview PDF
              </Button>
            </div>
          </div>
        </div>
      )}

      {profitViewMode === 'brief' ? (
        <>
          {/* Monday Brief — skeleton while data fetch is in progress */}
          {loading ? (
            <MondayBriefSkeleton variant={variant} />
          ) : (
            <section
              ref={mondayBriefSectionRef}
              id="monday-brief-section"
              className="mb-6 bg-background border border-border rounded-xl overflow-hidden scroll-mt-4"
            >
              <Button
                type="button"
                variant="ghost"
                onClick={() => setMondayBriefOpen((open) => !open)}
                className="w-full h-auto flex-wrap justify-between px-4 py-3 border-b border-border bg-card/60 text-left hover:bg-card/80"
                aria-expanded={mondayBriefOpen}
              >
                <div className="flex items-center gap-2 min-w-0">
                  {mondayBriefOpen ? (
                    <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden />
                  )}
                  <span className="text-base font-bold tracking-widest text-primary uppercase">Monday Brief</span>
                  <span className="text-sm text-muted-foreground">
                    {mondayBriefUi.meta.selectedYm || 'Selected month'}
                  </span>
                </div>
                {variant !== 'profit-agent' ? (
                <span className="text-sm text-muted-foreground">
                  Built from the same Profitability data on this page
                </span>
                ) : null}
              </Button>

              {mondayBriefOpen ? (
              <div key={String(mondayBriefOpen)} className="p-4 zoe-snap-in">
                <div className="rounded-lg border border-border bg-card/40 p-4">
                  <div className="text-sm font-semibold text-foreground uppercase tracking-wide mb-3">This week’s focus</div>

                  <div className="text-base text-foreground font-medium whitespace-pre-line leading-7 mb-3">
                    {mondayBriefUi.items.length > 0
                      ? `${mondayBriefUi.headerText}\n\n${mondayBriefUi.introText}`
                      : mondayBriefUi.headerText}
                  </div>

                  {mondayBriefUi.items.length > 0 ? (
                    <ol className="space-y-4 list-decimal list-inside text-base text-foreground">
                      {mondayBriefUi.items.map((it, idx) => (
                        <li key={`${idx}-${it.title}`} className="leading-7">
                          <div className="inline">{renderBriefTitle(it)}</div>
                          <div className="text-sm text-muted-foreground mt-1 ml-6">{it.body}</div>
                          <div className="text-sm text-primary/90 mt-1.5 ml-6">{it.action}</div>
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <p className="text-base text-muted-foreground leading-7">{mondayBriefUi.introText}</p>
                  )}

                  {variant !== 'profit-agent' ? (
                    <div className="mt-4 text-sm whitespace-pre-line leading-6 text-muted-foreground">
                      {mondayBriefUi.confidenceText}
                    </div>
                  ) : null}
                </div>

                {otherWorkLines.length > 0 && (
                  <div role="status" className="mt-4 rounded-lg border border-border bg-card/40 p-4 text-sm text-foreground">
                    <p className="font-semibold text-foreground">Other work, not in these figures</p>
                    {otherWorkLines.map((line) => (
                      <p key={line} className="text-sm text-muted-foreground mt-1">{line}</p>
                    ))}
                  </div>
                )}

                {/* Data Quality Notice (conditional) — separate container */}
                {variant !== 'profit-agent' && dataQualityNotice.items.length > 0 && (
                  <div className="mt-4 rounded-lg border border-border bg-card/40 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                      <div className="text-sm font-semibold tracking-widest uppercase text-warning">
                        Data Quality Notice
                      </div>
                    </div>
                    <div className="space-y-3 text-base text-foreground">
                      {dataQualityNotice.items.map((it, i) => {
                        const isCritical = it.severity === 'critical';
                        const Icon = isCritical ? Siren : TriangleAlert;
                        const titleCls = isCritical ? 'text-destructive' : 'text-warning';
                        const impactCls = isCritical ? 'text-destructive/90' : 'text-warning/90';
                        const impactText =
                          it.impact === 'excluded'
                            ? '→ Excluded from calculations this period.'
                            : it.impact === 'included_flagged'
                              ? '→ Included in calculations, but flagged for review.'
                              : '→ Included; insights may be incomplete.';
                        return (
                          <div key={`${it.title}-${i}`} className="leading-7">
                            <div className="flex items-start gap-2">
                              <Icon
                                className={`w-4 h-4 mt-0.5 shrink-0 ${isCritical ? 'text-destructive' : 'text-warning'}`}
                                aria-hidden
                              />
                              <div className="min-w-0">
                                <div className={`font-semibold ${titleCls}`}>
                                  {it.title}
                                </div>
                                <div className="text-sm text-foreground mt-1">
                                  {it.detail}
                                </div>
                                <div className={`text-sm mt-1 ${impactCls}`}>{impactText}</div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              ) : null}
            </section>
          )}
        </>
      ) : variant === 'profit-agent' ? (
        <section
          className="mb-8 rounded-xl border border-chart-10/40 bg-background/50 p-8 text-center"
          aria-labelledby="profit-agent-dynamic-pricing-heading"
        >
          <h3
            id="profit-agent-dynamic-pricing-heading"
            className="text-lg font-semibold flex flex-wrap items-center justify-center gap-2"
          >
            <span className="text-chart-10">Dynamic Pricing</span>
            <span className="text-sm font-medium text-muted-foreground">Coming soon</span>
          </h3>
          <p className="text-sm text-muted-foreground mt-3 max-w-lg mx-auto leading-relaxed">
            Lane-level pricing scenarios and recommendations will appear here.
          </p>
        </section>
      ) : (
        <>
          {/* Top Performers + Top Dates ??side-by-side carousels */}
          {(topPerformersFull.length > 0 || topDatesFull.length > 0) && (
        <section className="mb-6 flex gap-4">

          {/* ?? Left: Top Performers by Vehicle ?? */}
          {topPerformersFull.length > 0 && (
            <div className="w-1/2 bg-card border border-border rounded-xl overflow-hidden">
              <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border">
                <div className="flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-warning shrink-0" />
                  <span className="text-sm font-bold tracking-widest text-warning uppercase">
                    {performersShowBottom5 ? 'Bottom 5 Performers' : 'Top 5 Performers'}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="iconSm"
                    className="text-warning/90 hover:text-warning hover:bg-warning/10 border border-transparent hover:border-warning/30"
                    title={performersShowBottom5 ? 'Show top 5 performers' : 'Show bottom 5 performers'}
                    aria-pressed={performersShowBottom5}
                    onClick={() => setPerformersShowBottom5((v) => !v)}
                  >
                    <ArrowDownUp className="w-4 h-4" strokeWidth={2} aria-hidden />
                  </Button>
                </div>
                <span className="text-xs text-muted-foreground hover:text-warning cursor-pointer transition-colors">Click to filter →</span>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-foreground">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="px-3 py-2.5 text-left font-semibold text-muted-foreground">#</th>
                      <th className="px-3 py-2.5 text-left font-semibold text-muted-foreground">Vehicle</th>
                      <th className="px-3 py-2.5 text-right font-semibold text-muted-foreground">Profit</th>
                      <th className="px-3 py-2.5 text-left font-semibold text-muted-foreground">Margin %</th>
                      <th className="px-3 py-2.5 text-right font-semibold text-muted-foreground">Miles</th>
                      <th
                        className="px-3 py-2.5 text-right font-semibold text-muted-foreground"
                        title={
                          periodType === 'day'
                            ? 'Calendar days with miles, revenue, variable costs, or orders (idle standing-only days excluded)'
                            : periodType === 'week'
                              ? 'Weeks with at least one such day'
                              : 'Months with at least one such day'
                        }
                      >
                        {periodType === 'day' ? 'Active days' : periodType === 'week' ? 'Active wks' : 'Active mo.'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {topPerformersDisplayed.map((p) => (
                      <tr
                        key={p.vehicle}
                        className="border-b border-border/80 hover:bg-secondary/50 cursor-pointer transition-colors"
                        title={`Click to filter by ${p.vehicle} and jump to trend`}
                        onClick={() => {
                          setSelectedVehicleIds([p.vehicle]);
                          setTimeout(() => trendSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
                        }}
                      >
                        <td className="px-3 py-2.5 text-foreground tabular-nums">{p.rank}</td>
                        <td className="px-3 py-2.5 font-medium text-ring">{p.vehicle}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums font-medium whitespace-nowrap">
                          <span className={p.profit >= 0 ? 'text-primary' : 'text-destructive'}>
                            {p.profit > 0 ? '+' : p.profit < 0 ? '−' : ''}
                            £{Math.abs(p.profit).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                          </span>
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-1 min-w-28">
                            <div className="flex-1 h-2.5 bg-accent rounded-full overflow-hidden max-w-18">
                              <div
                                className={`h-full rounded-full ${
                                  p.margin == null
                                    ? 'bg-transparent'
                                    : p.margin >= 50
                                      ? 'bg-primary'
                                      : p.margin >= 20
                                        ? 'bg-warning'
                                        : p.margin >= 0
                                          ? 'bg-orange-500'
                                          : 'bg-destructive'
                                }`}
                                style={{
                                  width:
                                    p.margin == null
                                      ? '0%'
                                      : `${p.margin < 0 ? Math.min(100, Math.abs(p.margin)) : Math.min(Math.max(p.margin, 0), 100)}%`,
                                }}
                              />
                            </div>
                            <span
                              className={`text-sm font-semibold w-14 text-right tabular-nums shrink-0 ${
                                p.margin == null ? 'text-muted-foreground' : p.margin >= 0 ? 'text-foreground' : 'text-destructive'
                              }`}
                            >
                              {p.margin == null ? '—' : (
                                <>
                                  {p.margin > 0 ? '+' : ''}
                                  {p.margin}%
                                </>
                              )}
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-right text-foreground tabular-nums">{p.miles.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                        <td className="px-3 py-2.5 text-right text-foreground tabular-nums">{p.activeDays}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ?? Right: Top Performing Dates ?? */}
          {topDatesFull.length > 0 && (
            <div className="w-1/2 bg-card border border-border rounded-xl overflow-hidden">
              <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-warning shrink-0" />
                  <span className="text-sm font-bold tracking-widest text-warning uppercase">
                    {bestDaysShowBottom5 ? 'Bottom 5 Days' : 'Top 5 Best Days'}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="iconSm"
                    className="text-warning/90 hover:text-warning hover:bg-warning/10 border border-transparent hover:border-warning/30"
                    title={bestDaysShowBottom5 ? 'Show top 5 best days' : 'Show bottom 5 days'}
                    aria-pressed={bestDaysShowBottom5}
                    onClick={() => setBestDaysShowBottom5((v) => !v)}
                  >
                    <ArrowDownUp className="w-4 h-4" strokeWidth={2} aria-hidden />
                  </Button>
                </div>
                <span className="text-xs text-muted-foreground hover:text-warning cursor-pointer transition-colors">Click to filter →</span>
              </div>
              <div>
                <table className="min-w-full text-sm text-foreground">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="px-3 py-2.5 text-left font-semibold text-muted-foreground">#</th>
                      <th className="px-3 py-2.5 text-left font-semibold text-muted-foreground whitespace-nowrap w-0 min-w-18">Date</th>
                      <th className="px-3 py-2.5 text-right font-semibold text-muted-foreground">Profit</th>
                      <th className="px-3 py-2.5 text-left font-semibold text-muted-foreground">Margin %</th>
                      <th className="px-3 py-2.5 text-right font-semibold text-muted-foreground">Miles</th>
                      <th className="px-3 py-2.5 text-right font-semibold text-muted-foreground">Vehicles</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topDatesDisplayed.map((d) => (
                      <tr
                        key={d.date}
                        className="border-b border-border/80 hover:bg-secondary/50 cursor-pointer transition-colors"
                        title={`Click to filter by ${d.date} and jump to trend`}
                        onClick={() => {
                          setSelectedPeriodRange({ startDate: d.date, endDate: d.date });
                          setTimeout(() => trendSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
                        }}
                      >
                        <td className="px-3 py-2.5 text-foreground tabular-nums">{d.rank}</td>
                        <td className="px-3 py-2.5 font-medium text-ring whitespace-nowrap min-w-18">
                          {(() => {
                            const [, m, day] = d.date.split('-');
                            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                            const monthIndex = parseInt(m, 10) - 1;
                            const monthStr = (monthIndex >= 0 && monthIndex < 12) ? monthNames[monthIndex] : m;
                            return `${String(parseInt(day, 10)).padStart(2, '0')} ${monthStr}`;
                          })()}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums font-medium whitespace-nowrap">
                          <span className={d.profit >= 0 ? 'text-primary' : 'text-destructive'}>
                            {d.profit > 0 ? '+' : d.profit < 0 ? '−' : ''}
                            £{Math.abs(d.profit).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                          </span>
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-1 min-w-28">
                            <div className="flex-1 h-2.5 bg-accent rounded-full overflow-hidden max-w-18">
                              <div
                                className={`h-full rounded-full ${
                                  d.margin == null
                                    ? 'bg-transparent'
                                    : d.margin >= 50
                                      ? 'bg-primary'
                                      : d.margin >= 20
                                        ? 'bg-warning'
                                        : d.margin >= 0
                                          ? 'bg-orange-500'
                                          : 'bg-destructive'
                                }`}
                                style={{
                                  width:
                                    d.margin == null
                                      ? '0%'
                                      : `${d.margin < 0 ? Math.min(100, Math.abs(d.margin)) : Math.min(Math.max(d.margin, 0), 100)}%`,
                                }}
                              />
                            </div>
                            <span
                              className={`text-sm font-semibold w-14 text-right tabular-nums shrink-0 ${
                                d.margin == null ? 'text-muted-foreground' : d.margin >= 0 ? 'text-foreground' : 'text-destructive'
                              }`}
                            >
                              {d.margin == null ? '—' : (
                                <>
                                  {d.margin > 0 ? '+' : ''}
                                  {d.margin}%
                                </>
                              )}
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-right text-foreground tabular-nums">{d.miles.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                        <td className="px-3 py-2.5 text-right text-foreground tabular-nums">{d.activeVehicles}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </section>
      )}

      {/* Lookup selection with Period Type and Filter by Date */}
      <section className="mb-6 bg-card border border-border rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-foreground">
            Lookup selection
          </h3>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant={hideStIvesVehicles ? 'default' : 'secondary'}
              onClick={() => setHideStIvesVehicles(!hideStIvesVehicles)}
              className={hideStIvesVehicles
                ? 'border border-primary bg-primary/10 text-primary hover:bg-primary/20'
                : 'border border-border'}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              {hideStIvesVehicles ? 'Include depot vehicles' : 'Exclude depot vehicles'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => {
                setSelectedVehicleIds([]);
                setVehicleIdInputValue('');
                setSelectedSummaryDate('');
                setSelectedOrderIds([]);
                setOrderNameInputValue('');
                setSelectedCustomerNames([]);
              }}
              className="border border-border"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-1" viewBox="0 0 20 20" fill="currentColor" aria-hidden><path fillRule="evenodd" d="M10 8.586l4.95-4.95a1 1 0 111.414 1.414L11.414 10l4.95 4.95a1 1 0 01-1.414 1.414L10 11.414l-4.95 4.95a1 1 0 01-1.414-1.414l4.95-4.95-4.95-4.95A1 1 0 015.05 3.636l4.95 4.95z" clipRule="evenodd" /></svg>
              Clear Filters
            </Button>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 items-start">
          <div className="flex-1">
            <label
              htmlFor="vehicle-id"
              className="block text-xs font-medium text-muted-foreground mb-1"
            >
              Vehicle Reg
            </label>
            <input
              id="vehicle-id"
              type="text"
              list="vehicle-id-list"
              value={vehicleIdInputValue}
              onChange={(e) => {
                const raw = e.target.value;
                setVehicleIdInputValue(raw);
                const value = standardiseVehicleId(raw);
                if (value && vehicleOptions.includes(value) && !selectedVehicleIds.includes(value)) {
                  setSelectedVehicleIds([...selectedVehicleIds, value]);
                  setVehicleIdInputValue('');
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const value = standardiseVehicleId((e.target as HTMLInputElement).value);
                  if (value && !selectedVehicleIds.includes(value)) {
                    setSelectedVehicleIds([...selectedVehicleIds, value]);
                    setVehicleIdInputValue('');
                  }
                }
              }}
              onBlur={() => {
                const value = standardiseVehicleId(vehicleIdInputValue);
                if (value && !selectedVehicleIds.includes(value)) {
                  setSelectedVehicleIds([...selectedVehicleIds, value]);
                  setVehicleIdInputValue('');
                }
              }}
              placeholder="Search vehicle reg..."
              className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              autoComplete="off"
            />
            <datalist id="vehicle-id-list">
              {vehicleOptions
                .filter(id => !selectedVehicleIds.includes(id))
                .map((id) => (
                  <option key={id} value={id} />
                ))}
            </datalist>
            <div className="min-h-15 mt-2 flex flex-wrap gap-1.5 bg-secondary/30 rounded p-2">
              {selectedVehicleIds.map((id) => (
                <span
                  key={id}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-primary/10 text-primary border border-primary/50 rounded"
                >
                  {id}
                  <Button
                    type="button"
                    variant="ghost"
                    size="iconSm"
                    onClick={() => setSelectedVehicleIds(selectedVehicleIds.filter(v => v !== id))}
                    className="hover:text-primary"
                    aria-label={`Remove ${id}`}
                  >
                    <X className="w-3.5 h-3.5" aria-hidden />
                  </Button>
                </span>
              ))}
            </div>
          </div>
          <div className="flex-1">
            <label
              htmlFor="period-type-lookup"
              className="block text-xs font-medium text-muted-foreground mb-1"
            >
              Period Type
            </label>
            <select
              id="period-type-lookup"
              value={periodType}
              onChange={(e) => setPeriodType(e.target.value as 'day' | 'week' | 'month')}
              className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
            >
              <option value="day">Day</option>
              <option value="week">Week</option>
              <option value="month">Month</option>
            </select>
          </div>
          <div className="flex-1">
            <label
              htmlFor="filter-by-date-lookup"
              className="block text-xs font-medium text-muted-foreground mb-1"
            >
              Filter by {periodType === 'day' ? 'Date' : periodType === 'week' ? 'Week' : 'Month'}
            </label>
            <select
              id="filter-by-date-lookup"
              value={selectedSummaryDate}
              onChange={(e) => {
                if (e.target.value === '') {
                  setSelectedSummaryDate('');
                  setSelectedPeriodRange(null);
                } else {
                  const selectedOption = periodOptions.find(opt => opt.label === e.target.value);
                  if (selectedOption) {
                    setSelectedSummaryDate(selectedOption.label);
                    setSelectedPeriodRange({ startDate: selectedOption.startDate, endDate: selectedOption.endDate });
                  }
                }
              }}
              className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
            >
              <option value="">All Dates</option>
              {((selectedVehicleIds.length > 0 ? filteredPeriodOptionsForMap : periodOptions) || []).map((option, idx) => (
                <option key={option?.label ?? option?.startDate ?? `opt-${idx}`} value={option?.label ?? ''}>{option?.label ?? ''}</option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-50">
            <label
              htmlFor="order-name-lookup"
              className="block text-xs font-medium text-muted-foreground mb-1"
            >
              Order / Tracking ID / Customer
            </label>
            <input
              id="order-name-lookup"
              type="text"
              list="order-name-lookup-list"
              value={orderNameInputValue}
              onChange={(e) => {
                const raw = e.target.value;
                setOrderNameInputValue(raw);
                const trimmed = raw.trim();
                if (!trimmed) return;
                const exactCustomer = customerOptions.find((c) => c === trimmed || c.toLowerCase() === trimmed.toLowerCase());
                if (exactCustomer) {
                  if (!selectedCustomerNames.some((c) => c.toLowerCase() === exactCustomer.toLowerCase())) {
                    setSelectedCustomerNames([...selectedCustomerNames, exactCustomer]);
                  }
                  setOrderNameInputValue('');
                  return;
                }
                const exactOrder = orderOptions.find((o) => o === trimmed || o.toLowerCase() === trimmed.toLowerCase());
                if (exactOrder) {
                  if (!selectedOrderIds.some((id) => id.toLowerCase() === exactOrder.toLowerCase())) {
                    setSelectedOrderIds([...selectedOrderIds, exactOrder]);
                  }
                  setOrderNameInputValue('');
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const trimmed = (e.target as HTMLInputElement).value.trim();
                  if (!trimmed) return;
                  const exactCustomer = customerOptions.find((c) => c === trimmed || c.toLowerCase() === trimmed.toLowerCase());
                  if (exactCustomer) {
                    if (!selectedCustomerNames.some((c) => c.toLowerCase() === exactCustomer.toLowerCase())) {
                      setSelectedCustomerNames([...selectedCustomerNames, exactCustomer]);
                    }
                    setOrderNameInputValue('');
                    return;
                  }
                  const exact = orderOptions.find((o) => o === trimmed || o.toLowerCase() === trimmed.toLowerCase());
                  const toAdd =
                    exact ??
                    orderOptions.find((o) => o.includes(trimmed)) ??
                    (isLikelyOrderIdentifier(trimmed) && !isLikelyEntityUlid(trimmed) ? trimmed : undefined);
                  if (!toAdd) return;
                  if (!selectedOrderIds.includes(toAdd) && !selectedOrderIds.some((id) => id.toLowerCase() === toAdd.toLowerCase())) {
                    setSelectedOrderIds([...selectedOrderIds, toAdd]);
                  }
                  setOrderNameInputValue('');
                }
              }}
              onBlur={() => {
                const trimmed = orderNameInputValue.trim();
                if (!trimmed) return;
                const exactCustomer = customerOptions.find((c) => c === trimmed || c.toLowerCase() === trimmed.toLowerCase());
                if (exactCustomer) {
                  if (!selectedCustomerNames.some((c) => c.toLowerCase() === exactCustomer.toLowerCase())) {
                    setSelectedCustomerNames([...selectedCustomerNames, exactCustomer]);
                  }
                  setOrderNameInputValue('');
                  return;
                }
                const exact = orderOptions.find((o) => o === trimmed || o.toLowerCase() === trimmed.toLowerCase());
                const toAdd =
                  exact ??
                  orderOptions.find((o) => o.includes(trimmed)) ??
                  (isLikelyOrderIdentifier(trimmed) && !isLikelyEntityUlid(trimmed) ? trimmed : undefined);
                if (!toAdd) {
                  setOrderNameInputValue('');
                  return;
                }
                if (!selectedOrderIds.includes(toAdd) && !selectedOrderIds.some((id) => id.toLowerCase() === toAdd.toLowerCase())) {
                  setSelectedOrderIds([...selectedOrderIds, toAdd]);
                }
                setOrderNameInputValue('');
              }}
              placeholder="Search order, Tracking ID, or customer..."
              className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
              autoComplete="off"
            />
            <datalist id="order-name-lookup-list">
              {orderOptions
                .filter((id) => !selectedOrderIds.some((s) => s.toLowerCase() === id.toLowerCase()))
                .map((id) => (
                  <option key={`order-${id}`} value={id} />
                ))}
              {customerOptions
                .filter((c) => !selectedCustomerNames.some((sc) => sc.toLowerCase() === c.toLowerCase()))
                .map((c) => (
                  <option key={`customer-${c}`} value={c} />
                ))}
            </datalist>
            <div className="min-h-15 mt-2 flex flex-wrap gap-1.5 bg-secondary/30 rounded p-2">
              {selectedOrderIds.map((id) => (
                <span
                  key={id}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-chart-5/10 text-chart-5 border border-chart-5/50 rounded"
                >
                  {id}
                  <Button
                    type="button"
                    variant="ghost"
                    size="iconSm"
                    onClick={() => setSelectedOrderIds(selectedOrderIds.filter((o) => o !== id))}
                    className="hover:text-chart-5"
                    aria-label={`Remove order ${id}`}
                  >
                    <X className="w-3.5 h-3.5" aria-hidden />
                  </Button>
                </span>
              ))}
              {selectedCustomerNames.map((c) => (
                <span
                  key={`customer-chip-${c}`}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-chart-1/10 text-chart-1 border border-chart-1/50 rounded"
                >
                  {c}
                  <Button
                    type="button"
                    variant="ghost"
                    size="iconSm"
                    onClick={() => setSelectedCustomerNames(selectedCustomerNames.filter((x) => x !== c))}
                    className="hover:text-chart-1"
                    aria-label={`Remove customer ${c}`}
                  >
                    <X className="w-3.5 h-3.5" aria-hidden />
                  </Button>
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 2x2 KPI cards + line chart (same layout as FleetOverview) */}
      <div className="mb-8">
        {/* Custom styled title with line dividers (same style as Electrification Results) */}
        {(loading ||
          (profitabilityChartSeries.length > 0 && profitabilityChartSeries[0].data.length > 0)) && (
          <div ref={trendSectionRef} className="relative my-8">
            {/* Horizontal line divider */}
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            {/* Title text with background to create break in line */}
            <div className="relative flex flex-col items-center">
              <div className="bg-background px-4">
                {loading ? (
                  <div className="flex flex-col items-center gap-2 py-1">
                    <div className="h-7 w-56 max-w-screen-85 rounded-md bg-secondary/50 animate-pulse" />
                    <div className="h-4 w-72 max-w-screen-90 rounded-md bg-secondary/40 animate-pulse" />
                  </div>
                ) : (
                  <>
                <h3 
                  className="text-lg font-semibold text-white"
                  style={{
                    textShadow: '0 0 6px rgba(234, 179, 8, 0.6), 0 0 12px rgba(234, 179, 8, 0.4), 0 0 18px rgba(234, 179, 8, 0.2)',
                  }}
                >
                  {profitabilityChartTitleParts.periodPrefix}
                </h3>
                {(profitabilityChartTitleParts.vehicleRegs || profitabilityChartTitleParts.dateRange) && (
                  <div 
                    className="text-sm text-foreground mt-2 text-center"
                    style={{
                      textShadow: '0 0 6px rgba(234, 179, 8, 0.6), 0 0 12px rgba(234, 179, 8, 0.4), 0 0 18px rgba(234, 179, 8, 0.2)',
                    }}
                  >
                    {profitabilityChartTitleParts.vehicleRegs && profitabilityChartTitleParts.dateRange 
                      ? `${profitabilityChartTitleParts.vehicleRegs} • ${profitabilityChartTitleParts.dateRange}`
                      : profitabilityChartTitleParts.vehicleRegs || profitabilityChartTitleParts.dateRange}
                  </div>
                )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
        {/* KPI Cards - 4 cards in a row, styled like ClientCarbonReporting */}
        {loading ? (
          <ProfitabilityKpiCardsSkeleton />
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Profit Margin % */}
          <div 
            className="relative overflow-hidden bg-gradient-to-br from-chart-10/20 via-muted to-background rounded-xl border border-chart-10/30 p-6 shadow-xl hover:shadow-chart-10/20 transition-all duration-500 transform hover:scale-105 cursor-pointer"
            onMouseEnter={() => setChartHighlightSeries(null)}
            onMouseLeave={() => setChartHighlightSeries(null)}
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-chart-10/10 rounded-full -mr-16 -mt-16 blur-2xl"></div>
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 bg-chart-10 rounded-full animate-pulse"></div>
                <div className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Profit Margin %</div>
              </div>
              <div className="text-4xl font-bold text-white mb-1">
                {kpiRevenueDisplayed > 0
                  ? `${((kpiNetProfitDisplayed / kpiRevenueDisplayed) * 100).toFixed(1)}%`
                  : '—'}
              </div>
              <div className="text-xs text-muted-foreground font-medium">(Profit / Revenue) × 100</div>
            </div>
          </div>

          {/* Net Profit */}
          {kpiNetProfitDisplayed >= 0 ? (
            <div 
              className="relative overflow-hidden bg-gradient-to-br from-primary/20 via-muted to-background rounded-xl border border-primary/30 p-6 shadow-xl hover:shadow-primary/20 transition-all duration-500 transform hover:scale-105 cursor-pointer"
              onMouseEnter={() => setChartHighlightSeries('Net Profit')}
              onMouseLeave={() => setChartHighlightSeries(null)}
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-full -mr-16 -mt-16 blur-2xl"></div>
              <div className="relative">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                  <div className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Net Profit</div>
                </div>
                <div className="text-4xl font-bold text-white mb-1">
                  {kpiDisplayCurrency
                    ? kpiNetProfitDisplayed.toLocaleString(undefined, { style: 'currency', currency: kpiDisplayCurrency, maximumFractionDigits: 0 })
                    : '—'}
                </div>
                <div className="text-xs text-muted-foreground font-medium">Revenue minus total costs</div>
              </div>
            </div>
          ) : (
            <div 
              className="relative overflow-hidden bg-gradient-to-br from-destructive/20 via-muted to-background rounded-xl border border-destructive/30 p-6 shadow-xl hover:shadow-destructive/20 transition-all duration-500 transform hover:scale-105 cursor-pointer"
              onMouseEnter={() => setChartHighlightSeries('Net Profit')}
              onMouseLeave={() => setChartHighlightSeries(null)}
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-destructive/10 rounded-full -mr-16 -mt-16 blur-2xl"></div>
              <div className="relative">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 bg-destructive rounded-full animate-pulse"></div>
                  <div className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Net Profit</div>
                </div>
                <div className="text-4xl font-bold text-white mb-1">
                  {kpiDisplayCurrency
                    ? kpiNetProfitDisplayed.toLocaleString(undefined, { style: 'currency', currency: kpiDisplayCurrency, maximumFractionDigits: 0 })
                    : '—'}
                </div>
                <div className="text-xs text-muted-foreground font-medium">Revenue minus total costs</div>
              </div>
            </div>
          )}

          {/* Revenue — wrapper has no overflow-hidden so tooltip can show above */}
          <div
            className="group relative"
            onMouseEnter={() => setChartHighlightSeries('Revenue')}
            onMouseLeave={() => setChartHighlightSeries(null)}
          >
            <div className="overflow-hidden bg-gradient-to-br from-chart-5/20 via-muted to-background rounded-xl border border-chart-5/30 p-6 shadow-xl hover:shadow-chart-5/20 transition-all duration-500 transform hover:scale-105 cursor-pointer">
              <div className="absolute top-0 right-0 w-32 h-32 bg-chart-5/10 rounded-full -mr-16 -mt-16 blur-2xl"></div>
              <div className="relative">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 bg-chart-5 rounded-full animate-pulse"></div>
                  <div className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Revenue</div>
                </div>
                <div className="text-4xl font-bold text-white mb-1">
                {kpiDisplayCurrency
                  ? kpiRevenueDisplayed.toLocaleString(undefined, { style: 'currency', currency: kpiDisplayCurrency, maximumFractionDigits: 0 })
                    : '—'}
                </div>
                <div className="text-xs text-muted-foreground font-medium">
                  Sum from orders
                </div>
              </div>
            </div>
            {/* Hover tooltip: above and to the right so it doesn't block the card or heading */}
            {(kpiTotalsFromDailySummary.ordersWithOriginalRevenue > 0 || kpiTotalsFromDailySummary.ordersWithModelledRevenue > 0) && (
              <div className="absolute bottom-full right-0 mb-2 translate-x-20 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 flex justify-end">
                <div className="px-4 py-3 bg-popover border border-chart-5/50 rounded-lg shadow-xl text-left text-sm text-foreground min-w-50">
                  <div className="font-semibold text-chart-5 mb-2 pb-1 border-b border-border text-center">Revenue breakdown</div>
                  <div className="space-y-1">
                    <div>
                      <span className="text-muted-foreground">Original revenue:</span>{' '}
                      <span className="text-white font-medium">
                        {kpiTotalsFromDailySummary.currency
                          ? kpiTotalsFromDailySummary.totalRevenueOriginal.toLocaleString(undefined, { style: 'currency', currency: kpiTotalsFromDailySummary.currency, minimumFractionDigits: 0, maximumFractionDigits: 0 })
                          : '—'}
                      </span>
                      <span className="text-muted-foreground text-xs block">
                        ({kpiTotalsFromDailySummary.ordersWithOriginalRevenue} order{kpiTotalsFromDailySummary.ordersWithOriginalRevenue !== 1 ? 's' : ''})
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Modelled revenue:</span>{' '}
                      <span className="text-white font-medium">
                        {kpiTotalsFromDailySummary.currency
                          ? kpiTotalsFromDailySummary.totalRevenueModelled.toLocaleString(undefined, { style: 'currency', currency: kpiTotalsFromDailySummary.currency, minimumFractionDigits: 0, maximumFractionDigits: 0 })
                          : '—'}
                      </span>
                      <span className="text-muted-foreground text-xs block">
                        ({kpiTotalsFromDailySummary.ordersWithModelledRevenue} order{kpiTotalsFromDailySummary.ordersWithModelledRevenue !== 1 ? 's' : ''})
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Total Costs — wrapper has no overflow-hidden so tooltip can show above */}
          <div
            className="group relative"
            onMouseEnter={() => setChartHighlightSeries('Total Costs')}
            onMouseLeave={() => setChartHighlightSeries(null)}
          >
            <div className="overflow-hidden bg-gradient-to-br from-chart-2/20 via-muted to-background rounded-xl border border-chart-2/30 p-6 shadow-xl hover:shadow-chart-2/20 transition-all duration-500 transform hover:scale-105 cursor-pointer">
              <div className="absolute top-0 right-0 w-32 h-32 bg-chart-2/10 rounded-full -mr-16 -mt-16 blur-2xl"></div>
              <div className="relative">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 bg-chart-2 rounded-full animate-pulse"></div>
                  <div className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Total Costs</div>
                </div>
                <div className="text-4xl font-bold text-white mb-1">
                  {(kpiTotalsFromDailySummary.currency || qargoOrderTotals.currency)
                    ? kpiTotalCosts.toLocaleString(undefined, {
                        style: 'currency',
                        currency: kpiTotalsFromDailySummary.currency || qargoOrderTotals.currency,
                        maximumFractionDigits: 0,
                      })
                    : '—'}
                </div>
                <div className="text-xs text-muted-foreground font-medium">
                  {selectedCustomerNames.length > 0
                    ? 'Allocated cost'
                    : 'Fuel + Driver + Standing'}
                </div>
              </div>
            </div>
            {/* Hover tooltip: breakdown above card, vertically only (no horizontal offset) */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
              <div className="px-4 py-3 bg-popover border border-chart-2/50 rounded-lg shadow-xl text-left text-sm text-foreground min-w-50">
                {selectedCustomerNames.length > 0 ? (
                  <>
                    <div className="font-semibold text-chart-2 mb-2 pb-1 border-b border-border text-center">Allocated cost</div>
                    <p className="text-xs text-muted-foreground mb-2">
                      Sum of allocated costs for the selected customer(s) from the Customer Profitability table (not full vehicle-day costs from Daily Vehicle Summary).
                    </p>
                    <div>
                      <span className="text-muted-foreground">Total allocated:</span>{' '}
                      <span className="text-white font-medium">
                        {(kpiTotalsFromDailySummary.currency || qargoOrderTotals.currency)
                          ? kpiTotalCosts.toLocaleString(undefined, {
                              style: 'currency',
                              currency: kpiTotalsFromDailySummary.currency || qargoOrderTotals.currency,
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            })
                          : '—'}
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="font-semibold text-chart-2 mb-2 pb-1 border-b border-border text-center">Total Costs breakdown</div>
                    <div className="space-y-1">
                      <div>
                        <span className="text-muted-foreground">Fuel Cost:</span>{' '}
                        <span className="text-white font-medium">
                          {kpiTotalsFromDailySummary.currency
                            ? (kpiTotalsFromDailySummary.totalFuelCost ?? 0).toLocaleString(undefined, { style: 'currency', currency: kpiTotalsFromDailySummary.currency, minimumFractionDigits: 0, maximumFractionDigits: 0 })
                            : '—'}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Driver Cost:</span>{' '}
                        <span className="text-white font-medium">
                          {kpiTotalsFromDailySummary.currency
                            ? (kpiTotalsFromDailySummary.totalDriverCost ?? 0).toLocaleString(undefined, { style: 'currency', currency: kpiTotalsFromDailySummary.currency, minimumFractionDigits: 0, maximumFractionDigits: 0 })
                            : '—'}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Standing Cost:</span>{' '}
                        <span className="text-white font-medium">
                          {kpiTotalsFromDailySummary.currency
                            ? (kpiTotalsFromDailySummary.totalDepreciation ?? 0).toLocaleString(undefined, { style: 'currency', currency: kpiTotalsFromDailySummary.currency, minimumFractionDigits: 0, maximumFractionDigits: 0 })
                            : '—'}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Calendar + Line Chart - Side by side layout */}
        {loading ? (
          <ProfitabilityCalendarChartSkeleton />
        ) : profitabilityChartSeries.length > 0 && profitabilityChartSeries[0].data.length > 0 ? (
          <div className="w-full min-w-0 mb-6 flex flex-col lg:flex-row gap-4 lg:items-stretch">
            {/* Calendar View - Left side (Day / Week / Month by periodType) */}
            {(() => {
              const hasDayData = periodType === 'day' && dailyProfitForCalendar.size > 0;
              const hasWeekData = periodType === 'week' && weekProfitForCalendar.size > 0;
              const hasMonthData = periodType === 'month' && monthProfitForCalendar.size > 0;
              if (!hasDayData && !hasWeekData && !hasMonthData) return null;

              const applyDateFilter = (startDate: string, endDate: string, fallbackLabel: string) => {
                const alreadySelected = selectedPeriodRange?.startDate === startDate && selectedPeriodRange?.endDate === endDate;
                if (alreadySelected) {
                  setSelectedPeriodRange(null);
                  setSelectedSummaryDate('');
                } else {
                  setSelectedPeriodRange({ startDate, endDate });
                  const opt = periodOptions.find(o => o.startDate === startDate && o.endDate === endDate);
                  setSelectedSummaryDate(opt?.label ?? fallbackLabel);
                }
              };

              const dayCalendar = (isWeekView: boolean = false) => {
                const dates = Array.from(dailyProfitForCalendar.keys()).sort();
                if (dates.length === 0) return null;
                const firstDate = new Date(dates[0] + 'T12:00:00');
                const lastDate = new Date(dates[dates.length - 1] + 'T12:00:00');
                const startMonth = new Date(firstDate.getFullYear(), firstDate.getMonth(), 1);
                const endMonth = new Date(lastDate.getFullYear(), lastDate.getMonth() + 1, 0);
                const allDates: string[] = [];
                const d = new Date(startMonth);
                while (d <= endMonth) {
                  allDates.push(d.toISOString().slice(0, 10));
                  d.setDate(d.getDate() + 1);
                }
                const values = Array.from(dailyProfitForCalendar.values());
                const maxAbsProfit = Math.max(...values.map(v => Math.abs(v)), 1);
                
                // For week view: map each date to its week and find middle day (Wednesday)
                const dateToWeekMap = new Map<string, string>(); // date -> week key
                const weekMiddleDays = new Set<string>(); // dates that are middle days of their week
                if (isWeekView) {
                  for (const week of availableWeeks) {
                    const weekStart = new Date(week.startDate + 'T12:00:00');
                    // Find Wednesday (day 2 from Monday: Mon=0, Tue=1, Wed=2)
                    const middleDay = new Date(weekStart);
                    middleDay.setDate(weekStart.getDate() + 2); // Wednesday (Mon+2)
                    const middleDayStr = middleDay.toISOString().slice(0, 10);
                    weekMiddleDays.add(middleDayStr);
                    
                    // Map all days in this week
                    const dayIter = new Date(weekStart);
                    while (dayIter <= new Date(week.endDate + 'T12:00:00')) {
                      const dayStr = dayIter.toISOString().slice(0, 10);
                      dateToWeekMap.set(dayStr, `${week.startDate}|${week.endDate}`);
                      dayIter.setDate(dayIter.getDate() + 1);
                    }
                  }
                }
                
                const months: { year: number; month: number; dates: string[] }[] = [];
                let currentMonth: { year: number; month: number; dates: string[] } | null = null;
                for (const dateStr of allDates) {
                  const date = new Date(dateStr + 'T12:00:00');
                  const year = date.getFullYear();
                  const month = date.getMonth();
                  if (!currentMonth || currentMonth.year !== year || currentMonth.month !== month) {
                    currentMonth = { year, month, dates: [] };
                    months.push(currentMonth);
                  }
                  currentMonth.dates.push(dateStr);
                }
                // Single-month paging: show one month at a time with prev/next
                const currentIndex = calendarPageMonth == null ? 0 : months.findIndex(m => m.year === calendarPageMonth.year && m.month === calendarPageMonth.month);
                const displayIndex = currentIndex < 0 ? 0 : currentIndex;
                const monthData = months[displayIndex];
                if (!monthData) {
                  return <div className="text-sm text-muted-foreground">No date data for calendar.</div>;
                }
                const monthName = new Date(monthData.year, monthData.month, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
                const firstDayOfMonth = new Date(monthData.year, monthData.month, 1);
                const dayOfWeek = firstDayOfMonth.getDay();
                const daysInMonth = new Date(monthData.year, monthData.month + 1, 0).getDate();
                return (
                  <div className="space-y-3">
                    {/* Month navigation */}
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          if (displayIndex > 0) {
                            setCalendarPageMonth({ year: months[displayIndex - 1].year, month: months[displayIndex - 1].month });
                            setSelectedSummaryDate('');
                            setSelectedPeriodRange(null);
                          } else {
                            setCalendarPageMonth(null);
                            setSelectedSummaryDate('');
                            setSelectedPeriodRange(null);
                          }
                        }}
                        disabled={displayIndex === 0}
                        className="rounded-lg bg-secondary/50"
                        aria-label="Previous month"
                      >
                        <span className="text-sm font-medium">‹ Prev</span>
                      </Button>
                      <span className="text-sm font-semibold text-foreground min-w-35 text-center">{monthName}</span>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          if (displayIndex < months.length - 1) {
                            setCalendarPageMonth({ year: months[displayIndex + 1].year, month: months[displayIndex + 1].month });
                            setSelectedSummaryDate('');
                            setSelectedPeriodRange(null);
                          } else {
                            setCalendarPageMonth(null);
                            setSelectedSummaryDate('');
                            setSelectedPeriodRange(null);
                          }
                        }}
                        disabled={displayIndex >= months.length - 1}
                        className="rounded-lg bg-secondary/50"
                        aria-label="Next month"
                      >
                        <span className="text-sm font-medium">Next ›</span>
                      </Button>
                    </div>
                    <div className="grid grid-cols-7 gap-2 text-xs">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <div key={day} className="text-center text-muted-foreground font-medium py-1 text-xs">{day}</div>
                      ))}
                      {Array.from({ length: dayOfWeek }).map((_, i) => <div key={`e-${i}`} className="aspect-square" />)}
                      {Array.from({ length: daysInMonth }).map((_, i) => {
                              const day = i + 1;
                              const dateStr = `${monthData.year}-${String(monthData.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                              
                              // For week view: check if this day is in a week, and if it's the middle day
                              let weekProfit: number | null = null;
                              let weekKey: string | null = null;
                              if (isWeekView) {
                                weekKey = dateToWeekMap.get(dateStr) ?? null;
                                if (weekKey) {
                                  weekProfit = weekProfitForCalendar.get(weekKey) ?? null;
                                }
                              }
                              
                              const dailyProfit = dailyProfitForCalendar.get(dateStr) ?? null;
                              // In week view: only middle day shows profit and gets green; others stay grey
                              const isMiddleDay = isWeekView && weekKey && weekMiddleDays.has(dateStr);
                              const profit = isWeekView
                                ? (isMiddleDay && weekProfit !== null ? weekProfit : null)
                                : dailyProfit;
                              const isSelected = isWeekView && weekKey
                                ? selectedPeriodRange?.startDate === weekKey.split('|')[0] && selectedPeriodRange?.endDate === weekKey.split('|')[1]
                                : selectedPeriodRange?.startDate === dateStr && selectedPeriodRange?.endDate === dateStr;
                              
                              let bgColor = 'bg-secondary/30';
                              let textColor = 'text-muted-foreground';
                              let borderColor = 'border-border';
                              // Week view: middle day = green (if has profit), other days = grey. Day view: use profit-based colors.
                              if (isWeekView) {
                                if (isMiddleDay && profit !== null) {
                                  if (profit > 0) {
                                    const intensity = Math.min(profit / maxAbsProfit, 1);
                                    bgColor = 'bg-primary/15';
                                    textColor = 'text-primary';
                                    borderColor = 'border-primary/30';
                                    if (intensity > 0.5) { bgColor = 'bg-primary/40'; borderColor = 'border-primary/70'; }
                                  } else if (profit < 0) {
                                    const intensity = Math.min(Math.abs(profit) / maxAbsProfit, 1);
                                    bgColor = 'bg-destructive/15';
                                    textColor = 'text-destructive';
                                    borderColor = 'border-destructive/30';
                                    if (intensity > 0.5) { bgColor = 'bg-destructive/40'; borderColor = 'border-destructive/70'; }
                                  } else {
                                    bgColor = 'bg-accent/30';
                                    textColor = 'text-foreground';
                                    borderColor = 'border-border';
                                  }
                                }
                                // else: other days stay grey (bgColor/textColor/borderColor already set above)
                              } else if (profit !== null) {
                                if (profit > 0) {
                                  const intensity = Math.min(profit / maxAbsProfit, 1);
                                  bgColor = 'bg-primary/15';
                                  textColor = 'text-primary';
                                  borderColor = 'border-primary/30';
                                  if (intensity > 0.5) { bgColor = 'bg-primary/40'; borderColor = 'border-primary/70'; }
                                } else if (profit < 0) {
                                  const intensity = Math.min(Math.abs(profit) / maxAbsProfit, 1);
                                  bgColor = 'bg-destructive/15';
                                  textColor = 'text-destructive';
                                  borderColor = 'border-destructive/30';
                                  if (intensity > 0.5) { bgColor = 'bg-destructive/40'; borderColor = 'border-destructive/70'; }
                                } else {
                                  bgColor = 'bg-accent/30';
                                  textColor = 'text-foreground';
                                  borderColor = 'border-border';
                                }
                              }
                              if (isSelected) { borderColor = 'border-ring'; bgColor = bgColor.replace('/15', '/35').replace('/40', '/55'); }
                              
                              const hoverGlowColor = profit !== null 
                                ? (profit > 0 ? 'hover:shadow-glow-primary-lg' : 'hover:shadow-glow-critical-lg')
                                : 'hover:shadow-glow-blue-xl';
                              
                              return (
                                <Button
                                  variant="ghost"
                                  type="button"
                                  key={dateStr}
                                  onClick={() => {
                                    if (isWeekView && weekKey) {
                                      const [start, end] = weekKey.split('|');
                                      const fallback =
                                        periodOptions.find(o => o.startDate === start && o.endDate === end)?.label
                                        ?? availableWeeks.find(w => w.startDate === start && w.endDate === end)?.label
                                        ?? `${start} to ${end}`;
                                      applyDateFilter(start, end, fallback);
                                    } else {
                                      applyDateFilter(dateStr, dateStr, dateStr);
                                    }
                                  }}
                                  className={`aspect-square w-full h-auto ${bgColor} ${borderColor} border rounded-lg text-center p-3% transition-all duration-150 cursor-pointer hover:brightness-125 hover:border-opacity-100 ${hoverGlowColor} ${isSelected ? 'ring-2 ring-ring' : ''} relative overflow-hidden flex flex-col items-center justify-center`}
                                  title={profit !== null ? `Profit: £${profit.toFixed(2)}` : (isWeekView && weekKey ? 'Week (click to filter)' : 'No data')}
                                >
                                  <div className={`text-sm font-medium ${textColor} leading-tight`}>{day}</div>
                                  {profit !== null && (
                                    <div className={`text-xs font-semibold mt-0.5 ${textColor} leading-tight`}>
                                      {profit >= 0 ? '+' : ''}£{(profit / 1000).toFixed(profit >= 1000 || profit <= -1000 ? 0 : 1)}k
                                    </div>
                                  )}
                                </Button>
                              );
                            })}
                    </div>
                  </div>
                );
              };

              const weekCalendar = () => {
                // For week view, show the daily calendar with profit on middle day only; other days grey
                return dayCalendar(true);
              };

              const monthCalendar = () => {
                const values = Array.from(monthProfitForCalendar.values());
                const maxAbsProfit = values.length > 0 ? Math.max(...values.map(v => Math.abs(v)), 1) : 1;
                const monthsToShow = Array.isArray(availableMonths) ? availableMonths : [];
                return (
                  <div className="calendar-scroll grid grid-cols-2 gap-2 max-h-100 overflow-y-auto pr-2 pb-4">
                    {monthsToShow.map((month) => {
                      if (!month || month.startDate == null || month.endDate == null) return null;
                      const key = `${month.startDate}|${month.endDate}`;
                      const profit = monthProfitForCalendar.get(key) ?? null;
                      const isSelected = selectedPeriodRange?.startDate === month.startDate && selectedPeriodRange?.endDate === month.endDate;
                      let bgColor = 'bg-secondary/30';
                      let textColor = 'text-muted-foreground';
                      let borderColor = 'border-border';
                      if (profit !== null) {
                        if (profit > 0) {
                          const intensity = Math.min(profit / maxAbsProfit, 1);
                          bgColor = intensity > 0.5 ? 'bg-primary/40' : 'bg-primary/15';
                          textColor = 'text-primary';
                          borderColor = intensity > 0.5 ? 'border-primary/70' : 'border-primary/30';
                        } else if (profit < 0) {
                          const intensity = Math.min(Math.abs(profit) / maxAbsProfit, 1);
                          bgColor = intensity > 0.5 ? 'bg-destructive/40' : 'bg-destructive/15';
                          textColor = 'text-destructive';
                          borderColor = intensity > 0.5 ? 'border-destructive/70' : 'border-destructive/30';
                        } else {
                          bgColor = 'bg-accent/30';
                          textColor = 'text-foreground';
                          borderColor = 'border-border';
                        }
                      }
                      if (isSelected) { borderColor = 'border-ring'; bgColor = bgColor.replace('/15', '/35').replace('/40', '/55'); }
                      const monthLabel = (month.label && typeof month.label === 'string') ? month.label.split(' (')[0] : '';
                      
                      // Determine hover glow color based on profit
                      const hoverGlowColor = profit !== null 
                        ? (profit > 0 ? 'hover:shadow-glow-primary-lg' : 'hover:shadow-glow-critical-lg')
                        : 'hover:shadow-glow-blue-xl';
                      
                      return (
                        <Button
                          variant="ghost"
                          type="button"
                          key={key}
                          onClick={() => applyDateFilter(month.startDate, month.endDate, month.label)}
                          className={`h-auto ${bgColor} ${borderColor} border rounded-lg text-center p-3 transition-all duration-200 cursor-pointer hover:scale-105 hover:z-10 hover:shadow-lg ${hoverGlowColor} ${isSelected ? 'ring-2 ring-ring' : ''}`}
                          title={profit !== null ? `Profit: £${profit.toFixed(2)}` : 'No data'}
                        >
                          <div className="text-sm font-medium text-foreground">{monthLabel}</div>
                          {profit !== null && (
                            <div className={`text-sm font-semibold mt-1 ${textColor}`}>
                              {profit >= 0 ? '+' : ''}£{(profit / 1000).toFixed(profit >= 1000 || profit <= -1000 ? 0 : 1)}k
                            </div>
                          )}
                        </Button>
                      );
                    })}
                  </div>
                );
              };

              const calendarTitle = periodType === 'day' ? 'Daily Profit Calendar' : periodType === 'week' ? 'Weekly Profit Calendar' : 'Monthly Profit';
              return (
                <div className="w-full lg:w-1/2 min-w-0 bg-card border border-border rounded-lg p-5 h-full">
                  <h4 className="text-base font-semibold text-foreground mb-4">{calendarTitle}</h4>
                  {periodType === 'day' && dayCalendar(false)}
                  {periodType === 'week' && weekCalendar()}
                  {periodType === 'month' && monthCalendar()}
                </div>
              );
            })()}
            
            {/* Line Chart - Right side */}
            <div className={`min-w-0 flex flex-col ${(periodType === 'day' && dailyProfitForCalendar.size > 0) || (periodType === 'week' && weekProfitForCalendar.size > 0) || (periodType === 'month' && monthProfitForCalendar.size > 0) ? 'lg:w-1/2' : 'w-full'}`}>
              <div className="flex-1 bg-card border border-border rounded-lg overflow-hidden">
              <LineChart
                title=""
                series={profitabilityChartSeries}
                yAxisLabel="Amount (£)"
                formatYAxisAsK
                xAxisLabel={periodType === 'day' ? 'Day' : periodType === 'week' ? 'Week' : 'Month'}
                heightClass="h-full min-h-80"
                chartHeight={265}
                defaultZoomed
                highlightedSeriesName={chartHighlightSeries}
                formatXLabel={(label) => {
                  if (periodType === 'week') {
                    return label;
                  }
                  if (periodType === 'month') {
                    return label;
                  }
                  const dateStr = label.includes(' - ') ? label.split(' - ')[0].trim() : label;
                  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
                    const d = new Date(dateStr + 'T12:00:00');
                    const month = d.toLocaleDateString('en-GB', { month: 'short' });
                    const day = d.getDate();
                    return `${month} ${day}`;
                  }
                  return label;
                }}
                xAxisLabelInterval={periodType === 'day' ? 7 : undefined}
                selectedXLabel={selectedXLabelForChart}
                hideDataPoints
                gradientFillSeriesName="Net Profit"
                highlightLatestPointSeriesName="Net Profit"
              />
              </div>
            </div>
          </div>
        ) : (
          <div className="w-full mb-6 bg-card rounded-lg shadow-sm p-6 border border-border flex items-center justify-center min-h-80">
            <p className="text-muted-foreground">No time series data for selected period. Adjust filters.</p>
          </div>
        )}
      </div>

      {/* Daily Summary Section - Separate from Daily Profitability */}
      <section className="mb-6 bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-ring flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                {periodType === 'day' ? 'Daily' : periodType === 'week' ? 'Weekly' : 'Monthly'} Vehicle Summary 
              </h3>
              <p className="text-xs text-muted-foreground mt-1">
                Aggregated totals for each vehicle per {periodType} ({filteredSummaryData.length} {filteredSummaryData.length === 1 ? 'entry' : 'entries'})
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  setSelectedVehicleIds([]);
                  setSelectedSummaryDate('');
                  setSelectedOrderIds([]);
                  setOrderNameInputValue('');
                  setSelectedCustomerNames([]);
                }}
                className="border border-border"
                title="Clear all filters"
              >
                <X className="w-3.5 h-3.5" aria-hidden />
                Clear Filters
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="py-6">
              <div className="animate-pulse space-y-3">
                <div className="h-4 w-56 bg-secondary/40 rounded" />
                <div className="h-10 w-full bg-card/40 rounded-lg border border-border/40" />
                <div className="h-10 w-full bg-card/40 rounded-lg border border-border/40" />
                <div className="h-10 w-full bg-card/40 rounded-lg border border-border/40" />
                <div className="h-10 w-full bg-card/40 rounded-lg border border-border/40" />
              </div>
            </div>
          ) : error ? (
            <div className="text-sm text-destructive py-6 text-center">{error}</div>
          ) : filteredSummaryData.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">
              No data available for the selected filters
            </div>
          ) : (
            <div className="overflow-x-auto overflow-y-visible">
              <table className="w-full text-xs relative">
                <thead className="bg-secondary/30" style={{overflow: 'visible'}}>
                  <tr className="text-left text-muted-foreground border-b border-border" style={{overflow: 'visible'}}>
                    <th className="px-2 py-3 w-12">
                      {/* Checkbox column header */}
                    </th>
                    <th 
                      className="px-4 py-3 cursor-pointer hover:bg-secondary transition-colors"
                      onClick={() => {
                        if (summarySortField === 'date') {
                          setSummarySortAsc(!summarySortAsc);
                        } else {
                          setSummarySortField('date');
                          setSummarySortAsc(false);
                        }
                      }}
                    >
                      <div className="flex items-center gap-1">
                        {periodType === 'day' ? 'Date' : periodType === 'week' ? 'Week' : 'Month'}
                        {summarySortField === 'date' && (
                          <span className="text-ring">
                            {summarySortAsc ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-2 py-3 cursor-pointer hover:bg-secondary transition-colors"
                      style={{overflow: 'visible', width: '10%', minWidth: '80px', maxWidth: '120px'}}
                      onClick={() => {
                        if (summarySortField === 'vehicleId') {
                          setSummarySortAsc(!summarySortAsc);
                        } else {
                          setSummarySortField('vehicleId');
                          setSummarySortAsc(false);
                        }
                      }}
                    >
                      <div className="flex items-center gap-1">
                        Vehicle
                        <TableHeaderInfoIcon title="Click a Vehicle Reg to filter." />
                        {summarySortField === 'vehicleId' && (
                          <span className="text-ring">
                            {summarySortAsc ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-right cursor-pointer hover:bg-secondary transition-colors"
                      onClick={() => {
                        if (summarySortField === 'totalMileage') {
                          setSummarySortAsc(!summarySortAsc);
                        } else {
                          setSummarySortField('totalMileage');
                          setSummarySortAsc(false);
                        }
                      }}
                      title="Daily miles from telematics"
                    >
                      <div className="flex items-center justify-end gap-1">
                        Miles
                        {summarySortField === 'totalMileage' && (
                          <span className="text-ring">
                            {summarySortAsc ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    {useTruth && useTruthUtil && (
                    <th
                      className="px-4 py-3 text-right cursor-pointer hover:bg-secondary transition-colors"
                      onClick={() => {
                        if (summarySortField === 'utilisationScore') {
                          setSummarySortAsc(!summarySortAsc);
                        } else {
                          setSummarySortField('utilisationScore');
                          setSummarySortAsc(false);
                        }
                      }}
                      title="Composite utilisation score from truth layer (mileage, time, revenue vs targets)"
                    >
                      <div className="flex items-center justify-end gap-1">
                        Util %
                        <TableHeaderInfoIcon title="Weighted utilisation score (0–100) from the truth layer — mileage, time, and revenue efficiency vs fleet targets. Run Compute utilisation after profitability." />
                        {summarySortField === 'utilisationScore' && (
                          <span className="text-ring">
                            {summarySortAsc ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    )}
                    <th
                      className="px-4 py-3 text-right cursor-pointer hover:bg-secondary transition-colors"
                      onClick={() => {
                        if (summarySortField === 'pickupCount') {
                          setSummarySortAsc(!summarySortAsc);
                        } else {
                          setSummarySortField('pickupCount');
                          setSummarySortAsc(false);
                        }
                      }}
                      title="Orders where this vehicle is the pick-up / trunk leg"
                    >
                      <div className="flex items-center justify-end gap-1">
                        Pickups
                        <TableHeaderInfoIcon title="Count of multi-leg jobs where this vehicle carried the order before the final delivery vehicle." />
                        {summarySortField === 'pickupCount' && (
                          <span className="text-primary">
                            {summarySortAsc ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-right cursor-pointer hover:bg-secondary transition-colors"
                      onClick={() => {
                        if (summarySortField === 'orderCount') {
                          setSummarySortAsc(!summarySortAsc);
                        } else {
                          setSummarySortField('orderCount');
                          setSummarySortAsc(false);
                        }
                      }}
                      title="Orders where this vehicle is the delivery leg"
                    >
                      <div className="flex items-center justify-end gap-1">
                        Deliveries
                        <TableHeaderInfoIcon title="Count of orders delivered on this vehicle-day (final carrying leg). Revenue stays on deliveries." />
                        {summarySortField === 'orderCount' && (
                          <span className="text-ring">
                            {summarySortAsc ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 text-right cursor-pointer bg-chart-5 text-white hover:bg-chart-5/80 transition-colors"
                      onClick={() => {
                        if (summarySortField === 'totalRevenue') {
                          setSummarySortAsc(!summarySortAsc);
                        } else {
                          setSummarySortField('totalRevenue');
                          setSummarySortAsc(false);
                        }
                      }}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Revenue
                        {summarySortField === 'totalRevenue' && (
                          <span className="text-white">
                            {summarySortAsc ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-right cursor-pointer bg-chart-2 text-white hover:bg-chart-2/80 transition-colors"
                      onClick={() => {
                        if (summarySortField === 'totalFuelCost') {
                          setSummarySortAsc(!summarySortAsc);
                        } else {
                          setSummarySortField('totalFuelCost');
                          setSummarySortAsc(false);
                        }
                      }}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Fuel Cost
                        {summarySortField === 'totalFuelCost' && (
                          <span className="text-white">{summarySortAsc ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-right cursor-pointer bg-chart-7 text-white hover:bg-chart-7/80 transition-colors"
                      onClick={() => {
                        if (summarySortField === 'totalDriverCost') {
                          setSummarySortAsc(!summarySortAsc);
                        } else {
                          setSummarySortField('totalDriverCost');
                          setSummarySortAsc(false);
                        }
                      }}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Driver Cost
                        {summarySortField === 'totalDriverCost' && (
                          <span className="text-white">{summarySortAsc ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th className="px-4 py-3 text-right font-medium text-foreground bg-chart-8/50">
                      <div className="flex items-center justify-end gap-1">Standing Cost</div>
                    </th>
                    <th 
                      className="px-4 py-3 text-right cursor-pointer hover:bg-secondary transition-colors"
                      onClick={() => {
                        if (summarySortField === 'totalProfit') {
                          setSummarySortAsc(!summarySortAsc);
                        } else {
                          setSummarySortField('totalProfit');
                          setSummarySortAsc(false);
                        }
                      }}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Profit
                        <TableHeaderInfoIcon title="Profit = Revenue − Fuel Cost − Driver Cost − Standing Cost." />
                        {summarySortField === 'totalProfit' && (
                          <span className="text-primary">
                            {summarySortAsc ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {paginatedSummaryData.map((row) => {
                    const stdVid = standardiseVehicleId(row.vehicleId) || (row.vehicleId || '');
                    const isVehicleSelected = Boolean(stdVid && stdVid !== 'EMPTY_VALUE' && selectedVehicleIds.includes(stdVid));
                    const hasMap = hasMapDataForRow(row.vehicleId, row.date);
                    const isRowSelected = selectedDailySummaryRow != null && standardiseVehicleId(row.vehicleId) === standardiseVehicleId(selectedDailySummaryRow.vehicleId) && (getDateOnly(row.date) || row.date) === (getDateOnly(selectedDailySummaryRow.date) || selectedDailySummaryRow.date);
                    return (
                    <tr
                      key={`${row.date}-${row.vehicleId}`}
                      className={`hover:bg-secondary/30 transition-colors ${isRowSelected ? 'bg-primary/20 ring-2 ring-inset ring-primary' : ''}`}
                    >
                      <td className="px-2 py-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isVehicleSelected}
                          onChange={(e) => {
                            e.stopPropagation();
                            if (stdVid && stdVid !== 'EMPTY_VALUE') {
                              if (e.target.checked) {
                                if (!selectedVehicleIds.includes(stdVid)) {
                                  setSelectedVehicleIds([...selectedVehicleIds, stdVid]);
                                }
                              } else {
                                setSelectedVehicleIds(selectedVehicleIds.filter(id => id !== stdVid));
                              }
                            }
                          }}
                          className="w-4 h-4 text-ring bg-input border-border rounded focus:ring-ring focus:ring-2 cursor-pointer"
                          title="Check to add vehicle to filter"
                        />
                      </td>
                      <td className="px-4 py-3 text-foreground">
                        {row.periodLabel || row.date}
                      </td>
                      <td 
                        className="px-2 py-3 text-foreground font-mono text-xs"
                        style={{ width: '10%', minWidth: '80px', maxWidth: '120px' }}
                      >
                        <div className="flex items-center gap-1.5">
                          <span>{row.vehicleId || 'EMPTY_VALUE'}</span>
                          {hasMap && (row.totalMileage ?? 0) >= 1.0 && (
                            <span 
                              className="cursor-pointer transition-all duration-200 hover:scale-125 hover:drop-shadow-glow-primary-lg"
                              onClick={(e) => {
                                e.stopPropagation();
                                const dateOnly = getDateOnly(row.date) || row.date;
                                let startDate: string;
                                let endDate: string;
                                if (periodType === 'day') {
                                  startDate = endDate = dateOnly;
                                } else if (periodType === 'week') {
                                  const w = availableWeeks.find(wk => dateOnly >= wk.startDate && dateOnly <= wk.endDate);
                                  if (w) {
                                    startDate = w.startDate;
                                    endDate = w.endDate;
                                  } else {
                                    startDate = endDate = dateOnly;
                                  }
                                } else if (periodType === 'month') {
                                  const ym = dateOnly.length >= 7 ? dateOnly.substring(0, 7) : dateOnly;
                                  const y = parseInt(ym.slice(0, 4), 10);
                                  const m = parseInt(ym.slice(5, 7), 10);
                                  startDate = `${ym}-01`;
                                  const lastDay = new Date(y, m, 0).getDate();
                                  endDate = `${ym}-${String(lastDay).padStart(2, '0')}`;
                                } else {
                                  startDate = endDate = dateOnly;
                                }
                                setSelectedDailySummaryRow({ vehicleId: row.vehicleId, date: row.date });
                                setMapModalRow({
                                  vehicleId: row.vehicleId,
                                  date: row.date,
                                  periodLabel: row.periodLabel,
                                  orderIds: (row.orderIds ?? []) as string[],
                                  pickupOrderIds: ((row as { pickupOrderIds?: string[] }).pickupOrderIds ?? []) as string[],
                                  deliveryOrderIds: ((row as { deliveryOrderIds?: string[] }).deliveryOrderIds ?? []) as string[],
                                  startDate,
                                  endDate,
                                });
                                setMapModalOrder(null);
                              }}
                              title="Click to view map for this vehicle/date"
                            >
                              <MapPin 
                                className="w-5 h-5 text-primary flex-shrink-0 transition-all duration-200" 
                              />
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-foreground font-mono text-sm tabular-nums" title="Daily miles from telematics">
                        {(row.totalMileage ?? 0) > 0
                          ? (row.totalMileage ?? 0).toLocaleString(undefined, { maximumFractionDigits: 1 })
                          : '—'}
                      </td>
                      {useTruth && useTruthUtil && (
                      <td
                        className="px-4 py-3 text-right text-foreground font-mono text-sm tabular-nums"
                        title={(row as DailySummarySortRow).utilisationTooltip ?? 'Utilisation not computed for this vehicle-day'}
                      >
                        {(row as DailySummarySortRow).utilisationScore != null
                          ? `${(row as DailySummarySortRow).utilisationScore!.toFixed(1)}%`
                          : '—'}
                      </td>
                      )}
                      <td
                        className="px-4 py-3 text-right text-foreground font-mono text-sm tabular-nums group relative overflow-visible"
                        title="Pick-up count (right-click for IDs when available)"
                        onContextMenu={(e) => {
                          const orderIds = ((row as { pickupOrderIds?: string[] }).pickupOrderIds ?? []) as string[];
                          if (orderIds.length === 0) return;
                          e.preventDefault();
                          setRevenueOrdersModalSort({ field: 'trackingId', asc: true });
                          setRevenueOrdersModal({
                            mode: 'orders',
                            orderIds,
                            orderRevenues: [],
                            orderSameDayFlags: [],
                            currency: row.currency || 'GBP',
                            totalRevenue: 0,
                            periodLabel: row.periodLabel,
                            date: row.date,
                            vehicleId: row.vehicleId,
                          });
                        }}
                      >
                        {(() => {
                          const ids = ((row as { pickupOrderIds?: string[] }).pickupOrderIds ?? []) as string[];
                          const n = ids.length > 0
                            ? ids.length
                            : ((row as { pickupCount?: number }).pickupCount ?? 0);
                          return n > 0 ? n.toLocaleString() : '—';
                        })()}
                      </td>
                      <td
                        className="px-4 py-3 text-right text-foreground font-mono text-sm tabular-nums group relative overflow-visible"
                        title="Delivery count (right-click for IDs)"
                        onContextMenu={(e) => {
                          const orderIds = ((row as { deliveryOrderIds?: string[] }).deliveryOrderIds
                            ?? (row.orderIds ?? [])) as string[];
                          if (orderIds.length === 0) return;
                          e.preventDefault();
                          const orderSameDayFlags = ((row as { orderSameDayFlags?: boolean[] }).orderSameDayFlags ?? []) as boolean[];
                          setRevenueOrdersModalSort({ field: 'trackingId', asc: true });
                          setRevenueOrdersModal({
                            mode: 'orders',
                            orderIds,
                            orderRevenues: [],
                            orderSameDayFlags,
                            currency: row.currency || 'GBP',
                            totalRevenue: 0,
                            periodLabel: row.periodLabel,
                            date: row.date,
                            vehicleId: row.vehicleId,
                          });
                        }}
                      >
                        {(() => {
                          const orderIds = ((row as { deliveryOrderIds?: string[] }).deliveryOrderIds
                            ?? (row.orderIds ?? [])) as string[];
                          const n = orderIds.length > 0
                            ? orderIds.length
                            : ((row as { deliveryCount?: number }).deliveryCount
                              ?? row.orderCount
                              ?? 0);
                          return n > 0 ? n.toLocaleString() : '—';
                        })()}
                      </td>
                      <td
                        className="px-4 py-3 text-right text-white cursor-help group relative overflow-visible"
                        title="Right-click to view all orders and revenue"
                        onMouseEnter={(e) => {
                          const tooltip = e.currentTarget.querySelector('.revenue-orders-tooltip') as HTMLElement;
                          if (tooltip) {
                            const rect = e.currentTarget.getBoundingClientRect();
                            tooltip.style.left = `${rect.left + rect.width / 2}px`;
                            tooltip.style.top = `${rect.top}px`;
                          }
                        }}
                        onContextMenu={(e) => {
                          const hasCosts = (row.totalFuelCost ?? 0) > 0 || (row.totalDriverCost ?? 0) > 0;
                          const hasRevenue = (row.totalRevenue ?? 0) > 0;
                          if (hasCosts && !hasRevenue) return;
                          if (!row.currency || !hasRevenue) return;
                          e.preventDefault();
                          const orderIds = (row.orderIds ?? []) as string[];
                          const orderRevenues = ((row as { orderRevenues?: string[] }).orderRevenues ?? []) as string[];
                          const orderRevenueImputed = ((row as { orderRevenueImputed?: string[] }).orderRevenueImputed ?? []) as string[];
                          const orderSameDayFlags = ((row as { orderSameDayFlags?: boolean[] }).orderSameDayFlags ?? []) as boolean[];
                          setRevenueOrdersModalSort({ field: 'trackingId', asc: true });
                          setRevenueOrdersModal({
                            mode: 'revenue',
                            orderIds,
                            orderRevenues,
                            orderRevenueImputed,
                            orderSameDayFlags,
                            currency: row.currency,
                            totalRevenue: row.totalRevenue ?? 0,
                            periodLabel: row.periodLabel,
                            date: row.date,
                            vehicleId: row.vehicleId,
                          });
                        }}
                      >
                        {(() => {
                          // Revenue-only row: has revenue but no telematics mileage — show revenue
                          // Costs-only row: has mileage but no revenue — show '—'
                          const hasCosts = (row.totalFuelCost ?? 0) > 0 || (row.totalDriverCost ?? 0) > 0;
                          const hasRevenue = (row.totalRevenue ?? 0) > 0;
                          const orderIds = (row.orderIds ?? []) as string[];
                          const orderRevenues = ((row as { orderRevenues?: string[] }).orderRevenues ?? []) as string[];
                          const orderRevenueImputed = ((row as { orderRevenueImputed?: string[] }).orderRevenueImputed ?? []) as string[];
                          const hasOrders = (row.orderCount ?? orderIds.length) > 0;
                          if (hasCosts && !hasRevenue) return <span className="text-muted-foreground">—</span>;
                          return (
                            <>
                              <span
                                className={`group-hover:underline decoration-dotted ${hasRevenue && hasOrders ? 'cursor-pointer' : ''}`}
                                onClick={() => {
                                  if (hasRevenue && hasOrders) {
                                    setSelectedDailySummaryRow({ vehicleId: row.vehicleId, date: row.date });
                                    setSelectedOrderIds(orderIds);
                                    setTimeout(() => qargoOrderSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
                                  }
                                }}
                                role={hasRevenue && hasOrders ? 'button' : undefined}
                                tabIndex={hasRevenue && hasOrders ? 0 : undefined}
                                onKeyDown={hasRevenue && hasOrders ? (ev) => {
                                  if (ev.key === 'Enter' || ev.key === ' ') {
                                    ev.preventDefault();
                                    setSelectedDailySummaryRow({ vehicleId: row.vehicleId, date: row.date });
                                    setSelectedOrderIds(orderIds);
                                    setTimeout(() => qargoOrderSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
                                  }
                                } : undefined}
                              >
                                {row.currency ? row.totalRevenue.toLocaleString(undefined, {
                                  style: 'currency',
                                  currency: row.currency,
                                  maximumFractionDigits: 0,
                                }) : '—'}
                              </span>
                              {hasRevenue && hasOrders && (
                                <div className="revenue-orders-tooltip fixed top-auto left-auto w-72 max-w-screen-90 min-w-56 p-4 bg-popover border border-border rounded-lg shadow-xl text-sm text-foreground font-sans z-float opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" style={{ transform: 'translate(-50%, calc(-100% - 8px))' }}>
                                  <div className="font-semibold text-chart-5 mb-2 pb-2 border-b border-border text-center">Revenue Calculation</div>
                                  <div className="text-left space-y-1">
                                    <div><span className="text-muted-foreground">Total orders:</span> <span className="text-white font-medium">{row.orderCount ?? orderIds.length}</span></div>
                                    {orderIds.length > 0 && (
                                      <div className="mt-2">
                                        <span className="text-muted-foreground block mb-1">Orders (ID → revenue):</span>
                                        <ul className="list-disc list-inside text-xs max-h-40 overflow-y-auto space-y-0.5">
                                          {orderIds.map((oid: string, i: number) => {
                                            const rev = orderRevenues[i];
                                            const imp = orderRevenueImputed[i];
                                            const revStr = rev !== undefined && rev !== '' ? Number(rev).toLocaleString(undefined, { style: 'currency', currency: row.currency || 'GBP', maximumFractionDigits: 2 }) : '—';
                                            const modelledLabel = imp === '15' ? ' (Modelled £15)' : imp === '25' ? ' (Modelled £25)' : '';
                                            return (
                                              <li key={`${oid}-${i}`} className="font-mono truncate" title={oid}>
                                                {oid} → {revStr}{modelledLabel && <span className="text-warning"> {modelledLabel}</span>}
                                              </li>
                                            );
                                          })}
                                        </ul>
                                      </div>
                                    )}
                                  </div>
                                  <div className="pt-2 mt-2 border-t border-border font-semibold text-chart-5 text-center">
                                    {row.currency ? (row.totalRevenue ?? 0).toLocaleString(undefined, { style: 'currency', currency: row.currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
                                  </div>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </td>
                      <td 
                        className="px-4 py-3 text-right text-chart-2 font-mono text-sm tabular-nums cursor-help group relative overflow-visible"
                        onMouseEnter={(e) => {
                          const tooltip = e.currentTarget.querySelector('.fuel-cost-tooltip') as HTMLElement;
                          if (tooltip) {
                            const rect = e.currentTarget.getBoundingClientRect();
                            tooltip.style.left = `${rect.left + rect.width / 2}px`;
                            tooltip.style.top = `${rect.top}px`;
                          }
                        }}
                      >
                        {(() => {
                          // Revenue-only row: has revenue but no mileage/costs — show '—'
                          const hasCosts = (row.totalFuelCost ?? 0) > 0 || (row.totalDriverCost ?? 0) > 0;
                          const hasRevenue = (row.totalRevenue ?? 0) > 0;
                          if (hasRevenue && !hasCosts) return <span className="text-muted-foreground">—</span>;
                          return (
                            <>
                              <span className="group-hover:underline decoration-dotted">
                                {row.currency ? (row.totalFuelCost ?? 0).toLocaleString(undefined, {
                                  style: 'currency',
                                  currency: row.currency,
                                  maximumFractionDigits: 0,
                                }) : '—'}
                              </span>
                              {row.currency && (row.totalFuelCost ?? 0) > 0 && (
                                <div className="fuel-cost-tooltip fixed top-auto left-auto w-80 min-w-72 p-4 bg-background border border-border rounded-lg shadow-xl text-center text-sm text-foreground font-sans z-float opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" style={{ transform: 'translate(-50%, calc(-100% - 8px))' }}>
                                  <div className="font-semibold text-chart-2 mb-2 pb-2 border-b border-border">Fuel Cost Calculation</div>
                                  <div className="text-left mt-2 mb-2"><span className="text-muted-foreground">Formula:</span> Miles × £/mile (vehicle type from Configuration)</div>
                                  <div className="border-b border-border mb-2" aria-hidden />
                                  <div className="text-left mb-1"><span className="text-muted-foreground">Miles:</span> {(row.totalMileage ?? 0).toFixed(1)}</div>
                                  <div className="text-left mb-1"><span className="text-muted-foreground">Rate:</span> £{(row.fuelRate ?? getFuelCostPerMile(row.costClassLabel ?? resolveVehicleCostClassLabel(row.vehicleId), config.profitability)).toFixed(2)}/mile <span className="text-muted-foreground">({row.costClassLabel ?? resolveVehicleCostClassLabel(row.vehicleId)})</span></div>
                                  <div className="pt-2 mt-2 border-t border-border font-semibold text-chart-2 text-center">
                                    £{(row.totalFuelCost ?? 0).toFixed(2)}
                                  </div>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </td>
                      <td 
                        className="px-4 py-3 text-right text-chart-7 font-mono text-sm tabular-nums cursor-help group relative overflow-visible"
                        onMouseEnter={(e) => {
                          const tooltip = e.currentTarget.querySelector('.driver-cost-tooltip') as HTMLElement;
                          if (tooltip) {
                            const rect = e.currentTarget.getBoundingClientRect();
                            tooltip.style.left = `${rect.left + rect.width / 2}px`;
                            tooltip.style.top = `${rect.top}px`;
                          }
                        }}
                      >
                        {(() => {
                          // Revenue-only row: has revenue but no mileage/costs — show '—'
                          const hasCosts = (row.totalFuelCost ?? 0) > 0 || (row.totalDriverCost ?? 0) > 0;
                          const hasRevenue = (row.totalRevenue ?? 0) > 0;
                          if (hasRevenue && !hasCosts) return <span className="text-muted-foreground">—</span>;
                          return (
                            <>
                              <span className="group-hover:underline decoration-dotted">
                                {row.currency ? (row.totalDriverCost ?? 0).toLocaleString(undefined, {
                                  style: 'currency',
                                  currency: row.currency,
                                  maximumFractionDigits: 0,
                                }) : '—'}
                              </span>
                              {row.currency && (row.totalDriverCost ?? 0) > 0 && (
                                <div className="driver-cost-tooltip fixed top-auto left-auto w-80 min-w-72 p-4 bg-background border border-border rounded-lg shadow-xl text-center text-sm text-foreground font-sans z-float opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" style={{ transform: 'translate(-50%, calc(-100% - 8px))' }}>
                                  <div className="font-semibold text-chart-7 mb-2 pb-2 border-b border-border">Driver Cost Calculation</div>
                                  <div className="text-left mt-2 mb-2"><span className="text-muted-foreground">Formula:</span> (Miles × £/mile) + (Driving hrs × £/hr) + (Waiting hrs × £/hr)</div>
                                  <div className="border-b border-border mb-2" aria-hidden />
                                  {(() => {
                                    const costClass =
                                      row.costClassLabel ?? resolveVehicleCostClassLabel(row.vehicleId);
                                    const rates = resolveDriverRatesForDisplay(
                                      costClass,
                                      profitabilityAssumptions,
                                      driverConfig,
                                    );
                                    const milesCost = (row.totalMileage ?? 0) * rates.mileagePerMile;
                                    const drivingCost = (row.totalDrivingHours ?? 0) * rates.workingHourly;
                                    const waitingCost = (row.totalWaitingHours ?? 0) * rates.hourly;
                                    const tooltipTotal = milesCost + drivingCost + waitingCost;
                                    return (
                                      <>
                                        <div className="text-left mb-1 text-xs text-muted-foreground">Rate class: {rates.costClassLabel}</div>
                                        <div className="text-left mb-1"><span className="text-muted-foreground">I. Miles:</span> {(row.totalMileage ?? 0).toFixed(1)} × £{rates.mileagePerMile.toFixed(2)}/mile =<span className="text-chart-7"> £{milesCost.toFixed(2)}</span></div>
                                        <div className="text-left mb-1"><span className="text-muted-foreground">II. Driving hrs:</span> {(row.totalDrivingHours ?? 0).toFixed(1)} × £{rates.workingHourly.toFixed(2)}/hr =<span className="text-chart-7"> £{drivingCost.toFixed(2)}</span></div>
                                        <div className="text-left mb-1"><span className="text-muted-foreground">III. Waiting hrs:</span> {(row.totalWaitingHours ?? 0).toFixed(1)} × £{rates.hourly.toFixed(2)}/hr =<span className="text-chart-7"> £{waitingCost.toFixed(2)}</span></div>
                                        <div className="pt-2 mt-2 border-t border-border font-semibold text-chart-7 text-center">
                                          £{tooltipTotal.toFixed(2)}
                                        </div>
                                      </>
                                    );
                                  })()}
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3 text-right text-foreground font-mono text-sm tabular-nums">
                        {row.currency ? (row.totalDepreciation ?? 0).toLocaleString(undefined, {
                          style: 'currency',
                          currency: row.currency,
                          maximumFractionDigits: 0,
                        }) : '—'}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold font-mono text-sm tabular-nums">
                        {row.currency ? (() => {
                          const profit = (row.totalRevenue ?? 0) - (row.totalFuelCost ?? 0) - (row.totalDriverCost ?? 0) - (row.totalDepreciation ?? 0);
                          return (
                            <span className={profit >= 0 ? 'text-primary' : 'text-destructive'}>
                              {profit.toLocaleString(undefined, {
                                style: 'currency',
                                currency: row.currency,
                                minimumFractionDigits: Math.abs(profit) < 1 ? 1 : 0,
                                maximumFractionDigits: Math.abs(profit) < 1 ? 1 : 0,
                              })}
                            </span>
                          );
                        })() : '—'}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-secondary/50">
                  <tr>
                    <td colSpan={useTruth && useTruthUtil ? 12 : 11} className="px-0 py-0">
                      <div className="h-0.5 bg-white shadow-glow-neutral-lg animate-pulse" aria-hidden />
                    </td>
                  </tr>
                  <tr className="text-foreground font-semibold">
                    <td className="px-2 py-3"></td>
                    <td className="px-4 py-3">Total</td>
                    <td className="px-4 py-3 text-muted-foreground"></td>
                    <td className="px-4 py-3 text-right text-foreground font-mono tabular-nums">
                      {dailySummaryTotals.totalMileage.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                    </td>
                    {useTruth && useTruthUtil && (
                    <td className="px-4 py-3 text-right text-muted-foreground font-mono tabular-nums">
                      —
                    </td>
                    )}
                    <td className="px-4 py-3 text-right text-foreground font-mono tabular-nums">
                      {dailySummaryTotals.totalPickups.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right text-foreground font-mono tabular-nums">
                      {dailySummaryTotals.totalDeliveries.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right text-chart-5 font-mono tabular-nums">
                      {dailySummaryTotals.totalRevenue.toLocaleString(undefined, { style: 'currency', currency: dailySummaryTotals.currency, maximumFractionDigits: 0 })}
                    </td>
                    <td 
                      className="px-4 py-3 text-right text-chart-2 font-mono tabular-nums cursor-help group relative overflow-visible"
                      onMouseEnter={(e) => {
                        const tooltip = e.currentTarget.querySelector('.total-fuel-cost-tooltip') as HTMLElement;
                        if (tooltip) {
                          const rect = e.currentTarget.getBoundingClientRect();
                          tooltip.style.left = `${rect.left + rect.width / 2}px`;
                          tooltip.style.top = `${rect.top}px`;
                        }
                      }}
                    >
                      <span className="group-hover:underline decoration-dotted">
                        {dailySummaryTotals.totalFuelCost.toLocaleString(undefined, { style: 'currency', currency: dailySummaryTotals.currency, maximumFractionDigits: 0 })}
                      </span>
                      <div className="total-fuel-cost-tooltip fixed top-auto left-auto w-80 min-w-72 p-4 bg-background border border-border rounded-lg shadow-xl text-center text-sm text-foreground font-sans z-float opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" style={{ transform: 'translate(-50%, calc(-100% - 8px))' }}>
                        <div className="font-semibold text-chart-2 mb-2 pb-2 border-b border-border">Total Fuel Cost</div>
                        <div className="text-muted-foreground">Sum of all individual fuel costs</div>
                        <div className="pt-2 mt-2 border-t border-border font-semibold text-chart-2 text-center">
                          {dailySummaryTotals.totalFuelCost.toLocaleString(undefined, { style: 'currency', currency: dailySummaryTotals.currency, minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </div>
                    </td>
                    <td 
                      className="px-4 py-3 text-right text-chart-7 font-mono tabular-nums cursor-help group relative overflow-visible"
                      onMouseEnter={(e) => {
                        const tooltip = e.currentTarget.querySelector('.total-driver-cost-tooltip') as HTMLElement;
                        if (tooltip) {
                          const rect = e.currentTarget.getBoundingClientRect();
                          tooltip.style.left = `${rect.left + rect.width / 2}px`;
                          tooltip.style.top = `${rect.top}px`;
                        }
                      }}
                    >
                      <span className="group-hover:underline decoration-dotted">
                        {dailySummaryTotals.totalDriverCost.toLocaleString(undefined, { style: 'currency', currency: dailySummaryTotals.currency, maximumFractionDigits: 0 })}
                      </span>
                      <div className="total-driver-cost-tooltip fixed top-auto left-auto w-80 min-w-72 p-4 bg-background border border-border rounded-lg shadow-xl text-center text-sm text-foreground font-sans z-float opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" style={{ transform: 'translate(-50%, calc(-100% - 8px))' }}>
                        <div className="font-semibold text-chart-7 mb-2 pb-2 border-b border-border">Total Driver Cost</div>
                        {(() => {
                          const fmt = (n: number) => n.toLocaleString(undefined, { style: 'currency', currency: dailySummaryTotals.currency, minimumFractionDigits: 2, maximumFractionDigits: 2 });
                          return (
                            <>
                              <div className="text-muted-foreground mb-2">Sum of per-row driver costs (rates from Assumptions by cost class)</div>
                              <div className="pt-2 mt-2 border-t border-border font-semibold text-chart-7 text-center">
                                {fmt(dailySummaryTotals.totalDriverCost)}
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-foreground font-mono tabular-nums">
                      {dailySummaryTotals.totalDepreciation.toLocaleString(undefined, { style: 'currency', currency: dailySummaryTotals.currency, maximumFractionDigits: 0 })}
                    </td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums font-semibold">
                      {(() => {
                        const totalProfit = dailySummaryTotals.totalRevenue - dailySummaryTotals.totalFuelCost - dailySummaryTotals.totalDriverCost - dailySummaryTotals.totalDepreciation;
                        return (
                          <span className={totalProfit >= 0 ? 'text-primary' : 'text-destructive'}>
                            {totalProfit.toLocaleString(undefined, { style: 'currency', currency: dailySummaryTotals.currency, maximumFractionDigits: 0 })}
                          </span>
                        );
                      })()}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Pagination for Daily Summary */}
          {summaryTotalPages > 1 && (
            <div className="flex items-center justify-between py-4 mt-4 border-t border-border">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                {(() => {
                  if (filteredSummaryData.length === 0) {
                    return <span>Showing 0 results</span>;
                  }
                  const start = (summaryPage - 1) * summaryRowsPerPage + 1;
                  const end = Math.min(summaryPage * summaryRowsPerPage, filteredSummaryData.length);
                  return (
                    <>
                      <span>
                        Showing {start}-{end} of {filteredSummaryData.length} results
                      </span>
                      <span className="text-muted-foreground"></span>
                      <div className="flex items-center gap-1">
                        <span className="text-xs">Show</span>
                        <select
                          className="bg-background border border-border rounded px-2 py-1 text-xs text-foreground focus:ring-ring focus:border-ring"
                          value={summaryRowsPerPage}
                          onChange={e => {
                            setSummaryRowsPerPage(Number(e.target.value));
                            setSummaryPage(1);
                          }}
                        >
                          {[10, 25, 50, 100].map(n => (
                            <option key={n} value={n}>{n}</option>
                          ))}
                        </select>
                        <span className="text-xs">per page</span>
                      </div>
                    </>
                  );
                })()}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground min-w-18 text-center">Page {summaryPage}/{summaryTotalPages}</span>
                <Button
                  type="button"
                  variant="ghost"
                  className="h-auto px-2 py-1 text-muted-foreground hover:text-foreground"
                  disabled={summaryPage === 1}
                  onClick={() => setSummaryPage(summaryPage - 1)}
                >
                  &lt;
                </Button>
                <input
                  type="number"
                  min={1}
                  max={summaryTotalPages}
                  value={summaryPage}
                  onChange={e => {
                    let val = Number(e.target.value);
                    if (val < 1) val = 1;
                    if (val > summaryTotalPages) val = summaryTotalPages;
                    setSummaryPage(val);
                  }}
                  className="w-12 text-center bg-background border border-border rounded px-2 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                />
                <Button
                  type="button"
                  variant="ghost"
                  className="h-auto px-2 py-1 text-muted-foreground hover:text-foreground"
                  disabled={summaryPage === summaryTotalPages}
                  onClick={() => setSummaryPage(summaryPage + 1)}
                >
                  &gt;
                </Button>
              </div>
            </div>
          )}
        </section>

      {/* Qargo's Order Section */}
      <section ref={qargoOrderSectionRef} className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center justify-between mb-3 gap-2">
          <div className="flex flex-col gap-1">
            <h3 className="text-sm font-semibold text-chart-5 flex items-center gap-2">
              <ClipboardList className="w-4 h-4 shrink-0 text-chart-5" />
              {isDemoMode ? 'Order profitability' : "Order"}
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              One row per delivery order: customer, route, planned vs actual vehicle, and booked revenue.
              <span className="block mt-1 text-muted-foreground">Click a row to open the route map for that vehicle-day.</span>
            </p>
          </div>
          {(() => {
            const hasFilter = selectedOrderIds.length > 0 || selectedCustomerNames.length > 0 || selectedVehicleIds.length > 0 || selectedPeriodRange != null;
            return (
              <Button
                type="button"
                size="sm"
                variant={hasFilter ? 'warning' : 'secondary'}
                onClick={() => {
                  setSelectedOrderIds([]);
                  setSelectedVehicleIds([]);
                  setSelectedSummaryDate('');
                  setOrderNameInputValue('');
                  setSelectedCustomerNames([]);
                }}
                className={hasFilter
                  ? 'bg-warning/20 border border-warning/60 text-warning hover:bg-warning/30'
                  : 'border border-border'}
                title={hasFilter ? 'Clear order/vehicle/date filters' : 'No filters applied'}
              >
                <X className="w-3.5 h-3.5" aria-hidden />
                Clear Filters
              </Button>
            );
          })()}
        </div>

        {loading ? (
          <div className="py-6">
            <div className="animate-pulse space-y-3">
              <div className="h-4 w-56 bg-secondary/40 rounded" />
              <div className="h-10 w-full bg-card/40 rounded-lg border border-border/40" />
              <div className="h-10 w-full bg-card/40 rounded-lg border border-border/40" />
              <div className="h-10 w-full bg-card/40 rounded-lg border border-border/40" />
              <div className="h-10 w-full bg-card/40 rounded-lg border border-border/40" />
            </div>
          </div>
        ) : error ? (
          <div className="text-sm text-destructive py-6 text-center">{error}</div>
        ) : (
          <div>
            {customerProfitabilityQargo.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                {isDemoMode
                  ? 'No orders with a delivery date in the selected period for this vehicle.'
                  : 'No orders with a collection or delivery date in the selected period. Widen the date range or run Compute on the Jobs panel if data may be out of date.'}
              </p>
            ) : (
              <div className="overflow-x-auto" style={{overflow: 'visible'}}>
                <table className="min-w-full text-xs text-foreground">
                  <thead style={{overflow: 'visible'}}>
                  <tr className="bg-background/60 text-muted-foreground" style={{overflow: 'visible'}}>
                    <th
                      className="px-2 py-2 text-left font-medium cursor-pointer hover:bg-muted transition"
                      onClick={() => handleSort('orderId')}
                    >
                      <div className="flex items-center gap-1">
                        <span>Order ID</span>
                        {sortField === 'orderId' && (
                          <span className="text-primary">{sortAsc ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-2 py-2 text-left font-medium cursor-pointer hover:bg-muted transition"
                      onClick={() => handleSort('customer')}
                    >
                      <div className="flex items-center gap-1">
                        <span>Customer</span>
                        {sortField === 'customer' && (
                          <span className="text-primary">{sortAsc ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-2 py-2 text-left font-medium cursor-pointer hover:bg-muted transition"
                      onClick={() => handleSort('originDate')}
                    >
                      <div className="flex items-center gap-1">
                        <span>Origin Date</span>
                        {sortField === 'originDate' && (
                          <span className="text-primary">{sortAsc ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-2 py-2 text-left font-medium cursor-pointer hover:bg-muted transition"
                      onClick={() => handleSort('originCity')}
                    >
                      <div className="flex items-center gap-1">
                        <span>Origin → Destination</span>
                        {sortField === 'originCity' && (
                          <span className="text-primary">{sortAsc ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-2 py-2 text-left font-medium cursor-pointer hover:bg-muted transition"
                      onClick={() => handleSort('vehiclePickup')}
                      title="Pick-up / trunk vehicle (earlier ORDER_ON_VEHICLE leg on multi-leg jobs)"
                    >
                      <div className="flex items-center gap-1">
                        <span>Pick-up vehicle</span>
                        {sortField === 'vehiclePickup' && (
                          <span className="text-primary">{sortAsc ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-2 py-2 text-left font-medium cursor-pointer hover:bg-muted transition"
                      onClick={() => handleSort('vehicleObserved')}
                      title="Final carrying vehicle (delivery leg from ORDER_ON_VEHICLE)"
                    >
                      <div className="flex items-center gap-1">
                        <span>Delivery vehicle</span>
                        {sortField === 'vehicleObserved' && (
                          <span className="text-primary">{sortAsc ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-2 py-2 text-left font-medium cursor-pointer hover:bg-muted transition"
                      onClick={() => handleSort('serviceLevel')}
                    >
                      <div className="flex items-center gap-1">
                        <span>Service</span>
                        {sortField === 'serviceLevel' && (
                          <span className="text-primary">{sortAsc ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-2 py-2 text-right font-medium cursor-pointer hover:bg-muted transition"
                      onClick={() => handleSort('marginPct')}
                      title="Profit as a percentage of revenue for this order"
                    >
                      <div className="flex items-center gap-1 justify-end">
                        Margin %
                        {sortField === 'marginPct' && (
                          <span className="text-primary">{sortAsc ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-2 py-2 text-right font-medium cursor-pointer bg-chart-5 text-white hover:bg-chart-5/80 transition relative group"
                      onClick={() => handleSort('revenue')}
                      title="Revenue booked in your transport system for this order"
                    >
                      <div className="flex items-center gap-1 justify-end">
                        Revenue
                        {sortField === 'revenue' && (
                          <span className="text-white">{sortAsc ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-2 py-2 text-right font-medium cursor-pointer hover:bg-muted transition"
                      onClick={() => handleSort('cost')}
                      title="Order share of that day's running costs (fuel, driver, depreciation), matched to GPS delivery stops where possible"
                    >
                      <div className="flex items-center gap-1 justify-end">
                        <span>Cost</span>
                        {sortField === 'cost' && (
                          <span className="text-primary">{sortAsc ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-2 py-2 text-right font-medium cursor-pointer hover:bg-muted transition"
                      onClick={() => handleSort('profit')}
                    >
                      <div className="flex items-center gap-1 justify-end">
                        <span>Profit</span>
                        {sortField === 'profit' && (
                          <span className="text-primary">{sortAsc ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody style={{overflow: 'visible'}}>
                  {paginatedQargo.map((row, index) => {
                    const prevRow = paginatedQargo[index - 1];
                    const dateForDivider = (r: typeof row) => sortField === 'date' ? r.date : (r.origin_end_timestamp_local ?? r.origin_end_timestamp ?? r.date);
                    const showDateDivider = (sortField === 'date' || sortField === 'originDate') && index > 0 && getDateOnly(dateForDivider(prevRow)) !== getDateOnly(dateForDivider(row));
                    return (
                      <>
                        {showDateDivider && (
                          <tr key={`divider-${row.date}`}>
                            <td colSpan={11} className="px-0 py-0">
                              <div className="border-t-2 border-primary" aria-hidden />
                            </td>
                          </tr>
                        )}
                        {(() => {
                            const qargoRowKey = `${row.orderId ?? ''}|${row.vehicleId ?? ''}|${row.date ?? ''}`;
                            const isQargoRowSelected = selectedQargoOrderKey === qargoRowKey;
                            return (
                        <tr
                          key={`qargo-${row.vehicleId}-${row.date}-${row.orderId}`}
                          className={`border-t border-border/60 hover:bg-secondary/40 transition-colors cursor-pointer [&_td]:min-h-9 ${isQargoRowSelected ? 'bg-primary/20 ring-2 ring-inset ring-primary' : ''}`}
                          onClick={() => {
                            const observedId = row.vehicle_observed ?? row.vehicleId ?? '';
                            const pickupId = row.vehicle_pickup ?? '';
                            const vehicleId = observedId || pickupId;
                            const dateStr = row.destination_end_timestamp_local ?? row.date ?? '';
                            setSelectedQargoOrderKey(qargoRowKey);
                            if (vehicleId && dateStr) {
                              const oid = String(row.orderId ?? '').trim();
                              const emptyIds = new Set<string>();
                              const role = orderRoleOnVehicleMap(row, vehicleId, emptyIds, emptyIds);
                              const pu = standardiseVehicleId(pickupId);
                              const del = standardiseVehicleId(observedId);
                              const isMultiLeg = Boolean(pu && del && pu !== del);
                              setMapModalRow({
                                vehicleId,
                                date: dateStr,
                                orderIds: oid ? [oid] : [],
                                // Multi-leg: keep both lists so journey pins resolve; modal vehicle stays delivery plate.
                                pickupOrderIds: oid && (role === 'pickup' || isMultiLeg) ? [oid] : [],
                                deliveryOrderIds: oid && (role === 'delivery' || isMultiLeg) ? [oid] : [],
                              });
                              setMapModalOrder(row);
                            }
                          }}
                          title="Click to view route map for this order"
                        >
                          <td className="px-2 py-2 whitespace-nowrap text-xs text-foreground font-mono" title={row.orderId}>
                            {row.orderId ?? '—'}
                          </td>
                          <td className="px-2 py-2 text-xs text-foreground truncate max-w-35" title={row.customer ?? ''}>
                            {(row.customer ?? '').trim() || '—'}
                          </td>
                          <td className="px-2 py-2 text-xs text-foreground whitespace-nowrap">
                            {(() => {
                              const originDateStr = row.origin_end_timestamp_local ?? row.origin_end_timestamp ?? row.date ?? '';
                              return originDateStr ? getDateOnly(originDateStr) : '—';
                            })()}
                          </td>
                          <td className="px-2 py-2 text-xs text-foreground truncate max-w-50" title={formatRoute(row.origin_city, row.destination_city)}>
                            {formatRoute(row.origin_city, row.destination_city) || '—'}
                          </td>
                          <td className="px-2 py-2 text-xs text-foreground font-mono truncate max-w-25" title={row.vehicle_pickup ?? ''}>
                            {row.vehicle_pickup ? standardiseVehicleId(row.vehicle_pickup) : '—'}
                          </td>
                          <td className="px-2 py-2 text-xs text-foreground font-mono truncate max-w-25" title={row.vehicle_observed ?? row.vehicleId ?? ''}>
                            {(row.vehicle_observed ?? row.vehicleId) ? standardiseVehicleId(row.vehicle_observed ?? row.vehicleId ?? '') : '—'}
                          </td>
                          <td className="px-2 py-2 text-xs text-foreground">
                            {row.service_level_name || '—'}
                          </td>
                          <td
                            className={`px-2 py-2 text-right text-xs font-mono tabular-nums ${
                              (() => {
                                const margin = orderMarginPct(row as ProfitRecord);
                                if (margin == null) return 'text-muted-foreground';
                                return margin >= 0 ? 'text-primary' : 'text-destructive';
                              })()
                            }`}
                            title="Profit ÷ revenue for this order"
                          >
                            {(() => {
                              const margin = orderMarginPct(row as ProfitRecord);
                              return margin == null ? '—' : `${margin.toFixed(1)}%`;
                            })()}
                          </td>
                          <td
                            className={`px-2 py-2 text-right align-middle ${((row as ProfitRecord).revenue_imputed === 15 || (row as ProfitRecord).revenue_imputed === 25) ? 'text-warning bg-warning/10 ring-1 ring-warning/50 rounded' : ''}`}
                            style={{ minHeight: '36px', boxSizing: 'border-box' }}
                          >
                            <div className="flex items-center justify-end min-h-7 w-15 ml-auto box-border">
                              {((row as ProfitRecord).revenue_imputed === 15 || (row as ProfitRecord).revenue_imputed === 25) ? (
                                <span
                                  className="text-warning text-xs py-0.5"
                                  title={(row as ProfitRecord).revenue_imputed === 15 ? 'Revenue was missing; estimated £15 (PALLETLINE)' : 'Revenue was missing; estimated £25 (HAZCHEM)'}
                                >
                                  {(row as ProfitRecord).revenue_imputed === 15 ? '£15' : '£25'}
                                </span>
                              ) : (
                                <span className="inline-block w-15 text-right py-0.5 truncate text-foreground" title={String(row.revenue)}>
                                  {row.revenue.toLocaleString(undefined, { style: 'currency', currency: row.currency || 'GBP', maximumFractionDigits: 0 })}
                                </span>
                              )}
                            </div>
                          </td>
                          <td
                            className={`px-2 py-2 text-right text-xs font-mono tabular-nums ${
                              (row as ProfitRecord).allocation_method === 'fallback_equal_split'
                                ? 'text-warning'
                                : 'text-foreground'
                            }`}
                            title={
                              (row as ProfitRecord).allocation_method === 'fallback_equal_split'
                                ? 'Estimated equal split — not enough GPS delivery stops to match this order precisely'
                                : (row as ProfitRecord).allocation_method === 'marginal'
                                  ? 'Matched to GPS route mileage and that day’s vehicle running costs'
                                  : 'Run Compute on the Jobs panel to calculate order cost and profit'
                            }
                          >
                            {(row as ProfitRecord).allocation_method != null
                              ? (row.cost ?? 0).toLocaleString(undefined, {
                                  style: 'currency',
                                  currency: row.currency || 'GBP',
                                  maximumFractionDigits: 0,
                                })
                              : '—'}
                          </td>
                          <td
                            className={`px-2 py-2 text-right text-xs font-mono tabular-nums ${
                              (row as ProfitRecord).allocation_method != null
                                ? (row.profit ?? 0) >= 0
                                  ? 'text-primary'
                                  : 'text-destructive'
                                : 'text-muted-foreground'
                            }`}
                          >
                            {(row as ProfitRecord).allocation_method != null
                              ? (row.profit ?? 0).toLocaleString(undefined, {
                                  style: 'currency',
                                  currency: row.currency || 'GBP',
                                  maximumFractionDigits: 0,
                                })
                              : '—'}
                          </td>
                        </tr>
                            );
                          })()}
                      </>
                    );
                  })}
                </tbody>
                <tfoot className="bg-secondary/50">
                  <tr>
                    <td colSpan={11} className="px-0 py-0">
                      <div className="h-0.5 bg-white shadow-glow-neutral-lg animate-pulse" aria-hidden />
                    </td>
                  </tr>
                  <tr className="text-foreground font-semibold">
                    <td className="px-2 py-3">Total</td>
                    <td colSpan={6} className="px-4 py-3 text-foreground">
                      {customerProfitabilityQargo.length} {customerProfitabilityQargo.length === 1 ? 'order' : 'orders'}
                    </td>
                    <td className={`px-2 py-3 text-right font-mono tabular-nums ${qargoOrderMarginPct != null && qargoOrderMarginPct >= 0 ? 'text-primary' : qargoOrderMarginPct != null ? 'text-destructive' : 'text-muted-foreground'}`}>
                      {qargoOrderMarginPct != null ? `${qargoOrderMarginPct.toFixed(1)}%` : '—'}
                    </td>
                    <td className="px-2 py-3 text-right text-chart-5 font-mono tabular-nums">
                      {qargoOrderTotals.totalRevenue > 0
                        ? qargoOrderTotals.totalRevenue.toLocaleString(undefined, { style: 'currency', currency: qargoOrderTotals.currency, maximumFractionDigits: 0 })
                        : '—'}
                    </td>
                    <td className="px-2 py-3 text-right text-foreground font-mono tabular-nums">
                      {qargoOrderTotals.hasAllocatedCost
                        ? qargoOrderTotals.totalCost.toLocaleString(undefined, { style: 'currency', currency: qargoOrderTotals.currency, maximumFractionDigits: 0 })
                        : '—'}
                    </td>
                    <td className={`px-2 py-3 text-right font-mono tabular-nums ${qargoOrderTotals.hasAllocatedCost && qargoOrderTotals.totalProfit >= 0 ? 'text-primary' : qargoOrderTotals.hasAllocatedCost ? 'text-destructive' : 'text-muted-foreground'}`}>
                      {qargoOrderTotals.hasAllocatedCost
                        ? qargoOrderTotals.totalProfit.toLocaleString(undefined, { style: 'currency', currency: qargoOrderTotals.currency, maximumFractionDigits: 0 })
                        : '—'}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Pagination for Qargo's Order */}
          {qargoOrderTotalPages > 1 && (
            <div className="flex items-center justify-between py-4 mt-4 border-t border-border">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                {(() => {
                  const recordsToPage = customerProfitabilityQargo;
                  if (recordsToPage.length === 0) {
                    return <span>Showing 0 results</span>;
                  }
                  const start = (page - 1) * rowsPerPage + 1;
                  const end = Math.min(page * rowsPerPage, recordsToPage.length);
                  return (
                    <>
                      <span>
                        Showing {start}-{end} of {recordsToPage.length} results
                      </span>
                      <span className="text-muted-foreground"></span>
                      <div className="flex items-center gap-1">
                        <span className="text-xs">Show</span>
                        <select
                          className="bg-background border border-border rounded px-2 py-1 text-xs text-foreground focus:ring-ring focus:border-ring"
                          value={rowsPerPage}
                          onChange={e => {
                            setRowsPerPage(Number(e.target.value));
                            setPage(1);
                          }}
                        >
                          {[10, 25, 50, 100].map(n => (
                            <option key={n} value={n}>{n}</option>
                          ))}
                        </select>
                        <span className="text-xs">per page</span>
                      </div>
                    </>
                  );
                })()}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground min-w-18 text-center">Page {page}/{qargoOrderTotalPages}</span>
                <Button
                  type="button"
                  variant="ghost"
                  className="h-auto px-2 py-1 text-muted-foreground hover:text-foreground"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  &lt;
                </Button>
                <input
                  type="number"
                  min={1}
                  max={qargoOrderTotalPages}
                  value={page}
                  onChange={e => {
                    let val = Number(e.target.value);
                    if (val < 1) val = 1;
                    if (val > qargoOrderTotalPages) val = qargoOrderTotalPages;
                    setPage(val);
                  }}
                  className="w-12 text-center bg-background border border-border rounded px-2 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                />
                <Button
                  type="button"
                  variant="ghost"
                  className="h-auto px-2 py-1 text-muted-foreground hover:text-foreground"
                  disabled={page === qargoOrderTotalPages}
                  onClick={() => setPage(page + 1)}
                >
                  &gt;
                </Button>
              </div>
            </div>
          )}
          </div>
        )}
      </section>

      {/* Customer Profitability Section */}
      <section
        ref={customerProfitabilitySectionRef}
        className="bg-card border border-border rounded-lg p-4 mt-6"
      >
        <div className="flex items-center justify-between mb-3 gap-2">
          <div className="flex flex-col gap-1">
            <h3 className="text-sm font-semibold text-chart-5 flex items-center gap-2">
              <Users className="w-4 h-4 shrink-0 text-chart-5" />
              Customer
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Customer profit by day based on allocated miles and costs.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="py-6">
            <div className="animate-pulse space-y-3">
              <div className="h-4 w-56 bg-secondary/40 rounded" />
              <div className="h-10 w-full bg-card/40 rounded-lg border border-border/40" />
              <div className="h-10 w-full bg-card/40 rounded-lg border border-border/40" />
              <div className="h-10 w-full bg-card/40 rounded-lg border border-border/40" />
              <div className="h-10 w-full bg-card/40 rounded-lg border border-border/40" />
            </div>
          </div>
        ) : error ? (
          <div className="text-sm text-destructive py-6 text-center">{error}</div>
            ) : (
              <div>
                {customerProfitabilitySummary.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    No orders with customer data in the selected period.
                  </p>
                ) : customerProfitabilitySummaryFiltered.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    No Customer Profitability rows for the selected customer(s) in this period.
                  </p>
                ) : (
                  <>
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs text-foreground">
                  <thead>
                    <tr className="bg-background/60 text-muted-foreground">
                      <th className="px-2 py-2 text-left font-medium cursor-pointer hover:bg-muted transition" onClick={() => { setCustomerSortField('deliveryDate'); setCustomerSortAsc(customerSortField === 'deliveryDate' ? !customerSortAsc : false); }}>
                        <div className="flex items-center gap-1"><span>Period</span>{customerSortField === 'deliveryDate' && <span className="text-chart-5">{customerSortAsc ? '↑' : '↓'}</span>}</div>
                      </th>
                      <th className="px-2 py-2 text-left font-medium cursor-pointer hover:bg-muted transition" onClick={() => { setCustomerSortField('customer'); setCustomerSortAsc(customerSortField === 'customer' ? !customerSortAsc : true); }}>
                        <div className="flex items-center gap-1"><span>Customer</span>{customerSortField === 'customer' && <span className="text-chart-5">{customerSortAsc ? '↑' : '↓'}</span>}</div>
                      </th>
                      <th className="px-2 py-2 text-right font-medium cursor-pointer hover:bg-muted transition" onClick={() => { setCustomerSortField('orderCount'); setCustomerSortAsc(customerSortField === 'orderCount' ? !customerSortAsc : false); }}>
                        <div className="flex items-center justify-end gap-1"><span>Orders</span>{customerSortField === 'orderCount' && <span className="text-chart-5">{customerSortAsc ? '↑' : '↓'}</span>}</div>
                      </th>
                      <th className="px-2 py-2 text-right font-medium cursor-pointer hover:bg-muted transition" onClick={() => { setCustomerSortField('totalAllocatedMiles'); setCustomerSortAsc(customerSortField === 'totalAllocatedMiles' ? !customerSortAsc : false); }}>
                        <div className="flex items-center justify-end gap-1"><span>Miles</span>{customerSortField === 'totalAllocatedMiles' && <span className="text-chart-5">{customerSortAsc ? '↑' : '↓'}</span>}</div>
                      </th>
                      <th className="px-2 py-2 text-right font-medium cursor-pointer hover:bg-muted transition" onClick={() => { setCustomerSortField('costPerMileGbp'); setCustomerSortAsc(customerSortField === 'costPerMileGbp' ? !customerSortAsc : false); }}>
                        <div className="flex items-center justify-end gap-1"><span>Cost / Mile</span>{customerSortField === 'costPerMileGbp' && <span className="text-chart-5">{customerSortAsc ? '↑' : '↓'}</span>}</div>
                      </th>
                      <th className="px-2 py-2 text-right font-medium cursor-pointer hover:bg-muted transition" onClick={() => { setCustomerSortField('profitMarginPct'); setCustomerSortAsc(customerSortField === 'profitMarginPct' ? !customerSortAsc : false); }}>
                        <div className="flex items-center justify-end gap-1"><span>Margin %</span>{customerSortField === 'profitMarginPct' && <span className="text-chart-5">{customerSortAsc ? '↑' : '↓'}</span>}</div>
                      </th>
                      <th className="px-2 py-2 text-right font-medium cursor-pointer bg-chart-5 text-white hover:bg-chart-5/80 transition" onClick={() => { setCustomerSortField('totalRevenue'); setCustomerSortAsc(customerSortField === 'totalRevenue' ? !customerSortAsc : false); }}>
                        <div className="flex items-center justify-end gap-1"><span>Revenue</span>{customerSortField === 'totalRevenue' && <span className="opacity-90">{customerSortAsc ? '↑' : '↓'}</span>}</div>
                      </th>
                      <th className="px-2 py-2 text-right font-medium cursor-pointer hover:bg-muted transition" onClick={() => { setCustomerSortField('totalAllocatedCost'); setCustomerSortAsc(customerSortField === 'totalAllocatedCost' ? !customerSortAsc : false); }}>
                        <div className="flex items-center justify-end gap-1"><span>Cost</span>{customerSortField === 'totalAllocatedCost' && <span className="text-chart-5">{customerSortAsc ? '↑' : '↓'}</span>}</div>
                      </th>
                      <th className="px-2 py-2 text-right font-medium cursor-pointer hover:bg-muted transition" onClick={() => { setCustomerSortField('totalProfit'); setCustomerSortAsc(customerSortField === 'totalProfit' ? !customerSortAsc : false); }}>
                        <div className="flex items-center justify-end gap-1"><span>Profit</span>{customerSortField === 'totalProfit' && <span className="text-chart-5">{customerSortAsc ? '↑' : '↓'}</span>}</div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerPaginatedSummary.map((row) => (
                      <tr key={`${row.customer}|${row.deliveryDate}`} className="border-t border-border/60 hover:bg-secondary/40 transition-colors">
                        <td className="px-2 py-2 text-foreground whitespace-nowrap">{row.deliveryDate || '—'}</td>
                        <td className="px-2 py-2 text-foreground">{row.customer === '—' ? <span className="text-muted-foreground">—</span> : titleCase(row.customer)}</td>
                        <td className="px-2 py-2 text-right text-foreground tabular-nums">{row.orderCount}</td>
                        <td className="px-2 py-2 text-right text-foreground tabular-nums">
                          {row.totalAllocatedMiles > 0
                            ? row.totalAllocatedMiles.toLocaleString(undefined, { maximumFractionDigits: 1 })
                            : '—'}
                        </td>
                        <td className="px-2 py-2">
                          {row.costPerMileGbp > 0 ? (
                            <div className="flex items-center justify-end gap-2">
                              <span className="text-right text-foreground tabular-nums min-w-16">
                                {row.costPerMileGbp.toLocaleString(undefined, {
                                  style: 'currency',
                                  currency: row.currency || 'GBP',
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}
                              </span>
                              <div className="h-3 w-25 bg-accent rounded-full overflow-hidden shrink-0">
                                {(() => {
                                  const min = customerCostPerMileRange.min;
                                  const max = customerCostPerMileRange.max;
                                  const MIN_VISIBLE_BAR_PCT = 6;
                                  let rawWidthPct = 0;
                                  let invScore = 100;
                                  if (max <= 0) {
                                    rawWidthPct = 0;
                                  } else if (max === min) {
                                    rawWidthPct = 100;
                                  } else {
                                    const norm = (row.costPerMileGbp - min) / (max - min);
                                    const inv = 1 - norm;
                                    rawWidthPct = Math.max(0, Math.min(100, inv * 100));
                                    invScore = (1 - ((row.costPerMileGbp - min) / (max - min))) * 100;
                                  }
                                  const emptyWorst =
                                    rawWidthPct <= 0 && max > 0 && max !== min;
                                  const tooNarrow =
                                    rawWidthPct > 0 && rawWidthPct < MIN_VISIBLE_BAR_PCT;
                                  const useMinRedDot = emptyWorst || tooNarrow;
                                  const widthPct = useMinRedDot ? MIN_VISIBLE_BAR_PCT : rawWidthPct;
                                  const bgColor = useMinRedDot
                                    ? STATUS_COLORS.destructive
                                    : getCustomerCostPerMileBarColor(invScore);
                                  return (
                                    <div
                                      className="h-full rounded-full transition-all duration-700"
                                      style={{ width: `${widthPct}%`, backgroundColor: bgColor }}
                                    />
                                  );
                                })()}
                              </div>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className={`px-2 py-2 text-right tabular-nums ${row.profitMarginPct >= 0 ? 'text-primary' : 'text-destructive'}`}>{row.profitMarginPct.toFixed(1)}%</td>
                        <td className="px-2 py-2 text-right text-foreground tabular-nums">{row.totalRevenue.toLocaleString(undefined, { style: 'currency', currency: row.currency || 'GBP', maximumFractionDigits: 0 })}</td>
                        <td className="px-2 py-2 text-right text-foreground tabular-nums">{row.totalAllocatedCost.toLocaleString(undefined, { style: 'currency', currency: row.currency || 'GBP', maximumFractionDigits: 0 })}</td>
                        <td className={`px-2 py-2 text-right tabular-nums font-medium ${row.totalProfit >= 0 ? 'text-primary' : 'text-destructive'}`}>{row.totalProfit.toLocaleString(undefined, { style: 'currency', currency: row.currency || 'GBP', maximumFractionDigits: 0 })}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-secondary/50">
                    <tr className="text-foreground font-semibold border-t border-border">
                      <td className="px-2 py-2">Total</td>
                      <td className="px-2 py-2 text-muted-foreground">—</td>
                      <td className="px-2 py-2 text-right text-foreground font-mono tabular-nums">{customerProfitabilityTotals.totalOrders}</td>
                      <td className="px-2 py-2 text-right text-foreground font-mono tabular-nums">
                        {customerProfitabilityTotals.totalAllocatedMiles > 0
                          ? customerProfitabilityTotals.totalAllocatedMiles.toLocaleString(undefined, { maximumFractionDigits: 1 })
                          : '—'}
                      </td>
                      <td className="px-2 py-2 text-right text-foreground font-mono tabular-nums">
                        {customerProfitabilityTotals.totalAllocatedMiles > 0
                          ? (customerProfitabilityTotals.totalCostPerMileNumerator / customerProfitabilityTotals.totalAllocatedMiles).toLocaleString(undefined, { style: 'currency', currency: customerProfitabilityTotals.currency, minimumFractionDigits: 2, maximumFractionDigits: 2 })
                          : '—'}
                      </td>
                      <td className={`px-2 py-2 text-right font-mono tabular-nums ${customerProfitabilityTotals.totalRevenue > 0 && (100 * customerProfitabilityTotals.totalProfit / customerProfitabilityTotals.totalRevenue) >= 0 ? 'text-primary' : 'text-destructive'}`}>
                        {customerProfitabilityTotals.totalRevenue > 0
                          ? `${(100 * customerProfitabilityTotals.totalProfit / customerProfitabilityTotals.totalRevenue).toFixed(1)}%`
                          : '0.0%'}
                      </td>
                      <td className="px-2 py-2 text-right text-foreground font-mono tabular-nums">{customerProfitabilityTotals.totalRevenue.toLocaleString(undefined, { style: 'currency', currency: customerProfitabilityTotals.currency, maximumFractionDigits: 0 })}</td>
                      <td className="px-2 py-2 text-right text-foreground font-mono tabular-nums">{customerProfitabilityTotals.totalAllocatedCost.toLocaleString(undefined, { style: 'currency', currency: customerProfitabilityTotals.currency, maximumFractionDigits: 0 })}</td>
                      <td className={`px-2 py-2 text-right font-mono tabular-nums ${customerProfitabilityTotals.totalProfit >= 0 ? 'text-primary' : 'text-destructive'}`}>{customerProfitabilityTotals.totalProfit.toLocaleString(undefined, { style: 'currency', currency: customerProfitabilityTotals.currency, maximumFractionDigits: 0 })}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Pagination for Customer Profitability - same UI as Daily Profitability */}
              {customerTotalPages > 1 && (
                <div className="flex items-center justify-between py-4 mt-4 border-t border-border">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>
                      Showing {(customerPage - 1) * customerRowsPerPage + 1}-{Math.min(customerPage * customerRowsPerPage, customerSortedSummary.length)} of {customerSortedSummary.length} results
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-xs">Show</span>
                      <select
                        className="bg-background border border-border rounded px-2 py-1 text-xs text-foreground focus:ring-ring focus:border-ring"
                        value={customerRowsPerPage}
                        onChange={e => {
                          setCustomerRowsPerPage(Number(e.target.value));
                          setCustomerPage(1);
                        }}
                      >
                        {[10, 25, 50, 100].map(n => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                      <span className="text-xs">per page</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground min-w-18 text-center">Page {customerPage}/{customerTotalPages}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      className="h-auto px-2 py-1 text-muted-foreground hover:text-foreground"
                      disabled={customerPage === 1}
                      onClick={() => setCustomerPage(customerPage - 1)}
                    >
                      &lt;
                    </Button>
                    <input
                      type="number"
                      min={1}
                      max={customerTotalPages}
                      value={customerPage}
                      onChange={e => {
                        let val = Number(e.target.value);
                        if (val < 1) val = 1;
                        if (val > customerTotalPages) val = customerTotalPages;
                        setCustomerPage(val);
                      }}
                      className="w-12 text-center bg-background border border-border rounded px-2 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      className="h-auto px-2 py-1 text-muted-foreground hover:text-foreground"
                      disabled={customerPage === customerTotalPages}
                      onClick={() => setCustomerPage(customerPage + 1)}
                    >
                      &gt;
                    </Button>
                  </div>
                </div>
              )}
                  </>
                )}
          </div>
        )}
      </section>
        </>
      )}
    </div>
  );
}
